import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type SupportedLanguage = 'en' | 'zh' | 'my' | 'th' | 'ja';

interface LanguageInfo {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  flag: string;
}

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { code: 'my', name: 'Burmese', nativeName: 'မြန်မာ', flag: '🇲🇲' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
];

// Translations object - exported for validation scripts
export const translations: Record<SupportedLanguage, Record<string, string>> = {
  en: {
    // Common
    'common.signIn': 'Sign In',
    'common.signUp': 'Sign Up',
    'common.signOut': 'Sign Out',
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.clear': 'Clear',
    'common.submit': 'Submit',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.viewAll': 'View All',
    'common.learnMore': 'Learn More',
    
    // Navigation
    'nav.home': 'Dashboard',
    'nav.dashboard': 'Dashboard',
    'nav.services': 'Services',
    'nav.rentals': 'Rentals',
    'nav.map': 'Map',
    'nav.wallet': 'Payments',
    'nav.settings': 'Settings',
    'nav.messages': 'Messages',
    'nav.notifications': 'Notifications',
    'nav.concierge': 'Concierge',
    
    // Landing Page - Hero
    'landing.hero.title': 'Run Your Properties',
    'landing.hero.titleAccent': 'Without the Chaos',
    'landing.hero.subtitle': 'One platform for payments, guest access, service bookings, and resident communication. Less admin. Happier residents.',
    'landing.hero.tagline': 'For Property Developers & HOAs',
    'landing.cta.getStarted': 'Get Started',
    'landing.cta.watchDemo': 'Watch Demo',
    'landing.cta.startTrial': 'Start Free Trial',
    'landing.cta.contactSales': 'Contact Sales',
    'landing.cta.contactSupport': 'Contact Support',
    'landing.cta.exploreProperties': 'Explore Properties',
    'landing.cta.browseRentals': 'Browse Rentals',
    'landing.trust.security': 'Bank-Level Security',
    'landing.trust.properties': '500+ Properties',
    'landing.trust.residents': '10,000+ Residents',
    'landing.nav.howItWorks': 'How It Works',
    'landing.nav.features': 'Features',
    'landing.nav.pricing': 'Pricing',
    'landing.nav.signIn': 'Sign In',
    'landing.howItWorks': 'How It Works',
    'landing.features': 'Features',
    'landing.pricing': 'Pricing',
    
    // Landing Page - How It Works
    'landing.howItWorks.title': 'Get Started in 3 Simple Steps',
    'landing.howItWorks.subtitle': 'Go from signup to fully operational in under 10 minutes.',
    'landing.howItWorks.step1.title': 'Create Your Account',
    'landing.howItWorks.step1.desc': 'Sign up with your email. Add your community details and invite your team.',
    'landing.howItWorks.step2.title': 'Add Properties & Residents',
    'landing.howItWorks.step2.desc': 'Import your property list or add them manually. Send invites to residents.',
    'landing.howItWorks.step3.title': 'Go Live',
    'landing.howItWorks.step3.desc': 'Start accepting payments, managing guest passes, and booking services immediately.',
    
    // Landing Page - Personas
    'landing.personas.title': 'Built for Everyone in Your Community',
    'landing.personas.subtitle': 'Whether you manage, own, or visit—Aether works for you.',
    'landing.persona.manager.role': 'Community Manager',
    'landing.persona.manager.tagline': 'Run operations, not errands',
    'landing.persona.manager.benefit1': 'Full dashboard with all properties at a glance',
    'landing.persona.manager.benefit2': 'Automated payment collection and reminders',
    'landing.persona.manager.benefit3': 'Manage vendors and service providers',
    'landing.persona.manager.benefit4': 'Send announcements to all residents instantly',
    'landing.persona.manager.quote': '"I used to spend 3 hours a day on admin. Now it\'s 30 minutes."',
    'landing.persona.owner.role': 'Property Owner',
    'landing.persona.owner.tagline': 'Your property, your way',
    'landing.persona.owner.benefit1': 'Pay HOA dues with one tap',
    'landing.persona.owner.benefit2': 'Book concierge services for your home',
    'landing.persona.owner.benefit3': 'List your property for short-term rentals',
    'landing.persona.owner.benefit4': 'Issue guest passes to visitors securely',
    'landing.persona.owner.quote': '"Finally, an app that treats my property like the asset it is."',
    'landing.persona.guest.role': 'Guest',
    'landing.persona.guest.tagline': 'Arrive like you belong',
    'landing.persona.guest.benefit1': 'QR code entry—no waiting at gates',
    'landing.persona.guest.benefit2': 'Explore community maps and amenities',
    'landing.persona.guest.benefit3': 'Message your host directly in-app',
    'landing.persona.guest.benefit4': 'Access valid only during your stay',
    'landing.persona.guest.quote': '"The smoothest check-in I\'ve ever experienced."',
    
    // Landing Page - Property Types
    'landing.propertyTypes.title': 'Built for Premium Communities',
    'landing.propertyTypes.subtitle': 'Whether you manage condos, gated communities, or luxury rentals—Aether scales with your portfolio.',
    'landing.propertyTypes.penthouse': 'Urban Penthouses',
    'landing.propertyTypes.penthouseDesc': 'Skyline views, rooftop terraces',
    'landing.propertyTypes.estate': 'Modern Estates',
    'landing.propertyTypes.estateDesc': 'Architectural masterpieces',
    'landing.propertyTypes.beachfront': 'Beachfront Retreats',
    'landing.propertyTypes.beachfrontDesc': 'Private shores, endless horizons',
    
    // Landing Page - Features
    'landing.features.title': 'Everything You Need, Nothing You Don\'t',
    'landing.features.subtitle': 'Replace 5+ tools with one platform. Save time and reduce costs from day one.',
    'landing.feature.portfolio.title': 'Centralized Portfolio Control',
    'landing.feature.portfolio.desc': 'Manage all your properties from one dashboard. No more juggling spreadsheets, emails, and apps.',
    'landing.feature.bookings.title': 'Automate Service Bookings',
    'landing.feature.bookings.desc': 'Let residents book cleaning, maintenance, and concierge services themselves—reducing your admin workload by 80%.',
    'landing.feature.messaging.title': 'Faster Response Times',
    'landing.feature.messaging.desc': 'Built-in messaging keeps owners, guests, and vendors connected—no missed calls or lost emails.',
    'landing.feature.payments.title': 'Collect Payments On Time',
    'landing.feature.payments.desc': 'Automated HOA dues collection and service payments. Reduce late payments by 60%.',
    'landing.feature.maps.title': 'Showcase Your Community',
    'landing.feature.maps.desc': 'Interactive maps help residents and guests discover amenities, increasing engagement and satisfaction.',
    'landing.feature.guests.title': 'Secure Guest Access',
    'landing.feature.guests.desc': 'QR-coded passes with time limits. Know who enters your property and when—eliminating security gaps.',
    
    // Landing Page - Pricing
    'landing.pricing.title': 'Simple, Predictable Pricing',
    'landing.pricing.subtitle': 'Choose the plan that fits your needs. Start with a free trial.',
    'landing.pricing.mostPopular': 'Most Popular',
    'landing.pricing.perMonth': 'month',
    'landing.pricing.perYear': 'year',
    'landing.pricing.custom': 'Custom',
    'landing.pricing.getStarted': 'Get Started',
    'landing.pricing.contactSales': 'Contact Sales',
    'landing.pricing.billingMonthly': 'Monthly',
    'landing.pricing.billingAnnual': 'Annual',
    
    // New Pricing Plans - Explorer
    'pricing.explorer.name': 'Explorer',
    'pricing.explorer.desc': 'For travelers seeking extraordinary stays',
    'pricing.explorer.feature1': 'Book luxury short-term rentals',
    'pricing.explorer.feature2': 'Book long-term leases',
    'pricing.explorer.feature3': 'AI Concierge assistance',
    'pricing.explorer.feature4': 'Secure messaging with hosts',
    'pricing.explorer.feature5': 'Priority booking support',
    
    // New Pricing Plans - Sovereign
    'pricing.sovereign.name': 'Sovereign',
    'pricing.sovereign.desc': 'For homeowners ready to rent their properties',
    'pricing.sovereign.feature1': 'List property for short-term rentals',
    'pricing.sovereign.feature2': 'List property for long-term leases',
    'pricing.sovereign.feature3': 'Dynamic pricing tools',
    'pricing.sovereign.feature4': 'Guest management dashboard',
    'pricing.sovereign.feature5': 'Revenue analytics',
    'pricing.sovereign.feature6': 'Priority host support',
    
    // New Pricing Plans - Architect
    'pricing.architect.name': 'Architect',
    'pricing.architect.desc': 'For developers managing communities',
    'pricing.architect.feature1': 'Create and manage communities',
    'pricing.architect.feature2': 'Invite and manage homeowners',
    'pricing.architect.feature3': 'HOA payment collection',
    'pricing.architect.feature4': 'Vendor marketplace management',
    'pricing.architect.feature5': 'Community announcements',
    'pricing.architect.feature6': 'Advanced analytics dashboard',
    'pricing.architect.feature7': 'Custom branding',
    
    // Pricing Modal & Common
    'pricing.monthly': 'Monthly',
    'pricing.annual': 'Annual',
    'pricing.save': 'Save {percent}%',
    'pricing.trialDays': '{days}-day free trial',
    'pricing.startTrial': 'Start Free Trial',
    'pricing.subscribe': 'Subscribe Now',
    'pricing.securePayment': 'Secure payment powered by Stripe',
    'pricing.redirecting': 'Redirecting to checkout...',
    'pricing.inviteRequired': 'Invite from community admin required',
    'pricing.enterpriseOnboarding': 'Enterprise onboarding required',
    'pricing.error': 'Failed to create subscription. Please try again.',
    
    // Pricing Triggers
    'pricing.trigger.rentalMode': 'Subscribe to list your property for rent',
    'pricing.trigger.booking': 'Subscribe to book this property',
    'pricing.trigger.community': 'Subscribe to create and manage communities',
    'pricing.trigger.default': 'Upgrade your account',
    
    // Landing Page - Testimonials
    'landing.testimonials.title': 'Property Managers Love Aether',
    'landing.testimonials.subtitle': 'See how teams are saving time and keeping residents happy.',
    'landing.testimonial1.quote': 'We cut our admin time in half. Residents book services themselves and payments come in automatically.',
    'landing.testimonial1.author': 'Alexander Chen',
    'landing.testimonial1.role': 'Property Developer, Beverly Hills',
    'landing.testimonial2.quote': 'Guest access used to be a nightmare. Now visitors get a QR code and we have a full audit trail.',
    'landing.testimonial2.author': 'Victoria Sterling',
    'landing.testimonial2.role': 'HOA Board President, Miami',
    'landing.testimonial3.quote': 'Our residents love the app. Service requests dropped 40% because they can find answers themselves.',
    'landing.testimonial3.author': 'Marcus Williams',
    'landing.testimonial3.role': 'Community Manager, 200+ units',
    
    // Landing Page - CTA Section
    'landing.cta.title': 'Ready to Simplify Your Operations?',
    'landing.cta.subtitle': 'Set up in minutes. See results in days. No contracts, cancel anytime.',
    
    // Landing Page - FAQ
    'landing.faq.title': 'Frequently Asked Questions',
    'landing.faq.subtitle': 'Everything you need to know about getting started with Aether.',
    'landing.faq.stillQuestions': 'Still have questions?',
    'landing.faq.q1': 'How long does it take to set up Aether for my community?',
    'landing.faq.a1': 'Most communities are fully operational within 10-15 minutes. Simply create your account, add your properties and residents, and you\'re ready to go. Our onboarding wizard guides you through every step, and our support team is available 24/7 if you need assistance.',
    'landing.faq.q2': 'Is my data secure with Aether?',
    'landing.faq.a2': 'Absolutely. We use bank-level 256-bit SSL encryption for all data transmissions. Your data is stored in SOC 2 Type II certified data centers with 99.99% uptime. We also offer two-factor authentication and role-based access controls to ensure only authorized users can access sensitive information.',
    'landing.faq.q3': 'Can I try Aether before committing to a paid plan?',
    'landing.faq.a3': 'Yes! Guests can sign up directly and enjoy a 15-day free trial to book stays. Homeowners receive a 30-day free trial after being invited by their community administrator. Property developers should contact our sales team for a demo.',
    'landing.faq.q4': 'How does payment processing work?',
    'landing.faq.a4': 'Aether integrates with Stripe for secure payment processing. Residents can pay HOA dues, service fees, and rental charges directly through the app. Funds are deposited into your account within 2-3 business days. We charge a flat platform fee—no hidden per-transaction charges.',
    'landing.faq.q5': 'How do homeowners and developers join Aether?',
    'landing.faq.a5': 'Homeowners are invited by their community administrator. Once invited, they receive an email to create their account and get a 30-day free trial. Property developers contact our sales team for enterprise onboarding and custom pricing.',
    'landing.faq.q6': 'What support is available if I have issues?',
    'landing.faq.a6': 'All plans include email support with response times under 24 hours. Pro plans get priority support with 4-hour response times. Enterprise customers receive a dedicated account manager and 24/7 phone support. We also offer comprehensive documentation and video tutorials.',
    'landing.faq.contactSupport': 'Contact Support',
    
    // Landing Page - Footer
    'landing.footer.tagline': 'Sovereign Living',
    'landing.footer.copyright': '© {{year}} Aether. Property management, simplified.',
    'landing.footer.privacy': 'Privacy',
    'landing.footer.terms': 'Terms',
    'landing.footer.support': 'Support',
    
    // Google Auth
    'auth.orContinueWith': 'Or continue with',
    'auth.signInWithGoogle': 'Sign in with Google',
    'auth.googleSignInError': 'Failed to sign in with Google',
    
    // Auth
    'auth.welcomeBack': 'Welcome Back',
    'auth.createAccount': 'Create Account',
    'auth.resetPassword': 'Reset Password',
    'auth.setPassword': 'Set Your Password',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.forgotPassword': 'Forgot password?',
    'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account?',
    'auth.backToHome': 'Back to Home',
    
    // Dashboard
    'dashboard.welcome': 'Welcome back',
    'dashboard.properties': 'Properties',
    'dashboard.bookings': 'Bookings',
    'dashboard.payments': 'Payments',
    'dashboard.guests': 'Guests',
    
    // Rentals
    'rentals.title': 'Discover Exceptional Properties',
    'rentals.subtitle': 'Experience world-class amenities and personalized concierge services',
    'rentals.searchPlaceholder': 'Search by location or community...',
    'rentals.pricePerNight': 'per night',
    'rentals.bedrooms': 'bedrooms',
    'rentals.bathrooms': 'bathrooms',
    'rentals.bookNow': 'Book Now',
    'rentals.checkIn': 'Check-in',
    'rentals.checkOut': 'Check-out',
    'rentals.guests': 'Guests',
    'rentals.noResults': 'No properties match your filters',
    'rentals.askAI': 'Ask AI to find rentals',
    
    // AI Assistant
    'ai.greeting': 'Hello! I\'m your Aether concierge. How can I assist you today?',
    'ai.placeholder': 'Ask me anything...',
    'ai.listening': 'Listening...',
    'ai.findRentals': 'Find available rentals',
    
    // Settings
    'settings.title': 'Settings',
    'settings.language': 'Language',
    'settings.currency': 'Currency',
    'settings.timezone': 'Timezone',
    'settings.notifications': 'Notifications',
    'settings.appearance': 'Appearance',
    'settings.darkMode': 'Dark Mode',
    'settings.profile': 'Profile Information',
    'settings.profileDesc': 'Name, phone, avatar',
    'settings.notificationsDesc': 'Email and push preferences',
    'settings.payment': 'Payment Methods',
    'settings.paymentDesc': 'Manage cards and billing',
    'settings.subscriptions': 'Subscriptions',
    'settings.subscriptionsDesc': 'SaaS and HOA plans',
    'settings.guestPasses': 'Guest Passes',
    'settings.guestPassesDesc': 'Manage visitor access',
    'settings.languageCurrency': 'Language & Currency',
    'settings.languageCurrencyDesc': 'Regional preferences',
    'settings.smartHome': 'Smart Home Sharing',
    'settings.smartHomeDesc': 'Share Google Home access with guests',
    'settings.viewAs': 'View As (Role Simulator)',
    'settings.viewAsDesc': 'Test different user experiences',
    'settings.signOut': 'Sign Out',
    
    // Messages
    'messages.title': 'Messages',
    'messages.subtitle': 'Conversations, AI, and notifications',
    'messages.chats': 'Chats',
    'messages.aether': 'Aether',
    'messages.alerts': 'Alerts',
    'messages.noConversations': 'No conversations yet',
    'messages.noConversationsDesc': 'Messages with guests and property owners will appear here',
    'messages.noNotifications': 'No notifications',
    'messages.noNotificationsDesc': 'Community announcements will appear here',
    'messages.markAllRead': 'Mark all read',
    'messages.startConversation': 'Start Conversation',
    'messages.aetherTitle': 'Aether AI Concierge',
    'messages.aetherDesc': 'Your personal AI assistant for booking services, getting recommendations, and managing your property needs.',
    
    // Browse
    'nav.browse': 'Explore',
    
    // Dashboard specific
    'dashboard.guestAccess': 'Guest Access',
    'dashboard.accessExpires': 'Access Expires In',
    'dashboard.daysRemaining': 'days remaining of your guest access',
    'dashboard.browseRentals': 'Browse Rentals',
    'dashboard.services': 'Services',
    'dashboard.personalizeSearch': 'Personalize Your Search',
    'dashboard.updatePreferences': 'Update your rental preferences',
    'dashboard.availableServices': 'Available Services',
    'dashboard.communityMap': 'Community Map',
    'dashboard.exploreAmenities': 'Explore amenities',
    'dashboard.dining': 'Dining',
    'dashboard.requestMeals': 'Request meals',
    'dashboard.transport': 'Transport',
    'dashboard.requestValet': 'Request valet',
    'dashboard.housekeeping': 'Housekeeping',
    'dashboard.ownerOnly': 'Owner only',
    'dashboard.guestGuidelines': 'Guest Guidelines',
    'dashboard.propertyManager': 'Property Manager',
    'dashboard.guestAtProperty': 'Guest at this property',
    'dashboard.guidelineQuietHours': 'Quiet hours are from 10 PM to 7 AM',
    'dashboard.guidelinePoolGym': 'Pool and gym access available 6 AM - 10 PM',
    'dashboard.guidelineConcierge': 'Contact concierge for any assistance',
    'dashboard.guidelineRespect': 'Respect all community amenities and spaces',
    'dashboard.greetingMorning': 'Good Morning',
    'dashboard.greetingAfternoon': 'Good Afternoon',
    'dashboard.greetingEvening': 'Good Evening',
    'dashboard.loading': 'Loading your dashboard...',
    'dashboard.welcomeAether': 'Welcome to Aether',
    'dashboard.yourSmartHome': 'Your Smart Home',
    'dashboard.allSystemsOperational': 'All Systems Operational',
    'dashboard.quickActions': 'Quick Actions',
    'dashboard.propertyStatus': 'Property Status',
    'dashboard.communityUpdates': 'Community Updates',
    'dashboard.upcoming': 'Upcoming',
    'dashboard.confirmed': 'Confirmed',
    'dashboard.rentalMode': 'Rental Mode',
    'dashboard.rentalModeDesc': 'List for short-term stays',
    'dashboard.rentalModeEnabled': 'Rental mode enabled',
    'dashboard.rentalModeDisabled': 'Rental mode disabled',
    'dashboard.createGuestPass': 'Create Guest Pass',
    'dashboard.guestEmail': 'Guest email address',
    'dashboard.accessDuration': 'Access Duration (days)',
    'dashboard.sendGuestPass': 'Send Guest Pass',
    'dashboard.needProperty': 'You need to own a property to create guest passes',
    'dashboard.privateDinner': 'Private Dinner',
    
    // AI Page
    'ai.title': 'Aether AI',
    'ai.subtitle': 'Your sovereign assistant',
    'ai.welcomeMessage': 'Hello! I\'m your AI concierge. I can help you with:',
    'ai.helpBooking': 'Booking services and appointments',
    'ai.helpGuest': 'Managing guest access',
    'ai.helpMaintenance': 'Property maintenance requests',
    'ai.helpLifestyle': 'Lifestyle recommendations',
    'ai.trySaying': 'Try saying',
    'ai.suggestion1': 'Book a private chef for Saturday',
    'ai.suggestion2': 'Schedule pool maintenance',
    'ai.suggestion3': 'Authorize my guest for tomorrow',
    'ai.suggestion4': 'What services are available?',
    
    // Role Simulator
    'roleSimulator.title': 'Role Simulator',
    'roleSimulator.description': 'Select a role to simulate the experience. A banner will show while simulating.',
    'roleSimulator.godMode': 'God Mode',
    'roleSimulator.godModeDesc': 'Platform-wide admin access',
    'roleSimulator.admin': 'Community Admin',
    'roleSimulator.adminDesc': 'Manage community properties & users',
    'roleSimulator.homeowner': 'Homeowner',
    'roleSimulator.homeownerDesc': 'Property owner experience',
    'roleSimulator.guest': 'Guest',
    'roleSimulator.guestDesc': 'Time-bound visitor access',
    'roleSimulator.active': 'Active',
    'roleSimulator.exitSimulation': 'Exit Simulation Mode',
    
    // Simulation Banner
    'simulation.simulating': 'Simulating',
    'simulation.exit': 'Exit Simulation',
    
    // Cookie Consent
    'cookies.message': 'We use cookies to enhance your experience.',
    'cookies.learnMore': 'Read our Privacy Policy',
    'cookies.accept': 'Accept',
    'cookies.dismiss': 'Dismiss',
    'common.close': 'Close',
    
    // Notification Center
    'notificationCenter.new': 'new',
    'notificationCenter.noNotifications': 'No notifications yet',
    'notificationCenter.noNotificationsDesc': "You'll see payment alerts and updates here",
    
    // Toast Messages
    'toast.loadFailed': 'Failed to load data',
    'toast.emailRequired': 'Email is required',
    'toast.inviteSent': 'Invitation sent successfully',
    'toast.inviteFailed': 'Failed to send invitation',
    'toast.addressRequired': 'Address is required',
    'toast.propertyAdded': 'Property added successfully',
    'toast.propertyAddFailed': 'Failed to add property',
    'toast.rentalModeEnabled': 'Rental mode enabled',
    'toast.rentalModeDisabled': 'Rental mode disabled',
    'toast.rentalModeUpdateFailed': 'Failed to update rental mode',
    'toast.rentalListingSaved': 'Rental listing saved successfully',
    'toast.rentalListingSaveFailed': 'Failed to save rental listing',
    'toast.communityNameRequired': 'Community name is required',
    'toast.communityCreated': 'Community created successfully',
    'toast.communityCreateFailed': 'Failed to create community',
    'toast.adminInviteSent': 'Admin invitation sent',
    'toast.inviteDeleted': 'Invite deleted. You can now reinvite.',
    'toast.deleteFailed': 'Failed to delete',
    'toast.listingSaved': 'Listing saved successfully',
    'toast.listingSaveFailed': 'Failed to save listing',
    'toast.calendarUpdated': 'Calendar updated',
    'toast.calendarUpdateFailed': 'Failed to update calendar',
    'toast.ownerInviteSent': 'Owner invitation sent successfully',
    'toast.updateFailed': 'Failed to update',
    'common.error': 'Error',
    'common.unknown': 'Unknown',
    
    // Smart Home (Phase 5)
    'smartHome.title': 'Smart Home Access',
    'smartHome.shareTitle': 'Share Smart Home with Guests',
    'smartHome.shareDesc': 'Use Google Home to give guests access to your smart devices',
    'smartHome.quickSteps': 'Quick Steps',
    'smartHome.step1': 'Open Google Home app',
    'smartHome.step2': 'Go to Settings → Household',
    'smartHome.step3': 'Invite guest using their email',
    'smartHome.remember': 'Remember',
    'smartHome.reminderText': 'Add guest access before check-in and remove after checkout',
    'smartHome.viewGuide': 'View Full Guide',
    'smartHome.worksWith': 'Works with:',
    'smartHome.devicesList': 'Nest, Ring, Yale, August, Philips Hue, and 50,000+ Google Home compatible devices',
    
    // Rental Widget (Phase 5)
    'rental.status': 'Rental Status',
    'rental.bookings': 'Bookings',
    'rental.pending': 'Pending',
    'rental.revenue': 'Revenue',
    'rental.rating': 'Rating',
    'rental.view': 'View',
    'rental.edit': 'Edit',
    'rental.calendar': 'Calendar',
    'rental.completeListing': 'Complete your listing to start earning',
    'rental.enableMode': 'Enable rental mode to list your property and start earning',
    'rental.enableButton': 'Enable Rental Mode',
    'rental.modeEnabled': 'Rental mode enabled',
    'rental.modeDisabled': 'Rental mode disabled',
    'rental.updateFailed': 'Failed to update rental mode',
    
    // Homeowner Dashboard Widget (Phase 5)
    'homeowner.upcomingHOA': 'Upcoming HOA Dues',
    'homeowner.payNow': 'Pay Now',
    'homeowner.noHOADues': 'No HOA dues configured',
    'homeowner.dueToday': 'Due today',
    'homeowner.daysLeft': '{days} days',
    'homeowner.communityUpdates': 'Community Updates',
    'homeowner.new': 'new',
    'homeowner.noAnnouncements': 'No recent announcements',
    
    
    // Transactions
    'transaction.pending': 'Pending',
    'transaction.processing': 'Processing',
    'transaction.completed': 'Completed',
    'transaction.failed': 'Failed',
    'transaction.refunded': 'Refunded',
    'transaction.type.hoa_dues': 'HOA Dues',
    'transaction.type.service_payment': 'Service Payment',
    'transaction.type.refund': 'Refund',
    'transaction.type.deposit': 'Deposit',
    
    // HOA Payments
    'hoa.monthlyDues': 'Monthly HOA Dues',
    'hoa.dueOnDay': 'Due on day {day} each month',
    'hoa.perMonth': '/month',
    'hoa.nextPayment': 'Next Payment',
    'hoa.payNow': 'Pay Now',
    'hoa.allCaughtUp': 'All caught up!',
    'hoa.notConfigured': 'HOA dues not configured for this property',
    'hoa.paymentHistory': 'Payment History',
    'hoa.noProperties': 'No properties found',
    'hoa.noPropertiesDesc': 'You need to own a property to pay HOA dues',
    'hoa.choosePaymentMethod': 'Choose Payment Method',
    'hoa.paid': 'Paid',
    'hoa.verifying': 'Verifying',
    
    // Concierge Page
    'concierge.title': 'Concierge',
    'concierge.subtitle': 'Bespoke services at your command',
    'concierge.askAether': 'Ask Aether',
    'concierge.services': 'Services',
    'concierge.myBookings': 'My Bookings',
    'concierge.serviceCategories': 'Service Categories',
    'concierge.yourBookings': 'Your Bookings',
    'concierge.viewAllBookings': 'View All Bookings',
    'concierge.manageBookings': 'View and manage your upcoming service bookings',
    'concierge.notAvailable': 'Concierge Not Available',
    'concierge.notAvailableDesc': 'Concierge services are not enabled for your community. Contact your administrator for more information.',
    'concierge.noCommunity': 'No Community Access',
    'concierge.noCommunityDesc': 'Concierge services will be available after joining a community.',
    'concierge.createCommunity': 'Create Your First Community',
    'concierge.noServices': 'Services Coming Soon',
    'concierge.noServicesDesc': 'Your community administrator is setting up concierge services. Check back soon!',
    
    // Service Categories
    'service.housekeeping': 'Housekeeping',
    'service.housekeepingDesc': 'Premium cleaning & laundry',
    'service.wellness': 'Wellness',
    'service.wellnessDesc': 'Spa, fitness & rejuvenation',
    'service.maintenance': 'Maintenance',
    'service.maintenanceDesc': 'Property care & repairs',
    'service.dining': 'Private Dining',
    'service.diningDesc': 'Curated culinary experiences',
    'service.transport': 'Transport',
    'service.transportDesc': 'Luxury vehicle services',
    
    // Service Marketplace
    'marketplace.title': 'Services',
    'marketplace.subtitle': 'Book premium services',
    'marketplace.all': 'All',
    'marketplace.noServices': 'No services available',
    'marketplace.checkBack': 'Check back later for new offerings',
    'marketplace.book': 'Book',
    'marketplace.estimatedPrice': 'Estimated Price',
    'marketplace.priceVaries': '*Final price may vary based on property size',
    'marketplace.scheduleDateTime': 'Schedule Date & Time',
    'marketplace.specialRequests': 'Special Requests (optional)',
    'marketplace.specialRequestsPlaceholder': 'Any special instructions...',
    'marketplace.payWithCard': 'Pay with Card (USD)',
    
    // Sovereign Dashboard (Property Manager)
    'sovereign.propertyManager': 'Property Manager',
    'sovereign.properties': 'properties',
    'sovereign.selectProperty': 'Select property',
    'sovereign.noPropertyTitle': 'No Property Found',
    'sovereign.noPropertyDesc': "You don't have any properties assigned to your account.",
    'sovereign.goToDashboard': 'Go to Dashboard',
    'sovereign.standaloneProperty': 'Standalone Property',
    'sovereign.community': 'Community',
    'sovereign.rentalMode': 'Rental Mode',
    'sovereign.rentalModeDesc': 'List property for short-term stays',
    'sovereign.totalBookings': 'Total Bookings',
    'sovereign.pending': 'Pending',
    'sovereign.revenue': 'Revenue',
    'sovereign.rating': 'Rating',
    'sovereign.bookings': 'Bookings',
    'sovereign.calendar': 'Calendar',
    'sovereign.listing': 'Listing',
    'sovereign.reviews': 'Reviews',
    'sovereign.smartHome': 'Home',
    'sovereign.guestReviews': 'Guest Reviews',
    'sovereign.enableRentalMode': 'Enable Rental Mode',
    'sovereign.enableRentalModeDesc': 'List your property on the marketplace for short-term rentals. Set your own rates, manage availability, and earn income from your property.',
    'sovereign.setRates': 'Set custom nightly rates and cleaning fees',
    'sovereign.controlCalendar': 'Control your availability calendar',
    'sovereign.manageGuests': 'Approve guests and manage bookings',
    
    // Admin Dashboard
    'admin.title': 'Dashboard',
    'admin.role': 'Admin',
    'admin.godMode': 'God Mode',
    'admin.properties': 'Properties',
    'admin.owners': 'Owners',
    'admin.collected': 'Collected',
    'admin.overview': 'Overview',
    'admin.forSale': 'For Sale',
    'admin.announcements': 'News',
    'admin.bookings': 'Bookings',
    'admin.services': 'Services',
    'admin.vendors': 'Vendors',
    'admin.earnings': 'Earnings',
    'admin.reviews': 'Reviews',
    'admin.more': 'More',
    'admin.hoaStatus': 'HOA Collection Status',
    'admin.paid': 'Paid',
    'admin.pending': 'Pending',
    'admin.overdue': 'Overdue',
    'admin.invite': 'Invite',
    'admin.inviteHomeowner': 'Invite Homeowner',
    'admin.addProperty': 'Add Property',
    'admin.sendInvitation': 'Send Invitation',
    'admin.noProperties': 'No properties yet',
    'admin.addFirstProperty': 'Add your first property to get started',
    'admin.moreProperties': 'more properties',
    'admin.unassigned': 'Unassigned',
    'admin.rental': 'Rental',
    'admin.rentalMode': 'Rental Mode',
    'admin.manageListing': 'Manage',
    'admin.rentalListingTitle': 'Manage Rental Listing',
    'admin.streetAddress': 'Street Address',
    'admin.unitNumber': 'Unit Number (optional)',
    'admin.bedrooms': 'Bedrooms',
    'admin.bathrooms': 'Bathrooms',
    'admin.manageAnnouncements': 'Manage Announcements',
    'admin.dragToReschedule': 'Drag to Reschedule',
    'admin.allReservations': 'All Reservations',
    'admin.createCommunity': 'Create New Community',
    'admin.createCommunityDesc': 'Set up a new community to manage properties and homeowners',
    'admin.communityMap': 'Community Map',
    'admin.welcome': 'Welcome',
    'admin.createFirstCommunity': 'Create Your First Community',
    'admin.createFirstCommunityDesc': 'Get started by creating a community to manage properties and homeowners.',
    
    // Browse Rentals
    'browse.title': 'Browse Rentals',
    'browse.luxuryStays': 'Luxury Stays',
    'browse.heroTitle': 'Discover Exceptional Properties',
    'browse.heroSubtitle': 'Experience world-class amenities and personalized concierge services in our exclusive communities',
    'browse.searchPlaceholder': 'Search by location or community...',
    'browse.filters': 'Filters',
    'browse.priceRange': 'Price Range (per night)',
    'browse.minBedrooms': 'Minimum Bedrooms',
    'browse.amenities': 'Amenities',
    'browse.any': 'Any',
    'browse.clearFilters': 'Clear All Filters',
    'browse.propertiesFound': '{count} properties found',
    'browse.propertyFound': '1 property found',
    'browse.noReviews': 'No reviews yet',
    'browse.luxuryProperty': 'Luxury Property',
    'browse.privateCommunity': 'Private Community',
    'browse.beds': 'beds',
    'browse.baths': 'baths',
    'browse.moreAmenities': '+{count} more',
    'browse.additionalAmenities': 'Additional amenities:',
    'browse.viewDetails': 'View Details',
    'browse.bookNow': 'Book Now',
    'browse.bookYourStay': 'Book Your Stay',
    'browse.booking': 'Booking',
    'browse.reviews': 'Reviews',
    'browse.addressRevealed': 'Full property address revealed after booking confirmation',
    'browse.checkIn': 'Check-in',
    'browse.checkOut': 'Check-out',
    'browse.selectDate': 'Select date',
    'browse.guests': 'Guests',
    'browse.nights': 'nights',
    'browse.cleaningFee': 'Cleaning fee',
    'browse.total': 'Total',
    'browse.confirmPay': 'Confirm & Pay',
    'browse.signInToBook': 'Sign in to Book',
    'browse.processing': 'Processing...',
    'browse.minNights': 'Min {min} nights',
    'browse.maxNights': 'Max {max} nights',
    'browse.noPropertiesMatch': 'No properties match your filters',
    'browse.tryAdjusting': 'Try adjusting your search criteria',
    
    // Listing Detail Page
    'listing.notFound': 'Listing Not Found',
    'listing.notFoundDesc': 'This property may no longer be available',
    'listing.browseRentals': 'Browse Rentals',
    'listing.viewAllPhotos': 'View all {count} photos',
    'listing.allPhotos': 'All Photos',
    'listing.about': 'About this property',
    'listing.aboutProperty': 'About this property',
    'listing.amenities': 'Amenities',
    'listing.houseRules': 'House Rules',
    'listing.sqft': 'sqft',
    'listing.night': 'night',
    'listing.image': 'Photo',
    'listing.payWithCard': 'Pay with Card',
    'listing.payWithKBZ': 'Pay with KBZPay',
    'listing.payWithWave': 'Pay with Wave Money',
    'listing.signInRequired': 'Please sign in to book this property',
    'listing.pleaseSignIn': 'Please sign in to book this property',
    'listing.selectDates': 'Please select check-in and check-out dates',
    'listing.minStay': 'Minimum stay is {min} nights',
    'listing.maxStay': 'Maximum stay is {max} nights',
    
    // Booking Confirmation Page
    'confirmation.invalid': 'Invalid Booking',
    'confirmation.invalidDesc': 'No booking ID was provided',
    'confirmation.browseProperties': 'Browse Properties',
    'confirmation.notFound': 'Booking Not Found',
    'confirmation.notFoundDesc': "We couldn't find this booking",
    'confirmation.confirmed': 'Booking Confirmed!',
    'confirmation.pending': 'Booking Pending',
    'confirmation.accessPass': 'Property Access Pass',
    'confirmation.propertyDetails': 'Property Details',
    'confirmation.stayDetails': 'Stay Details',
    'confirmation.paymentSummary': 'Payment Summary',
    'confirmation.leaveReview': 'Leave a Review',
    'confirmation.viewDirections': 'View Property Directions & Map',
    'confirmation.messageOwner': 'Message Property Owner',
    'confirmation.viewAllBookings': 'View All Bookings',
    'confirmation.goToDashboard': 'Go to Dashboard',
    'confirmation.checkIn': 'Check-in',
    'confirmation.checkOut': 'Check-out',
    'confirmation.guests': 'Guests',
    'confirmation.nights': 'nights',
    'confirmation.cleaningFee': 'Cleaning Fee',
    'confirmation.total': 'Total',
    'confirmation.paymentMethod': 'Payment Method',
    'confirmation.verificationPending': 'Payment verification pending',
    
    // Wallet Page
    'wallet.title': 'Wallet',
    'wallet.subtitle': 'Payments & transactions',
    'wallet.paymentMethods': 'Payment Methods',
    'wallet.hoaDues': 'HOA Dues',
    'wallet.serviceHistory': 'Service History',
    'wallet.viewAll': 'View All',
    'wallet.noTransactions': 'No transactions yet',
    'wallet.noTransactionsDesc': 'Book a service to see it here',
    'wallet.paymentSuccess': 'Payment Successful',
    'wallet.paymentSuccessDesc': 'Your payment has been processed.',
    'wallet.paymentCanceled': 'Payment Canceled',
    'wallet.paymentCanceledDesc': 'Your payment was canceled.',
    'wallet.cardAdded': 'Card Added',
    'wallet.cardAddedDesc': 'Your payment method has been saved.',
    'wallet.noPaymentMethods': 'No payment methods saved',
    'wallet.addCardDesc': 'Add a card to pay for services and dues',
    'wallet.addCard': 'Add Card',
    'wallet.continueAddCard': 'Continue to Add Card',
    'wallet.addPaymentMethod': 'Add Payment Method',
    'wallet.default': 'Default',
    'wallet.manage': 'Manage',
    'wallet.securePayment': 'Secure Payment',
    'wallet.cardSecure': 'Your card details are encrypted and secure',
    'wallet.stripeRedirect': "You'll be redirected to Stripe's secure payment page to add your card.",
    'wallet.openingStripe': 'Opening Stripe to add your card...',
    'wallet.failedAddMethod': 'Failed to add payment method',
    'wallet.failedOpenPortal': 'Failed to open billing portal',
    'wallet.expires': 'Exp',
    
    // Payments Page (renamed from Wallet in nav)
    'payments.title': 'Payments',
    'payments.subtitle': 'Manage payments & transactions',
    'payments.noCommunity': 'No Community Access',
    'payments.noCommunityDesc': 'Payment features will be available after joining a community.',
    'payments.createCommunity': 'Create Your First Community',
    
    // Amenity translations
    'amenity.privatePool': 'Private Pool',
    'amenity.pool': 'Pool',
    'amenity.hotTub': 'Hot Tub',
    'amenity.oceanView': 'Ocean View',
    'amenity.sunsetViews': 'Sunset Views',
    'amenity.beachAccess': 'Beach Access',
    'amenity.smartHome': 'Smart Home',
    'amenity.wineCellar': 'Wine Cellar',
    'amenity.homeTheater': 'Home Theater',
    'amenity.gym': 'Gym',
    'amenity.spa': 'Spa',
    'amenity.rooftopTerrace': 'Rooftop Terrace',
    'amenity.concierge': 'Concierge',
    'amenity.wifi': 'WiFi',
    'amenity.parking': 'Parking',
    'amenity.airConditioning': 'Air Conditioning',
    'amenity.heating': 'Heating',
    'amenity.kitchen': 'Kitchen',
    'amenity.washer': 'Washer',
    'amenity.dryer': 'Dryer',
    'amenity.washerDryer': 'Washer/Dryer',
    'amenity.petFriendly': 'Pet Friendly',
    'amenity.garden': 'Garden',
    'amenity.fireplace': 'Fireplace',
    'amenity.balcony': 'Balcony',
    
    // Guest Pass
    'guestPass.title': 'Guest Passes',
    'guestPass.subtitle': 'Manage temporary visitor access',
    'guestPass.header': 'Guest Passes',
    'guestPass.newPass': 'New Pass',
    'guestPass.createPass': 'Create Guest Pass',
    'guestPass.property': 'Property',
    'guestPass.selectProperty': 'Select property',
    'guestPass.guestEmail': 'Guest Email',
    'guestPass.guestName': 'Guest Name (optional)',
    'guestPass.accessStart': 'Access Start',
    'guestPass.accessEnd': 'Access End',
    'guestPass.selectStartDate': 'Select start date',
    'guestPass.selectEndDate': 'Select end date',
    'guestPass.duration': 'Duration: {days} days',
    'guestPass.sendPass': 'Send Guest Pass',
    'guestPass.noProperties': 'No properties found',
    'guestPass.noPropertiesDesc': 'You need to own a property to create guest passes',
    'guestPass.noPassesYet': 'No guest passes yet',
    'guestPass.noPassesDesc': 'Create a pass to give temporary access to visitors',
    'guestPass.active': 'Active',
    'guestPass.expired': 'Expired',
    'guestPass.revoked': 'Revoked',
    'guestPass.daysLeft': '{days} day{s} left',
    'guestPass.passCreated': 'Guest Pass Created',
    'guestPass.passRevoked': 'Pass Revoked',
    'guestPass.passRevokedDesc': 'Guest access has been revoked',
    'guestPass.missingInfo': 'Missing Information',
    'guestPass.missingInfoDesc': 'Please enter guest email, select a property, and set access dates',
    
    // Reviews
    'reviews.title': 'Reviews',
    'reviews.noReviews': 'No reviews yet',
    'reviews.reviews': 'reviews',
    'reviews.cleanliness': 'Cleanliness',
    'reviews.accuracy': 'Accuracy',
    'reviews.communication': 'Communication',
    'reviews.location': 'Location',
    'reviews.value': 'Value',
    'reviews.ownerResponse': 'Owner Response',
    'reviews.moreReviews': '+{count} more reviews',
    'reviews.guest': 'Guest',
    
    // AI Chat
    'chat.title': 'Aether',
    'chat.subtitle': 'Personal Concierge',
    'chat.placeholder': 'Ask Aether anything...',
    'chat.voiceError': 'Voice Error',
    'chat.voiceErrorDesc': 'Could not recognize speech',
    'chat.notSupported': 'Not Supported',
    'chat.notSupportedDesc': 'Voice recognition is not available in this browser',
    'chat.connectionError': 'Connection Error',
    'chat.connectionErrorDesc': 'Unable to reach Aether. Please try again.',
    'chat.bookingConfirmed': 'Booking Confirmed',
    'chat.serviceScheduled': 'Your {service} has been scheduled.',
    
    // Auth Page Extended
    'auth.sovereignLiving': 'Sovereign Living',
    'auth.setPasswordDesc': 'Create a password to secure your account',
    'auth.joinFuture': 'Join the future of luxury living',
    'auth.resetDesc': 'Enter your email to receive a reset link',
    'auth.signInDesc': 'Sign in to access your sovereign estate',
    'auth.newPassword': 'New Password',
    'auth.settingPassword': 'Setting password for this account',
    'auth.settingPasswordBtn': 'Setting password...',
    'auth.setPasswordContinue': 'Set Password & Continue',
    'auth.sendResetLink': 'Send Reset Link',
    'auth.sendingResetLink': 'Sending reset link...',
    'auth.backToSignIn': '← Back to Sign In',
    'auth.signingIn': 'Signing in...',
    'auth.creatingAccount': 'Creating account...',
    'auth.dontHaveAccount': "Don't have an account?",
    'auth.alreadyHaveAccount': 'Already have an account?',
    'auth.enterpriseSecurity': 'Protected by enterprise-grade security',
    'auth.passwordSetSuccess': 'Password set successfully! Redirecting...',
    'auth.welcomeBackToast': 'Welcome back!',
    'auth.accountCreated': 'Account created successfully!',
    'auth.emailRegistered': 'This email is already registered. Please sign in.',
    'auth.passwordResetSent': 'Password reset email sent! Check your inbox.',
    'auth.setPasswordComplete': 'Please set your password to complete setup',
    'auth.fillAllFields': 'Please fill in all fields',
    'auth.passwordMinLength': 'Password must be at least 6 characters',
    'auth.passwordsNoMatch': 'Passwords do not match',
    'auth.enterEmail': 'Please enter your email address',
    'auth.unexpectedError': 'An unexpected error occurred',
    
    // Payment History
    'paymentHistory.title': 'Payment History',
    'paymentHistory.subtitle': 'View all your payments and receipts',
    'paymentHistory.all': 'All',
    'paymentHistory.hoa': 'HOA',
    'paymentHistory.rentals': 'Rentals',
    'paymentHistory.services': 'Services',
    'paymentHistory.noPayments': 'No payments found',
    'paymentHistory.noPaymentsSubtitle': 'Your payment history will appear here',
    'paymentHistory.hoaDues': 'HOA Dues',
    'paymentHistory.rentalBooking': 'Rental Booking',
    'paymentHistory.servicePayment': 'Service Payment',
    'paymentHistory.paid': 'Paid',
    'paymentHistory.pending': 'Pending',
    'paymentHistory.rejected': 'Rejected',
    'paymentHistory.period': 'Period',
    'paymentHistory.paymentDetails': 'Payment Details',
    'paymentHistory.amount': 'Amount',
    'paymentHistory.type': 'Type',
    'paymentHistory.status': 'Status',
    'paymentHistory.date': 'Date',
    'paymentHistory.paymentMethod': 'Payment Method',
    'paymentHistory.property': 'Property',
    'paymentHistory.verified': 'Verified',
    'paymentHistory.paymentProof': 'Payment Proof',
    'paymentHistory.downloadReceipt': 'Download Receipt',
    'paymentHistory.bankTransfer': 'Bank Transfer',
    'paymentHistory.card': 'Card',
    'common.notAvailable': 'N/A',
    
    // Support Page
    'support.liveChat': 'Live Chat',
    'support.liveChatDesc': 'Chat with our support team in real-time',
    'support.liveChatAvailability': 'Available 9 AM - 6 PM EST',
    'support.startChat': 'Start Chat',
    'support.emailSupport': 'Email Support',
    'support.emailSupportDesc': 'Get a response within 24 hours',
    'support.sendEmail': 'Send Email',
    'support.phoneSupport': 'Phone Support',
    'support.phoneSupportDesc': 'For Enterprise customers only',
    'support.callNow': 'Call Now',
    'support.backToHome': 'Back to Home',
    'support.supportCenter': 'Support Center',
    'support.howCanWeHelp': 'How Can We Help?',
    'support.heroSubtitle': 'Our team is here to assist you with any questions or issues you may have.',
    'support.quickAnswers': 'Quick Answers',
    'support.commonQuestions': 'Common questions from our users',
    'support.viewAllFAQs': 'View All FAQs',
    'support.sendUsMessage': 'Send Us a Message',
    'support.name': 'Name',
    'support.namePlaceholder': 'Your name',
    'support.email': 'Email',
    'support.emailPlaceholder': 'your@email.com',
    'support.subject': 'Subject',
    'support.subjectPlaceholder': 'How can we help?',
    'support.message': 'Message',
    'support.messagePlaceholder': 'Describe your issue or question...',
    'support.sendMessage': 'Send Message',
    'support.sending': 'Sending...',
    'support.messageSent': "Your message has been sent! We'll get back to you soon.",
    'support.messageFailed': 'Failed to send message. Please try again.',
    'support.footer': '© 2025 Aether. Property management, simplified.',
    'support.faq1Question': 'How do I sign up for Aether?',
    'support.faq1Answer': 'Guests can sign up directly on our website and get a 15-day free trial. Homeowners need an invite from their community administrator to join (with a 30-day trial). Property developers should contact our sales team.',
    'support.faq2Question': "What's included in each subscription plan?",
    'support.faq2Answer': 'Explorer ($9.99/mo) is for travelers - book rentals, use AI concierge. Sovereign ($19.99/mo) is for homeowners - list properties, manage guests. Architect (custom pricing) is for developers managing communities.',
    'support.faq3Question': 'How do free trials work?',
    'support.faq3Answer': 'Explorer plan: 15-day free trial, no credit card required. Sovereign plan: 30-day free trial after receiving a community invite. Architect plan: No trial - contact sales for a demo and custom pricing.',
    'support.faq4Question': 'How do I get invited as a homeowner?',
    'support.faq4Answer': 'Contact your community administrator or property developer and ask them to send you an invite through Aether. You will receive an email with instructions to set up your account.',
    'support.faq5Question': 'How do I become a property developer on Aether?',
    'support.faq5Answer': 'Contact our sales team through the support page or email sales@aether.app. We offer enterprise onboarding with custom pricing based on your portfolio size.',
    'support.faq6Question': 'How do I cancel or change my subscription?',
    'support.faq6Answer': 'Go to Settings > Subscriptions and click "Manage Subscription" to access the Stripe Customer Portal. You can cancel anytime - your access continues until the end of your billing period.',
    
    // Privacy Page
    'privacy.backToHome': 'Back to Home',
    'privacy.title': 'Privacy Policy',
    'privacy.lastUpdated': 'Last updated: December 2025',
    'privacy.footer': '© 2025 Aether. Property management, simplified.',
    'privacy.section1.title': '1. Information We Collect',
    'privacy.section1.intro': 'At Aether, we collect information you provide directly to us, such as when you create an account, make a payment, or contact us for support. This may include:',
    'privacy.section1.item1': 'Name and contact information (email, phone number)',
    'privacy.section1.item2': 'Property details and addresses',
    'privacy.section1.item3': 'Payment information (processed securely through Stripe)',
    'privacy.section1.item4': 'Communication preferences',
    'privacy.section1.item5': 'Usage data and analytics',
    'privacy.section2.title': '2. How We Use Your Information',
    'privacy.section2.intro': 'We use the information we collect to:',
    'privacy.section2.item1': 'Provide, maintain, and improve our services',
    'privacy.section2.item2': 'Process transactions and send related notifications',
    'privacy.section2.item3': 'Respond to your comments, questions, and support requests',
    'privacy.section2.item4': 'Send you technical notices, updates, and security alerts',
    'privacy.section2.item5': 'Communicate with you about products, services, and events',
    'privacy.section3.title': '3. Subscription Tiers & Data Handling',
    'privacy.section3.intro': 'We handle data based on your subscription tier:',
    'privacy.section3.explorer': 'Explorer (Guests)',
    'privacy.section3.explorerDesc': 'Booking history, preferences, payment records for 15-day trial and beyond',
    'privacy.section3.sovereign': 'Sovereign (Homeowners)',
    'privacy.section3.sovereignDesc': 'Property data, rental listings, guest management, revenue analytics for 30-day trial and beyond',
    'privacy.section3.architect': 'Architect (Developers)',
    'privacy.section3.architectDesc': 'Community data, homeowner management, HOA payments, vendor relationships',
    'privacy.section4.title': '4. Invite-Only Access',
    'privacy.section4.intro': 'For certain subscription tiers, access is invite-only:',
    'privacy.section4.item1': 'Homeowners (Sovereign) receive invites from community administrators',
    'privacy.section4.item2': 'Property developers (Architect) receive invites from Aether staff after sales consultation',
    'privacy.section5.title': '5. Data Security',
    'privacy.section5.content': 'We use bank-level 256-bit SSL encryption for all data transmissions. Your data is stored in SOC 2 Type II certified data centers with 99.99% uptime. We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.',
    'privacy.section6.title': '6. Third-Party Services',
    'privacy.section6.intro': 'We use trusted third-party services to provide our platform:',
    'privacy.section6.stripe': 'Secure payment processing for subscriptions, HOA dues, and rental payments',
    'privacy.section6.mapbox': 'Interactive maps for property locations and community amenities',
    'privacy.section6.openai': 'AI-powered concierge for service booking and recommendations',
    'privacy.section7.title': '7. Data Retention',
    'privacy.section7.content': 'We retain your data for as long as your account is active or as needed to provide services. Subscription and transaction data is retained for 7 years for tax and legal compliance. You may request deletion of your account and personal data at any time.',
    'privacy.section8.title': '8. Your Rights',
    'privacy.section8.intro': 'You have the right to:',
    'privacy.section8.item1': 'Access and receive a copy of your personal data',
    'privacy.section8.item2': 'Rectify inaccurate personal data',
    'privacy.section8.item3': 'Request deletion of your personal data',
    'privacy.section8.item4': 'Object to processing of your personal data',
    'privacy.section8.item5': 'Request restriction of processing your personal data',
    'privacy.section8.item6': 'Data portability',
    'privacy.section9.title': '9. Contact Us',
    'privacy.section9.content': 'If you have any questions about this Privacy Policy, please contact us at',
    
    // Terms Page
    'terms.backToHome': 'Back to Home',
    'terms.title': 'Terms of Service',
    'terms.lastUpdated': 'Last updated: December 2025',
    'terms.footer': '© 2025 Aether. Property management, simplified.',
    'terms.section1.title': '1. Acceptance of Terms',
    'terms.section1.content': 'By accessing or using Aether ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service. We reserve the right to update these terms at any time, and your continued use of the Service constitutes acceptance of any changes.',
    'terms.section2.title': '2. Description of Service',
    'terms.section2.content': 'Aether is a property management platform that provides tools for community managers, property owners, and guests. Our services include but are not limited to: payment processing, guest pass management, service booking, community announcements, rental listings, and AI-powered concierge services.',
    'terms.section3.title': '3. Subscription Plans',
    'terms.section3.intro': 'Aether offers three subscription tiers:',
    'terms.section3.explorer': 'Explorer ($9.99/month)',
    'terms.section3.explorerDesc': 'For travelers seeking luxury stays. Book short-term rentals, long-term leases, and access AI concierge services.',
    'terms.section3.sovereign': 'Sovereign ($19.99/month)',
    'terms.section3.sovereignDesc': 'For homeowners. List properties, manage guests, access analytics, and generate revenue from rentals.',
    'terms.section3.architect': 'Architect (Custom pricing)',
    'terms.section3.architectDesc': 'For property developers. Create communities, manage homeowners, collect HOA payments, and access enterprise features.',
    'terms.section4.title': '4. Trial Periods',
    'terms.section4.intro': 'Free trials are available for select plans:',
    'terms.section4.explorer': 'Explorer',
    'terms.section4.explorerTrial': '15-day free trial. No credit card required to start.',
    'terms.section4.sovereign': 'Sovereign',
    'terms.section4.sovereignTrial': '30-day free trial after receiving a community invite from an administrator.',
    'terms.section4.architect': 'Architect',
    'terms.section4.architectTrial': 'No trial. Contact sales for a demo and custom pricing.',
    'terms.section5.title': '5. Invite-Only Access',
    'terms.section5.intro': 'Certain plans require an invitation:',
    'terms.section5.item1': 'Homeowners (Sovereign plan) must be invited by a community administrator',
    'terms.section5.item2': 'Property developers (Architect plan) must be invited by Aether staff after sales consultation',
    'terms.section5.item3': 'Invitations are non-transferable and intended for the specific recipient',
    'terms.section6.title': '6. User Accounts',
    'terms.section6.intro': 'To access certain features of the Service, you must create an account. You agree to:',
    'terms.section6.item1': 'Provide accurate, current, and complete information',
    'terms.section6.item2': 'Maintain and promptly update your account information',
    'terms.section6.item3': 'Maintain the security of your password and account',
    'terms.section6.item4': 'Accept responsibility for all activities under your account',
    'terms.section6.item5': 'Notify us immediately of any unauthorized use',
    'terms.section7.title': '7. Role-Based Access Control',
    'terms.section7.intro': 'Aether uses role-based access to manage permissions:',
    'terms.section7.admin': 'Admin (Community Manager)',
    'terms.section7.adminDesc': 'Full access to manage properties, homeowners, vendors, and community settings',
    'terms.section7.sovereign': 'Sovereign (Property Owner)',
    'terms.section7.sovereignDesc': 'Access to owned properties, HOA payments, rental management, and concierge services',
    'terms.section7.demiUser': 'Demi-User (Guest)',
    'terms.section7.demiUserDesc': 'Time-bound access to maps, basic services, and communication with hosts',
    'terms.section8.title': '8. Acceptable Use',
    'terms.section8.intro': 'You agree not to:',
    'terms.section8.item1': 'Use the Service for any unlawful purpose',
    'terms.section8.item2': 'Attempt to gain unauthorized access to any part of the Service',
    'terms.section8.item3': 'Interfere with or disrupt the Service or servers',
    'terms.section8.item4': 'Upload malicious code or content',
    'terms.section8.item5': 'Impersonate any person or entity',
    'terms.section8.item6': 'Violate any applicable laws or regulations',
    'terms.section9.title': '9. Billing & Cancellation',
    'terms.section9.content': 'Subscription fees are billed in advance on a monthly or annual basis. All fees are non-refundable except as required by law. You may cancel your subscription at any time through the Stripe Customer Portal in Settings. Your access continues until the end of your billing period.',
    'terms.section10.title': '10. Limitation of Liability',
    'terms.section10.content': 'To the maximum extent permitted by law, Aether shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Service. Our total liability shall not exceed the amount you paid us in the twelve months prior to the claim.',
    'terms.section11.title': '11. Contact',
    'terms.section11.content': 'For questions about these Terms, please contact us at',
    
    // Notifications Page
    'notifications.title': 'Notifications',
    'notifications.markAllRead': 'Mark all read',
    'notifications.all': 'All',
    'notifications.urgent': 'Urgent',
    'notifications.unread': 'Unread',
    'notifications.info': 'Info',
    'notifications.noNotifications': 'No notifications',
    'notifications.allCaughtUp': 'All caught up!',
    'notifications.checkBackLater': 'Check back later for updates',
    'notifications.today': 'Today',
    'notifications.yesterday': 'Yesterday',
    'notifications.thisWeek': 'This Week',
    'notifications.earlier': 'Earlier',
    
    // Onboarding Page
    'onboarding.homeowner': 'Homeowner',
    'onboarding.homeownerDesc': 'I own property and want to use Aether services',
    'onboarding.guest': 'Guest',
    'onboarding.guestDesc': "I'm looking to rent or stay at a property",
    'onboarding.profile': 'Profile',
    'onboarding.profileDesc': 'Tell us about yourself',
    'onboarding.properties': 'Properties',
    'onboarding.propertiesDesc': 'Add your properties',
    'onboarding.preferences': 'Preferences',
    'onboarding.preferencesDesc': 'Customize your experience',
    'onboarding.fullName': 'Full Name',
    'onboarding.fullNamePlaceholder': 'Enter your full name',
    'onboarding.phoneNumber': 'Phone Number',
    'onboarding.phonePlaceholder': 'Enter phone number',
    'onboarding.iAmA': 'I am a...',
    'onboarding.selectRole': 'Select your role',
    'onboarding.community': 'Community',
    'onboarding.loadingCommunities': 'Loading communities...',
    'onboarding.noCommunities': 'No communities available',
    'onboarding.noCommunitiesDesc': 'Please check with your property developer or check back later.',
    'onboarding.selectCommunity': 'Select a community',
    'onboarding.propertyAddress': 'Property Address',
    'onboarding.addressPlaceholder': 'Start typing your address...',
    'onboarding.unitNumber': 'Unit #',
    'onboarding.propertyType': 'Property Type',
    'onboarding.singleFamilyHome': 'Single Family Home',
    'onboarding.condominium': 'Condominium',
    'onboarding.townhouse': 'Townhouse',
    'onboarding.villa': 'Villa',
    'onboarding.penthouse': 'Penthouse',
    'onboarding.gatedCommunity': 'Gated Community',
    'onboarding.bedrooms': 'Bedrooms',
    'onboarding.bathrooms': 'Bathrooms',
    'onboarding.sqft': 'Sq Ft',
    'onboarding.enableRentalMode': 'Enable Rental Mode',
    'onboarding.rentalModeDesc': 'List this property for short-term rentals',
    'onboarding.rentalSettings': 'Rental Settings',
    'onboarding.nightlyRate': 'Nightly Rate ($)',
    'onboarding.cleaningFee': 'Cleaning Fee ($)',
    'onboarding.amenities': 'Amenities',
    'onboarding.wifi': 'WiFi',
    'onboarding.parking': 'Parking',
    'onboarding.pool': 'Pool',
    'onboarding.gym': 'Gym',
    'onboarding.kitchen': 'Kitchen',
    'onboarding.ac': 'A/C',
    'onboarding.smartTv': 'Smart TV',
    'onboarding.hotTub': 'Hot Tub',
    'onboarding.garden': 'Garden',
    'onboarding.security': '24/7 Security',
    'onboarding.property': 'Property',
    'onboarding.add': 'Add',
    'onboarding.language': 'Language',
    'onboarding.currency': 'Currency',
    'onboarding.timezone': 'Timezone',
    'onboarding.notifications': 'Notifications',
    'onboarding.bookingUpdates': 'Booking updates',
    'onboarding.messageAlerts': 'Message alerts',
    'onboarding.skipForNow': 'Skip for now',
    'onboarding.welcomeToAether': 'Welcome to Aether',
    'onboarding.letsSetup': "Let's set up your account",
    'onboarding.back': 'Back',
    'onboarding.continue': 'Continue',
    'onboarding.getStarted': 'Get Started',
    'onboarding.skipped': 'Onboarding skipped',
    'onboarding.skippedDesc': 'You can complete your profile anytime in Settings.',
    'onboarding.propertyAdded': 'property added successfully!',
    'onboarding.propertiesAdded': 'properties added successfully!',
    'onboarding.setupComplete': 'Your account has been set up successfully.',
    'onboarding.setupFailed': 'Failed to complete setup. Please try again.',
    
    // Rental Editor
    'rentalEditor.pricing': 'Pricing',
    'rentalEditor.nightlyRate': 'Nightly Rate',
    'rentalEditor.cleaningFee': 'Cleaning Fee',
    'rentalEditor.minNights': 'Min Nights',
    'rentalEditor.maxNights': 'Max Nights',
    'rentalEditor.description': 'Description',
    'rentalEditor.descriptionPlaceholder': 'Describe your property...',
    'rentalEditor.amenities': 'Amenities',
    'rentalEditor.houseRules': 'House Rules',
    'rentalEditor.houseRulesPlaceholder': 'Enter house rules (e.g., no smoking, no parties, quiet hours...)',
    'rentalEditor.smartHomeInstructions': 'Smart Home Instructions',
    'rentalEditor.smartHomeDesc': 'Revealed to guests after booking confirmation',
    'rentalEditor.tip': 'Tip',
    'rentalEditor.smartHomeTip': 'Include WiFi password, door codes, voice commands, and device locations here. This info stays private until the booking is confirmed.',
    'rentalEditor.publishListing': 'Publish Listing',
    'rentalEditor.publishDesc': 'Make your listing visible to guests',
    'rentalEditor.saveListing': 'Save Listing',
    'rentalEditor.rentalType': 'Rental Type',
    'rentalEditor.shortTerm': 'Short-Term Rental',
    'rentalEditor.shortTermDesc': 'Nightly bookings for vacations and short stays (1-30 nights)',
    'rentalEditor.longTerm': 'Long-Term Rental',
    'rentalEditor.longTermDesc': 'Monthly leases for extended stays (6+ months)',
    'rentalEditor.bothTypes': 'Both Short & Long-Term',
    'rentalEditor.bothTypesDesc': 'Accept both nightly and monthly bookings',
    'rentalEditor.shortTermPricing': 'Short-Term Pricing',
    'rentalEditor.longTermPricing': 'Long-Term Pricing',
    'rentalEditor.monthlyRate': 'Monthly Rate',
    'rentalEditor.monthlyRateHint': 'Price per month for long-term tenants',
    'rentalEditor.minLeaseDuration': 'Minimum Lease Duration (months)',
    
    // Admin Dashboard
    'admin.rentals': 'Rentals',
    'admin.news': 'News',
    'admin.inviteOwner': 'Invite Owner',
    'admin.inviteOwnerTitle': 'Invite Property Owner',
    'admin.inviteOwnerDesc': 'The owner will receive an email invitation to set up their account and manage this property.',
    
    // Community Switcher
    'community.selectCommunity': 'Select community',
    'community.createNew': 'Create New Community',
    
    // Map Page
    'map.title': 'Community Map',
    'map.amenities': 'Amenities',
    'map.properties': 'Properties',
    
    // Service Marketplace additional
    'service.services': 'Services',
    'service.bookPremium': 'Book premium services',
    'service.all': 'All',
    'service.noServices': 'No services available',
    'service.checkBackLater': 'Check back later for new offerings',
    'service.by': 'by',
    'service.book': 'Book',
    'service.service': 'Service',
    'service.estimatedPrice': 'Estimated Price',
    'service.priceVaryNote': 'Final price may vary based on property size',
    'service.scheduleDateTime': 'Schedule Date & Time',
    'service.specialRequests': 'Special Requests (optional)',
    'service.specialInstructions': 'Any special instructions...',
    'service.payWithCard': 'Pay with Card (USD)',
    'service.category.housekeeping': 'Housekeeping',
    'service.category.wellness': 'Wellness',
    'service.category.maintenance': 'Maintenance',
    'service.category.dining': 'Dining',
    'service.category.transport': 'Transport',
    
    // Chat (additional unique keys)
    'chat.fallbackError': "I apologize, but I couldn't process your request. Please try again.",
    'chat.bookingSuccess': 'Booking confirmed successfully.',
    'chat.bookingIssue': 'There was an issue creating your booking. Please try again.',
    'chat.temporaryError': "I apologize, but I'm experiencing a temporary connection issue. Please try again in a moment.",
    
    // Map (additional)
    'map.loading': 'Loading map...',
    
    // God Mode Dashboard
    'godMode.title': 'God Mode',
    'godMode.commandCenter': 'Admin Panel',
    'godMode.adminPanel': 'Admin Panel',
    'godMode.communities': 'Communities',
    'godMode.homes': 'Homes',
    'godMode.mrr': 'MRR',
    'godMode.propertyDevelopers': 'Property Developers',
    'godMode.developerInviteDesc': 'Invite property developers to create and manage communities on the platform.',
    'godMode.add': 'Add',
    'godMode.createNewCommunity': 'Create New Community',
    'godMode.communityName': 'Community Name',
    'godMode.city': 'City',
    'godMode.state': 'State',
    'godMode.baseFee': 'Base Fee/mo',
    'godMode.perHomeFee': 'Per Home Fee',
    'godMode.createCommunity': 'Create Community',
    'godMode.noCommunities': 'No communities yet',
    'godMode.createFirstCommunity': 'Create your first community to get started',
    'godMode.active': 'Active',
    'godMode.inactive': 'Inactive',
    'godMode.noLocation': 'No location',
    
    // Admin additional
    'admin.owner': 'Owner',
    'admin.homeownerEmail': 'Homeowner email',
    'admin.property': 'Property',
    'admin.totalProperties': 'Total Properties',
    'admin.rentalModeOn': 'Rental Mode On',
    'admin.activeListings': 'Active Listings',
    'admin.upcomingArrivals': 'Upcoming Arrivals',
    'admin.rentalProperties': 'Rental Properties',
    'admin.rentalOn': 'Rental On',
    'admin.rentalOff': 'Rental Off',
    'admin.upcomingBookings': 'Upcoming Bookings',
    'admin.listingInactive': 'Listing Inactive',
    
    // Rental types
    'rental.shortTerm': 'Short-term',
    'rental.longTerm': 'Long-term',
    'rental.bothTypes': 'Both',
    
    // Review Form
    'reviewForm.howWasStay': 'How was your stay?',
    'reviewForm.overallExperience': 'Overall Experience',
    'reviewForm.ratingRequired': 'Please provide an overall rating',
    'reviewForm.submitSuccess': 'Review submitted successfully!',
    'reviewForm.submitFailed': 'Failed to submit review',
    'reviewForm.titleLabel': 'Review Title (optional)',
    'reviewForm.titlePlaceholder': 'Summarize your experience',
    'reviewForm.contentLabel': 'Your Review (optional)',
    'reviewForm.contentPlaceholder': 'Share details about your stay...',
    'reviewForm.submit': 'Submit Review',
    
    // Review Moderation
    'moderation.title': 'Review Moderation',
    'moderation.total': 'Total',
    'moderation.pending': 'Pending',
    'moderation.flagged': 'Flagged',
    'moderation.all': 'All',
    'moderation.published': 'Published',
    'moderation.hidden': 'Hidden',
    'moderation.lowRating': 'Low Rating',
    'moderation.unknownProperty': 'Unknown Property',
    'moderation.anonymous': 'Anonymous',
    'moderation.commShort': 'Comm',
    'moderation.hide': 'Hide',
    'moderation.publish': 'Publish',
    'moderation.edit': 'Edit',
    'moderation.respond': 'Respond',
    'moderation.statusUpdated': 'Review status updated',
    'moderation.updateFailed': 'Failed to update review',
    'moderation.responseAdded': 'Response added successfully',
    'moderation.responseFailed': 'Failed to add response',
    'moderation.noReviews': 'No reviews yet',
    'moderation.noPending': 'No pending reviews',
    'moderation.noPublished': 'No published reviews',
    'moderation.noFlagged': 'No flagged reviews',
    'moderation.editResponse': 'Edit Response',
    'moderation.addResponse': 'Add Response',
    'moderation.by': 'by',
    'moderation.responsePlaceholder': 'Write your response to this review...',
    'moderation.saveResponse': 'Save Response',
    
    // Audit Log
    'audit.title': 'Audit Log',
    'audit.subtitle': 'System activity log',
    'audit.noLogs': 'No audit logs found',
    'audit.noLogsDesc': 'System activity will appear here',
    'audit.filter': 'Filter by action',
    'audit.all': 'All Actions',
    'audit.allActions': 'All Actions',
    'audit.allResources': 'All Resources',
    'audit.searchPlaceholder': 'Search actions, resources...',
    'audit.loadMore': 'Load More',
    'audit.action.create': 'Create',
    'audit.action.update': 'Update',
    'audit.action.delete': 'Delete',
    'audit.action.login': 'Login',
    'audit.action.logout': 'Logout',
    'audit.action.view': 'View',
    'audit.resource': 'Resource',
    'audit.user': 'User',
    'audit.timestamp': 'Timestamp',
    'audit.details': 'Details',
    'common.refresh': 'Refresh',
    
    // Services Manager
    'services.title': 'Services',
    'services.addService': 'Add Service',
    'services.editService': 'Edit Service',
    'services.serviceName': 'Service Name',
    'services.serviceNamePlaceholder': 'e.g., Deep Cleaning',
    'services.description': 'Description',
    'services.descriptionPlaceholder': 'Describe the service...',
    'services.category': 'Category',
    'services.selectCategory': 'Select category',
    'services.vendor': 'Vendor (optional)',
    'services.selectVendor': 'Select vendor',
    'services.noVendor': 'No vendor',
    'services.basePrice': 'Base Price',
    'services.pricePerBedroom': 'Price per Bedroom',
    'services.pricePerSqft': 'Price per Sq Ft',
    'services.duration': 'Duration (minutes)',
    'services.isGlobal': 'Available to all communities',
    'services.isActive': 'Active',
    'services.saveService': 'Save Service',
    'services.deleteConfirm': 'Delete this service?',
    'services.deleteSuccess': 'Service deleted',
    'services.deleteFailed': 'Failed to delete service',
    'services.saveSuccess': 'Service saved',
    'services.saveFailed': 'Failed to save service',
    'services.noServices': 'No services yet',
    'services.addFirst': 'Add your first service to get started',
    'services.global': 'Global',
    'services.housekeeping': 'Housekeeping',
    'services.wellness': 'Wellness',
    'services.maintenance': 'Maintenance',
    'services.dining': 'Dining',
    'services.transport': 'Transport',
    
    // Vendor Manager
    'vendor.title': 'Vendors',
    'vendor.addVendor': 'Add Vendor',
    'vendor.editVendor': 'Edit Vendor',
    'vendor.vendorName': 'Vendor Name',
    'vendor.vendorNamePlaceholder': 'e.g., Premium Cleaning Co.',
    'vendor.email': 'Email',
    'vendor.emailPlaceholder': 'vendor@example.com',
    'vendor.description': 'Description',
    'vendor.descriptionPlaceholder': 'Describe the vendor...',
    'vendor.category': 'Category',
    'vendor.selectCategory': 'Select category',
    'vendor.commissionRate': 'Commission Rate (%)',
    'vendor.isGlobal': 'Available to all communities',
    'vendor.isActive': 'Active',
    'vendor.saveVendor': 'Save Vendor',
    'vendor.deleteConfirm': 'Delete this vendor?',
    'vendor.deleteSuccess': 'Vendor deleted',
    'vendor.deleteFailed': 'Failed to delete vendor',
    'vendor.saveSuccess': 'Vendor saved',
    'vendor.saveFailed': 'Failed to save vendor',
    'vendor.noVendors': 'No vendors yet',
    'vendor.addFirst': 'Add your first vendor to get started',
    'vendor.global': 'Global',
    'vendor.commission': 'commission',
    'vendor.paymentSettings': 'Payment Settings',
    'vendor.stripeConnected': 'Stripe Connected',
    'vendor.stripeNotConnected': 'Stripe Not Connected',
    'vendor.mobilePayment': 'Mobile Payment',
    'vendor.accountName': 'Account Name',
    'vendor.phoneNumber': 'Phone Number',
    
    // Payment Verification
    'paymentVerification.title': 'Pending Verification',
    'paymentVerification.noPayments': 'No pending payments to verify',
    'paymentVerification.allProcessed': 'All payments have been processed',
    'paymentVerification.verifyPayment': 'Verify Payment',
    'paymentVerification.amount': 'Amount',
    'paymentVerification.paymentMethod': 'Payment Method',
    'paymentVerification.customer': 'Customer',
    'paymentVerification.date': 'Date',
    'paymentVerification.property': 'Property',
    'paymentVerification.paymentProof': 'Payment Proof',
    'paymentVerification.fullSize': 'Full Size',
    'paymentVerification.noProof': 'No payment proof uploaded',
    'paymentVerification.verifyManually': 'Verify this payment manually in your banking app',
    'paymentVerification.rejectionReason': 'Rejection Reason (if rejecting)',
    'paymentVerification.rejectionPlaceholder': "e.g. Amount doesn't match, invalid screenshot...",
    'paymentVerification.approve': 'Approve',
    'paymentVerification.reject': 'Reject',
    'paymentVerification.approved': 'Payment approved successfully',
    'paymentVerification.rejected': 'Payment rejected',
    'paymentVerification.approveFailed': 'Failed to approve payment',
    'paymentVerification.rejectFailed': 'Failed to reject payment',
    'paymentVerification.provideReason': 'Please provide a reason for rejection',
    'paymentVerification.hasProof': 'Has proof',
    'paymentVerification.hoa': 'HOA',
    'paymentVerification.rental': 'Rental',
    'paymentVerification.loadFailed': 'Failed to load pending payments',
    
    // Vendor Payouts
    'vendorPayouts.totalRevenue': 'Total Revenue',
    'vendorPayouts.platformFees': 'Platform Fees',
    'vendorPayouts.vendorPayouts': 'Vendor Payouts',
    'vendorPayouts.pendingPayouts': 'Pending Payouts',
    'vendorPayouts.allTransactions': 'All Transactions',
    'vendorPayouts.byVendor': 'By Vendor',
    'vendorPayouts.recentTransactions': 'Recent Service Transactions',
    'vendorPayouts.noTransactions': 'No transactions yet',
    'vendorPayouts.unknownVendor': 'Unknown Vendor',
    'vendorPayouts.fee': 'fee',
    'vendorPayouts.backToVendors': '← Back to Vendors',
    'vendorPayouts.connected': 'Connected',
    'vendorPayouts.notConnected': 'Not Connected',
    
    // Draggable Calendar
    'calendar.today': 'Today',
    'calendar.allAmenities': 'All Amenities',
    'calendar.filterAmenity': 'Filter amenity',
    'calendar.dragToReschedule': 'Drag bookings to reschedule them to a new time slot',
    'calendar.time': 'Time',
    'calendar.guest': 'Guest',
    'calendar.confirmReschedule': 'Confirm Reschedule',
    'calendar.moveBooking': 'Move booking for',
    'calendar.to': 'to:',
    'calendar.at': 'at',
    'calendar.confirm': 'Confirm',
    'calendar.rescheduled': 'Booking rescheduled successfully',
    'calendar.rescheduleFailed': 'Failed to reschedule booking',
    'calendar.availabilityCalendar': 'Availability Calendar',
    'calendar.viewBookings': 'View bookings and manage blocked dates',
    'calendar.booked': 'Booked',
    'calendar.blocked': 'Blocked',
    'calendar.available': 'Available',
    'calendar.upcomingBookings': 'Upcoming Bookings',
    'calendar.noUpcomingBookings': 'No upcoming bookings',
    'calendar.blockedDates': 'Blocked Dates',
    'calendar.clickToBlock': 'Click on available dates to block them',
    'calendar.saveAvailability': 'Save Availability',
    'calendar.availabilitySaved': 'Availability updated successfully',
    'calendar.availabilitySaveFailed': 'Failed to update availability',
    'calendar.guests': 'guests',
    'calendar.dragHint': 'Tip: Click and drag to select multiple dates at once',

    // Relative Dates
    'dates.today': 'Today',
    'dates.yesterday': 'Yesterday',
    'dates.tomorrow': 'Tomorrow',
    'dates.daysAgo': '{count} days ago',
    'dates.inDays': 'in {count} days',
    'dates.justNow': 'Just now',
    'dates.dueToday': 'Due today',
    
    // Settings Page (Phase 4 additions)
    'settings.manageAccount': 'Manage your account',
    'settings.developerIntegrations': 'Developer & Integrations',
    'settings.yourProperties': 'Your Properties',
    'settings.setupProfile': 'Set up your profile',
    'settings.profileUpdated': 'Profile updated successfully',
    'settings.profileUpdateFailed': 'Failed to update profile',
    'settings.pushNotSupported': 'Push notifications not supported in this browser',
    'settings.pushEnabled': 'Push notifications enabled',
    'settings.pushDenied': 'Permission denied for push notifications',
    'settings.pushEnableFailed': 'Failed to enable push notifications',
    'settings.openingPortal': 'Opening payment portal...',
    'settings.portalFailed': 'Failed to open payment portal',
    'settings.editProfile': 'Edit Profile',
    'settings.fullName': 'Full Name',
    'settings.enterName': 'Enter your name',
    'settings.email': 'Email',
    'settings.phoneNumber': 'Phone Number',
    'settings.enterPhone': 'Enter your phone number',
    'settings.saveChanges': 'Save Changes',
    'settings.emailNotifications': 'Email Notifications',
    'settings.bookingUpdates': 'Booking Updates',
    'settings.bookingUpdatesDesc': 'Receive email updates about your bookings',
    'settings.paymentAlerts': 'Payment Alerts',
    'settings.paymentAlertsDesc': 'Receive email alerts for payment activity',
    'settings.guestPassActivity': 'Guest Pass Activity',
    'settings.guestPassActivityDesc': 'Receive notifications when guests use passes',
    'settings.pushNotifications': 'Push Notifications',
    'settings.enablePush': 'Enable Push Notifications',
    'settings.enablePushDesc': 'Receive real-time alerts on your device',
    'settings.bookingNotifications': 'Booking Notifications',
    'settings.bookingNotificationsDesc': 'New bookings and changes',
    'settings.messageNotifications': 'Message Notifications',
    'settings.messageNotificationsDesc': 'New messages from guests',
    'settings.announcementNotifications': 'Announcement Notifications',
    'settings.announcementNotificationsDesc': 'Community announcements',
    'settings.managePaymentMethods': 'Manage Payment Methods',
    'settings.managePaymentMethodsDesc': 'Update your payment methods through our secure portal',
    'settings.openPaymentPortal': 'Open Payment Portal',
    'settings.paymentSecurityNote': 'Your payment information is handled securely by Stripe',
    'settings.preferences': 'Preferences',
    'settings.selectTimezone': 'Select timezone',
    'settings.timezoneSet': 'Timezone set to',
    'settings.selectLanguage': 'Select language',
    'settings.languageChanged': 'Language changed to',
    'settings.selectCurrency': 'Select currency',
    'settings.currencyChanged': 'Currency changed to',
    'settings.preferencesSaved': 'Your preferences are saved automatically',
    
    // Service Marketplace (Phase 4)
    'service.selectDateTime': 'Please select a date and time',
    'service.bookingCreated': 'Booking created! Complete payment to confirm.',
    'service.bookingFailed': 'Failed to book service',
    'service.paymentSubmitted': 'Payment submitted! Awaiting verification.',
    
    // Browse Rentals (Phase 4)
    'browse.pleaseSignIn': 'Please sign in to book a rental',
    'browse.selectDates': 'Please select check-in and check-out dates',
    'browse.minStayError': 'Minimum stay is {nights} nights',
    'browse.maxStayError': 'Maximum stay is {nights} nights',
    'browse.redirectingPayment': 'Redirecting to payment...',
    'browse.bookingFailed': 'Failed to create booking',
    
    // Booking History (Phase 4)
    'bookingHistory.title': 'Booking History',
    'bookingHistory.totalBookings': '{count} total bookings',
    'bookingHistory.upcoming': 'Upcoming',
    'bookingHistory.past': 'Past',
    'bookingHistory.filterStatus': 'Filter by status',
    'bookingHistory.allStatuses': 'All Statuses',
    'bookingHistory.inProgress': 'In Progress',
    'bookingHistory.noUpcoming': 'No upcoming bookings',
    'bookingHistory.noPast': 'No past bookings',
    'bookingHistory.bookService': 'Book a service to see it here',
    'bookingHistory.willAppear': 'Completed bookings will appear here',
    'bookingHistory.browseServices': 'Browse Services',
    'bookingHistory.viewReceipt': 'View Receipt',
    'bookingHistory.reschedule': 'Reschedule',
    'bookingHistory.cancel': 'Cancel',
    'bookingHistory.rescheduleBooking': 'Reschedule Booking',
    'bookingHistory.service': 'Service',
    'bookingHistory.newDateTime': 'New Date & Time',
    'bookingHistory.confirmReschedule': 'Confirm Reschedule',
    'bookingHistory.cancelSuccess': 'Booking cancelled successfully',
    'bookingHistory.cancelFailed': 'Failed to cancel booking',
    'bookingHistory.rescheduleSuccess': 'Booking rescheduled successfully',
    'bookingHistory.rescheduleFailed': 'Failed to reschedule booking',
    'bookingHistory.paymentPaid': 'Paid',
    'bookingHistory.paymentProcessing': 'Processing',
    'bookingHistory.paymentFailed': 'Failed',
    'bookingHistory.paymentPending': 'Pending Payment',
    
    // Rental Management Page
    'rentalManagement.title': 'Rental Management',
    'rentalManagement.notEnabled': 'Rental Mode Not Enabled',
    'rentalManagement.enableDesc': 'Enable rental mode on your property from the dashboard to start listing',
    'rentalManagement.live': 'Live',
    'rentalManagement.draft': 'Draft',
    'rentalManagement.perNight': 'per night',
    'rentalManagement.nights': 'nights',
    'rentalManagement.bedrooms': 'bedrooms',
    'rentalManagement.listing': 'Listing',
    'rentalManagement.calendar': 'Calendar',
    'rentalManagement.analytics': 'Analytics',
    'rentalManagement.bookings': 'Bookings',
    'rentalManagement.createFirst': 'Create a listing first to manage availability',
    'rentalManagement.saveSuccess': 'Listing saved successfully',
    'rentalManagement.saveFailed': 'Failed to save listing',
    'rentalManagement.availabilityUpdated': 'Availability updated',
    'rentalManagement.availabilityFailed': 'Failed to update availability',
    
    // Messaging
    'messaging.noMessages': 'No messages yet',
    'messaging.startConversation': 'Start a conversation!',
    'messaging.typeMessage': 'Type a message...',
    'messaging.loadFailed': 'Failed to load messages',
    'messaging.sendFailed': 'Failed to send message',
    
    // Form Validation
    'validation.nameRequired': 'Name is required',
    'validation.nameMaxLength': 'Name must be less than 100 characters',
    'validation.emailInvalid': 'Invalid email address',
    'validation.emailMaxLength': 'Email must be less than 255 characters',
    'validation.subjectRequired': 'Subject is required',
    'validation.subjectMaxLength': 'Subject must be less than 200 characters',
    'validation.messageRequired': 'Message is required',
    'validation.messageMaxLength': 'Message must be less than 2000 characters',
    
    // QR Payment Dialog
    'qrPayment.notAvailable': 'Payment Not Available',
    'qrPayment.notSetUp': '{method} payment is not set up for this property.',
    'qrPayment.contactManager': 'Please contact the property manager or use a different payment method.',
    'qrPayment.payWith': 'Pay with {method}',
    'qrPayment.amountToPay': 'Amount to Pay',
    'qrPayment.copyAmount': 'Copy amount',
    'qrPayment.howToPay': 'How to pay:',
    'qrPayment.step1': 'Open your {method} app',
    'qrPayment.step2': 'Tap "Scan QR" and scan the code above',
    'qrPayment.step3': 'Enter the exact amount: {amount}',
    'qrPayment.step4': 'Complete the payment',
    'qrPayment.step5': 'Take a screenshot of the confirmation',
    'qrPayment.madePayment': "I've Made the Payment",
    'qrPayment.confirmPayment': 'Confirm Your Payment',
    'qrPayment.uploadScreenshot': 'Upload a screenshot of your payment confirmation for faster verification.',
    'qrPayment.paymentScreenshot': 'Payment Screenshot (Recommended)',
    'qrPayment.tapToUpload': 'Tap to upload screenshot',
    'qrPayment.remove': 'Remove',
    'qrPayment.submitting': 'Submitting...',
    'qrPayment.confirm': 'Confirm Payment',
    'qrPayment.submitted': 'Payment Submitted!',
    'qrPayment.beingVerified': "Your payment is being verified. You'll receive a notification once it's confirmed.",
    'qrPayment.verificationTime': 'This usually takes 1-24 hours during business days.',
    'qrPayment.done': 'Done',
    'qrPayment.phoneCopied': 'Phone number copied',
    'qrPayment.amountCopied': 'Amount copied',
    'qrPayment.uploadImage': 'Please upload an image file',
    'qrPayment.screenshotUploaded': 'Payment screenshot uploaded',
    'qrPayment.uploadFailed': 'Failed to upload screenshot',
    'qrPayment.submitFailed': 'Failed to submit payment',
    
    // Community Creator
    'communityCreator.title': 'Create New Community',
    'communityCreator.completePaymentSetup': 'Complete Payment Setup',
    'communityCreator.uploadQrCodes': 'Upload Payment QR Codes',
    'communityCreator.createButton': 'Create Community',
    'communityCreator.communityName': 'Community Name',
    'communityCreator.communityNamePlaceholder': 'e.g. Oceanview Estates',
    'communityCreator.streetAddress': 'Street Address',
    'communityCreator.streetAddressPlaceholder': 'Enter street address...',
    'communityCreator.city': 'City',
    'communityCreator.cityPlaceholder': 'City',
    'communityCreator.state': 'State',
    'communityCreator.statePlaceholder': 'State',
    'communityCreator.zipCode': 'Zip Code',
    'communityCreator.zipCodePlaceholder': 'Zip',
    'communityCreator.description': 'Description',
    'communityCreator.descriptionPlaceholder': 'A brief description of your community...',
    'communityCreator.detailsTab': 'Details',
    'communityCreator.paymentsTab': 'Payments',
    'communityCreator.nextPaymentSettings': 'Next: Payment Settings',
    'communityCreator.defaultCurrency': 'Default Currency',
    'communityCreator.currencyHintMMK': 'Myanmar Kyat for local payments',
    'communityCreator.currencyHintUSD': 'US Dollars for international payments',
    'communityCreator.acceptedPaymentMethods': 'Accepted Payment Methods',
    'communityCreator.selectPaymentMethod': 'Select at least one payment method',
    'communityCreator.qrCodePrompt': "You'll be prompted to upload QR codes for mobile wallet payments after creating the community.",
    'communityCreator.creating': 'Creating...',
    'communityCreator.completeStripeSetup': 'Complete Stripe Setup',
    'communityCreator.stripeOnboardingDescription': 'Your community has been created! To accept HOA payments, complete the Stripe onboarding process.',
    'communityCreator.doLater': 'Do Later',
    'communityCreator.qrUploadDescription': 'Upload your KBZPay and Wave Money QR codes so homeowners can pay their dues.',
    'communityCreator.skipForNow': 'Skip for Now',
    'communityCreator.success': 'Community created successfully!',
    'communityCreator.error': 'Failed to create community',
    
    // Property Delete Dialog
    'propertyDelete.title': 'Delete Property',
    'propertyDelete.confirm': 'Are you sure you want to delete',
    'propertyDelete.willRemove': 'This action will:',
    'propertyDelete.removeProperty': 'Remove the property from your account',
    'propertyDelete.deactivateListings': 'Deactivate any active rental listings',
    'propertyDelete.cancelBookings': 'Cancel any pending bookings',
    'propertyDelete.cannotUndo': 'This action cannot be undone.',
    'propertyDelete.deleting': 'Deleting...',
    'propertyDelete.success': 'Property deleted successfully',
    'propertyDelete.failed': 'Failed to delete property',
    
    // Sale Listing Manager
    'saleListing.title': 'Sale Listing',
    'saleListing.edit': 'Edit Sale Listing',
    'saleListing.create': 'Create Sale Listing',
    'saleListing.noProperties': 'No Properties',
    'saleListing.addPropertiesFirst': 'Add properties to this community first, then you can list them for sale.',
    'saleListing.listForSale': 'List for Sale',
    'saleListing.published': 'Published',
    'saleListing.available': 'Available',
    'saleListing.pending': 'Pending',
    'saleListing.sold': 'Sold',
    'saleListing.priceRange': 'Price Range',
    'saleListing.description': 'List properties for sale with an estimated price range. Actual sales are handled offline.',
    'saleListing.updated': 'Sale listing updated',
    'saleListing.created': 'Sale listing created',
    'saleListing.saveFailed': 'Failed to save listing',
    
    // Amenity Booking Dialog
    'amenityBooking.book': 'Book',
    'amenityBooking.reserveSpace': 'Reserve this space for up to {capacity} guests',
    'amenityBooking.selectDate': 'Select Date',
    'amenityBooking.startTime': 'Start Time',
    'amenityBooking.duration': 'Duration',
    'amenityBooking.minutes': '{minutes} minutes (until {endTime})',
    'amenityBooking.guestCount': 'Number of Guests (max {max})',
    'amenityBooking.specialRequests': 'Special Requests (optional)',
    'amenityBooking.specialPlaceholder': 'Any special requirements or requests...',
    'amenityBooking.confirmBooking': 'Confirm Booking',
    'amenityBooking.booking': 'Booking...',
    'amenityBooking.confirmed': 'Booking Confirmed!',
    'amenityBooking.on': 'on',
    'amenityBooking.success': '{name} booked successfully!',
    'amenityBooking.selectDateError': 'Please select a date',
    'amenityBooking.fullyBooked': 'This time slot is fully booked. Maximum capacity: {capacity}',
    'amenityBooking.spotsAvailable': 'Only {spots} spots available at this time. Reduce guest count.',
    'amenityBooking.failed': 'Failed to book amenity. Please try again.',
    
    // Common additions
    'common.notAuthenticated': 'Not authenticated',
    'common.note': 'Note',
    
    // Admin - Developer Invite
    'admin.developer.invite': 'Invite Developer',
    'admin.developer.inviteTitle': 'Invite Property Developer',
    'admin.developer.description': 'Property developers can create and manage communities, add properties, services, and vendors.',
    'admin.developer.emailLabel': 'Email Address',
    'admin.developer.emailPlaceholder': 'developer@company.com',
    'admin.developer.nameLabel': 'Full Name (optional)',
    'admin.developer.namePlaceholder': 'John Developer',
    'admin.developer.note': 'The developer will receive an email with instructions to set up their account. They will have admin-level access to create communities.',
    'admin.developer.sendInvite': 'Send Invitation',
    'admin.developer.emailRequired': 'Email is required',
    'admin.developer.inviteSent': 'Developer invitation sent successfully',
    'admin.developer.inviteFailed': 'Failed to send invitation',
    
    // Admin - Amenity Manager
    'admin.amenity.title': 'Community Amenities',
    'admin.amenity.add': 'Add Amenity',
    'admin.amenity.addTitle': 'Add Amenity',
    'admin.amenity.namePlaceholder': 'Amenity Name',
    'admin.amenity.selectCategory': 'Select category',
    'admin.amenity.descriptionPlaceholder': 'Description (optional)',
    'admin.amenity.latitudePlaceholder': 'Latitude (e.g. 25.7617)',
    'admin.amenity.longitudePlaceholder': 'Longitude (e.g. -80.1918)',
    'admin.amenity.coordsTip': 'Tip: Find coordinates on Google Maps by right-clicking on a location',
    'admin.amenity.addButton': 'Add Amenity',
    'admin.amenity.noAmenities': 'No amenities yet',
    'admin.amenity.addFirst': 'Add your first community amenity',
    'admin.amenity.loadFailed': 'Failed to load amenities',
    'admin.amenity.nameAndCoordsRequired': 'Name and coordinates are required',
    'admin.amenity.addSuccess': 'Amenity added successfully',
    'admin.amenity.addFailed': 'Failed to add amenity',
    'admin.amenity.confirmDelete': 'Are you sure you want to delete this amenity?',
    'admin.amenity.deleteSuccess': 'Amenity deleted',
    'admin.amenity.deleteFailed': 'Failed to delete amenity',
    'admin.amenity.category.pool': 'Pool',
    'admin.amenity.category.gym': 'Fitness Center',
    'admin.amenity.category.restaurant': 'Restaurant',
    'admin.amenity.category.valet': 'Valet',
    'admin.amenity.category.spa': 'Spa & Wellness',
    'admin.amenity.category.park': 'Park / Garden',
    'admin.amenity.category.clubhouse': 'Clubhouse',
    'admin.amenity.category.other': 'Other',
    
    // Admin - Announcement Manager
    'admin.announcement.title': 'Announcements',
    'admin.announcement.new': 'New',
    'admin.announcement.newTitle': 'New Announcement',
    'admin.announcement.editTitle': 'Edit Announcement',
    'admin.announcement.titleLabel': 'Title',
    'admin.announcement.titlePlaceholder': 'Announcement title',
    'admin.announcement.contentLabel': 'Content',
    'admin.announcement.contentPlaceholder': 'Write your announcement...',
    'admin.announcement.priorityLabel': 'Priority',
    'admin.announcement.priority.normal': 'Normal',
    'admin.announcement.priority.important': 'Important',
    'admin.announcement.priority.urgent': 'Urgent',
    'admin.announcement.publishImmediately': 'Publish immediately',
    'admin.announcement.visibleToResidents': 'Make visible to all residents',
    'admin.announcement.create': 'Create Announcement',
    'admin.announcement.update': 'Update Announcement',
    'admin.announcement.publish': 'Publish',
    'admin.announcement.unpublish': 'Unpublish',
    'admin.announcement.status.published': 'Published',
    'admin.announcement.status.draft': 'Draft',
    'admin.announcement.noAnnouncements': 'No Announcements',
    'admin.announcement.createToInform': 'Create announcements to keep residents informed',
    'admin.announcement.titleContentRequired': 'Title and content are required',
    'admin.announcement.createSuccess': 'Announcement created',
    'admin.announcement.updateSuccess': 'Announcement updated',
    'admin.announcement.saveFailed': 'Failed to save announcement',
    'admin.announcement.deleteSuccess': 'Announcement deleted',
    'admin.announcement.deleteFailed': 'Failed to delete announcement',
    'admin.announcement.published': 'Announcement published',
    'admin.announcement.unpublished': 'Announcement unpublished',
    'admin.announcement.updateFailed': 'Failed to update announcement',
    
    // Admin - Guest Pass Manager
    'admin.guestPass.title': 'Guest Pass Management',
    'admin.guestPass.subtitle': 'Manage all visitor access',
    'admin.guestPass.status.active': 'Active',
    'admin.guestPass.status.expired': 'Expired',
    'admin.guestPass.status.revoked': 'Revoked',
    'admin.guestPass.noGuestPasses': 'No guest passes found',
    'admin.guestPass.unknownGuest': 'Unknown Guest',
    'admin.guestPass.expires': 'Expires',
    'admin.guestPass.extend': 'Extend',
    'admin.guestPass.reactivate': 'Reactivate (7 days)',
    'admin.guestPass.extendTitle': 'Extend Guest Access',
    'admin.guestPass.guestLabel': 'Guest',
    'admin.guestPass.newEndDate': 'New End Date',
    'admin.guestPass.selectEndDate': 'Select new end date',
    'admin.guestPass.extendAccess': 'Extend Access',
    'admin.guestPass.loadFailed': 'Failed to load guest passes',
    'admin.guestPass.revoked': 'Guest pass revoked',
    'admin.guestPass.revokeFailed': 'Failed to revoke pass',
    'admin.guestPass.extendedTo': 'Access extended to',
    'admin.guestPass.extendFailed': 'Failed to extend access',
    'admin.guestPass.reactivated': 'Guest pass reactivated for 7 days',
    'admin.guestPass.reactivateFailed': 'Failed to reactivate pass',
    
    // Date/Time Picker
    'datePicker.pickDate': 'Pick a date',
    'datePicker.selectDate': 'Select date and time',
    'datePicker.time': 'Time',
    
    // Services Manager Additional
    'services.addNewService': 'Add New Service',
    'services.serviceUpdated': 'Service Updated',
    'services.hasBeenUpdated': 'has been updated',
    'services.serviceCreated': 'Service Created',
    'services.hasBeenAdded': 'has been added',
    'services.failedToSave': 'Failed to save service',
    'services.noVendorAssigned': 'No vendor assigned',
    'services.unknownVendor': 'Unknown vendor',
    'services.loadingServices': 'Loading services...',
    'services.perBedroom': 'Per Bedroom',
    'services.perSqft': 'Per Sqft',
    'services.pricing': 'Pricing',
    'services.min': 'min',
    'services.active': 'Active',
    'services.inactive': 'Inactive',
    'services.createService': 'Create Service',
    'services.updateService': 'Update Service',
    
    // Service Names (from database)
    'serviceName.poolService': 'Pool Service',
    'serviceName.poolMaintenance': 'Pool Maintenance',
    'serviceName.poolSpaMaintenance': 'Pool & Spa Maintenance',
    'serviceName.deepCleaning': 'Deep Cleaning',
    'serviceName.deepClean': 'Deep Clean',
    'serviceName.regularCleaning': 'Regular Cleaning',
    'serviceName.housekeeping': 'Housekeeping',
    'serviceName.expressHousekeeping': 'Express Housekeeping',
    'serviceName.signatureHousekeeping': 'Signature Housekeeping',
    'serviceName.fullEstateCleaning': 'Full Estate Cleaning',
    'serviceName.weeklyMaintenanceClean': 'Weekly Maintenance Clean',
    'serviceName.turndownService': 'Turndown Service',
    'serviceName.laundryService': 'Laundry Service',
    'serviceName.laundry': 'Laundry',
    'serviceName.laundryPressing': 'Laundry & Pressing',
    'serviceName.carpetCleaning': 'Carpet Cleaning',
    'serviceName.windowCleaning': 'Window Cleaning',
    'serviceName.inHomeSpa': 'In-Home Spa & Wellness',
    'serviceName.spaWellness': 'Spa & Wellness',
    'serviceName.couplesSpaDay': 'Couples Spa Day',
    'serviceName.couplesSpaPackage': 'Couples Spa Package',
    'serviceName.inVillaMassage': 'In-Villa Massage',
    'serviceName.swedishMassage': 'Swedish Massage',
    'serviceName.hotStoneTherapy': 'Hot Stone Therapy',
    'serviceName.personalTraining': 'Personal Training',
    'serviceName.personalTrainingSession': 'Personal Training Session',
    'serviceName.privateYogaClass': 'Private Yoga Class',
    'serviceName.privateYogaSession': 'Private Yoga Session',
    'serviceName.massageTherapy': 'Massage Therapy',
    'serviceName.massage': 'Massage',
    'serviceName.yogaSession': 'Yoga Session',
    'serviceName.yoga': 'Yoga',
    'serviceName.meditation': 'Meditation',
    'serviceName.beautyServices': 'Beauty Services',
    'serviceName.salonServices': 'Salon Services',
    'serviceName.generalMaintenance': 'General Maintenance',
    'serviceName.plumbing': 'Plumbing',
    'serviceName.emergencyPlumbing': 'Emergency Plumbing',
    'serviceName.electrical': 'Electrical',
    'serviceName.hvacService': 'HVAC Service',
    'serviceName.hvac': 'HVAC',
    'serviceName.hvacMaintenance': 'HVAC Maintenance',
    'serviceName.handyman': 'Handyman',
    'serviceName.handymanServices': 'Handyman Services',
    'serviceName.landscaping': 'Landscaping',
    'serviceName.landscapingService': 'Landscaping Service',
    'serviceName.gardenLandscaping': 'Garden Landscaping',
    'serviceName.gardenMaintenance': 'Garden Maintenance',
    'serviceName.pestControl': 'Pest Control',
    'serviceName.privateChef': 'Private Chef',
    'serviceName.privateChefDinner': 'Private Chef Dinner',
    'serviceName.privateChefExperience': 'Private Chef Experience',
    'serviceName.italianDinnerExperience': 'Italian Dinner Experience',
    'serviceName.sushiOmakase': 'Sushi Omakase',
    'serviceName.winePairingDinner': 'Wine Pairing Dinner',
    'serviceName.wineSommelierService': 'Wine Sommelier Service',
    'serviceName.breakfastInBed': 'Breakfast in Bed',
    'serviceName.cocktailPartyCatering': 'Cocktail Party Catering',
    'serviceName.catering': 'Catering',
    'serviceName.mealDelivery': 'Meal Delivery',
    'serviceName.groceryShopping': 'Grocery Shopping',
    'serviceName.bartenderService': 'Bartender Service',
    'serviceName.bartender': 'Bartender',
    'serviceName.airportTransfer': 'Airport Transfer',
    'serviceName.airportLimousine': 'Airport Limousine',
    'serviceName.carRental': 'Car Rental',
    'serviceName.golfCartRental': 'Golf Cart Rental',
    'serviceName.chauffeurService': 'Chauffeur Service',
    'serviceName.chauffeur': 'Chauffeur',
    'serviceName.valetParking': 'Valet Parking',
    'serviceName.valetParkingService': 'Valet Parking Service',
    'serviceName.valet': 'Valet',
    'serviceName.boatCharter': 'Boat Charter',
    'serviceName.yachtCharter': 'Yacht Charter',
    'serviceName.yachtCharterHalfDay': 'Yacht Charter - Half Day',
    'serviceName.yachtDetailing': 'Yacht Detailing',
    
    // Vendor Manager Additional
    'vendor.failedToLoad': 'Failed to load vendors',
    'vendor.nameRequired': 'Vendor name is required',
    'vendor.addedSuccess': 'Vendor added successfully',
    'vendor.failedToAdd': 'Failed to add vendor',
    'vendor.updatedSuccess': 'Vendor updated successfully',
    'vendor.failedToUpdate': 'Failed to update vendor',
    'vendor.confirmDelete': 'Are you sure you want to delete this vendor?',
    'vendor.deleted': 'Vendor deactivated',
    'vendor.failedToDelete': 'Failed to delete vendor',
    'vendor.stripeOnboardingOpened': 'Stripe onboarding opened in new tab',
    'vendor.failedToStartOnboarding': 'Failed to start Stripe onboarding',
    'vendor.vendorsAndProviders': 'Vendors & Service Providers',
    'vendor.addNewVendor': 'Add New Vendor',
    'vendor.enterVendorName': 'Enter vendor name',
    'vendor.contactEmail': 'Contact Email',
    'vendor.emailNotification': 'For payment and booking notifications',
    'vendor.platformKeeps': 'Platform keeps',
    'vendor.notConnected': 'Not Connected',
    'vendor.fullSetup': 'Full Setup',
    'vendor.stripeOnly': 'Stripe Only',
    'vendor.qrOnly': 'QR Only',
    'vendor.noVendorsDesc': 'Add vendors to offer services',
    'vendor.updateStripeAccount': 'Update Stripe Account',
    'vendor.connectStripeAccount': 'Connect Stripe Account',
    'vendor.editDetails': 'Edit Vendor',
    'vendor.qrCodes': 'QR Codes',
    
    // Guest Preferences Quiz
    'quiz.saved': 'Preferences saved!',
    'quiz.savedDesc': "We'll personalize your rental search based on your preferences.",
    'quiz.saveFailed': 'Failed to save preferences',
    'quiz.howTravel': 'How do you like to travel?',
    'quiz.selectAll': 'Select all that apply',
    'quiz.whatBrings': 'What brings you here?',
    'quiz.whoTraveling': "Who's traveling with you?",
    'quiz.selectGroupSize': 'Select your typical group size',
    'quiz.stayLength': 'Typical stay length',
    'quiz.nights': 'nights',
    'quiz.whatBudget': "What's your budget?",
    'quiz.perNightPref': 'Per night rate preference',
    'quiz.mustHaveAmenities': 'Must-have amenities',
    'quiz.selectEssentials': 'Select your essentials',
    'quiz.petFriendly': 'Pet-Friendly',
    'quiz.petFriendlyDesc': 'Traveling with pets',
    'quiz.accessibility': 'Accessibility',
    'quiz.accessibilityDesc': 'Need accessible features',
    'quiz.personalizeExperience': 'Personalize Your Experience',
    'quiz.updatePreferences': 'Update Preferences',
    'quiz.helpFind': 'Help us find the perfect properties for you',
    'quiz.skipForNow': 'Skip for now',
    'quiz.savePreferences': 'Save Preferences',
    'quiz.style.short_trip': 'Quick Getaways',
    'quiz.style.short_tripDesc': '1-3 night stays',
    'quiz.style.long_term': 'Extended Stays',
    'quiz.style.long_termDesc': 'Weeks or months',
    'quiz.style.luxury': 'Luxury Retreats',
    'quiz.style.luxuryDesc': 'Premium experiences',
    'quiz.style.budget': 'Budget-Friendly',
    'quiz.style.budgetDesc': 'Value-focused',
    'quiz.purpose.vacation': 'Vacation',
    'quiz.purpose.business': 'Business',
    'quiz.purpose.relocation': 'Relocation',
    'quiz.purpose.remote_work': 'Remote Work',
    'quiz.group.solo': 'Solo',
    'quiz.group.couple': 'Couple',
    'quiz.group.family': 'Family',
    'quiz.group.group': 'Group',
    'quiz.budget.budget': 'Budget',
    'quiz.budget.moderate': 'Moderate',
    'quiz.budget.luxury': 'Luxury',
    'quiz.budget.ultra_luxury': 'Ultra Luxury',
    'quiz.amenity.wifi': 'WiFi',
    'quiz.amenity.parking': 'Parking',
    'quiz.amenity.pool': 'Pool',
    'quiz.amenity.gym': 'Gym',
    'quiz.amenity.kitchen': 'Kitchen',
    'quiz.amenity.air_conditioning': 'A/C',
    'quiz.amenity.tv': 'Smart TV',
    'quiz.amenity.hot_tub': 'Hot Tub',
    'quiz.amenity.garden': 'Garden',
    'quiz.amenity.security': 'Security',
    
    // Payment Method Selector
    'payment.totalAmount': 'Total Amount',
    'payment.myanmarOptions': 'Myanmar Payment Options',
    'payment.localPayment': 'Local Payment',
    'payment.internationalCards': 'International Cards',
    'payment.processing': 'Processing...',
    'payment.payWith': 'Pay with {method}',
    'payment.selectMethod': 'Select a payment method',
    'payment.securityNote': 'Your payment is secured with end-to-end encryption',
    
    // Amenity Booking Manager - Admin
    'amenityBooking.reservations': 'Amenity Reservations',
    'amenityBooking.allStatus': 'All Status',
    'amenityBooking.statusConfirmed': 'Confirmed',
    'amenityBooking.statusCancelled': 'Cancelled',
    'amenityBooking.statusCompleted': 'Completed',
    'amenityBooking.statusPending': 'Pending',
    'amenityBooking.noBookings': 'No bookings found',
    'amenityBooking.noBookingsDesc': 'Reservations will appear here when residents book amenities',
    'amenityBooking.failedToLoad': 'Failed to load bookings',
    'amenityBooking.failedToUpdate': 'Failed to update booking',
    'amenityBooking.bookingUpdated': 'Booking updated',
    'amenityBooking.amenity': 'Amenity',
    'amenityBooking.dateTime': 'Date & Time',
    'amenityBooking.guest': 'Guest',
    'amenityBooking.status': 'Status',
    'amenityBooking.actions': 'Actions',
    'amenityBooking.filterStatus': 'Filter status',
    
    // Common additional
    'common.guest': 'guest',
    'common.guests': 'guests',
    
    // Pricing Page
    'pricingPage.backToHome': 'Back to Home',
    'pricingPage.title': 'Simple, Transparent Pricing',
    'pricingPage.subtitle': 'Choose the plan that fits your needs. No hidden fees.',
    'pricingPage.saveUpTo': 'Save 17%',
    'pricingPage.compareFeatures': 'Compare Features',
    'pricingPage.feature': 'Feature',
    'pricingPage.feature.bookRentals': 'Book luxury rentals',
    'pricingPage.feature.aiConcierge': 'AI Concierge assistant',
    'pricingPage.feature.secureMessaging': 'Secure messaging',
    'pricingPage.feature.listProperty': 'List property for rent',
    'pricingPage.feature.guestManagement': 'Guest management dashboard',
    'pricingPage.feature.revenueAnalytics': 'Revenue analytics',
    'pricingPage.feature.createCommunities': 'Create communities',
    'pricingPage.feature.inviteHomeowners': 'Invite & manage homeowners',
    'pricingPage.feature.hoaCollection': 'HOA payment collection',
    'pricingPage.feature.vendorManagement': 'Vendor marketplace',
    'pricingPage.feature.customBranding': 'Custom branding',
    'pricingPage.contactSalesTitle': 'Contact Sales',
    'pricingPage.contactSalesSubtitle': 'Interested in Sovereign or Architect plans? Get in touch with our team.',
    'pricingPage.yourName': 'Your Name',
    'pricingPage.namePlaceholder': 'John Smith',
    'pricingPage.yourEmail': 'Your Email',
    'pricingPage.emailPlaceholder': 'john@example.com',
    'pricingPage.companyOptional': 'Company (Optional)',
    'pricingPage.companyPlaceholder': 'Your company name',
    'pricingPage.planInterest': 'Plan Interest',
    'pricingPage.homeownerPlan': 'Sovereign (Homeowner)',
    'pricingPage.developerPlan': 'Architect (Developer)',
    'pricingPage.tellUsMore': 'Tell Us More',
    'pricingPage.messagePlaceholder': 'Tell us about your needs, property portfolio, or questions...',
    'pricingPage.sending': 'Sending...',
    'pricingPage.sendInquiry': 'Send Inquiry',
    'pricingPage.inquirySent': 'Your inquiry has been sent! Our sales team will contact you soon.',
    'pricingPage.inquiryFailed': 'Failed to send inquiry. Please try again.',
    
    // Support Page - Sales Inquiry
    'support.salesInquiryTitle': 'Homeowner & Developer Signup',
    'support.salesInquirySubtitle': 'Interested in listing your property or managing a community? Contact our sales team.',
    'support.homeownerPlan': 'Homeowner (Sovereign)',
    'support.homeownerPlanDesc': 'List your property for short or long-term rentals',
    'support.developerPlan': 'Developer (Architect)',
    'support.developerPlanDesc': 'Create and manage communities with multiple properties',
    'support.yourName': 'Your Name',
    'support.yourEmail': 'Your Email',
    'support.companyOptional': 'Company (Optional)',
    'support.companyPlaceholder': 'Your company name',
    'support.tellUsMore': 'Tell Us More',
    'support.salesMessagePlaceholder': 'Tell us about your property, community size, or any questions...',
    'support.submitSalesInquiry': 'Submit Sales Inquiry',
    'support.salesInquirySent': 'Thank you! Our sales team will contact you within 24 hours.',
    'support.salesInquiryFailed': 'Failed to submit inquiry. Please try again.',
    'support.otherWaysToReach': 'Other Ways to Reach Us',
    
    // SmartHome Guide Page
    'smartHome.guide.title': 'Smart Home Sharing Guide',
    'smartHome.guide.subtitle': 'Share your Google Home or Apple HomeKit with guests for a seamless smart home experience during their stay. No complex integrations needed.',
    'smartHome.guide.backToSettings': 'Back to Settings',
    'smartHome.guide.whyGoogleHome': 'Why We Recommend Google Home',
    'smartHome.guide.easyGuestAccess': 'Easy Guest Access',
    'smartHome.guide.easyGuestAccessDesc': 'Invite guests as Home Members with just their email address',
    'smartHome.guide.timeLimitedAccess': 'Time-Limited Access',
    'smartHome.guide.timeLimitedAccessDesc': 'Remove guests instantly when their booking ends',
    'smartHome.guide.devicesSupported': '50,000+ Devices',
    'smartHome.guide.devicesSupportedDesc': 'Works with Nest, Ring, Yale, Philips Hue, and more',
    'smartHome.guide.forPropertyOwners': 'For Property Owners',
    'smartHome.guide.settingUpProperty': 'Setting Up Your Property with Google Home',
    'smartHome.guide.settingUpPropertyDesc': 'One-time setup to prepare your property for guest access',
    'smartHome.guide.step1Download': 'Download the Google Home app',
    'smartHome.guide.step1DownloadDesc': 'Available on iOS and Android. Sign in with your Google account.',
    'smartHome.guide.step2CreateHome': 'Create a "Home" for your property',
    'smartHome.guide.step2CreateHomeDesc': 'Tap the + icon → Create new home → Name it after your property (e.g., "Beach Villa").',
    'smartHome.guide.step3ConnectDevices': 'Connect your smart devices',
    'smartHome.guide.step3ConnectDevicesDesc': 'Link your Nest thermostat, Ring doorbell, smart locks, lights, and other devices to this Home.',
    'smartHome.guide.step4OrganizeRooms': 'Organize devices by room',
    'smartHome.guide.step4OrganizeRoomsDesc': 'Group devices into rooms (Living Room, Bedroom, etc.) so guests can easily control them.',
    'smartHome.guide.invitingGuests': 'Inviting Guests to Your Home',
    'smartHome.guide.inviteStep1': 'Open Google Home → Select your property',
    'smartHome.guide.inviteStep1Desc': 'Make sure you\'re viewing the correct Home for the booking.',
    'smartHome.guide.inviteStep2': 'Go to Settings → Household',
    'smartHome.guide.inviteStep2Desc': 'This is where you manage who has access to your Home.',
    'smartHome.guide.inviteStep3': 'Tap "Invite person" → Enter guest\'s email',
    'smartHome.guide.inviteStep3Desc': 'Use the email from the booking confirmation. They\'ll receive an invitation.',
    'smartHome.guide.inviteStep4': 'Remove guest after checkout',
    'smartHome.guide.inviteStep4Desc': 'Return to Household settings and tap the guest\'s name → Remove from home.',
    'smartHome.guide.tipsForDevelopers': 'Tips for Property Developers',
    'smartHome.guide.tipsForDevelopersDesc': 'Managing multiple properties efficiently',
    'smartHome.guide.tipOneAccount': 'One Google account, multiple Homes',
    'smartHome.guide.tipOneAccountDesc': 'Create a single property management Google account and set up separate "Homes" for each property.',
    'smartHome.guide.tipNameClearly': 'Name properties clearly',
    'smartHome.guide.tipNameClearlyDesc': 'Use descriptive names like "Oceanview Unit 5A" so you can quickly find the right property.',
    'smartHome.guide.tipChecklist': 'Keep a guest access checklist',
    'smartHome.guide.tipChecklistDesc': 'Add guest access the day before check-in and remove it on checkout day.',
    'smartHome.guide.forGuests': 'For Guests',
    'smartHome.guide.usingSmartHome': 'Using Smart Home Features During Your Stay',
    'smartHome.guide.usingSmartHomeDesc': 'Simple steps to control the property\'s smart devices',
    'smartHome.guide.guestStep1': 'Check your email for an invitation',
    'smartHome.guide.guestStep1Desc': 'You\'ll receive a Google Home invitation from the property owner before check-in.',
    'smartHome.guide.guestStep2': 'Accept the invitation',
    'smartHome.guide.guestStep2Desc': 'Click "Accept" in the email. If you don\'t have Google Home, download it first.',
    'smartHome.guide.guestStep3': 'Open the Google Home app',
    'smartHome.guide.guestStep3Desc': 'The property will appear in your list of Homes. Tap to view all smart devices.',
    'smartHome.guide.whatYouCanControl': 'What You Can Control',
    'smartHome.guide.climate': 'Climate',
    'smartHome.guide.climateDesc': 'Adjust thermostat temperature',
    'smartHome.guide.lighting': 'Lighting',
    'smartHome.guide.lightingDesc': 'Control smart lights and scenes',
    'smartHome.guide.smartLocks': 'Smart Locks',
    'smartHome.guide.smartLocksDesc': 'Lock/unlock doors (if enabled)',
    'smartHome.guide.doorbell': 'Doorbell',
    'smartHome.guide.doorbellDesc': 'See who\'s at the door',
    'smartHome.guide.voiceCommands': 'Voice Commands',
    'smartHome.guide.voiceCommandsNote': 'If the property has Google Nest speakers, try these commands:',
    'smartHome.guide.appleHomeKit': 'Apple HomeKit Alternative',
    'smartHome.guide.appleHomeKitDesc': 'For properties using Apple ecosystem exclusively',
    'smartHome.guide.howToShareHomeKit': 'How to share HomeKit access with guests',
    'smartHome.guide.helpfulResources': 'Helpful Resources',
    'smartHome.guide.homeKitNote': 'HomeKit requires guests to have an Apple ID and iOS/macOS device. Google Home supports more device types and works across platforms.',
    'smartHome.guide.downloadApp': 'Download the Google Home App',
    'smartHome.guide.availableOn': 'Available on iOS and Android',
    'smartHome.guide.appStore': 'App Store',
    'smartHome.guide.playStore': 'Play Store',
    
    // AI Page (additional)
    'aiPage.title': 'Aether AI',
    'aiPage.subtitle': 'Your sovereign assistant',
    'aiPage.welcome': 'Hello! I\'m your AI concierge. I can help you with:',
    'aiPage.help.booking': 'Booking services and appointments',
    'aiPage.help.guestAccess': 'Managing guest access',
    'aiPage.help.maintenance': 'Property maintenance requests',
    'aiPage.help.lifestyle': 'Lifestyle recommendations',
    'aiPage.trySaying': 'Try saying',
    'aiPage.placeholder': 'Ask me anything...',
    'aiPage.suggestion1': 'Book a private chef for Saturday',
    'aiPage.suggestion2': 'Schedule pool maintenance',
    'aiPage.suggestion3': 'Authorize my guest for tomorrow',
    'aiPage.suggestion4': 'What services are available?',
    
    // Public Sales Page
    'sales.priceRange': 'Price Range',
    'sales.minBedrooms': 'Minimum Bedrooms',
    'sales.any': 'Any',
    'sales.features': 'Features',
    'sales.clearFilters': 'Clear All Filters',
    'sales.exclusiveProperties': 'Exclusive Properties',
    'sales.propertiesForSale': 'Properties For Sale',
    'sales.discoverProperties': 'Discover exceptional properties in our exclusive luxury communities',
    'sales.forSale': 'For Sale',
    'sales.sqft': 'Sq Ft',
    'sales.keyFeatures': 'Key Features',
    'sales.propertyDirections': 'Property Directions',
    'sales.searchPlaceholder': 'Search by location or community...',
    'sales.filters': 'Filters',
    'sales.propertiesFound': '{count} properties found',
    'sales.propertyFound': '1 property found',
    'sales.viewRentals': 'View Rentals',
    'sales.noProperties': 'No properties match your filters',
    'sales.tryAdjusting': 'Try adjusting your search criteria',
    'sales.luxuryProperty': 'Luxury Property',
    'sales.exclusiveCommunity': 'Exclusive Community',
    'sales.bed': 'bed',
    'sales.bath': 'bath',
    'sales.parking': 'parking',
    'sales.built': 'Built',
    'sales.moreFeatures': '+{count} more',
    'sales.contactForDetails': 'Contact for Details',
    
    // SaleDetailPage
    'saleDetail.back': 'Back',
    'saleDetail.forSale': 'For Sale',
    'saleDetail.bedrooms': 'Bedrooms',
    'saleDetail.bathrooms': 'Bathrooms',
    'saleDetail.parking': 'Parking',
    'saleDetail.builtIn': 'Built',
    'saleDetail.floor': 'Floor',
    'saleDetail.sqft': 'Sq Ft',
    'saleDetail.aboutProperty': 'About This Property',
    'saleDetail.keyFeatures': 'Key Features',
    'saleDetail.amenities': 'Amenities',
    'saleDetail.requestInfo': 'Request Information',
    'saleDetail.name': 'Name',
    'saleDetail.email': 'Email',
    'saleDetail.phone': 'Phone',
    'saleDetail.message': 'Message',
    'saleDetail.interestedMessage': 'I\'m interested in this property...',
    'saleDetail.sendInquiry': 'Send Inquiry',
    'saleDetail.sending': 'Sending...',
    'saleDetail.listingNotFound': 'Listing Not Found',
    'saleDetail.propertyUnavailable': 'This property may no longer be available.',
    'saleDetail.browseProperties': 'Browse Properties',
    'saleDetail.exclusiveLocation': 'Exclusive Location',
    'saleDetail.fillNameEmail': 'Please fill in your name and email',
    'saleDetail.inquirySubmitted': 'Your inquiry has been submitted! A representative will contact you soon.',
    'saleDetail.inquiryFailed': 'Failed to submit inquiry. Please try again.',
    'saleDetail.yourFullName': 'Your full name',
    'saleDetail.yourEmail': 'your@email.com',
    'saleDetail.contactWithin24h': 'A sales representative will contact you within 24 hours.',
    
    // GodMode Dashboard (additional)
    'godMode.tenantAnalytics': 'Tenant Analytics',
    'godMode.tenantAnalyticsDesc': 'View usage metrics and revenue breakdown',
    'godMode.communityAdmins': 'Community Admins',
    'godMode.revenue': 'Revenue',
    'godMode.configure': 'Configure',
    'godMode.admins': 'Admins',
    
    // PropertyDirectionsPage
    'directions.propertyDirections': 'Property Directions',
    'directions.booking': 'Booking',
    'directions.confirmed': 'Confirmed',
    'directions.fullPropertyAddress': 'Full Property Address',
    'directions.copyAddress': 'Copy Address',
    'directions.openInMaps': 'Open in Maps',
    'directions.getDirections': 'Get Directions',
    'directions.stayDetails': 'Stay Details',
    'directions.checkIn': 'Check-in',
    'directions.checkOut': 'Check-out',
    'directions.after3pm': 'After 3:00 PM',
    'directions.before11am': 'Before 11:00 AM',
    'directions.guests': 'Guests',
    'directions.guest': 'Guest',
    'directions.houseRules': 'House Rules',
    'directions.smartHomeAccess': 'Smart Home Access',
    'directions.confidentialInfo': 'This information is confidential and should not be shared with others.',
    'directions.needHelp': 'Need Help?',
    'directions.messageHost': 'Message Host',
    'directions.contactSupport': 'Contact Support',
    'directions.bookingNotFound': 'Booking Not Found',
    'directions.noAccess': 'This booking doesn\'t exist or you don\'t have access.',
    'directions.goToBookings': 'Go to Bookings',
    'directions.addressCopied': 'Address copied to clipboard',
    'directions.addressOnlyConfirmed': 'Property address is only available for confirmed bookings',
    'directions.failedToLoad': 'Failed to load property details',
    
  },
  zh: {
    // Common
    'common.signIn': '登录',
    'common.signUp': '注册',
    'common.signOut': '退出',
    'common.loading': '加载中...',
    'common.save': '保存',
    'common.cancel': '取消',
    'common.delete': '删除',
    'common.edit': '编辑',
    'common.search': '搜索',
    'common.filter': '筛选',
    'common.clear': '清除',
    'common.submit': '提交',
    'common.back': '返回',
    'common.next': '下一步',
    'common.viewAll': '查看全部',
    'common.learnMore': '了解更多',
    
    // Navigation
    'nav.home': '控制台',
    'nav.dashboard': '控制台',
    'nav.services': '服务',
    'nav.rentals': '租赁',
    'nav.map': '地图',
    'nav.wallet': '付款',
    'nav.settings': '设置',
    'nav.messages': '消息',
    'nav.notifications': '通知',
    'nav.concierge': '礼宾服务',
    
    // Landing Page - Hero
    'landing.hero.title': '管理您的房产',
    'landing.hero.titleAccent': '轻松无忧',
    'landing.hero.subtitle': '一个平台满足支付、访客管理、服务预订和住户沟通。减少行政工作，提升住户满意度。',
    'landing.hero.tagline': '面向物业开发商和业主委员会',
    'landing.cta.getStarted': '开始使用',
    'landing.cta.watchDemo': '观看演示',
    'landing.cta.startTrial': '开始免费试用',
    'landing.cta.contactSales': '联系销售',
    'landing.cta.contactSupport': '联系支持',
    'landing.trust.security': '银行级安全',
    'landing.trust.properties': '500+ 物业',
    'landing.trust.residents': '10,000+ 住户',
    'landing.nav.howItWorks': '使用方法',
    'landing.nav.features': '功能',
    'landing.nav.pricing': '价格',
    'landing.nav.signIn': '登录',
    'landing.howItWorks': '使用方法',
    'landing.features': '功能',
    'landing.pricing': '价格',
    
    // Landing Page - How It Works
    'landing.howItWorks.title': '3个简单步骤开始',
    'landing.howItWorks.subtitle': '从注册到正式运营只需不到10分钟。',
    'landing.howItWorks.step1.title': '创建您的账户',
    'landing.howItWorks.step1.desc': '使用邮箱注册。添加社区信息并邀请您的团队。',
    'landing.howItWorks.step2.title': '添加物业和住户',
    'landing.howItWorks.step2.desc': '导入物业列表或手动添加。发送邀请给住户。',
    'landing.howItWorks.step3.title': '正式上线',
    'landing.howItWorks.step3.desc': '立即开始收款、管理访客通行证和预订服务。',
    
    // Landing Page - Personas
    'landing.personas.title': '为社区每个人打造',
    'landing.personas.subtitle': '无论您是管理者、业主还是访客——Aether都适合您。',
    'landing.persona.manager.role': '社区管理员',
    'landing.persona.manager.tagline': '管理运营，而非琐事',
    'landing.persona.manager.benefit1': '一目了然的全物业仪表板',
    'landing.persona.manager.benefit2': '自动收款和提醒',
    'landing.persona.manager.benefit3': '管理供应商和服务提供商',
    'landing.persona.manager.benefit4': '即时向所有住户发送公告',
    'landing.persona.manager.quote': '"我以前每天花3小时处理行政事务。现在只需30分钟。"',
    'landing.persona.owner.role': '物业业主',
    'landing.persona.owner.tagline': '您的物业，您做主',
    'landing.persona.owner.benefit1': '一键支付物业费',
    'landing.persona.owner.benefit2': '为您的房屋预订礼宾服务',
    'landing.persona.owner.benefit3': '将您的物业列入短租',
    'landing.persona.owner.benefit4': '安全地为访客发放通行证',
    'landing.persona.owner.quote': '"终于有一款将我的房产当作资产对待的应用。"',
    'landing.persona.guest.role': '访客',
    'landing.persona.guest.tagline': '像归属者一样到达',
    'landing.persona.guest.benefit1': '二维码入门——无需在门口等待',
    'landing.persona.guest.benefit2': '探索社区地图和设施',
    'landing.persona.guest.benefit3': '在应用内直接与房东联系',
    'landing.persona.guest.benefit4': '仅在入住期间有效的访问权限',
    'landing.persona.guest.quote': '"这是我经历过的最顺畅的入住体验。"',
    
    // Landing Page - Property Types
    'landing.propertyTypes.title': '为高端社区打造',
    'landing.propertyTypes.subtitle': '无论您管理公寓、封闭社区还是豪华租赁——Aether都能满足您的需求。',
    'landing.propertyTypes.penthouse': '城市顶层公寓',
    'landing.propertyTypes.penthouseDesc': '天际线美景，屋顶露台',
    'landing.propertyTypes.estate': '现代庄园',
    'landing.propertyTypes.estateDesc': '建筑杰作',
    'landing.propertyTypes.beachfront': '海滨度假屋',
    'landing.propertyTypes.beachfrontDesc': '私人海滩，无尽地平线',
    
    // Landing Page - Features
    'landing.features.title': '您需要的一切，无需多余',
    'landing.features.subtitle': '用一个平台替代5+种工具。从第一天起节省时间和成本。',
    'landing.feature.portfolio.title': '集中投资组合控制',
    'landing.feature.portfolio.desc': '在一个仪表板上管理所有物业。不再需要在电子表格、邮件和应用之间切换。',
    'landing.feature.bookings.title': '自动化服务预订',
    'landing.feature.bookings.desc': '让住户自行预订清洁、维护和礼宾服务——减少80%的行政工作。',
    'landing.feature.messaging.title': '更快的响应时间',
    'landing.feature.messaging.desc': '内置消息系统让业主、访客和供应商保持联系——不再错过电话或丢失邮件。',
    'landing.feature.payments.title': '按时收款',
    'landing.feature.payments.desc': '自动化物业费收取和服务付款。减少60%的逾期付款。',
    'landing.feature.maps.title': '展示您的社区',
    'landing.feature.maps.desc': '交互式地图帮助住户和访客发现设施，提高参与度和满意度。',
    'landing.feature.guests.title': '安全的访客访问',
    'landing.feature.guests.desc': '带时间限制的二维码通行证。了解谁何时进入您的物业——消除安全漏洞。',
    
    // Landing Page - Pricing
    'landing.pricing.title': '简单、可预测的定价',
    'landing.pricing.subtitle': '没有隐藏费用。没有按交易收费。只有一个固定费率。',
    'landing.pricing.mostPopular': '最受欢迎',
    'landing.pricing.perMonth': '月',
    'landing.pricing.custom': '定制',
    'landing.pricing.sovereign.name': 'Sovereign',
    'landing.pricing.sovereign.desc': '适合追求卓越的物业业主',
    'landing.pricing.sovereign.feature1': '最多3个物业',
    'landing.pricing.sovereign.feature2': 'AI礼宾服务',
    'landing.pricing.sovereign.feature3': '访客通行证管理',
    'landing.pricing.sovereign.feature4': '物业费支付门户',
    'landing.pricing.sovereign.feature5': '实时通知',
    'landing.pricing.sovereign.feature6': '基础分析',
    'landing.pricing.elite.name': 'Elite',
    'landing.pricing.elite.desc': '适合拥有大量物业的用户',
    'landing.pricing.elite.feature1': '无限物业',
    'landing.pricing.elite.feature2': '优先AI礼宾服务',
    'landing.pricing.elite.feature3': '高级访客管理',
    'landing.pricing.elite.feature4': '租赁市场访问',
    'landing.pricing.elite.feature5': '收入分析仪表板',
    'landing.pricing.elite.feature6': '白手套支持',
    'landing.pricing.elite.feature7': '定制集成',
    'landing.pricing.enterprise.name': 'Enterprise',
    'landing.pricing.enterprise.desc': '适合社区和业主委员会',
    'landing.pricing.enterprise.feature1': '全社区部署',
    'landing.pricing.enterprise.feature2': '专属客户经理',
    'landing.pricing.enterprise.feature3': '定制品牌',
    'landing.pricing.enterprise.feature4': 'API访问',
    'landing.pricing.enterprise.feature5': 'SLA保证',
    'landing.pricing.enterprise.feature6': '现场培训',
    'landing.pricing.enterprise.feature7': '多社区支持',
    'landing.pricing.getStarted': '开始使用',
    'landing.pricing.contactSales': '联系销售',
    
    // Landing Page - Testimonials
    'landing.testimonials.title': '物业管理者热爱Aether',
    'landing.testimonials.subtitle': '看看团队如何节省时间并让住户满意。',
    'landing.testimonial1.quote': '我们的行政时间减少了一半。住户自行预订服务，付款自动到账。',
    'landing.testimonial1.author': 'Alexander Chen',
    'landing.testimonial1.role': '物业开发商，比弗利山庄',
    'landing.testimonial2.quote': '访客访问曾是噩梦。现在访客获得二维码，我们有完整的审计记录。',
    'landing.testimonial2.author': 'Victoria Sterling',
    'landing.testimonial2.role': '业委会主席，迈阿密',
    'landing.testimonial3.quote': '我们的住户喜欢这个应用。服务请求减少了40%，因为他们可以自己找到答案。',
    'landing.testimonial3.author': 'Marcus Williams',
    'landing.testimonial3.role': '社区经理，200+户',
    
    // Landing Page - CTA Section
    'landing.cta.title': '准备简化您的运营？',
    'landing.cta.subtitle': '几分钟内设置完成。几天内看到效果。无合同，随时取消。',
    
    // Landing Page - FAQ
    'landing.faq.title': '常见问题',
    'landing.faq.subtitle': '关于开始使用Aether的所有信息。',
    'landing.faq.stillQuestions': '还有问题？',
    'landing.faq.q1': '为我的社区设置Aether需要多长时间？',
    'landing.faq.a1': '大多数社区在10-15分钟内即可正式运营。只需创建账户、添加物业和住户，即可开始使用。我们的引导向导会指导您完成每一步，如果需要帮助，我们的支持团队24/7在线。',
    'landing.faq.q2': '我的数据在Aether安全吗？',
    'landing.faq.a2': '绝对安全。我们使用银行级256位SSL加密进行所有数据传输。您的数据存储在SOC 2 Type II认证的数据中心，正常运行时间99.99%。我们还提供双因素认证和基于角色的访问控制，确保只有授权用户才能访问敏感信息。',
    'landing.faq.q3': '我可以在承诺付费计划之前试用Aether吗？',
    'landing.faq.a3': '可以！我们提供14天免费试用，完全访问所有功能。无需信用卡即可开始。您可以探索平台、添加测试物业，在做出任何承诺之前了解Aether如何简化您的运营。',
    'landing.faq.q4': '支付处理是如何工作的？',
    'landing.faq.a4': 'Aether与Stripe集成进行安全支付处理。住户可以直接通过应用支付物业费、服务费和租金。资金在2-3个工作日内存入您的账户。我们收取固定平台费——没有隐藏的按交易收费。',
    'landing.faq.q5': '我可以将Aether与现有工具集成吗？',
    'landing.faq.a5': '我们的Elite和Enterprise计划包括API访问和定制集成。我们与流行的会计软件、物业管理系统和通信工具集成。我们的团队可以帮助您将Aether与现有技术栈连接。',
    'landing.faq.q6': '如果遇到问题有什么支持？',
    'landing.faq.a6': '所有计划都包括邮件支持，响应时间在24小时内。专业计划获得优先支持，响应时间4小时。企业客户有专属客户经理和24/7电话支持。我们还提供全面的文档和视频教程。',
    'landing.faq.contactSupport': '联系支持',
    
    
    // Landing Page - Footer
    'landing.footer.tagline': 'Sovereign Living',
    'landing.footer.copyright': '© {{year}} Aether. 物业管理，简化。',
    'landing.footer.privacy': '隐私政策',
    'landing.footer.terms': '服务条款',
    'landing.footer.support': '支持',
    
    // Google Auth
    'auth.orContinueWith': '或使用以下方式继续',
    'auth.signInWithGoogle': '使用 Google 登录',
    'auth.googleSignInError': 'Google 登录失败',
    
    // Auth
    'auth.welcomeBack': '欢迎回来',
    'auth.createAccount': '创建账户',
    'auth.resetPassword': '重置密码',
    'auth.setPassword': '设置密码',
    'auth.email': '邮箱',
    'auth.password': '密码',
    'auth.confirmPassword': '确认密码',
    'auth.forgotPassword': '忘记密码？',
    'auth.noAccount': '还没有账户？',
    'auth.hasAccount': '已有账户？',
    'auth.backToHome': '返回首页',
    
    // Dashboard
    'dashboard.welcome': '欢迎回来',
    'dashboard.properties': '物业',
    'dashboard.bookings': '预订',
    'dashboard.payments': '支付',
    'dashboard.guests': '访客',
    
    // Rentals
    'rentals.title': '发现优质房产',
    'rentals.subtitle': '体验世界级设施和个性化礼宾服务',
    'rentals.searchPlaceholder': '按位置或社区搜索...',
    'rentals.pricePerNight': '每晚',
    'rentals.bedrooms': '卧室',
    'rentals.bathrooms': '浴室',
    'rentals.bookNow': '立即预订',
    'rentals.checkIn': '入住',
    'rentals.checkOut': '退房',
    'rentals.guests': '客人',
    'rentals.noResults': '没有符合条件的房产',
    'rentals.askAI': '让AI帮您找房',
    
    // AI Assistant
    'ai.greeting': '您好！我是您的Aether礼宾。有什么可以帮您的吗？',
    'ai.placeholder': '问我任何问题...',
    'ai.listening': '正在听...',
    'ai.findRentals': '查找可用租赁',
    
    // Settings
    'settings.title': '设置',
    'settings.language': '语言',
    'settings.currency': '货币',
    'settings.timezone': '时区',
    'settings.notifications': '通知',
    'settings.appearance': '外观',
    'settings.darkMode': '深色模式',
    'settings.profile': '个人信息',
    'settings.profileDesc': '姓名、电话、头像',
    'settings.notificationsDesc': '邮件和推送设置',
    'settings.payment': '支付方式',
    'settings.paymentDesc': '管理银行卡和账单',
    'settings.subscriptions': '订阅',
    'settings.subscriptionsDesc': 'SaaS和HOA计划',
    'settings.guestPasses': '访客通行证',
    'settings.guestPassesDesc': '管理访客权限',
    'settings.languageCurrency': '语言和货币',
    'settings.languageCurrencyDesc': '地区偏好设置',
    'settings.smartHome': '智能家居共享',
    'settings.smartHomeDesc': '与客人共享Google Home访问权限',
    'settings.viewAs': '视角模拟器',
    'settings.viewAsDesc': '测试不同用户体验',
    'settings.signOut': '退出登录',
    
    // Messages
    'messages.title': '消息',
    'messages.subtitle': '对话、AI和通知',
    'messages.chats': '聊天',
    'messages.aether': 'Aether',
    'messages.alerts': '提醒',
    'messages.noConversations': '暂无对话',
    'messages.noConversationsDesc': '与客人和房东的消息将显示在这里',
    'messages.noNotifications': '暂无通知',
    'messages.noNotificationsDesc': '社区公告将显示在这里',
    'messages.markAllRead': '全部标为已读',
    'messages.startConversation': '开始对话',
    'messages.aetherTitle': 'Aether AI礼宾',
    'messages.aetherDesc': '您的个人AI助手，用于预订服务、获取建议和管理物业需求。',
    
    // Browse
    'nav.browse': '探索',
    
    // Dashboard specific
    'dashboard.guestAccess': '访客权限',
    'dashboard.accessExpires': '权限到期',
    'dashboard.daysRemaining': '天访客权限剩余',
    'dashboard.browseRentals': '浏览租赁',
    'dashboard.services': '服务',
    'dashboard.personalizeSearch': '个性化搜索',
    'dashboard.updatePreferences': '更新您的租赁偏好',
    'dashboard.availableServices': '可用服务',
    'dashboard.communityMap': '社区地图',
    'dashboard.exploreAmenities': '探索设施',
    'dashboard.dining': '餐饮',
    'dashboard.requestMeals': '请求餐食',
    'dashboard.transport': '交通',
    'dashboard.requestValet': '请求代客泊车',
    'dashboard.housekeeping': '客房服务',
    'dashboard.ownerOnly': '仅限业主',
    'dashboard.guestGuidelines': '访客须知',
    'dashboard.propertyManager': '物业管理',
    'dashboard.guestAtProperty': '此物业的访客',
    'dashboard.guidelineQuietHours': '静音时间为晚上10点至早上7点',
    'dashboard.guidelinePoolGym': '泳池和健身房开放时间为早上6点至晚上10点',
    'dashboard.guidelineConcierge': '如需帮助请联系礼宾服务',
    'dashboard.guidelineRespect': '请尊重所有社区设施和空间',
    'dashboard.greetingMorning': '早上好',
    'dashboard.greetingAfternoon': '下午好',
    'dashboard.greetingEvening': '晚上好',
    'dashboard.loading': '正在加载您的仪表板...',
    'dashboard.welcomeAether': '欢迎来到Aether',
    'dashboard.yourSmartHome': '您的智能家居',
    'dashboard.allSystemsOperational': '所有系统正常运行',
    'dashboard.quickActions': '快速操作',
    'dashboard.propertyStatus': '物业状态',
    'dashboard.communityUpdates': '社区更新',
    'dashboard.upcoming': '即将到来',
    'dashboard.confirmed': '已确认',
    'dashboard.rentalMode': '租赁模式',
    'dashboard.rentalModeDesc': '上架短租',
    'dashboard.rentalModeEnabled': '租赁模式已启用',
    'dashboard.rentalModeDisabled': '租赁模式已禁用',
    'dashboard.createGuestPass': '创建访客通行证',
    'dashboard.guestEmail': '访客邮箱地址',
    'dashboard.accessDuration': '访问期限（天）',
    'dashboard.sendGuestPass': '发送访客通行证',
    'dashboard.needProperty': '您需要拥有物业才能创建访客通行证',
    'dashboard.privateDinner': '私人晚餐',
    
    // AI Page
    'ai.title': 'Aether AI',
    'ai.subtitle': '您的专属助理',
    'ai.welcomeMessage': '您好！我是您的AI礼宾。我可以帮助您：',
    'ai.helpBooking': '预订服务和约会',
    'ai.helpGuest': '管理访客权限',
    'ai.helpMaintenance': '物业维护请求',
    'ai.helpLifestyle': '生活方式建议',
    'ai.trySaying': '试着说',
    'ai.suggestion1': '为周六预订私人厨师',
    'ai.suggestion2': '安排泳池维护',
    'ai.suggestion3': '授权我明天的客人',
    'ai.suggestion4': '有哪些服务可用？',
    
    // Role Simulator (Chinese)
    'roleSimulator.title': '角色模拟器',
    'roleSimulator.description': '选择一个角色来模拟体验。模拟时会显示提示条。',
    'roleSimulator.godMode': '超级管理',
    'roleSimulator.godModeDesc': '平台全权限访问',
    'roleSimulator.admin': '社区管理员',
    'roleSimulator.adminDesc': '管理社区物业和用户',
    'roleSimulator.homeowner': '业主',
    'roleSimulator.homeownerDesc': '物业所有者体验',
    'roleSimulator.guest': '访客',
    'roleSimulator.guestDesc': '限时访客权限',
    'roleSimulator.active': '当前',
    'roleSimulator.exitSimulation': '退出模拟模式',
    
    // Simulation Banner
    'simulation.simulating': '模拟中',
    'simulation.exit': '退出模拟',
    
    // Cookie Consent
    'cookies.message': '我们使用cookies来增强您的体验。',
    'cookies.learnMore': '阅读我们的隐私政策',
    'cookies.accept': '接受',
    'cookies.dismiss': '忽略',
    'common.close': '关闭',
    
    // Notification Center
    'notificationCenter.new': '条新',
    'notificationCenter.noNotifications': '暂无通知',
    'notificationCenter.noNotificationsDesc': '付款提醒和更新将显示在这里',
    
    // Toast Messages
    'toast.loadFailed': '加载数据失败',
    'toast.emailRequired': '请输入邮箱',
    'toast.inviteSent': '邀请发送成功',
    'toast.inviteFailed': '发送邀请失败',
    'toast.addressRequired': '请输入地址',
    'toast.propertyAdded': '物业添加成功',
    'toast.propertyAddFailed': '添加物业失败',
    'toast.rentalModeEnabled': '租赁模式已启用',
    'toast.rentalModeDisabled': '租赁模式已禁用',
    'toast.rentalModeUpdateFailed': '更新租赁模式失败',
    'toast.rentalListingSaved': '出租房源保存成功',
    'toast.rentalListingSaveFailed': '保存出租房源失败',
    'toast.communityNameRequired': '请输入社区名称',
    'toast.communityCreated': '社区创建成功',
    'toast.communityCreateFailed': '创建社区失败',
    'toast.adminInviteSent': '管理员邀请已发送',
    'toast.inviteDeleted': '邀请已删除，可以重新邀请',
    'toast.deleteFailed': '删除失败',
    'toast.listingSaved': '列表保存成功',
    'toast.listingSaveFailed': '保存列表失败',
    'toast.calendarUpdated': '日历已更新',
    'toast.calendarUpdateFailed': '更新日历失败',
    'toast.ownerInviteSent': '业主邀请发送成功',
    'toast.updateFailed': '更新失败',
    'common.error': '错误',
    'common.unknown': '未知',
    
    // Smart Home (Phase 5)
    'smartHome.title': '智能家居访问',
    'smartHome.shareTitle': '与客人共享智能家居',
    'smartHome.shareDesc': '使用Google Home为客人提供智能设备访问权限',
    'smartHome.quickSteps': '快速步骤',
    'smartHome.step1': '打开Google Home应用',
    'smartHome.step2': '转到设置 → 家庭',
    'smartHome.step3': '使用邮箱邀请客人',
    'smartHome.remember': '请记住',
    'smartHome.reminderText': '入住前添加客人权限，退房后移除',
    'smartHome.viewGuide': '查看完整指南',
    'smartHome.worksWith': '适用于：',
    'smartHome.devicesList': 'Nest、Ring、Yale、August、Philips Hue及50,000+Google Home兼容设备',
    
    // Rental Widget (Phase 5)
    'rental.status': '租赁状态',
    'rental.bookings': '预订',
    'rental.pending': '待处理',
    'rental.revenue': '收入',
    'rental.rating': '评分',
    'rental.view': '查看',
    'rental.edit': '编辑',
    'rental.calendar': '日历',
    'rental.completeListing': '完成列表以开始赚钱',
    'rental.enableMode': '启用租赁模式以列出您的房产并开始赚钱',
    'rental.enableButton': '启用租赁模式',
    'rental.modeEnabled': '租赁模式已启用',
    'rental.modeDisabled': '租赁模式已禁用',
    'rental.updateFailed': '更新租赁模式失败',
    
    // Homeowner Dashboard Widget (Phase 5)
    'homeowner.upcomingHOA': '即将到期的HOA费用',
    'homeowner.payNow': '立即付款',
    'homeowner.noHOADues': '未配置HOA费用',
    'homeowner.dueToday': '今天到期',
    'homeowner.daysLeft': '{days}天',
    'homeowner.communityUpdates': '社区更新',
    'homeowner.new': '新',
    'homeowner.noAnnouncements': '暂无最新公告',
    
    // Transactions
    'transaction.pending': '待处理',
    'transaction.processing': '处理中',
    'transaction.completed': '已完成',
    'transaction.failed': '失败',
    'transaction.refunded': '已退款',
    'transaction.type.hoa_dues': 'HOA费用',
    'transaction.type.service_payment': '服务付款',
    'transaction.type.refund': '退款',
    'transaction.type.deposit': '押金',
    
    // HOA Payments
    'hoa.monthlyDues': '每月HOA费用',
    'hoa.dueOnDay': '每月{day}日到期',
    'hoa.perMonth': '/月',
    'hoa.nextPayment': '下次付款',
    'hoa.payNow': '立即付款',
    'hoa.allCaughtUp': '全部缴清！',
    'hoa.notConfigured': '此物业未配置HOA费用',
    'hoa.paymentHistory': '付款历史',
    'hoa.noProperties': '未找到物业',
    'hoa.noPropertiesDesc': '您需要拥有物业才能支付HOA费用',
    'hoa.choosePaymentMethod': '选择付款方式',
    'hoa.paid': '已付',
    'hoa.verifying': '验证中',
    
    // Concierge Page
    'concierge.title': '礼宾服务',
    'concierge.subtitle': '专属定制服务',
    'concierge.askAether': '询问Aether',
    'concierge.services': '服务',
    'concierge.myBookings': '我的预订',
    'concierge.serviceCategories': '服务类别',
    'concierge.yourBookings': '您的预订',
    'concierge.viewAllBookings': '查看所有预订',
    'concierge.manageBookings': '查看和管理您即将到来的服务预订',
    'concierge.notAvailable': '礼宾服务不可用',
    'concierge.notAvailableDesc': '您的社区尚未启用礼宾服务。请联系管理员了解更多信息。',
    'concierge.noCommunity': '无社区访问权限',
    'concierge.noCommunityDesc': '加入社区后即可使用礼宾服务。',
    'concierge.createCommunity': '创建您的第一个社区',
    'concierge.noServices': '服务即将推出',
    'concierge.noServicesDesc': '您的社区管理员正在设置礼宾服务。请稍后再来查看！',
    
    // Service Categories
    'service.housekeeping': '客房服务',
    'service.housekeepingDesc': '高级清洁和洗衣服务',
    'service.wellness': '健康',
    'service.wellnessDesc': '水疗、健身和养生',
    'service.maintenance': '维护',
    'service.maintenanceDesc': '物业保养和维修',
    'service.dining': '私人餐饮',
    'service.diningDesc': '精选美食体验',
    'service.transport': '交通',
    'service.transportDesc': '豪华车辆服务',
    
    // Service Marketplace
    'marketplace.title': '服务',
    'marketplace.subtitle': '预订优质服务',
    'marketplace.all': '全部',
    'marketplace.noServices': '暂无可用服务',
    'marketplace.checkBack': '稍后再来查看新服务',
    'marketplace.book': '预订',
    'marketplace.estimatedPrice': '预估价格',
    'marketplace.priceVaries': '*最终价格可能因物业大小而异',
    'marketplace.scheduleDateTime': '安排日期和时间',
    'marketplace.specialRequests': '特殊要求（可选）',
    'marketplace.specialRequestsPlaceholder': '任何特殊说明...',
    'marketplace.payWithCard': '用卡支付（美元）',
    
    // Sovereign Dashboard
    'sovereign.propertyManager': '物业管理',
    'sovereign.properties': '个物业',
    'sovereign.selectProperty': '选择物业',
    'sovereign.noPropertyTitle': '未找到物业',
    'sovereign.noPropertyDesc': '您的账户没有分配任何物业。',
    'sovereign.goToDashboard': '前往仪表板',
    'sovereign.standaloneProperty': '独立物业',
    'sovereign.community': '社区',
    'sovereign.rentalMode': '租赁模式',
    'sovereign.rentalModeDesc': '将物业列入短期出租',
    'sovereign.totalBookings': '总预订',
    'sovereign.pending': '待处理',
    'sovereign.revenue': '收入',
    'sovereign.rating': '评分',
    'sovereign.bookings': '预订',
    'sovereign.calendar': '日历',
    'sovereign.listing': '列表',
    'sovereign.reviews': '评价',
    'sovereign.smartHome': '智能家居',
    'sovereign.guestReviews': '客人评价',
    'sovereign.enableRentalMode': '启用租赁模式',
    'sovereign.enableRentalModeDesc': '在市场上列出您的物业进行短期租赁。设置您自己的价格，管理可用性，并从您的物业中获得收入。',
    'sovereign.setRates': '设置自定义每晚价格和清洁费',
    'sovereign.controlCalendar': '控制您的可用性日历',
    'sovereign.manageGuests': '批准客人并管理预订',
    
    // Admin Dashboard
    'admin.title': '仪表板',
    'admin.role': '管理员',
    'admin.godMode': '上帝模式',
    'admin.properties': '物业',
    'admin.owners': '业主',
    'admin.collected': '已收取',
    'admin.overview': '概览',
    'admin.forSale': '出售',
    'admin.announcements': '公告',
    'admin.bookings': '预订',
    'admin.services': '服务',
    'admin.vendors': '供应商',
    'admin.earnings': '收益',
    'admin.reviews': '评价',
    'admin.more': '更多',
    'admin.hoaStatus': 'HOA收款状态',
    'admin.paid': '已付',
    'admin.pending': '待付',
    'admin.overdue': '逾期',
    'admin.invite': '邀请',
    'admin.inviteHomeowner': '邀请业主',
    'admin.addProperty': '添加物业',
    'admin.sendInvitation': '发送邀请',
    'admin.noProperties': '暂无物业',
    'admin.addFirstProperty': '添加您的第一个物业以开始',
    'admin.moreProperties': '更多物业',
    'admin.unassigned': '未分配',
    'admin.rental': '出租',
    'admin.rentalMode': '出租模式',
    'admin.manageListing': '管理',
    'admin.rentalListingTitle': '管理出租房源',
    'admin.streetAddress': '街道地址',
    'admin.unitNumber': '单元号（可选）',
    'admin.bedrooms': '卧室',
    'admin.bathrooms': '浴室',
    'admin.manageAnnouncements': '管理公告',
    'admin.dragToReschedule': '拖动以重新安排',
    'admin.allReservations': '所有预订',
    'admin.createCommunity': '创建新社区',
    'admin.createCommunityDesc': '设置新社区以管理物业和业主',
    'admin.communityMap': '社区地图',
    'admin.welcome': '欢迎',
    'admin.createFirstCommunity': '创建您的第一个社区',
    'admin.createFirstCommunityDesc': '开始创建社区以管理物业和业主。',
    
    // Browse Rentals
    'browse.title': '浏览租赁',
    'browse.luxuryStays': '豪华住宿',
    'browse.heroTitle': '发现优质房产',
    'browse.heroSubtitle': '在我们的专属社区体验世界级设施和个性化礼宾服务',
    'browse.searchPlaceholder': '按位置或社区搜索...',
    'browse.filters': '筛选',
    'browse.priceRange': '价格范围（每晚）',
    'browse.minBedrooms': '最少卧室',
    'browse.amenities': '设施',
    'browse.any': '不限',
    'browse.clearFilters': '清除所有筛选',
    'browse.propertiesFound': '找到 {count} 处房产',
    'browse.propertyFound': '找到 1 处房产',
    'browse.noReviews': '暂无评价',
    'browse.luxuryProperty': '豪华房产',
    'browse.privateCommunity': '私人社区',
    'browse.beds': '卧室',
    'browse.baths': '浴室',
    'browse.moreAmenities': '+{count} 更多',
    'browse.additionalAmenities': '其他设施：',
    'browse.viewDetails': '查看详情',
    'browse.bookNow': '立即预订',
    'browse.bookYourStay': '预订住宿',
    'browse.booking': '预订',
    'browse.reviews': '评价',
    'browse.addressRevealed': '预订确认后将显示完整地址',
    'browse.checkIn': '入住',
    'browse.checkOut': '退房',
    'browse.selectDate': '选择日期',
    'browse.guests': '客人',
    'browse.nights': '晚',
    'browse.cleaningFee': '清洁费',
    'browse.total': '总计',
    'browse.confirmPay': '确认并支付',
    'browse.signInToBook': '登录预订',
    'browse.processing': '处理中...',
    'browse.minNights': '最少 {min} 晚',
    'browse.maxNights': '最多 {max} 晚',
    'browse.noPropertiesMatch': '没有符合条件的房产',
    'browse.tryAdjusting': '请尝试调整搜索条件',
    
    // Listing Detail Page (new)
    'listing.notFound': '未找到房源',
    'listing.notFoundDesc': '此房产可能已不可用',
    'listing.browseRentals': '浏览房源',
    'listing.viewAllPhotos': '查看全部 {count} 张照片',
    'listing.allPhotos': '所有照片',
    'listing.about': '关于此房产',
    'listing.aboutProperty': '关于此房产',
    'listing.amenities': '设施',
    'listing.houseRules': '房屋规则',
    'listing.sqft': '平方英尺',
    'listing.night': '晚',
    'listing.image': '照片',
    'listing.payWithCard': '用卡支付',
    'listing.payWithKBZ': '用KBZPay支付',
    'listing.payWithWave': '用Wave Money支付',
    'listing.signInRequired': '请登录以预订此房产',
    'listing.pleaseSignIn': '请登录以预订此房产',
    'listing.selectDates': '请选择入住和退房日期',
    'listing.minStay': '最少住宿 {min} 晚',
    'listing.maxStay': '最多住宿 {max} 晚',
    
    // Booking Confirmation Page (new)
    'confirmation.invalid': '无效预订',
    'confirmation.invalidDesc': '未提供预订ID',
    'confirmation.browseProperties': '浏览房产',
    'confirmation.notFound': '未找到预订',
    'confirmation.notFoundDesc': '我们找不到此预订',
    'confirmation.confirmed': '预订已确认！',
    'confirmation.pending': '预订待确认',
    'confirmation.accessPass': '房产访问通行证',
    'confirmation.propertyDetails': '房产详情',
    'confirmation.stayDetails': '住宿详情',
    'confirmation.paymentSummary': '付款摘要',
    'confirmation.leaveReview': '留下评价',
    'confirmation.viewDirections': '查看房产方向和地图',
    'confirmation.messageOwner': '联系房东',
    'confirmation.viewAllBookings': '查看所有预订',
    'confirmation.goToDashboard': '前往仪表板',
    'confirmation.checkIn': '入住',
    'confirmation.checkOut': '退房',
    'confirmation.guests': '客人',
    'confirmation.nights': '晚',
    'confirmation.cleaningFee': '清洁费',
    'confirmation.total': '总计',
    'confirmation.paymentMethod': '付款方式',
    'confirmation.verificationPending': '付款验证中',
    
    // Wallet Page (new)
    'wallet.title': '钱包',
    'wallet.subtitle': '付款和交易',
    'wallet.paymentMethods': '付款方式',
    'wallet.hoaDues': 'HOA费用',
    'wallet.serviceHistory': '服务历史',
    'wallet.viewAll': '查看全部',
    'wallet.noTransactions': '暂无交易',
    'wallet.noTransactionsDesc': '预订服务后将显示在这里',
    'wallet.paymentSuccess': '付款成功',
    'wallet.paymentSuccessDesc': '您的付款已处理。',
    'wallet.paymentCanceled': '付款已取消',
    'wallet.paymentCanceledDesc': '您的付款已取消。',
    'wallet.cardAdded': '卡已添加',
    'wallet.cardAddedDesc': '您的付款方式已保存。',
    'wallet.noPaymentMethods': '没有保存的付款方式',
    'wallet.addCardDesc': '添加卡以支付服务和费用',
    'wallet.addCard': '添加卡',
    'wallet.continueAddCard': '继续添加卡',
    'wallet.addPaymentMethod': '添加付款方式',
    'wallet.default': '默认',
    'wallet.manage': '管理',
    'wallet.securePayment': '安全支付',
    'wallet.cardSecure': '您的卡信息已加密并安全',
    'wallet.stripeRedirect': '您将被重定向到Stripe的安全支付页面添加您的卡。',
    'wallet.openingStripe': '正在打开Stripe添加您的卡...',
    'wallet.failedAddMethod': '添加付款方式失败',
    'wallet.failedOpenPortal': '打开账单门户失败',
    'wallet.expires': '有效期',
    
    // Payments Page (Chinese)
    'payments.title': '付款',
    'payments.subtitle': '管理付款和交易',
    'payments.noCommunity': '无社区访问权限',
    'payments.noCommunityDesc': '加入社区后即可使用付款功能。',
    'payments.createCommunity': '创建您的第一个社区',
    
    // Amenity translations (Chinese)
    'amenity.privatePool': '私人泳池',
    'amenity.pool': '游泳池',
    'amenity.hotTub': '热水浴缸',
    'amenity.oceanView': '海景',
    'amenity.sunsetViews': '日落美景',
    'amenity.beachAccess': '海滩通道',
    'amenity.smartHome': '智能家居',
    'amenity.wineCellar': '酒窖',
    'amenity.homeTheater': '家庭影院',
    'amenity.gym': '健身房',
    'amenity.spa': '水疗',
    'amenity.rooftopTerrace': '屋顶露台',
    'amenity.concierge': '礼宾服务',
    'amenity.wifi': 'WiFi',
    'amenity.parking': '停车位',
    'amenity.airConditioning': '空调',
    'amenity.heating': '暖气',
    'amenity.kitchen': '厨房',
    'amenity.washer': '洗衣机',
    'amenity.dryer': '烘干机',
    'amenity.washerDryer': '洗烘一体机',
    'amenity.petFriendly': '宠物友好',
    'amenity.garden': '花园',
    'amenity.fireplace': '壁炉',
    'amenity.balcony': '阳台',
    
    // Guest Pass (new)
    'guestPass.title': '访客通行证',
    'guestPass.subtitle': '管理临时访客权限',
    'guestPass.header': '访客通行证',
    'guestPass.newPass': '新建通行证',
    'guestPass.createPass': '创建访客通行证',
    'guestPass.property': '房产',
    'guestPass.selectProperty': '选择房产',
    'guestPass.guestEmail': '访客邮箱',
    'guestPass.guestName': '访客姓名（可选）',
    'guestPass.accessStart': '访问开始',
    'guestPass.accessEnd': '访问结束',
    'guestPass.selectStartDate': '选择开始日期',
    'guestPass.selectEndDate': '选择结束日期',
    'guestPass.duration': '时长：{days} 天',
    'guestPass.sendPass': '发送访客通行证',
    'guestPass.sending': '发送中...',
    'guestPass.noProperties': '无法创建通行证',
    'guestPass.noPropertiesDesc': '您需要先拥有房产才能创建访客通行证。',
    'guestPass.activePasses': '有效通行证',
    'guestPass.expiredPasses': '已过期通行证',
    'guestPass.noActivePasses': '暂无有效访客通行证',
    'guestPass.noExpiredPasses': '暂无过期访客通行证',
    'guestPass.guest': '访客',
    'guestPass.expires': '到期时间',
    'guestPass.expired': '已过期',
    'guestPass.showQR': '显示二维码',
    'guestPass.revoke': '撤销',
    'guestPass.revokeConfirm': '确定撤销此访客通行证？此操作不可撤销。',
    
    // Reviews (new)
    'reviews.title': '评价',
    'reviews.noReviews': '暂无评价',
    'reviews.noReviewsDesc': '此房产尚无评价',
    'reviews.overallRating': '总体评分',
    'reviews.cleanliness': '清洁度',
    'reviews.accuracy': '准确性',
    'reviews.communication': '沟通',
    'reviews.location': '位置',
    'reviews.value': '性价比',
    'reviews.ownerResponse': '房东回复',
    'reviews.basedOn': '基于 {count} 条评价',
    
    // Chat (new)
    'chat.title': 'Aether AI 助手',
    'chat.placeholder': '输入消息或说"你好"开始...',
    'chat.send': '发送',
    'chat.voiceInput': '语音输入',
    'chat.listening': '正在听...',
    'chat.clearHistory': '清除历史',
    'chat.suggestedPrompts': '建议提示',
    'chat.findRental': '帮我找合适的租房',
    'chat.bookService': '我想预订服务',
    'chat.checkPayment': '查看我的付款状态',
    'chat.getDirections': '获取我房产的方向',
    
    // Auth Extended (new)
    'auth.fullName': '全名',
    'auth.phone': '电话号码',
    'auth.signingIn': '登录中...',
    'auth.signingUp': '注册中...',
    'auth.resetting': '重置中...',
    'auth.checkEmail': '请查收邮件中的重置链接',
    'auth.emailRequired': '请输入邮箱',
    'auth.passwordRequired': '请输入密码',
    'auth.passwordMismatch': '密码不匹配',
    'auth.termsAgree': '我同意服务条款和隐私政策',
    
    // Booking History (new)
    'bookingHistory.title': '预订历史',
    'bookingHistory.totalBookings': '共 {count} 个预订',
    'bookingHistory.upcoming': '即将到来',
    'bookingHistory.past': '历史',
    'bookingHistory.noUpcoming': '没有即将到来的预订',
    'bookingHistory.noPast': '没有历史预订',
    'bookingHistory.bookService': '预订服务后将显示在这里',
    'bookingHistory.willAppear': '您完成的预订将显示在这里',
    'bookingHistory.browseServices': '浏览服务',
    'bookingHistory.reschedule': '改期',
    'bookingHistory.cancel': '取消',
    'bookingHistory.viewReceipt': '查看收据',
    'bookingHistory.rescheduleBooking': '改期预订',
    'bookingHistory.service': '服务',
    'bookingHistory.newDateTime': '新日期和时间',
    'bookingHistory.confirmReschedule': '确认改期',
    'bookingHistory.filterStatus': '筛选状态',
    'bookingHistory.allStatuses': '所有状态',
    'bookingHistory.inProgress': '进行中',
    
    // Payment History (new)
    'paymentHistory.title': '付款历史',
    'paymentHistory.subtitle': '查看所有付款和收据',
    'paymentHistory.all': '全部',
    'paymentHistory.hoa': 'HOA',
    'paymentHistory.rentals': '租赁',
    'paymentHistory.services': '服务',
    'paymentHistory.noPayments': '未找到付款',
    'paymentHistory.willAppear': '您的付款历史将显示在这里',
    'paymentHistory.paymentDetails': '付款详情',
    'paymentHistory.amount': '金额',
    'paymentHistory.type': '类型',
    'paymentHistory.status': '状态',
    'paymentHistory.date': '日期',
    'paymentHistory.paymentMethod': '付款方式',
    'paymentHistory.property': '房产',
    'paymentHistory.verified': '已验证',
    'paymentHistory.paymentProof': '付款凭证',
    'paymentHistory.downloadReceipt': '下载收据',
    'paymentHistory.paid': '已付',
    'paymentHistory.pending': '待处理',
    'paymentHistory.rejected': '已拒绝',
    
    // Onboarding (new)
    'onboarding.welcome': '欢迎使用Aether',
    'onboarding.profile': '个人资料',
    'onboarding.profileDesc': '告诉我们关于您的信息',
    'onboarding.properties': '房产',
    'onboarding.propertiesDesc': '添加您的房产',
    'onboarding.preferences': '偏好设置',
    'onboarding.preferencesDesc': '自定义您的体验',
    'onboarding.skip': '跳过',
    'onboarding.continue': '继续',
    'onboarding.complete': '完成设置',
    'onboarding.userType': '您是？',
    'onboarding.homeowner': '房主',
    'onboarding.homeownerDesc': '我拥有房产并想使用Aether服务',
    'onboarding.guest': '访客',
    'onboarding.guestDesc': '我想租房或入住房产',
    'onboarding.community': '社区',
    'onboarding.selectCommunity': '选择社区',
    'onboarding.propertyAddress': '房产地址',
    'onboarding.unitNumber': '单元号',
    'onboarding.propertyType': '房产类型',
    'onboarding.rentalMode': '启用租赁模式',
    'onboarding.addProperty': '添加房产',
    'onboarding.removeProperty': '移除',
    'onboarding.nightlyRate': '每晚价格',
    'onboarding.cleaningFee': '清洁费',
    'onboarding.selectAmenities': '选择设施',
    'onboarding.uploadImages': '上传图片',
    'onboarding.notifyBookings': '预订通知',
    'onboarding.notifyMessages': '消息通知',
    
    // Support (new)
    'support.title': '支持中心',
    'support.heroTitle': '我们能帮您什么？',
    'support.heroSubtitle': '我们的团队随时为您解答问题或解决问题。',
    'support.liveChat': '在线聊天',
    'support.liveChatDesc': '与我们的支持团队实时聊天',
    'support.emailSupport': '邮件支持',
    'support.emailSupportDesc': '24小时内回复',
    'support.phoneSupport': '电话支持',
    'support.phoneSupportDesc': '仅限企业客户',
    'support.startChat': '开始聊天',
    'support.sendEmail': '发送邮件',
    'support.callNow': '立即拨打',
    'support.quickAnswers': '快速解答',
    'support.commonQuestions': '用户常见问题',
    'support.viewAllFAQs': '查看所有常见问题',
    'support.sendMessage': '发送消息',
    'support.name': '姓名',
    'support.email': '邮箱',
    'support.subject': '主题',
    'support.message': '消息',
    'support.yourName': '您的姓名',
    'support.yourEmail': '您的邮箱',
    'support.howCanWeHelp': '有什么可以帮您？',
    'support.describeIssue': '描述您的问题或疑问...',
    'support.sending': '发送中...',
    
    // Notifications (new)
    'notifications.title': '通知',
    'notifications.markAllRead': '全部标为已读',
    'notifications.all': '全部',
    'notifications.urgent': '紧急',
    'notifications.unread': '未读',
    'notifications.noNotifications': '暂无通知',
    'notifications.allCaughtUp': '已全部查看！',
    'notifications.checkBack': '稍后再来查看更新',
    'notifications.today': '今天',
    'notifications.yesterday': '昨天',
    'notifications.thisWeek': '本周',
    'notifications.earlier': '更早',
    
    // Rental Editor (Chinese)
    'rentalEditor.pricing': '定价',
    'rentalEditor.nightlyRate': '每晚价格',
    'rentalEditor.cleaningFee': '清洁费',
    'rentalEditor.minNights': '最少晚数',
    'rentalEditor.maxNights': '最多晚数',
    'rentalEditor.description': '描述',
    'rentalEditor.descriptionPlaceholder': '描述您的房产...',
    'rentalEditor.amenities': '设施',
    'rentalEditor.houseRules': '房屋规则',
    'rentalEditor.houseRulesPlaceholder': '输入房屋规则（例如：禁止吸烟、禁止聚会、安静时间...）',
    'rentalEditor.smartHomeInstructions': '智能家居说明',
    'rentalEditor.smartHomeDesc': '预订确认后向客人显示',
    'rentalEditor.tip': '提示',
    'rentalEditor.smartHomeTip': '在此处包含WiFi密码、门禁密码、语音命令和设备位置。此信息在预订确认前保持私密。',
    'rentalEditor.publishListing': '发布列表',
    'rentalEditor.publishDesc': '让客人可以看到您的列表',
    'rentalEditor.saveListing': '保存列表',
    'rentalEditor.rentalType': '租赁类型',
    'rentalEditor.shortTerm': '短期租赁',
    'rentalEditor.shortTermDesc': '度假和短期住宿的每晚预订（1-30晚）',
    'rentalEditor.longTerm': '长期租赁',
    'rentalEditor.longTermDesc': '长期住宿的月租（6个月以上）',
    'rentalEditor.bothTypes': '短期和长期都可',
    'rentalEditor.bothTypesDesc': '接受每晚和每月预订',
    'rentalEditor.shortTermPricing': '短期定价',
    'rentalEditor.longTermPricing': '长期定价',
    'rentalEditor.monthlyRate': '月租价格',
    'rentalEditor.monthlyRateHint': '长期租户的每月价格',
    'rentalEditor.minLeaseDuration': '最短租期（月）',
    
    // Admin Dashboard (Chinese)
    'admin.rentals': '租赁',
    'admin.news': '公告',
    'admin.inviteOwner': '邀请业主',
    'admin.inviteOwnerTitle': '邀请物业业主',
    'admin.inviteOwnerDesc': '业主将收到电子邮件邀请，用于设置账户并管理此物业。',
    
    // Community Switcher (Chinese)
    'community.selectCommunity': '选择社区',
    'community.createNew': '创建新社区',
    
    // Map Page (Chinese)
    'map.title': '社区地图',
    'map.amenities': '设施',
    'map.properties': '房产',
    
    // Service Marketplace additional (Chinese)
    'service.services': '服务',
    'service.bookPremium': '预订优质服务',
    'service.all': '全部',
    'service.noServices': '暂无可用服务',
    'service.checkBackLater': '稍后再来查看新服务',
    'service.by': '由',
    'service.book': '预订',
    'service.service': '服务',
    'service.estimatedPrice': '预估价格',
    'service.priceVaryNote': '最终价格可能因房产大小而异',
    'service.scheduleDateTime': '安排日期和时间',
    'service.specialRequests': '特殊要求（可选）',
    'service.specialInstructions': '任何特殊说明...',
    'service.payWithCard': '用卡支付（美元）',
    'service.category.housekeeping': '客房服务',
    'service.category.wellness': '健康',
    'service.category.maintenance': '维护',
    'service.category.dining': '餐饮',
    'service.category.transport': '交通',
    
    // Chat (additional - Chinese)
    'chat.fallbackError': '抱歉，无法处理您的请求。请重试。',
    'chat.bookingSuccess': '预订确认成功。',
    'chat.bookingIssue': '创建预订时出现问题。请重试。',
    'chat.temporaryError': '抱歉，我遇到了临时连接问题。请稍后重试。',
    
    // Map (additional - Chinese)
    'map.loading': '正在加载地图...',
    
    // God Mode Dashboard (Chinese)
    'godMode.title': '超级管理',
    'godMode.commandCenter': '管理面板',
    'godMode.adminPanel': '管理面板',
    'godMode.communities': '社区',
    'godMode.homes': '房屋',
    'godMode.mrr': '月收入',
    'godMode.propertyDevelopers': '房产开发商',
    'godMode.developerInviteDesc': '邀请房产开发商在平台上创建和管理社区。',
    'godMode.add': '添加',
    'godMode.createNewCommunity': '创建新社区',
    'godMode.communityName': '社区名称',
    'godMode.city': '城市',
    'godMode.state': '省/州',
    'godMode.baseFee': '基础费用/月',
    'godMode.perHomeFee': '每户费用',
    'godMode.createCommunity': '创建社区',
    'godMode.noCommunities': '暂无社区',
    'godMode.createFirstCommunity': '创建您的第一个社区开始使用',
    'godMode.active': '活跃',
    'godMode.inactive': '未激活',
    'godMode.noLocation': '无位置',
    
    // Admin additional
    'admin.owner': '业主',
    'admin.homeownerEmail': '业主邮箱',
    'admin.property': '物业',
    'admin.totalProperties': '物业总数',
    'admin.rentalModeOn': '租赁模式开启',
    'admin.activeListings': '活跃房源',
    'admin.upcomingArrivals': '即将到来',
    'admin.rentalProperties': '出租物业',
    'admin.rentalOn': '租赁开启',
    'admin.rentalOff': '租赁关闭',
    'admin.upcomingBookings': '即将预订',
    'admin.listingInactive': '房源未激活',
    
    // Rental types
    'rental.shortTerm': '短租',
    'rental.longTerm': '长租',
    'rental.bothTypes': '两者都有',
    
    // Review Form
    'reviewForm.howWasStay': '您的入住体验如何？',
    'reviewForm.overallExperience': '整体体验',
    'reviewForm.ratingRequired': '请提供整体评分',
    'reviewForm.submitSuccess': '评价提交成功！',
    'reviewForm.submitFailed': '提交评价失败',
    'reviewForm.titleLabel': '评价标题（可选）',
    'reviewForm.titlePlaceholder': '总结您的体验',
    'reviewForm.contentLabel': '您的评价（可选）',
    'reviewForm.contentPlaceholder': '分享您的入住细节...',
    'reviewForm.submit': '提交评价',
    
    // Review Moderation
    'moderation.title': '评价管理',
    'moderation.total': '总计',
    'moderation.pending': '待审核',
    'moderation.flagged': '已标记',
    'moderation.all': '全部',
    'moderation.published': '已发布',
    'moderation.hidden': '已隐藏',
    'moderation.lowRating': '低评分',
    'moderation.unknownProperty': '未知物业',
    'moderation.anonymous': '匿名',
    'moderation.commShort': '沟通',
    'moderation.hide': '隐藏',
    'moderation.publish': '发布',
    'moderation.edit': '编辑',
    'moderation.respond': '回复',
    'moderation.statusUpdated': '评价状态已更新',
    'moderation.updateFailed': '更新评价失败',
    'moderation.responseAdded': '回复添加成功',
    'moderation.responseFailed': '添加回复失败',
    'moderation.noReviews': '暂无评价',
    'moderation.noPending': '暂无待审核评价',
    'moderation.noPublished': '暂无已发布评价',
    'moderation.noFlagged': '暂无已标记评价',
    'moderation.editResponse': '编辑回复',
    'moderation.addResponse': '添加回复',
    'moderation.by': '来自',
    'moderation.responsePlaceholder': '写下您对这条评价的回复...',
    'moderation.saveResponse': '保存回复',
    
    // Audit Log (Chinese)
    'audit.title': '审计日志',
    'audit.subtitle': '系统活动记录',
    'audit.noLogs': '未找到审计日志',
    'audit.noLogsDesc': '系统活动将显示在这里',
    'audit.filter': '按操作筛选',
    'audit.allActions': '所有操作',
    'audit.allResources': '所有资源',
    'audit.searchPlaceholder': '搜索操作、资源...',
    'audit.loadMore': '加载更多',
    'common.refresh': '刷新',
    
    // Services Manager (Chinese)
    'services.title': '服务管理',
    'services.addService': '添加服务',
    'services.editService': '编辑服务',
    'services.serviceName': '服务名称',
    'services.serviceNamePlaceholder': '例如：深度清洁',
    'services.description': '描述',
    'services.descriptionPlaceholder': '描述此服务...',
    'services.category': '类别',
    'services.selectCategory': '选择类别',
    'services.vendor': '供应商（可选）',
    'services.selectVendor': '选择供应商',
    'services.noVendor': '无供应商',
    'services.basePrice': '基础价格',
    'services.pricePerBedroom': '每卧室价格',
    'services.pricePerSqft': '每平方英尺价格',
    'services.duration': '时长（分钟）',
    'services.isGlobal': '对所有社区可用',
    'services.isActive': '启用',
    'services.saveService': '保存服务',
    'services.deleteConfirm': '删除此服务？',
    'services.deleteSuccess': '服务已删除',
    'services.deleteFailed': '删除服务失败',
    'services.saveSuccess': '服务已保存',
    'services.saveFailed': '保存服务失败',
    'services.noServices': '暂无服务',
    'services.addFirst': '添加您的第一个服务开始使用',
    'services.global': '全局',
    'services.housekeeping': '家政服务',
    'services.wellness': '健康养生',
    'services.maintenance': '维护保养',
    'services.dining': '餐饮服务',
    'services.transport': '交通出行',
    
    // Vendor Manager (Chinese)
    'vendor.title': '供应商管理',
    'vendor.addVendor': '添加供应商',
    'vendor.editVendor': '编辑供应商',
    'vendor.vendorName': '供应商名称',
    'vendor.vendorNamePlaceholder': '例如：优质清洁公司',
    'vendor.email': '邮箱',
    'vendor.emailPlaceholder': 'vendor@example.com',
    'vendor.description': '描述',
    'vendor.descriptionPlaceholder': '描述此供应商...',
    'vendor.category': '类别',
    'vendor.selectCategory': '选择类别',
    'vendor.commissionRate': '佣金率 (%)',
    'vendor.isGlobal': '对所有社区可用',
    'vendor.isActive': '启用',
    'vendor.saveVendor': '保存供应商',
    'vendor.deleteConfirm': '删除此供应商？',
    'vendor.deleteSuccess': '供应商已删除',
    'vendor.deleteFailed': '删除供应商失败',
    'vendor.saveSuccess': '供应商已保存',
    'vendor.saveFailed': '保存供应商失败',
    'vendor.noVendors': '暂无供应商',
    'vendor.addFirst': '添加您的第一个供应商开始使用',
    'vendor.global': '全局',
    'vendor.commission': '佣金',
    'vendor.paymentSettings': '付款设置',
    'vendor.stripeConnected': 'Stripe已连接',
    'vendor.stripeNotConnected': 'Stripe未连接',
    'vendor.mobilePayment': '移动支付',
    'vendor.accountName': '账户名',
    'vendor.phoneNumber': '电话号码',
    
    // Payment Verification (Chinese)
    'paymentVerification.title': '待验证付款',
    'paymentVerification.noPayments': '没有待验证的付款',
    'paymentVerification.allProcessed': '所有付款已处理',
    'paymentVerification.verifyPayment': '验证付款',
    'paymentVerification.amount': '金额',
    'paymentVerification.paymentMethod': '付款方式',
    'paymentVerification.customer': '客户',
    'paymentVerification.date': '日期',
    'paymentVerification.property': '物业',
    'paymentVerification.paymentProof': '付款凭证',
    'paymentVerification.fullSize': '完整尺寸',
    'paymentVerification.noProof': '未上传付款凭证',
    'paymentVerification.verifyManually': '请在您的银行应用中手动验证此付款',
    'paymentVerification.rejectionReason': '拒绝原因（如果拒绝）',
    'paymentVerification.rejectionPlaceholder': '例如：金额不匹配、截图无效...',
    'paymentVerification.approve': '批准',
    'paymentVerification.reject': '拒绝',
    'paymentVerification.approved': '付款批准成功',
    'paymentVerification.rejected': '付款已拒绝',
    'paymentVerification.approveFailed': '批准付款失败',
    'paymentVerification.rejectFailed': '拒绝付款失败',
    'paymentVerification.provideReason': '请提供拒绝原因',
    'paymentVerification.hasProof': '有凭证',
    'paymentVerification.hoa': 'HOA',
    'paymentVerification.rental': '租赁',
    'paymentVerification.loadFailed': '加载待验证付款失败',
    
    // Vendor Payouts (Chinese)
    'vendorPayouts.totalRevenue': '总收入',
    'vendorPayouts.platformFees': '平台费用',
    'vendorPayouts.vendorPayouts': '供应商支付',
    'vendorPayouts.pendingPayouts': '待支付',
    'vendorPayouts.completedPayouts': '已完成支付',
    'vendorPayouts.noVendors': '暂无供应商',
    'vendorPayouts.addVendorsFirst': '添加供应商以查看收入',
    'vendorPayouts.vendor': '供应商',
    'vendorPayouts.category': '类别',
    'vendorPayouts.earnings': '收入',
    'vendorPayouts.commission': '佣金',
    'vendorPayouts.payout': '支付',
    'vendorPayouts.status': '状态',
    'vendorPayouts.actions': '操作',
    'vendorPayouts.markPaid': '标记已付',
    'vendorPayouts.viewHistory': '查看历史',
    'vendorPayouts.paid': '已付',
    'vendorPayouts.pending': '待处理',
    
    // Calendar (Chinese)
    'calendar.title': '预订日历',
    'calendar.today': '今天',
    'calendar.week': '周',
    'calendar.month': '月',
    'calendar.noBookings': '暂无预订',
    'calendar.dragToReschedule': '拖动以重新安排',
    'calendar.reschedule': '重新安排',
    'calendar.newDate': '新日期',
    'calendar.newTime': '新时间',
    'calendar.from': '从:',
    'calendar.to': '到:',
    'calendar.at': '于',
    'calendar.confirm': '确认',
    'calendar.rescheduled': '预订重新安排成功',
    'calendar.rescheduleFailed': '重新安排预订失败',
    'calendar.availabilityCalendar': '可用性日历',
    
    // Relative Dates (Chinese)
    'dates.today': '今天',
    'dates.yesterday': '昨天',
    'dates.tomorrow': '明天',
    'dates.daysAgo': '{count}天前',
    'dates.inDays': '{count}天后',
    'dates.justNow': '刚刚',
    'dates.dueToday': '今天到期',
    
    // Settings Page (Phase 4 additions - Chinese)
    'settings.manageAccount': '管理您的账户',
    'settings.developerIntegrations': '开发者与集成',
    'settings.yourProperties': '您的物业',
    'settings.setupProfile': '设置您的个人资料',
    'settings.profileUpdated': '个人资料更新成功',
    'settings.profileUpdateFailed': '更新个人资料失败',
    'settings.pushNotSupported': '此浏览器不支持推送通知',
    'settings.pushEnabled': '推送通知已启用',
    'settings.pushDenied': '推送通知权限被拒绝',
    'settings.pushEnableFailed': '启用推送通知失败',
    'settings.openingPortal': '正在打开支付门户...',
    'settings.portalFailed': '无法打开支付门户',
    'settings.editProfile': '编辑个人资料',
    'settings.fullName': '全名',
    'settings.enterName': '输入您的姓名',
    'settings.email': '邮箱',
    'settings.phoneNumber': '电话号码',
    'settings.enterPhone': '输入您的电话号码',
    'settings.saveChanges': '保存更改',
    'settings.emailNotifications': '邮件通知',
    'settings.bookingUpdates': '预订更新',
    'settings.bookingUpdatesDesc': '接收预订更新邮件',
    'settings.paymentAlerts': '付款提醒',
    'settings.paymentAlertsDesc': '接收付款活动邮件提醒',
    'settings.guestPassActivity': '访客通行证活动',
    'settings.guestPassActivityDesc': '当访客使用通行证时接收通知',
    'settings.pushNotifications': '推送通知',
    'settings.enablePush': '启用推送通知',
    'settings.enablePushDesc': '在您的设备上接收实时提醒',
    'settings.bookingNotifications': '预订通知',
    'settings.bookingNotificationsDesc': '新预订和变更',
    'settings.messageNotifications': '消息通知',
    'settings.messageNotificationsDesc': '来自客人的新消息',
    'settings.announcementNotifications': '公告通知',
    'settings.announcementNotificationsDesc': '社区公告',
    'settings.managePaymentMethods': '管理付款方式',
    'settings.managePaymentMethodsDesc': '通过安全门户更新您的付款方式',
    'settings.openPaymentPortal': '打开支付门户',
    'settings.paymentSecurityNote': '您的付款信息由Stripe安全处理',
    'settings.preferences': '偏好设置',
    'settings.selectTimezone': '选择时区',
    'settings.timezoneSet': '时区已设置为',
    'settings.selectLanguage': '选择语言',
    'settings.languageChanged': '语言已更改为',
    'settings.selectCurrency': '选择货币',
    'settings.currencyChanged': '货币已更改为',
    'settings.preferencesSaved': '您的偏好设置会自动保存',
    
    // Service Marketplace (Phase 4 - Chinese)
    'service.selectDateTime': '请选择日期和时间',
    'service.bookingCreated': '预订已创建！请完成付款以确认。',
    'service.bookingFailed': '预订服务失败',
    'service.paymentSubmitted': '付款已提交！等待验证。',
    
    // Browse Rentals (Phase 4 - Chinese)
    'browse.pleaseSignIn': '请登录以预订租赁',
    'browse.selectDates': '请选择入住和退房日期',
    'browse.minStayError': '最少入住{nights}晚',
    'browse.maxStayError': '最多入住{nights}晚',
    'browse.redirectingPayment': '正在跳转到付款页面...',
    'browse.bookingFailed': '创建预订失败',
    
    // Booking History (Phase 4 - Chinese additions)
    'bookingHistory.cancelSuccess': '预订已成功取消',
    'bookingHistory.cancelFailed': '取消预订失败',
    'bookingHistory.rescheduleSuccess': '预订已成功改期',
    'bookingHistory.rescheduleFailed': '预订改期失败',
    'bookingHistory.paymentPaid': '已付款',
    'bookingHistory.paymentProcessing': '处理中',
    'bookingHistory.paymentFailed': '失败',
    'bookingHistory.paymentPending': '待付款',
    
    // Rental Management (Chinese)
    'rentalManagement.title': '租赁管理',
    'rentalManagement.notEnabled': '未启用租赁模式',
    'rentalManagement.enableDesc': '从仪表板在您的物业上启用租赁模式以开始列表',
    'rentalManagement.live': '上线',
    'rentalManagement.draft': '草稿',
    'rentalManagement.perNight': '每晚',
    'rentalManagement.nights': '晚',
    'rentalManagement.bedrooms': '卧室',
    'rentalManagement.listing': '列表',
    'rentalManagement.calendar': '日历',
    'rentalManagement.analytics': '分析',
    'rentalManagement.bookings': '预订',
    'rentalManagement.createFirst': '请先创建列表以管理可用性',
    'rentalManagement.saveSuccess': '列表保存成功',
    'rentalManagement.saveFailed': '保存列表失败',
    'rentalManagement.availabilityUpdated': '可用性已更新',
    'rentalManagement.availabilityFailed': '更新可用性失败',
    
    // Messaging (Chinese)
    'messaging.noMessages': '暂无消息',
    'messaging.startConversation': '开始对话！',
    'messaging.typeMessage': '输入消息...',
    'messaging.loadFailed': '加载消息失败',
    'messaging.sendFailed': '发送消息失败',
    
    // Form Validation (Chinese)
    'validation.nameRequired': '请输入姓名',
    'validation.nameMaxLength': '姓名不能超过100个字符',
    'validation.emailInvalid': '邮箱地址无效',
    'validation.emailMaxLength': '邮箱不能超过255个字符',
    'validation.subjectRequired': '请输入主题',
    'validation.subjectMaxLength': '主题不能超过200个字符',
    'validation.messageRequired': '请输入消息',
    'validation.messageMaxLength': '消息不能超过2000个字符',
    
    // QR Payment Dialog
    'qrPayment.notAvailable': '支付不可用',
    'qrPayment.notSetUp': '此物业未设置{method}支付。',
    'qrPayment.contactManager': '请联系物业经理或使用其他支付方式。',
    'qrPayment.payWith': '使用{method}支付',
    'qrPayment.amountToPay': '应付金额',
    'qrPayment.copyAmount': '复制金额',
    'qrPayment.howToPay': '如何支付：',
    'qrPayment.step1': '打开您的{method}应用',
    'qrPayment.step2': '点击"扫码"并扫描上方二维码',
    'qrPayment.step3': '输入准确金额：{amount}',
    'qrPayment.step4': '完成支付',
    'qrPayment.step5': '截图保存支付确认',
    'qrPayment.madePayment': '我已完成支付',
    'qrPayment.confirmPayment': '确认您的支付',
    'qrPayment.uploadScreenshot': '上传支付确认截图以加快验证速度。',
    'qrPayment.paymentScreenshot': '支付截图（推荐）',
    'qrPayment.tapToUpload': '点击上传截图',
    'qrPayment.remove': '移除',
    'qrPayment.submitting': '提交中...',
    'qrPayment.confirm': '确认支付',
    'qrPayment.submitted': '支付已提交！',
    'qrPayment.beingVerified': '您的支付正在验证中。确认后您将收到通知。',
    'qrPayment.verificationTime': '工作日通常需要1-24小时。',
    'qrPayment.done': '完成',
    'qrPayment.phoneCopied': '电话号码已复制',
    'qrPayment.amountCopied': '金额已复制',
    'qrPayment.uploadImage': '请上传图片文件',
    'qrPayment.screenshotUploaded': '支付截图已上传',
    'qrPayment.uploadFailed': '上传截图失败',
    'qrPayment.submitFailed': '提交支付失败',
    
    // Community Creator
    'communityCreator.title': '创建新社区',
    'communityCreator.completePaymentSetup': '完成支付设置',
    'communityCreator.uploadQrCodes': '上传支付二维码',
    'communityCreator.createButton': '创建社区',
    'communityCreator.communityName': '社区名称',
    'communityCreator.communityNamePlaceholder': '例如：海景庄园',
    'communityCreator.streetAddress': '街道地址',
    'communityCreator.streetAddressPlaceholder': '输入街道地址...',
    'communityCreator.city': '城市',
    'communityCreator.cityPlaceholder': '城市',
    'communityCreator.state': '省/州',
    'communityCreator.statePlaceholder': '省/州',
    'communityCreator.zipCode': '邮编',
    'communityCreator.zipCodePlaceholder': '邮编',
    'communityCreator.description': '描述',
    'communityCreator.descriptionPlaceholder': '简要描述您的社区...',
    'communityCreator.detailsTab': '详情',
    'communityCreator.paymentsTab': '支付',
    'communityCreator.nextPaymentSettings': '下一步：支付设置',
    'communityCreator.defaultCurrency': '默认货币',
    'communityCreator.currencyHintMMK': '缅甸元用于本地支付',
    'communityCreator.currencyHintUSD': '美元用于国际支付',
    'communityCreator.acceptedPaymentMethods': '接受的支付方式',
    'communityCreator.selectPaymentMethod': '请至少选择一种支付方式',
    'communityCreator.qrCodePrompt': '创建社区后，您将被提示上传移动钱包支付的二维码。',
    'communityCreator.creating': '创建中...',
    'communityCreator.completeStripeSetup': '完成Stripe设置',
    'communityCreator.stripeOnboardingDescription': '您的社区已创建！要接受HOA付款，请完成Stripe入驻流程。',
    'communityCreator.doLater': '稍后再说',
    'communityCreator.qrUploadDescription': '上传您的KBZPay和Wave Money二维码，以便业主支付费用。',
    'communityCreator.skipForNow': '暂时跳过',
    'communityCreator.success': '社区创建成功！',
    'communityCreator.error': '创建社区失败',
    
    // Property Delete Dialog
    'propertyDelete.title': '删除物业',
    'propertyDelete.confirm': '确定要删除',
    'propertyDelete.willRemove': '此操作将：',
    'propertyDelete.removeProperty': '从您的账户中移除该物业',
    'propertyDelete.deactivateListings': '停用所有活跃的租赁列表',
    'propertyDelete.cancelBookings': '取消所有待处理的预订',
    'propertyDelete.cannotUndo': '此操作无法撤销。',
    'propertyDelete.deleting': '删除中...',
    'propertyDelete.success': '物业删除成功',
    'propertyDelete.failed': '删除物业失败',
    
    // Sale Listing Manager
    'saleListing.title': '出售列表',
    'saleListing.edit': '编辑出售列表',
    'saleListing.create': '创建出售列表',
    'saleListing.noProperties': '暂无物业',
    'saleListing.addPropertiesFirst': '请先在此社区添加物业，然后才能挂牌出售。',
    'saleListing.listForSale': '挂牌出售',
    'saleListing.published': '已发布',
    'saleListing.available': '可售',
    'saleListing.pending': '待售',
    'saleListing.sold': '已售',
    'saleListing.priceRange': '价格区间',
    'saleListing.description': '以估计价格区间列出待售物业。实际销售在线下处理。',
    'saleListing.updated': '出售列表已更新',
    'saleListing.created': '出售列表已创建',
    'saleListing.saveFailed': '保存列表失败',
    
    // Amenity Booking Dialog
    'amenityBooking.book': '预订',
    'amenityBooking.reserveSpace': '预留此空间，最多可容纳{capacity}位客人',
    'amenityBooking.selectDate': '选择日期',
    'amenityBooking.startTime': '开始时间',
    'amenityBooking.duration': '时长',
    'amenityBooking.minutes': '{minutes}分钟（至{endTime}）',
    'amenityBooking.guestCount': '客人人数（最多{max}人）',
    'amenityBooking.specialRequests': '特殊要求（可选）',
    'amenityBooking.specialPlaceholder': '任何特殊要求或请求...',
    'amenityBooking.confirmBooking': '确认预订',
    'amenityBooking.booking': '预订中...',
    'amenityBooking.confirmed': '预订已确认！',
    'amenityBooking.on': '于',
    'amenityBooking.success': '{name}预订成功！',
    'amenityBooking.selectDateError': '请选择日期',
    'amenityBooking.fullyBooked': '此时段已满。最大容量：{capacity}',
    'amenityBooking.spotsAvailable': '此时段仅剩{spots}个名额。请减少客人数量。',
    'amenityBooking.failed': '预订设施失败。请重试。',
    
    // Common additions
    'common.notAuthenticated': '未认证',
    'common.note': '注意',
    
    // Admin - Developer Invite
    'admin.developer.invite': '邀请开发者',
    'admin.developer.inviteTitle': '邀请物业开发者',
    'admin.developer.description': '物业开发者可以创建和管理社区、添加物业、服务和供应商。',
    'admin.developer.emailLabel': '电子邮件地址',
    'admin.developer.emailPlaceholder': 'developer@company.com',
    'admin.developer.nameLabel': '全名（可选）',
    'admin.developer.namePlaceholder': '张开发者',
    'admin.developer.note': '开发者将收到一封包含账户设置说明的电子邮件。他们将拥有管理员级别的权限来创建社区。',
    'admin.developer.sendInvite': '发送邀请',
    'admin.developer.emailRequired': '邮箱为必填项',
    'admin.developer.inviteSent': '开发者邀请发送成功',
    'admin.developer.inviteFailed': '发送邀请失败',
    
    // Admin - Amenity Manager
    'admin.amenity.title': '社区设施',
    'admin.amenity.add': '添加设施',
    'admin.amenity.addTitle': '添加设施',
    'admin.amenity.namePlaceholder': '设施名称',
    'admin.amenity.selectCategory': '选择类别',
    'admin.amenity.descriptionPlaceholder': '描述（可选）',
    'admin.amenity.latitudePlaceholder': '纬度（例如 25.7617）',
    'admin.amenity.longitudePlaceholder': '经度（例如 -80.1918）',
    'admin.amenity.coordsTip': '提示：在Google地图上右键点击位置可获取坐标',
    'admin.amenity.addButton': '添加设施',
    'admin.amenity.noAmenities': '暂无设施',
    'admin.amenity.addFirst': '添加您的第一个社区设施',
    'admin.amenity.loadFailed': '加载设施失败',
    'admin.amenity.nameAndCoordsRequired': '名称和坐标为必填项',
    'admin.amenity.addSuccess': '设施添加成功',
    'admin.amenity.addFailed': '添加设施失败',
    'admin.amenity.confirmDelete': '确定要删除此设施吗？',
    'admin.amenity.deleteSuccess': '设施已删除',
    'admin.amenity.deleteFailed': '删除设施失败',
    'admin.amenity.category.pool': '游泳池',
    'admin.amenity.category.gym': '健身中心',
    'admin.amenity.category.restaurant': '餐厅',
    'admin.amenity.category.valet': '代客泊车',
    'admin.amenity.category.spa': '水疗健康',
    'admin.amenity.category.park': '公园/花园',
    'admin.amenity.category.clubhouse': '会所',
    'admin.amenity.category.other': '其他',
    
    // Admin - Announcement Manager
    'admin.announcement.title': '公告',
    'admin.announcement.new': '新建',
    'admin.announcement.newTitle': '新公告',
    'admin.announcement.editTitle': '编辑公告',
    'admin.announcement.titleLabel': '标题',
    'admin.announcement.titlePlaceholder': '公告标题',
    'admin.announcement.contentLabel': '内容',
    'admin.announcement.contentPlaceholder': '撰写您的公告...',
    'admin.announcement.priorityLabel': '优先级',
    'admin.announcement.priority.normal': '普通',
    'admin.announcement.priority.important': '重要',
    'admin.announcement.priority.urgent': '紧急',
    'admin.announcement.publishImmediately': '立即发布',
    'admin.announcement.visibleToResidents': '对所有居民可见',
    'admin.announcement.create': '创建公告',
    'admin.announcement.update': '更新公告',
    'admin.announcement.publish': '发布',
    'admin.announcement.unpublish': '取消发布',
    'admin.announcement.status.published': '已发布',
    'admin.announcement.status.draft': '草稿',
    'admin.announcement.noAnnouncements': '暂无公告',
    'admin.announcement.createToInform': '创建公告以通知居民',
    'admin.announcement.titleContentRequired': '标题和内容为必填项',
    'admin.announcement.createSuccess': '公告创建成功',
    'admin.announcement.updateSuccess': '公告更新成功',
    'admin.announcement.saveFailed': '保存公告失败',
    'admin.announcement.deleteSuccess': '公告已删除',
    'admin.announcement.deleteFailed': '删除公告失败',
    'admin.announcement.published': '公告已发布',
    'admin.announcement.unpublished': '公告已取消发布',
    'admin.announcement.updateFailed': '更新公告失败',
    
    // Admin - Guest Pass Manager
    'admin.guestPass.title': '访客通行证管理',
    'admin.guestPass.subtitle': '管理所有访客访问权限',
    'admin.guestPass.status.active': '有效',
    'admin.guestPass.status.expired': '已过期',
    'admin.guestPass.status.revoked': '已撤销',
    'admin.guestPass.noGuestPasses': '未找到访客通行证',
    'admin.guestPass.unknownGuest': '未知访客',
    'admin.guestPass.expires': '到期',
    'admin.guestPass.extend': '延期',
    'admin.guestPass.reactivate': '重新激活（7天）',
    'admin.guestPass.extendTitle': '延长访客访问权限',
    'admin.guestPass.guestLabel': '访客',
    'admin.guestPass.newEndDate': '新结束日期',
    'admin.guestPass.selectEndDate': '选择新结束日期',
    'admin.guestPass.extendAccess': '延长访问权限',
    'admin.guestPass.loadFailed': '加载访客通行证失败',
    'admin.guestPass.revoked': '访客通行证已撤销',
    'admin.guestPass.revokeFailed': '撤销通行证失败',
    'admin.guestPass.extendedTo': '访问权限延长至',
    'admin.guestPass.extendFailed': '延长访问权限失败',
    'admin.guestPass.reactivated': '访客通行证已重新激活7天',
    'admin.guestPass.reactivateFailed': '重新激活通行证失败',
    
    // Date/Time Picker
    'datePicker.pickDate': '选择日期',
    'datePicker.selectDate': '选择日期和时间',
    'datePicker.time': '时间',
    
    // Services Manager Additional
    'services.addNewService': '添加新服务',
    'services.serviceUpdated': '服务已更新',
    'services.hasBeenUpdated': '已更新',
    'services.serviceCreated': '服务已创建',
    'services.hasBeenAdded': '已添加',
    'services.failedToSave': '保存服务失败',
    'services.noVendorAssigned': '未分配供应商',
    'services.unknownVendor': '未知供应商',
    'services.loadingServices': '加载服务中...',
    'services.perBedroom': '每卧室',
    'services.perSqft': '每平方英尺',
    'services.pricing': '定价',
    'services.min': '分钟',
    'services.active': '活跃',
    'services.inactive': '非活跃',
    'services.createService': '创建服务',
    'services.updateService': '更新服务',
    
    // Service Names (from database) - Chinese
    'serviceName.poolService': '泳池服务',
    'serviceName.poolMaintenance': '泳池维护',
    'serviceName.poolSpaMaintenance': '泳池和水疗维护',
    'serviceName.deepCleaning': '深度清洁',
    'serviceName.deepClean': '深度清洁',
    'serviceName.regularCleaning': '常规清洁',
    'serviceName.housekeeping': '家政服务',
    'serviceName.expressHousekeeping': '快速家政',
    'serviceName.signatureHousekeeping': '精品家政',
    'serviceName.fullEstateCleaning': '全屋清洁',
    'serviceName.weeklyMaintenanceClean': '每周维护清洁',
    'serviceName.turndownService': '夜床服务',
    'serviceName.laundryService': '洗衣服务',
    'serviceName.laundry': '洗衣',
    'serviceName.laundryPressing': '洗衣熨烫',
    'serviceName.carpetCleaning': '地毯清洁',
    'serviceName.windowCleaning': '窗户清洁',
    'serviceName.inHomeSpa': '上门水疗养生',
    'serviceName.spaWellness': '水疗养生',
    'serviceName.couplesSpaDay': '情侣水疗日',
    'serviceName.couplesSpaPackage': '情侣水疗套餐',
    'serviceName.inVillaMassage': '别墅内按摩',
    'serviceName.swedishMassage': '瑞典按摩',
    'serviceName.hotStoneTherapy': '热石疗法',
    'serviceName.personalTraining': '私人训练',
    'serviceName.personalTrainingSession': '私人训练课程',
    'serviceName.privateYogaClass': '私人瑜伽课',
    'serviceName.privateYogaSession': '私人瑜伽课程',
    'serviceName.massageTherapy': '按摩理疗',
    'serviceName.massage': '按摩',
    'serviceName.yogaSession': '瑜伽课程',
    'serviceName.yoga': '瑜伽',
    'serviceName.meditation': '冥想',
    'serviceName.beautyServices': '美容服务',
    'serviceName.salonServices': '美发服务',
    'serviceName.generalMaintenance': '一般维修',
    'serviceName.plumbing': '水管维修',
    'serviceName.emergencyPlumbing': '紧急水管维修',
    'serviceName.electrical': '电气维修',
    'serviceName.hvacService': '空调服务',
    'serviceName.hvac': '空调',
    'serviceName.hvacMaintenance': '空调维护',
    'serviceName.handyman': '杂工',
    'serviceName.handymanServices': '杂工服务',
    'serviceName.landscaping': '园艺',
    'serviceName.landscapingService': '园艺服务',
    'serviceName.gardenLandscaping': '花园景观',
    'serviceName.gardenMaintenance': '花园维护',
    'serviceName.pestControl': '害虫防治',
    'serviceName.privateChef': '私人厨师',
    'serviceName.privateChefDinner': '私人厨师晚餐',
    'serviceName.privateChefExperience': '私人厨师体验',
    'serviceName.italianDinnerExperience': '意式晚餐体验',
    'serviceName.sushiOmakase': '寿司料理长推荐',
    'serviceName.winePairingDinner': '葡萄酒配餐晚宴',
    'serviceName.wineSommelierService': '侍酒师服务',
    'serviceName.breakfastInBed': '床上早餐',
    'serviceName.cocktailPartyCatering': '鸡尾酒会餐饮',
    'serviceName.catering': '餐饮服务',
    'serviceName.mealDelivery': '送餐服务',
    'serviceName.groceryShopping': '代购杂货',
    'serviceName.bartenderService': '调酒服务',
    'serviceName.bartender': '调酒师',
    'serviceName.airportTransfer': '机场接送',
    'serviceName.airportLimousine': '机场豪华轿车',
    'serviceName.carRental': '租车',
    'serviceName.golfCartRental': '高尔夫球车租赁',
    'serviceName.chauffeurService': '专车服务',
    'serviceName.chauffeur': '司机',
    'serviceName.valetParking': '代客泊车',
    'serviceName.valetParkingService': '代客泊车服务',
    'serviceName.valet': '代客服务',
    'serviceName.boatCharter': '游船租赁',
    'serviceName.yachtCharter': '游艇租赁',
    'serviceName.yachtCharterHalfDay': '游艇租赁 - 半日',
    'serviceName.yachtDetailing': '游艇美容',
    
    // Vendor Manager Additional
    'vendor.failedToLoad': '加载供应商失败',
    'vendor.nameRequired': '供应商名称是必填的',
    'vendor.addedSuccess': '供应商添加成功',
    'vendor.failedToAdd': '添加供应商失败',
    'vendor.updatedSuccess': '供应商更新成功',
    'vendor.failedToUpdate': '更新供应商失败',
    'vendor.confirmDelete': '确定要删除此供应商吗？',
    'vendor.deleted': '供应商已停用',
    'vendor.failedToDelete': '删除供应商失败',
    'vendor.stripeOnboardingOpened': 'Stripe入驻页面已在新标签页打开',
    'vendor.failedToStartOnboarding': '启动Stripe入驻失败',
    'vendor.vendorsAndProviders': '供应商和服务提供商',
    'vendor.addNewVendor': '添加新供应商',
    'vendor.enterVendorName': '输入供应商名称',
    'vendor.contactEmail': '联系邮箱',
    'vendor.emailNotification': '用于支付和预订通知',
    'vendor.platformKeeps': '平台保留',
    'vendor.notConnected': '未连接',
    'vendor.fullSetup': '完整设置',
    'vendor.stripeOnly': '仅Stripe',
    'vendor.qrOnly': '仅二维码',
    'vendor.noVendorsDesc': '添加供应商以提供服务',
    'vendor.updateStripeAccount': '更新Stripe账户',
    'vendor.connectStripeAccount': '连接Stripe账户',
    'vendor.editDetails': '编辑供应商',
    'vendor.qrCodes': '二维码',
    
    // Guest Preferences Quiz (Chinese)
    'quiz.saved': '偏好已保存！',
    'quiz.savedDesc': '我们将根据您的偏好个性化您的租赁搜索。',
    'quiz.saveFailed': '保存偏好失败',
    'quiz.howTravel': '您喜欢怎样旅行？',
    'quiz.selectAll': '选择所有适用的',
    'quiz.whatBrings': '什么带您来这里？',
    'quiz.whoTraveling': '谁和您一起旅行？',
    'quiz.selectGroupSize': '选择您的典型团队规模',
    'quiz.stayLength': '典型住宿时长',
    'quiz.nights': '晚',
    'quiz.whatBudget': '您的预算是多少？',
    'quiz.perNightPref': '每晚价格偏好',
    'quiz.mustHaveAmenities': '必备设施',
    'quiz.selectEssentials': '选择您的必需品',
    'quiz.petFriendly': '宠物友好',
    'quiz.petFriendlyDesc': '带宠物旅行',
    'quiz.accessibility': '无障碍设施',
    'quiz.accessibilityDesc': '需要无障碍功能',
    'quiz.personalizeExperience': '个性化您的体验',
    'quiz.updatePreferences': '更新偏好',
    'quiz.helpFind': '帮助我们为您找到完美的房产',
    'quiz.skipForNow': '暂时跳过',
    'quiz.savePreferences': '保存偏好',
    'quiz.style.short_trip': '短途旅行',
    'quiz.style.short_tripDesc': '1-3晚住宿',
    'quiz.style.long_term': '长期住宿',
    'quiz.style.long_termDesc': '数周或数月',
    'quiz.style.luxury': '奢华度假',
    'quiz.style.luxuryDesc': '高端体验',
    'quiz.style.budget': '经济实惠',
    'quiz.style.budgetDesc': '注重性价比',
    'quiz.purpose.vacation': '度假',
    'quiz.purpose.business': '商务',
    'quiz.purpose.relocation': '搬迁',
    'quiz.purpose.remote_work': '远程办公',
    'quiz.group.solo': '独自',
    'quiz.group.couple': '情侣',
    'quiz.group.family': '家庭',
    'quiz.group.group': '团体',
    'quiz.budget.budget': '经济型',
    'quiz.budget.moderate': '中等',
    'quiz.budget.luxury': '奢华',
    'quiz.budget.ultra_luxury': '超奢华',
    'quiz.amenity.wifi': 'WiFi',
    'quiz.amenity.parking': '停车位',
    'quiz.amenity.pool': '游泳池',
    'quiz.amenity.gym': '健身房',
    'quiz.amenity.kitchen': '厨房',
    'quiz.amenity.air_conditioning': '空调',
    'quiz.amenity.tv': '智能电视',
    'quiz.amenity.hot_tub': '热水浴缸',
    'quiz.amenity.garden': '花园',
    'quiz.amenity.security': '安保',
    
    // Payment Method Selector (Chinese)
    'payment.totalAmount': '总金额',
    'payment.myanmarOptions': '缅甸支付选项',
    'payment.localPayment': '本地支付',
    'payment.internationalCards': '国际卡',
    'payment.processing': '处理中...',
    'payment.payWith': '使用{method}支付',
    'payment.selectMethod': '选择支付方式',
    'payment.securityNote': '您的支付受端到端加密保护',
    
    // Amenity Booking Manager - Admin
    'amenityBooking.reservations': '设施预订',
    'amenityBooking.allStatus': '所有状态',
    'amenityBooking.statusConfirmed': '已确认',
    'amenityBooking.statusCancelled': '已取消',
    'amenityBooking.statusCompleted': '已完成',
    'amenityBooking.statusPending': '待处理',
    'amenityBooking.noBookings': '未找到预订',
    'amenityBooking.noBookingsDesc': '住户预订设施后将在此处显示',
    'amenityBooking.failedToLoad': '加载预订失败',
    'amenityBooking.failedToUpdate': '更新预订失败',
    'amenityBooking.bookingUpdated': '预订已更新',
    'amenityBooking.amenity': '设施',
    'amenityBooking.dateTime': '日期和时间',
    'amenityBooking.guest': '住客',
    'amenityBooking.status': '状态',
    'amenityBooking.actions': '操作',
    'amenityBooking.filterStatus': '筛选状态',
    
    // Common additional
    'common.guest': '位住客',
    'common.guests': '位住客',
    
    // New Pricing Plans - Explorer (Chinese)
    'pricing.explorer.name': '探索者',
    'pricing.explorer.desc': '为寻找卓越住宿的旅行者',
    'pricing.explorer.feature1': '预订豪华短租',
    'pricing.explorer.feature2': '预订长期租赁',
    'pricing.explorer.feature3': 'AI礼宾服务',
    'pricing.explorer.feature4': '与房东安全通讯',
    'pricing.explorer.feature5': '优先预订支持',
    
    // New Pricing Plans - Sovereign (Chinese)
    'pricing.sovereign.name': '主权者',
    'pricing.sovereign.desc': '为准备出租房产的业主',
    'pricing.sovereign.feature1': '发布短租房源',
    'pricing.sovereign.feature2': '发布长租房源',
    'pricing.sovereign.feature3': '动态定价工具',
    'pricing.sovereign.feature4': '房客管理面板',
    'pricing.sovereign.feature5': '收入分析',
    'pricing.sovereign.feature6': '优先房东支持',
    
    // New Pricing Plans - Architect (Chinese)
    'pricing.architect.name': '建筑师',
    'pricing.architect.desc': '为管理社区的开发商',
    'pricing.architect.feature1': '创建和管理社区',
    'pricing.architect.feature2': '邀请和管理业主',
    'pricing.architect.feature3': 'HOA费用收取',
    'pricing.architect.feature4': '供应商市场管理',
    'pricing.architect.feature5': '社区公告',
    'pricing.architect.feature6': '高级分析面板',
    'pricing.architect.feature7': '自定义品牌',
    
    // Pricing Modal & Common (Chinese)
    'pricing.monthly': '月付',
    'pricing.annual': '年付',
    'pricing.save': '节省 {percent}%',
    'pricing.trialDays': '{days}天免费试用',
    'pricing.startTrial': '开始免费试用',
    'pricing.subscribe': '立即订阅',
    'pricing.securePayment': '安全支付由Stripe提供',
    'pricing.redirecting': '正在跳转到结账页面...',
    'pricing.error': '创建订阅失败，请重试。',
    
    // Pricing Triggers (Chinese)
    'pricing.trigger.rentalMode': '订阅以发布您的房产出租',
    'pricing.trigger.booking': '订阅以预订此房产',
    'pricing.trigger.community': '订阅以创建和管理社区',
    'pricing.trigger.default': '升级您的账户',
    'landing.pricing.perYear': '年',
    'landing.pricing.billingMonthly': '月付',
    'landing.pricing.billingAnnual': '年付',
    
    // Pricing Page (Chinese)
    'pricingPage.backToHome': '返回首页',
    'pricingPage.title': '简单透明的定价',
    'pricingPage.subtitle': '选择适合您需求的方案。无隐藏费用。',
    'pricingPage.saveUpTo': '节省17%',
    'pricingPage.compareFeatures': '功能对比',
    'pricingPage.feature': '功能',
    'pricingPage.feature.bookRentals': '预订豪华租赁',
    'pricingPage.feature.aiConcierge': 'AI礼宾助手',
    'pricingPage.feature.secureMessaging': '安全消息',
    'pricingPage.feature.listProperty': '发布房产出租',
    'pricingPage.feature.guestManagement': '客人管理面板',
    'pricingPage.feature.revenueAnalytics': '收入分析',
    'pricingPage.feature.createCommunities': '创建社区',
    'pricingPage.feature.inviteHomeowners': '邀请和管理业主',
    'pricingPage.feature.hoaCollection': 'HOA费用收取',
    'pricingPage.feature.vendorManagement': '供应商市场',
    'pricingPage.feature.customBranding': '自定义品牌',
    'pricingPage.contactSalesTitle': '联系销售',
    'pricingPage.contactSalesSubtitle': '对Sovereign或Architect方案感兴趣？请联系我们的团队。',
    'pricingPage.yourName': '您的姓名',
    'pricingPage.namePlaceholder': '张三',
    'pricingPage.yourEmail': '您的邮箱',
    'pricingPage.emailPlaceholder': 'zhangsan@example.com',
    'pricingPage.companyOptional': '公司（可选）',
    'pricingPage.companyPlaceholder': '您的公司名称',
    'pricingPage.planInterest': '感兴趣的方案',
    'pricingPage.homeownerPlan': 'Sovereign（业主）',
    'pricingPage.developerPlan': 'Architect（开发商）',
    'pricingPage.tellUsMore': '告诉我们更多',
    'pricingPage.messagePlaceholder': '告诉我们您的需求、房产组合或问题...',
    'pricingPage.sending': '发送中...',
    'pricingPage.sendInquiry': '发送咨询',
    'pricingPage.inquirySent': '您的咨询已发送！我们的销售团队将很快联系您。',
    'pricingPage.inquiryFailed': '发送咨询失败。请重试。',
    'pricing.inviteRequired': '需要社区管理员邀请',
    'pricing.enterpriseOnboarding': '需要企业入驻',
    
    // Support Page - Sales Inquiry (Chinese)
    'support.salesInquiryTitle': '业主和开发商注册',
    'support.salesInquirySubtitle': '有兴趣发布您的房产或管理社区？请联系我们的销售团队。',
    'support.homeownerPlan': '业主（Sovereign）',
    'support.homeownerPlanDesc': '发布您的房产进行短期或长期出租',
    'support.developerPlan': '开发商（Architect）',
    'support.developerPlanDesc': '创建和管理多个房产的社区',
    'support.companyOptional': '公司（可选）',
    'support.companyPlaceholder': '您的公司名称',
    'support.tellUsMore': '告诉我们更多',
    'support.salesMessagePlaceholder': '告诉我们您的房产、社区规模或任何问题...',
    'support.submitSalesInquiry': '提交销售咨询',
    'support.salesInquirySent': '谢谢！我们的销售团队将在24小时内联系您。',
    'support.salesInquiryFailed': '提交咨询失败。请重试。',
    'support.otherWaysToReach': '其他联系方式',
    
    // SmartHome Guide Page (Chinese)
    'smartHome.guide.lighting': '照明',
    'smartHome.guide.lightingDesc': '控制智能灯光和场景',
    'smartHome.guide.smartLocks': '智能门锁',
    'smartHome.guide.smartLocksDesc': '锁定/解锁门（如果启用）',
    'smartHome.guide.doorbell': '门铃',
    'smartHome.guide.doorbellDesc': '查看门口是谁',
    'smartHome.guide.voiceCommands': '语音命令',
    'smartHome.guide.voiceCommandsNote': '如果物业有Google Nest扬声器，试试这些命令：',
    'smartHome.guide.appleHomeKit': 'Apple HomeKit替代方案',
    'smartHome.guide.appleHomeKitDesc': '适用于专门使用Apple生态系统的物业',
    'smartHome.guide.howToShareHomeKit': '如何与客人共享HomeKit访问权限',
    'smartHome.guide.helpfulResources': '有用资源',
    'smartHome.guide.homeKitNote': 'HomeKit要求客人拥有Apple ID和iOS/macOS设备。Google Home支持更多设备类型，可跨平台使用。',
    
    // AI Page (Chinese)
    'aiPage.title': 'Aether AI',
    'aiPage.subtitle': '您的专属助手',
    'aiPage.welcome': '您好！我是您的AI礼宾。我可以帮您：',
    'aiPage.help.booking': '预订服务和预约',
    'aiPage.help.guestAccess': '管理访客访问',
    'aiPage.help.maintenance': '物业维护请求',
    'aiPage.help.lifestyle': '生活方式建议',
    'aiPage.trySaying': '试着说',
    'aiPage.placeholder': '问我任何问题...',
    'aiPage.suggestion1': '预订周六的私人厨师',
    'aiPage.suggestion2': '安排泳池维护',
    'aiPage.suggestion3': '授权我明天的访客',
    'aiPage.suggestion4': '有哪些服务可用？',
    
    // Public Sales Page (Chinese)
    'sales.priceRange': '价格范围',
    'sales.minBedrooms': '最少卧室',
    'sales.any': '任何',
    'sales.features': '特点',
    'sales.clearFilters': '清除所有筛选',
    'sales.exclusiveProperties': '独家房产',
    'sales.propertiesForSale': '待售房产',
    'sales.discoverProperties': '在我们的独家豪华社区发现卓越的房产',
    'sales.forSale': '出售',
    'sales.sqft': '平方英尺',
    'sales.keyFeatures': '主要特点',
    'sales.propertyDirections': '房产路线',
    'sales.searchPlaceholder': '按位置或社区搜索...',
    'sales.filters': '筛选',
    'sales.propertiesFound': '找到 {count} 个房产',
    'sales.propertyFound': '找到 1 个房产',
    'sales.viewRentals': '查看租赁',
    'sales.noProperties': '没有符合筛选条件的房产',
    'sales.tryAdjusting': '尝试调整搜索条件',
    'sales.luxuryProperty': '豪华房产',
    'sales.exclusiveCommunity': '独家社区',
    'sales.bed': '卧室',
    'sales.bath': '浴室',
    'sales.parking': '停车位',
    'sales.built': '建于',
    'sales.moreFeatures': '+{count} 更多',
    'sales.contactForDetails': '联系获取详情',
    
    // GodMode Dashboard (Chinese)
    'godMode.tenantAnalytics': '租户分析',
    'godMode.tenantAnalyticsDesc': '查看使用指标和收入明细',
    'godMode.communityAdmins': '社区管理员',
    'godMode.revenue': '收入',
    'godMode.configure': '配置',
    'godMode.admins': '管理员',
    
    // SaleDetailPage (Chinese)
    'saleDetail.back': '返回',
    'saleDetail.forSale': '出售',
    'saleDetail.bedrooms': '卧室',
    'saleDetail.bathrooms': '浴室',
    'saleDetail.parking': '停车位',
    'saleDetail.builtIn': '建于',
    'saleDetail.floor': '楼层',
    'saleDetail.sqft': '平方英尺',
    'saleDetail.aboutProperty': '关于此房产',
    'saleDetail.keyFeatures': '主要特点',
    'saleDetail.amenities': '设施',
    'saleDetail.requestInfo': '请求信息',
    'saleDetail.name': '姓名',
    'saleDetail.email': '邮箱',
    'saleDetail.phone': '电话',
    'saleDetail.message': '留言',
    'saleDetail.interestedMessage': '我对这个房产感兴趣...',
    'saleDetail.sendInquiry': '发送询问',
    'saleDetail.sending': '发送中...',
    'saleDetail.listingNotFound': '房源未找到',
    'saleDetail.propertyUnavailable': '此房产可能已不可用。',
    'saleDetail.browseProperties': '浏览房产',
    'saleDetail.exclusiveLocation': '独家位置',
    'saleDetail.fillNameEmail': '请填写您的姓名和邮箱',
    'saleDetail.inquirySubmitted': '您的询问已提交！代表将很快与您联系。',
    'saleDetail.inquiryFailed': '提交询问失败。请重试。',
    'saleDetail.yourFullName': '您的全名',
    'saleDetail.yourEmail': 'your@email.com',
    'saleDetail.contactWithin24h': '销售代表将在24小时内与您联系。',
    
    // PropertyDirectionsPage (Chinese)
    'directions.propertyDirections': '房产方向',
    'directions.booking': '预订',
    'directions.confirmed': '已确认',
    'directions.fullPropertyAddress': '完整房产地址',
    'directions.copyAddress': '复制地址',
    'directions.openInMaps': '在地图中打开',
    'directions.getDirections': '获取方向',
    'directions.stayDetails': '住宿详情',
    'directions.checkIn': '入住',
    'directions.checkOut': '退房',
    'directions.after3pm': '下午3:00后',
    'directions.before11am': '上午11:00前',
    'directions.guests': '访客',
    'directions.guest': '访客',
    'directions.houseRules': '房屋规则',
    'directions.smartHomeAccess': '智能家居访问',
    'directions.confidentialInfo': '此信息是保密的，不应与他人分享。',
    'directions.needHelp': '需要帮助？',
    'directions.messageHost': '联系房东',
    'directions.contactSupport': '联系支持',
    'directions.bookingNotFound': '预订未找到',
    'directions.noAccess': '此预订不存在或您无权访问。',
    'directions.goToBookings': '前往预订',
    'directions.addressCopied': '地址已复制到剪贴板',
    'directions.addressOnlyConfirmed': '房产地址仅对已确认的预订可用',
    'directions.failedToLoad': '加载房产详情失败',
    
    // Privacy Page (Chinese)
    'privacy.backToHome': '返回首页',
    'privacy.title': '隐私政策',
    'privacy.lastUpdated': '最后更新：2025年12月',
    'privacy.footer': '© 2025 Aether. 让物业管理更简单。',
    'privacy.section1.title': '1. 我们收集的信息',
    'privacy.section1.intro': '在Aether，我们收集您直接提供给我们的信息，例如当您创建账户、进行支付或联系我们寻求支持时。这可能包括：',
    'privacy.section1.item1': '姓名和联系信息（电子邮件、电话号码）',
    'privacy.section1.item2': '物业详情和地址',
    'privacy.section1.item3': '支付信息（通过Stripe安全处理）',
    'privacy.section1.item4': '通信偏好',
    'privacy.section1.item5': '使用数据和分析',
    'privacy.section2.title': '2. 我们如何使用您的信息',
    'privacy.section2.intro': '我们使用收集的信息来：',
    'privacy.section2.item1': '提供、维护和改进我们的服务',
    'privacy.section2.item2': '处理交易并发送相关通知',
    'privacy.section2.item3': '回应您的评论、问题和支持请求',
    'privacy.section2.item4': '向您发送技术通知、更新和安全警报',
    'privacy.section2.item5': '就产品、服务和活动与您沟通',
    'privacy.section3.title': '3. 订阅层级与数据处理',
    'privacy.section3.intro': '我们根据您的订阅层级处理数据：',
    'privacy.section3.explorer': '探索者（访客）',
    'privacy.section3.explorerDesc': '15天试用期及之后的预订历史、偏好设置、付款记录',
    'privacy.section3.sovereign': '主权者（业主）',
    'privacy.section3.sovereignDesc': '30天试用期及之后的物业数据、租赁列表、客人管理、收入分析',
    'privacy.section3.architect': '建筑师（开发商）',
    'privacy.section3.architectDesc': '社区数据、业主管理、HOA付款、供应商关系',
    'privacy.section4.title': '4. 仅限邀请访问',
    'privacy.section4.intro': '对于某些订阅层级，访问仅限邀请：',
    'privacy.section4.item1': '业主（Sovereign）由社区管理员邀请',
    'privacy.section4.item2': '物业开发商（Architect）在销售咨询后由Aether员工邀请',
    'privacy.section5.title': '5. 数据安全',
    'privacy.section5.content': '我们对所有数据传输使用银行级256位SSL加密。您的数据存储在通过SOC 2 Type II认证的数据中心，正常运行时间为99.99%。我们采取适当的技术和组织措施，保护您的个人信息免受未经授权的访问、更改、披露或破坏。',
    'privacy.section6.title': '6. 第三方服务',
    'privacy.section6.intro': '我们使用可信的第三方服务来提供我们的平台：',
    'privacy.section6.stripe': '为订阅、HOA费用和租赁付款提供安全的支付处理',
    'privacy.section6.mapbox': '为物业位置和社区设施提供交互式地图',
    'privacy.section6.openai': '为服务预订和推荐提供AI驱动的礼宾服务',
    'privacy.section7.title': '7. 数据保留',
    'privacy.section7.content': '只要您的账户处于活跃状态或需要提供服务，我们就会保留您的数据。订阅和交易数据将保留7年，以符合税务和法律规定。您可以随时请求删除您的账户和个人数据。',
    'privacy.section8.title': '8. 您的权利',
    'privacy.section8.intro': '您有权：',
    'privacy.section8.item1': '访问并接收您个人数据的副本',
    'privacy.section8.item2': '更正不准确的个人数据',
    'privacy.section8.item3': '请求删除您的个人数据',
    'privacy.section8.item4': '反对处理您的个人数据',
    'privacy.section8.item5': '请求限制处理您的个人数据',
    'privacy.section8.item6': '数据可携带性',
    'privacy.section9.title': '9. 联系我们',
    'privacy.section9.content': '如果您对本隐私政策有任何疑问，请通过以下方式联系我们：',
    
    // Terms Page (Chinese)
    'terms.backToHome': '返回首页',
    'terms.title': '服务条款',
    'terms.lastUpdated': '最后更新：2025年12月',
    'terms.footer': '© 2025 Aether. 让物业管理更简单。',
    'terms.section1.title': '1. 条款接受',
    'terms.section1.content': '通过访问或使用Aether（"服务"），您同意受这些服务条款的约束。如果您不同意这些条款，请不要使用我们的服务。我们保留随时更新这些条款的权利，您继续使用服务即表示接受任何更改。',
    'terms.section2.title': '2. 服务描述',
    'terms.section2.content': 'Aether是一个物业管理平台，为社区管理员、物业业主和访客提供工具。我们的服务包括但不限于：支付处理、访客通行证管理、服务预订、社区公告、租赁列表和AI驱动的礼宾服务。',
    'terms.section3.title': '3. 订阅计划',
    'terms.section3.intro': 'Aether提供三个订阅层级：',
    'terms.section3.explorer': '探索者（$9.99/月）',
    'terms.section3.explorerDesc': '适合寻找豪华住宿的旅行者。预订短租、长租，并使用AI礼宾服务。',
    'terms.section3.sovereign': '主权者（$19.99/月）',
    'terms.section3.sovereignDesc': '适合业主。发布物业、管理客人、查看分析数据，并从租赁中获得收入。',
    'terms.section3.architect': '建筑师（定制价格）',
    'terms.section3.architectDesc': '适合物业开发商。创建社区、管理业主、收取HOA费用，并使用企业功能。',
    'terms.section4.title': '4. 试用期',
    'terms.section4.intro': '部分计划提供免费试用：',
    'terms.section4.explorer': '探索者',
    'terms.section4.explorerTrial': '15天免费试用。无需信用卡即可开始。',
    'terms.section4.sovereign': '主权者',
    'terms.section4.sovereignTrial': '收到社区管理员邀请后，可享30天免费试用。',
    'terms.section4.architect': '建筑师',
    'terms.section4.architectTrial': '无试用期。联系销售获取演示和定制价格。',
    'terms.section5.title': '5. 仅限邀请访问',
    'terms.section5.intro': '某些计划需要邀请：',
    'terms.section5.item1': '业主（Sovereign计划）必须由社区管理员邀请',
    'terms.section5.item2': '物业开发商（Architect计划）必须在销售咨询后由Aether员工邀请',
    'terms.section5.item3': '邀请不可转让，仅供特定收件人使用',
    'terms.section6.title': '6. 用户账户',
    'terms.section6.intro': '要访问服务的某些功能，您必须创建账户。您同意：',
    'terms.section6.item1': '提供准确、最新和完整的信息',
    'terms.section6.item2': '维护并及时更新您的账户信息',
    'terms.section6.item3': '维护您密码和账户的安全',
    'terms.section6.item4': '对您账户下的所有活动负责',
    'terms.section6.item5': '立即通知我们任何未经授权的使用',
    'terms.section7.title': '7. 基于角色的访问控制',
    'terms.section7.intro': 'Aether使用基于角色的访问来管理权限：',
    'terms.section7.admin': '管理员（社区管理员）',
    'terms.section7.adminDesc': '完全访问权限，可管理物业、业主、供应商和社区设置',
    'terms.section7.sovereign': '主权者（物业业主）',
    'terms.section7.sovereignDesc': '访问拥有的物业、HOA付款、租赁管理和礼宾服务',
    'terms.section7.demiUser': '半用户（访客）',
    'terms.section7.demiUserDesc': '时间限制的访问权限，可使用地图、基本服务和与房东通信',
    'terms.section8.title': '8. 可接受的使用',
    'terms.section8.intro': '您同意不：',
    'terms.section8.item1': '将服务用于任何非法目的',
    'terms.section8.item2': '试图未经授权访问服务的任何部分',
    'terms.section8.item3': '干扰或破坏服务或服务器',
    'terms.section8.item4': '上传恶意代码或内容',
    'terms.section8.item5': '冒充任何人或实体',
    'terms.section8.item6': '违反任何适用的法律或法规',
    'terms.section9.title': '9. 账单和取消',
    'terms.section9.content': '订阅费用按月或按年预先计费。除法律要求外，所有费用不予退还。您可以随时通过设置中的Stripe客户门户取消订阅。您的访问权限将持续到计费周期结束。',
    'terms.section10.title': '10. 责任限制',
    'terms.section10.content': '在法律允许的最大范围内，Aether不对因您使用或无法使用服务而导致的任何间接、附带、特殊、后果性或惩罚性损害承担责任。我们的总责任不得超过您在索赔前十二个月内向我们支付的金额。',
    'terms.section11.title': '11. 联系方式',
    'terms.section11.content': '如对这些条款有疑问，请通过以下方式联系我们：',
    
    // Support FAQ (Chinese - Updated)
    'support.faq1Question': '如何注册Aether？',
    'support.faq1Answer': '访客可以直接在我们的网站上注册，享受15天免费试用。业主需要社区管理员邀请才能加入（30天试用）。物业开发商请联系我们的销售团队。',
    'support.faq2Question': '每个订阅计划包含什么？',
    'support.faq2Answer': '探索者（$9.99/月）适合旅行者 - 预订租赁、使用AI礼宾。主权者（$19.99/月）适合业主 - 发布物业、管理客人。建筑师（定制价格）适合管理社区的开发商。',
    'support.faq3Question': '免费试用如何运作？',
    'support.faq3Answer': '探索者计划：15天免费试用，无需信用卡。主权者计划：收到社区邀请后30天免费试用。建筑师计划：无试用期 - 联系销售获取演示和定制价格。',
    'support.faq4Question': '业主如何获得邀请？',
    'support.faq4Answer': '联系您的社区管理员或物业开发商，请他们通过Aether向您发送邀请。您将收到一封包含账户设置说明的电子邮件。',
    'support.faq5Question': '如何成为Aether上的物业开发商？',
    'support.faq5Answer': '通过支持页面或发送电子邮件至sales@aether.app联系我们的销售团队。我们根据您的物业组合规模提供企业入驻和定制价格。',
    'support.faq6Question': '如何取消或更改订阅？',
    'support.faq6Answer': '前往设置 > 订阅，点击"管理订阅"访问Stripe客户门户。您可以随时取消 - 您的访问权限将持续到计费周期结束。',
  },
  my: {
    // Common
    'common.signIn': 'ဝင်ရောက်ရန်',
    'common.signUp': 'စာရင်းသွင်းရန်',
    'common.signOut': 'ထွက်ရန်',
    'common.loading': 'တင်နေသည်...',
    'common.save': 'သိမ်းရန်',
    'common.cancel': 'ပယ်ဖျက်ရန်',
    'common.delete': 'ဖျက်ရန်',
    'common.edit': 'ပြင်ဆင်ရန်',
    'common.search': 'ရှာဖွေရန်',
    'common.filter': 'စစ်ထုတ်ရန်',
    'common.clear': 'ရှင်းလင်းရန်',
    'common.submit': 'တင်သွင်းရန်',
    'common.back': 'နောက်သို့',
    'common.next': 'ရှေ့သို့',
    'common.viewAll': 'အားလုံးကြည့်ရန်',
    'common.learnMore': 'ပိုမိုလေ့လာရန်',
    
    // Navigation
    'nav.home': 'ထိန်းချုပ်ခန်း',
    'nav.dashboard': 'ထိန်းချုပ်ခန်း',
    'nav.services': 'ဝန်ဆောင်မှုများ',
    'nav.rentals': 'ငှားရမ်းခြင်း',
    'nav.map': 'မြေပုံ',
    'nav.wallet': 'ငွေပေးချေမှုများ',
    'nav.settings': 'ဆက်တင်များ',
    'nav.messages': 'မက်ဆေ့ချ်များ',
    'nav.notifications': 'အကြောင်းကြားချက်များ',
    'nav.concierge': 'ဧည့်ခံဝန်ဆောင်မှု',
    
    // Landing Page - Hero
    'landing.hero.title': 'သင့်အိမ်ခြံများကို',
    'landing.hero.titleAccent': 'လွယ်ကူစွာစီမံခန့်ခွဲပါ',
    'landing.hero.subtitle': 'ငွေပေးချေမှု၊ ဧည့်သည်ဝင်ရောက်ခွင့်၊ ဝန်ဆောင်မှုများနှင့် နေထိုင်သူဆက်သွယ်ရေးအားလုံးကို တစ်နေရာတည်းတွင်။',
    'landing.hero.tagline': 'အိမ်ခြံမြေဖွံ့ဖြိုးရေးနှင့် HOA များအတွက်',
    'landing.cta.getStarted': 'စတင်ရန်',
    'landing.cta.watchDemo': 'သရုပ်ပြကြည့်ရန်',
    'landing.cta.startTrial': 'အခမဲ့စမ်းသုံးရန်',
    'landing.cta.contactSales': 'အရောင်းဆက်သွယ်ရန်',
    'landing.cta.contactSupport': 'ပံ့ပိုးကူညီမှုဆက်သွယ်ရန်',
    'landing.trust.security': 'ဘဏ်အဆင့်လုံခြုံရေး',
    'landing.trust.properties': '500+ အိမ်ခြံများ',
    'landing.trust.residents': '10,000+ နေထိုင်သူများ',
    'landing.nav.howItWorks': 'အလုပ်လုပ်ပုံ',
    'landing.nav.features': 'အင်္ဂါရပ်များ',
    'landing.nav.pricing': 'စျေးနှုန်း',
    'landing.nav.signIn': 'ဝင်ရောက်ရန်',
    'landing.howItWorks': 'အလုပ်လုပ်ပုံ',
    'landing.features': 'အင်္ဂါရပ်များ',
    'landing.pricing': 'စျေးနှုန်း',
    
    // Landing Page - How It Works
    'landing.howItWorks.title': 'ရိုးရှင်းသောအဆင့် 3 ခုဖြင့်စတင်ပါ',
    'landing.howItWorks.subtitle': 'စာရင်းသွင်းခြင်းမှ အပြည့်အဝလည်ပတ်နိုင်ခြင်းအထိ 10 မိနစ်အတွင်း။',
    'landing.howItWorks.step1.title': 'သင့်အကောင့်ဖန်တီးပါ',
    'landing.howItWorks.step1.desc': 'အီးမေးလ်ဖြင့်စာရင်းသွင်းပါ။ သင့်ရပ်ကွက်အသေးစိတ်ထည့်သွင်းပြီး အဖွဲ့ဝင်များကိုဖိတ်ကြားပါ။',
    'landing.howItWorks.step2.title': 'အိမ်ခြံနှင့်နေထိုင်သူများထည့်သွင်းပါ',
    'landing.howItWorks.step2.desc': 'အိမ်ခြံစာရင်းတင်သွင်းပါ သို့မဟုတ် ကိုယ်တိုင်ထည့်သွင်းပါ။ နေထိုင်သူများကိုဖိတ်ကြားပါ။',
    'landing.howItWorks.step3.title': 'စတင်အသုံးပြုပါ',
    'landing.howItWorks.step3.desc': 'ငွေပေးချေမှုလက်ခံခြင်း၊ ဧည့်သည်ပါစ်စီမံခြင်းနှင့် ဝန်ဆောင်မှုကြိုတင်စာရင်းသွင်းခြင်းတို့ကို ချက်ချင်းစတင်ပါ။',
    
    // Landing Page - Personas
    'landing.personas.title': 'သင့်ရပ်ကွက်ရှိလူတိုင်းအတွက်တည်ဆောက်ထားသည်',
    'landing.personas.subtitle': 'စီမံခန့်ခွဲသူ၊ ပိုင်ရှင် သို့မဟုတ် ဧည့်သည်ဖြစ်ဖြစ်—Aether သင့်အတွက်အလုပ်လုပ်သည်။',
    'landing.persona.manager.role': 'ရပ်ကွက်စီမံခန့်ခွဲသူ',
    'landing.persona.manager.tagline': 'လုပ်ငန်းစဉ်များကိုစီမံပါ၊ အသေးအမွှားကိစ္စများမဟုတ်',
    'landing.persona.manager.benefit1': 'အိမ်ခြံအားလုံးတစ်ချက်ကြည့်ရှုနိုင်သော ဒက်ရှ်ဘုတ်',
    'landing.persona.manager.benefit2': 'အလိုအလျောက်ငွေကောက်ခံခြင်းနှင့်သတိပေးချက်များ',
    'landing.persona.manager.benefit3': 'ရောင်းချသူနှင့်ဝန်ဆောင်မှုပေးသူများစီမံခန့်ခွဲခြင်း',
    'landing.persona.manager.benefit4': 'နေထိုင်သူအားလုံးထံ ကြေညာချက်များချက်ချင်းပို့ခြင်း',
    'landing.persona.manager.quote': '"ငါ အရင်တုန်းက နေ့တိုင်း 3 နာရီ စီမံခန့်ခွဲရေးကိစ္စများလုပ်ခဲ့ရတယ်။ အခု 30 မိနစ်ပဲ။"',
    'landing.persona.owner.role': 'အိမ်ခြံပိုင်ရှင်',
    'landing.persona.owner.tagline': 'သင့်အိမ်ခြံ၊ သင့်နည်းလမ်း',
    'landing.persona.owner.benefit1': 'တစ်ချက်နှိပ်ရုံဖြင့် HOA ကြေးပေးချေပါ',
    'landing.persona.owner.benefit2': 'သင့်အိမ်အတွက် ဧည့်ခံဝန်ဆောင်မှုများကြိုတင်စာရင်းသွင်းပါ',
    'landing.persona.owner.benefit3': 'သင့်အိမ်ခြံကို ရေတိုငှားရမ်းခြင်းစာရင်းသွင်းပါ',
    'landing.persona.owner.benefit4': 'ဧည့်သည်များကို လုံခြုံစွာပါစ်ထုတ်ပေးပါ',
    'landing.persona.owner.quote': '"နောက်ဆုံးတော့ ကျွန်တော့်အိမ်ခြံကို ပိုင်ဆိုင်မှုတစ်ခုအဖြစ် ဆက်ဆံတဲ့ အက်ပ်တစ်ခုရလာပြီ။"',
    'landing.persona.guest.role': 'ဧည့်သည်',
    'landing.persona.guest.tagline': 'ပိုင်ဆိုင်သူတစ်ယောက်လိုရောက်ရှိပါ',
    'landing.persona.guest.benefit1': 'QR ကုဒ်ဝင်ခွင့်—တံခါးဝမှာမစောင့်ရ',
    'landing.persona.guest.benefit2': 'ရပ်ကွက်မြေပုံနှင့်သာယာစရာများလေ့လာပါ',
    'landing.persona.guest.benefit3': 'အက်ပ်ထဲမှာတိုက်ရိုက်အိမ်ရှင်နဲ့ဆက်သွယ်ပါ',
    'landing.persona.guest.benefit4': 'သင်တည်းခိုနေစဉ်အချိန်အတွင်းသာအကျုံးဝင်သည့်ဝင်ခွင့်',
    'landing.persona.guest.quote': '"ငါကြုံဖူးသမျှထဲမှာ အချောမောဆုံး ချက်အင်ဖြစ်ခဲ့တယ်။"',
    
    // Landing Page - Property Types
    'landing.propertyTypes.title': 'အဆင့်မြင့်ရပ်ကွက်များအတွက်တည်ဆောက်ထားသည်',
    'landing.propertyTypes.subtitle': 'ကွန်ဒို၊ တံခါးပိတ်ရပ်ကွက် သို့မဟုတ် ဇိမ်ခံငှားရမ်းခြင်း စီမံခန့်ခွဲသည်ဖြစ်စေ—Aether သင့်ပေါ်တ်ဖိုလီယိုနှင့်အတူတိုးတက်သည်။',
    'landing.propertyTypes.penthouse': 'မြို့ပြ Penthouse များ',
    'landing.propertyTypes.penthouseDesc': 'မြို့ရှုခင်း၊ အမိုးပြင်ဆင်',
    'landing.propertyTypes.estate': 'ခေတ်မီ Estate များ',
    'landing.propertyTypes.estateDesc': 'ဗိသုကာလက်ရာများ',
    'landing.propertyTypes.beachfront': 'ကမ်းစပ်အပန်းဖြေအိမ်များ',
    'landing.propertyTypes.beachfrontDesc': 'ပုဂ္ဂလိကကမ်းခြေ၊ အဆုံးမဲ့မိုးကုပ်စည်း',
    
    // Landing Page - Features
    'landing.features.title': 'သင်လိုအပ်သမျှ၊ မလိုအပ်သည်များမပါ',
    'landing.features.subtitle': '5+ ကိရိယာများကို ပလက်ဖောင်းတစ်ခုတည်းဖြင့်အစားထိုးပါ။ ပထမနေ့ကတည်းက အချိန်နှင့်ကုန်ကျစရိတ်သက်သာစေပါ။',
    'landing.feature.portfolio.title': 'ဗဟိုချုပ်ကိုင်မှု ပေါ်တ်ဖိုလီယိုထိန်းချုပ်ခြင်း',
    'landing.feature.portfolio.desc': 'ဒက်ရှ်ဘုတ်တစ်ခုတည်းမှ အိမ်ခြံအားလုံးစီမံခန့်ခွဲပါ။ စာရွက်စာတမ်းများ၊ အီးမေးလ်များနှင့် အက်ပ်များကြားခုန်ခါခုန်ပြန်မလိုတော့ပါ။',
    'landing.feature.bookings.title': 'ဝန်ဆောင်မှုကြိုတင်စာရင်းသွင်းခြင်းအလိုအလျောက်လုပ်ဆောင်ခြင်း',
    'landing.feature.bookings.desc': 'နေထိုင်သူများကို သန့်ရှင်းရေး၊ ပြုပြင်ထိန်းသိမ်းရေးနှင့် ဧည့်ခံဝန်ဆောင်မှုများ ကိုယ်တိုင်ကြိုတင်စာရင်းသွင်းခွင့်ပြုပါ—စီမံခန့်ခွဲရေးလုပ်ငန်းဝန် 80% လျှော့ချပါ။',
    'landing.feature.messaging.title': 'ပိုမြန်သောတုံ့ပြန်မှုအချိန်များ',
    'landing.feature.messaging.desc': 'ပါဝင်သောမက်ဆေ့ချ်စနစ်က ပိုင်ရှင်များ၊ ဧည့်သည်များနှင့် ရောင်းချသူများကို ချိတ်ဆက်ထားသည်—လွတ်သွားသောဖုန်းခေါ်မှုများ သို့မဟုတ် ပျောက်ဆုံးသွားသောအီးမေးလ်များမရှိ။',
    'landing.feature.payments.title': 'အချိန်မီငွေကောက်ခံပါ',
    'landing.feature.payments.desc': 'အလိုအလျောက် HOA ကြေးကောက်ခံခြင်းနှင့် ဝန်ဆောင်မှုငွေပေးချေမှုများ။ နောက်ကျသောငွေပေးချေမှုများ 60% လျှော့ချပါ။',
    'landing.feature.maps.title': 'သင့်ရပ်ကွက်ကိုပြသပါ',
    'landing.feature.maps.desc': 'အပြန်အလှန်တုံ့ပြန်နိုင်သောမြေပုံများက နေထိုင်သူများနှင့် ဧည့်သည်များကို သာယာစရာများရှာဖွေရန်ကူညီသည်၊ ပါဝင်မှုနှင့် ကျေနပ်မှုတိုးမြှင့်သည်။',
    'landing.feature.guests.title': 'လုံခြုံသောဧည့်သည်ဝင်ခွင့်',
    'landing.feature.guests.desc': 'အချိန်ကန့်သတ်ချက်ပါသော QR ကုဒ်ပါစ်များ။ သင့်အိမ်ခြံထဲ ဘယ်သူဘယ်အချိန်ဝင်သည်ကိုသိပါ—လုံခြုံရေးအဟာကွက်များဖယ်ရှားပါ။',
    
    // Landing Page - Pricing
    'landing.pricing.title': 'ရိုးရှင်း၊ ခန့်မှန်းနိုင်သောစျေးနှုန်း',
    'landing.pricing.subtitle': 'ဝှက်ထားသောကြေးများမရှိ။ အရောင်းအဝယ်တစ်ခုချင်းစီအတွက်အခကြေးမရှိ။ ပုံသေနှုန်းထားတစ်ခုသာ။',
    'landing.pricing.mostPopular': 'အရေပန်းအစားဆုံး',
    'landing.pricing.perMonth': 'လ',
    'landing.pricing.custom': 'စိတ်ကြိုက်',
    'landing.pricing.sovereign.name': 'Sovereign',
    'landing.pricing.sovereign.desc': 'အရည်အသွေးကောင်းတောင်းသောအိမ်ခြံပိုင်ရှင်များအတွက်',
    'landing.pricing.sovereign.feature1': 'အိမ်ခြံ 3 ခုအထိ',
    'landing.pricing.sovereign.feature2': 'AI ဧည့်ခံဝန်ဆောင်မှုအသုံးပြုခွင့်',
    'landing.pricing.sovereign.feature3': 'ဧည့်သည်ပါစ်စီမံခန့်ခွဲခြင်း',
    'landing.pricing.sovereign.feature4': 'HOA ငွေပေးချေမှုပေါ်တယ်',
    'landing.pricing.sovereign.feature5': 'အချိန်နှင့်တပြေးညီအကြောင်းကြားချက်များ',
    'landing.pricing.sovereign.feature6': 'အခြေခံခွဲခြမ်းစိတ်ဖြာမှု',
    'landing.pricing.elite.name': 'Elite',
    'landing.pricing.elite.desc': 'အိမ်ခြံများစွာပိုင်ဆိုင်သူများအတွက်',
    'landing.pricing.elite.feature1': 'အကန့်အသတ်မဲ့အိမ်ခြံများ',
    'landing.pricing.elite.feature2': 'ဦးစားပေး AI ဧည့်ခံဝန်ဆောင်မှု',
    'landing.pricing.elite.feature3': 'အဆင့်မြင့်ဧည့်သည်စီမံခန့်ခွဲခြင်း',
    'landing.pricing.elite.feature4': 'ငှားရမ်းခြင်းဈေးကွက်အသုံးပြုခွင့်',
    'landing.pricing.elite.feature5': '၀င်ငွေခွဲခြမ်းစိတ်ဖြာမှုဒက်ရှ်ဘုတ်',
    'landing.pricing.elite.feature6': 'အထူးဂရုစိုက်ပံ့ပိုးမှု',
    'landing.pricing.elite.feature7': 'စိတ်ကြိုက်ပေါင်းစပ်မှုများ',
    'landing.pricing.enterprise.name': 'Enterprise',
    'landing.pricing.enterprise.desc': 'ရပ်ကွက်များနှင့် HOA များအတွက်',
    'landing.pricing.enterprise.feature1': 'ရပ်ကွက်တစ်ခုလုံးအသုံးပြုခြင်း',
    'landing.pricing.enterprise.feature2': 'သီးသန့်အကောင့်မန်နေဂျာ',
    'landing.pricing.enterprise.feature3': 'စိတ်ကြိုက်အမှတ်တံဆိပ်',
    'landing.pricing.enterprise.feature4': 'API အသုံးပြုခွင့်',
    'landing.pricing.enterprise.feature5': 'SLA အာမခံချက်များ',
    'landing.pricing.enterprise.feature6': 'လက်တွေ့သင်တန်း',
    'landing.pricing.enterprise.feature7': 'ရပ်ကွက်များစွာပံ့ပိုးမှု',
    'landing.pricing.getStarted': 'စတင်ရန်',
    'landing.pricing.contactSales': 'အရောင်းကိုဆက်သွယ်ရန်',
    
    // Landing Page - Testimonials
    'landing.testimonials.title': 'အိမ်ခြံစီမံခန့်ခွဲသူများ Aether ကိုနှစ်သက်သည်',
    'landing.testimonials.subtitle': 'အဖွဲ့များ အချိန်သက်သာပြီး နေထိုင်သူများကျေနပ်အောင်ပြုလုပ်ပုံကြည့်ပါ။',
    'landing.testimonial1.quote': 'ကျွန်တော်တို့ စီမံခန့်ခွဲရေးအချိန် တစ်ဝက်လျှော့ကျသွားတယ်။ နေထိုင်သူများ ကိုယ်တိုင်ဝန်ဆောင်မှုကြိုတင်စာရင်းသွင်းပြီး ငွေပေးချေမှုများ အလိုအလျောက်ဝင်လာတယ်။',
    'landing.testimonial1.author': 'Alexander Chen',
    'landing.testimonial1.role': 'အိမ်ခြံဖွံ့ဖြိုးရေးသမား၊ Beverly Hills',
    'landing.testimonial2.quote': 'ဧည့်သည်ဝင်ခွင့်က အရင်တုန်းက အိပ်မက်ဆိုးတစ်ခုဖြစ်ခဲ့တယ်။ အခုတော့ လာရောက်သူများ QR ကုဒ်ရပြီး ကျွန်တော်တို့မှာ ပြည့်စုံတဲ့မှတ်တမ်းရှိတယ်။',
    'landing.testimonial2.author': 'Victoria Sterling',
    'landing.testimonial2.role': 'HOA ဥက္ကဋ္ဌ၊ Miami',
    'landing.testimonial3.quote': 'ကျွန်တော်တို့နေထိုင်သူများ ဒီအက်ပ်ကိုနှစ်သက်တယ်။ ဝန်ဆောင်မှုတောင်းဆိုမှုများ 40% ကျဆင်းသွားတယ် ဘာဖြစ်လို့လဲဆိုတော့ သူတို့ကိုယ်တိုင်အဖြေရှာနိုင်လို့။',
    'landing.testimonial3.author': 'Marcus Williams',
    'landing.testimonial3.role': 'ရပ်ကွက်မန်နေဂျာ၊ 200+ အိမ်ခြံ',
    
    // Landing Page - CTA Section
    'landing.cta.title': 'သင့်လုပ်ငန်းစဉ်များရိုးရှင်းအောင်ပြုလုပ်ဖို့အဆင်သင့်ဖြစ်ပြီလား?',
    'landing.cta.subtitle': 'မိနစ်ပိုင်းအတွင်းစတင်ပါ။ ရက်ပိုင်းအတွင်းရလဒ်မြင်ပါ။ စာချုပ်မရှိ၊ အချိန်မရွေးဖျက်သိမ်းနိုင်သည်။',
    
    // Landing Page - FAQ
    'landing.faq.title': 'မေးလေ့ရှိသောမေးခွန်းများ',
    'landing.faq.subtitle': 'Aether စတင်အသုံးပြုခြင်းအကြောင်း သိလိုသမျှ။',
    'landing.faq.stillQuestions': 'မေးခွန်းများရှိသေးပါသလား?',
    'landing.faq.q1': 'ကျွန်တော့်ရပ်ကွက်အတွက် Aether စတင်သတ်မှတ်ရန် အချိန်မည်မျှကြာမည်နည်း?',
    'landing.faq.a1': 'ရပ်ကွက်အများစုသည် 10-15 မိနစ်အတွင်း အပြည့်အဝလည်ပတ်နိုင်သည်။ အကောင့်ဖန်တီးပါ၊ အိမ်ခြံများနှင့် နေထိုင်သူများထည့်သွင်းပါ၊ ပြီးလျှင်စတင်အသုံးပြုနိုင်ပါပြီ။',
    'landing.faq.q2': 'ကျွန်တော့်ဒေတာ Aether မှာလုံခြုံပါသလား?',
    'landing.faq.a2': 'လုံးဝလုံခြုံပါသည်။ ကျွန်တော်တို့သည် ဒေတာလွှဲပြောင်းမှုအားလုံးအတွက် ဘဏ်အဆင့် 256-bit SSL encryption အသုံးပြုပါသည်။',
    'landing.faq.q3': 'အခပေးအစီအစဉ်မဝယ်မီ Aether ကိုစမ်းသုံးနိုင်ပါသလား?',
    'landing.faq.a3': 'ဟုတ်ကဲ့! ကျွန်တော်တို့သည် 14 ရက်အခမဲ့စမ်းသုံးခွင့်ပေးပါသည်၊ လုပ်ဆောင်ချက်အားလုံးအပြည့်အဝအသုံးပြုခွင့်ရှိသည်။ စတင်ရန် ခရက်ဒစ်ကတ်မလိုအပ်ပါ။',
    'landing.faq.q4': 'ငွေပေးချေမှုလုပ်ငန်းစဉ်မည်သို့အလုပ်လုပ်သနည်း?',
    'landing.faq.a4': 'Aether သည် လုံခြုံသောငွေပေးချေမှုလုပ်ငန်းစဉ်အတွက် Stripe နှင့်ပေါင်းစပ်ထားသည်။ နေထိုင်သူများသည် HOA ကြေး၊ ဝန်ဆောင်ခနှင့် ငှားရမ်းခများကို အက်ပ်မှတိုက်ရိုက်ပေးချေနိုင်သည်။',
    'landing.faq.q5': 'Aether ကို ကျွန်တော့်ရှိပြီးသားကိရိယာများနှင့်ပေါင်းစပ်နိုင်ပါသလား?',
    'landing.faq.a5': 'ကျွန်တော်တို့၏ Elite နှင့် Enterprise အစီအစဉ်များတွင် API အသုံးပြုခွင့်နှင့် စိတ်ကြိုက်ပေါင်းစပ်မှုများပါဝင်သည်။',
    'landing.faq.q6': 'ပြဿနာရှိပါက မည်သည့်ပံ့ပိုးမှုရရှိနိုင်သနည်း?',
    'landing.faq.a6': 'အစီအစဉ်အားလုံးတွင် 24 နာရီအတွင်းတုံ့ပြန်မှုဖြင့် အီးမေးလ်ပံ့ပိုးမှုပါဝင်သည်။ Pro အစီအစဉ်များသည် 4 နာရီတုံ့ပြန်မှုဖြင့် ဦးစားပေးပံ့ပိုးမှုရရှိသည်။',
    'landing.faq.contactSupport': 'ပံ့ပိုးကူညီရေးသို့ဆက်သွယ်ရန်',
    
    // Landing Page - Footer
    'landing.footer.tagline': 'Sovereign Living',
    'landing.footer.copyright': '© {{year}} Aether. အိမ်ခြံစီမံခန့်ခွဲမှု၊ ရိုးရှင်းစေသည်။',
    'landing.footer.privacy': 'ကိုယ်ရေးကိုယ်တာ',
    'landing.footer.terms': 'စည်းကမ်းချက်များ',
    'landing.footer.support': 'ပံ့ပိုးကူညီမှု',
    
    // Google Auth
    'auth.orContinueWith': 'သို့မဟုတ် ဆက်လက်ဝင်ရောက်ရန်',
    'auth.signInWithGoogle': 'Google ဖြင့် ဝင်ရောက်ပါ',
    'auth.googleSignInError': 'Google ဖြင့် ဝင်ရောက်မှု မအောင်မြင်ပါ',
    
    // Auth
    'auth.welcomeBack': 'ပြန်လာတာကြိုဆိုပါတယ်',
    'auth.createAccount': 'အကောင့်ဖန်တီးရန်',
    'auth.resetPassword': 'စကားဝှက်ပြန်သတ်မှတ်ရန်',
    'auth.setPassword': 'စကားဝှက်သတ်မှတ်ရန်',
    'auth.email': 'အီးမေးလ်',
    'auth.password': 'စကားဝှက်',
    'auth.confirmPassword': 'စကားဝှက်အတည်ပြုရန်',
    'auth.forgotPassword': 'စကားဝှက်မေ့နေပါသလား?',
    'auth.noAccount': 'အကောင့်မရှိသေးဘူးလား?',
    'auth.hasAccount': 'အကောင့်ရှိပြီးသားလား?',
    'auth.backToHome': 'ပင်မစာမျက်နှာသို့',
    
    // Dashboard
    'dashboard.welcome': 'ပြန်လာတာကြိုဆိုပါတယ်',
    'dashboard.properties': 'အိမ်ခြံများ',
    'dashboard.bookings': 'ကြိုတင်မှာယူမှုများ',
    'dashboard.payments': 'ငွေပေးချေမှုများ',
    'dashboard.guests': 'ဧည့်သည်များ',
    
    // Rentals
    'rentals.title': 'အထူးကောင်းမွန်သောအိမ်ခြံများကိုရှာဖွေပါ',
    'rentals.subtitle': 'ကမ္ဘာ့အဆင့်မီအဆောက်အအုံများနှင့် ကိုယ်ပိုင်ဧည့်ခံဝန်ဆောင်မှုများကို ခံစားပါ',
    'rentals.searchPlaceholder': 'တည်နေရာဖြင့်ရှာဖွေပါ...',
    'rentals.pricePerNight': 'တစ်ညလျှင်',
    'rentals.bedrooms': 'အိပ်ခန်း',
    'rentals.bathrooms': 'ရေချိုးခန်း',
    'rentals.bookNow': 'ယခုဘွတ်ကင်လုပ်ရန်',
    'rentals.checkIn': 'ဝင်ရောက်ရန်',
    'rentals.checkOut': 'ထွက်ခွာရန်',
    'rentals.guests': 'ဧည့်သည်',
    'rentals.noResults': 'စစ်ထုတ်မှုနှင့်ကိုက်ညီသောအိမ်ခြံမရှိပါ',
    'rentals.askAI': 'AI ဖြင့်ငှားရမ်းရန်ရှာပါ',
    
    // AI Assistant
    'ai.greeting': 'မင်္ဂလာပါ! ကျွန်ုပ်သည် သင်၏ Aether ဧည့်ခံဖြစ်ပါသည်။ ဘာကူညီပေးရမလဲ?',
    'ai.placeholder': 'မေးလိုတာမေးပါ...',
    'ai.listening': 'နားထောင်နေပါသည်...',
    'ai.findRentals': 'ငှားရန်ရှာဖွေပါ',
    
    // Settings
    'settings.title': 'ဆက်တင်များ',
    'settings.language': 'ဘာသာစကား',
    'settings.currency': 'ငွေကြေး',
    'settings.timezone': 'အချိန်ဇုန်',
    'settings.notifications': 'အကြောင်းကြားချက်များ',
    'settings.appearance': 'အသွင်အပြင်',
    'settings.darkMode': 'အမှောင်မုဒ်',
    'settings.profile': 'ကိုယ်ရေးအချက်အလက်',
    'settings.profileDesc': 'အမည်၊ ဖုန်း၊ ပရိုဖိုင်ပုံ',
    'settings.notificationsDesc': 'အီးမေးနှင့် တွန်းအကြောင်းကြားချက်ဆက်တင်များ',
    'settings.payment': 'ငွေပေးချေမှုနည်းလမ်းများ',
    'settings.paymentDesc': 'ကတ်နှင့် ဘေလ်များစီမံခန့်ခွဲရန်',
    'settings.subscriptions': 'စာရင်းသွင်းမှုများ',
    'settings.subscriptionsDesc': 'SaaS နှင့် HOA အစီအစဉ်များ',
    'settings.guestPasses': 'ဧည့်သည်ပတ်စ်များ',
    'settings.guestPassesDesc': 'လာရောက်သူဝင်ခွင့်စီမံခန့်ခွဲရန်',
    'settings.languageCurrency': 'ဘာသာစကားနှင့် ငွေကြေး',
    'settings.languageCurrencyDesc': 'ဒေသဆိုင်ရာဦးစားပေးမှုများ',
    'settings.smartHome': 'စမတ်အိမ်မျှဝေခြင်း',
    'settings.smartHomeDesc': 'ဧည့်သည်များနှင့် Google Home ဝင်ခွင့်မျှဝေပါ',
    'settings.viewAs': 'အခန်းကဏ္ဍပုံစံပြ',
    'settings.viewAsDesc': 'မတူညီသောအသုံးပြုသူအတွေ့အကြုံများစမ်းသပ်ပါ',
    'settings.signOut': 'ထွက်ရန်',
    
    // Messages
    'messages.title': 'မက်ဆေ့ချ်များ',
    'messages.subtitle': 'စကားဝိုင်းများ၊ AI နှင့် အကြောင်းကြားချက်များ',
    'messages.chats': 'ချက်တင်များ',
    'messages.aether': 'Aether',
    'messages.alerts': 'သတိပေးချက်များ',
    'messages.noConversations': 'စကားဝိုင်းများမရှိသေးပါ',
    'messages.noConversationsDesc': 'ဧည့်သည်များနှင့် အိမ်ပိုင်ရှင်များထံမှမက်ဆေ့ချ်များ ဤနေရာတွင်ပေါ်လာပါမည်',
    'messages.noNotifications': 'အကြောင်းကြားချက်များမရှိပါ',
    'messages.noNotificationsDesc': 'အသိုင်းအဝိုင်းကြေညာချက်များ ဤနေရာတွင်ပေါ်လာပါမည်',
    'messages.markAllRead': 'အားလုံးဖတ်ပြီးဟုမှတ်သားပါ',
    'messages.startConversation': 'စကားပြောရန်စတင်ပါ',
    'messages.aetherTitle': 'Aether AI ဧည့်ခံ',
    'messages.aetherDesc': 'ဝန်ဆောင်မှုများကြိုတင်မှာယူရန်၊ အကြံပြုချက်များရယူရန်နှင့် သင့်အိမ်ခြံလိုအပ်ချက်များစီမံခန့်ခွဲရန် သင်၏ကိုယ်ပိုင် AI လက်ထောက်။',
    
    // Browse
    'nav.browse': 'စူးစမ်းရှာဖွေရန်',
    
    // Dashboard specific
    'dashboard.guestAccess': 'ဧည့်သည်ဝင်ခွင့်',
    'dashboard.accessExpires': 'ဝင်ခွင့်သက်တမ်းကုန်ဆုံးရန်',
    'dashboard.daysRemaining': 'ရက် ဧည့်သည်ဝင်ခွင့်ကျန်ရှိ',
    'dashboard.browseRentals': 'ငှားရမ်းရန်ရှာဖွေပါ',
    'dashboard.services': 'ဝန်ဆောင်မှုများ',
    'dashboard.personalizeSearch': 'ရှာဖွေမှုကို ကိုယ်ပိုင်ပြုလုပ်ပါ',
    'dashboard.updatePreferences': 'သင့်ငှားရမ်းရေးဦးစားပေးမှုများကို မွမ်းမံပါ',
    'dashboard.availableServices': 'ရရှိနိုင်သောဝန်ဆောင်မှုများ',
    'dashboard.communityMap': 'အသိုင်းအဝိုင်းမြေပုံ',
    'dashboard.exploreAmenities': 'အဆောက်အအုံများရှာဖွေပါ',
    'dashboard.dining': 'စားသောက်ခြင်း',
    'dashboard.requestMeals': 'အစားအသောက်တောင်းခံပါ',
    'dashboard.transport': 'သယ်ယူပို့ဆောင်ရေး',
    'dashboard.requestValet': 'ကားအပ်နှံရန်တောင်းခံပါ',
    'dashboard.housekeeping': 'အိမ်သန့်ရှင်းရေး',
    'dashboard.ownerOnly': 'ပိုင်ရှင်သီးသန့်',
    'dashboard.guestGuidelines': 'ဧည့်သည်လမ်းညွှန်ချက်များ',
    'dashboard.propertyManager': 'အိမ်ခြံစီမံခန့်ခွဲသူ',
    'dashboard.guestAtProperty': 'ဤအိမ်ခြံ၏ဧည့်သည်',
    'dashboard.guidelineQuietHours': 'ညဉ့် ၁၀ နာရီမှ မနက် ၇ နာရီထိ တိတ်ဆိတ်ရမည်',
    'dashboard.guidelinePoolGym': 'ရေကူးကန်နှင့်ကျန်းမာရေးခန်း မနက် ၆ နာရီမှ ည ၁၀ နာရီထိ',
    'dashboard.guidelineConcierge': 'အကူအညီလိုပါက ဧည့်ခံကို ဆက်သွယ်ပါ',
    'dashboard.guidelineRespect': 'အသိုင်းအဝိုင်းအဆောက်အအုံများကို လေးစားပါ',
    'dashboard.greetingMorning': 'မင်္ဂလာနံနက်ခင်းပါ',
    'dashboard.greetingAfternoon': 'မင်္ဂလာနေ့လည်ခင်းပါ',
    'dashboard.greetingEvening': 'မင်္ဂလာညနေခင်းပါ',
    'dashboard.loading': 'သင့်ဒက်ရှ်ဘုတ်ကို ဖွင့်နေပါသည်...',
    'dashboard.welcomeAether': 'Aether မှ ကြိုဆိုပါသည်',
    'dashboard.yourSmartHome': 'သင့်စမတ်အိမ်',
    'dashboard.allSystemsOperational': 'စနစ်အားလုံး ပုံမှန်အလုပ်လုပ်နေသည်',
    'dashboard.quickActions': 'အမြန်လုပ်ဆောင်ချက်များ',
    'dashboard.propertyStatus': 'အိမ်ခြံအခြေအနေ',
    'dashboard.communityUpdates': 'အသိုင်းအဝိုင်းသတင်းများ',
    'dashboard.upcoming': 'လာမည့်',
    'dashboard.confirmed': 'အတည်ပြုပြီး',
    'dashboard.rentalMode': 'ငှားရမ်းမုဒ်',
    'dashboard.rentalModeDesc': 'ရေတိုငှားရမ်းရန်စာရင်းတင်ပါ',
    'dashboard.rentalModeEnabled': 'ငှားရမ်းမုဒ်ဖွင့်ထားသည်',
    'dashboard.rentalModeDisabled': 'ငှားရမ်းမုဒ်ပိတ်ထားသည်',
    'dashboard.createGuestPass': 'ဧည့်သည်ပါ့စ်ဖန်တီးပါ',
    'dashboard.guestEmail': 'ဧည့်သည်အီးမေးလ်',
    'dashboard.accessDuration': 'ဝင်ခွင့်ကာလ (ရက်)',
    'dashboard.sendGuestPass': 'ဧည့်သည်ပါ့စ်ပို့ပါ',
    'dashboard.needProperty': 'ဧည့်သည်ပါ့စ်ဖန်တီးရန် အိမ်ခြံပိုင်ဆိုင်ရန်လိုပါသည်',
    'dashboard.privateDinner': 'သီးသန့်ညစာ',
    
    // AI Page
    'ai.title': 'Aether AI',
    'ai.subtitle': 'သင့်ကိုယ်ပိုင်အကူအညီ',
    'ai.welcomeMessage': 'မင်္ဂလာပါ! ကျွန်ုပ်သည် သင်၏ AI ဧည့်ခံဖြစ်ပါသည်။ ကူညီပေးနိုင်သည်များ:',
    'ai.helpBooking': 'ဝန်ဆောင်မှုနှင့်ချိန်းဆိုမှုများ ကြိုတင်မှာယူခြင်း',
    'ai.helpGuest': 'ဧည့်သည်ဝင်ခွင့်စီမံခန့်ခွဲခြင်း',
    'ai.helpMaintenance': 'အိမ်ခြံပြုပြင်ထိန်းသိမ်းရေးတောင်းဆိုချက်များ',
    'ai.helpLifestyle': 'လူနေမှုပုံစံအကြံပြုချက်များ',
    'ai.trySaying': 'ပြောကြည့်ပါ',
    'ai.suggestion1': 'စနေနေ့အတွက် သီးသန့်စားဖိုမှူးကြိုတင်မှာပါ',
    'ai.suggestion2': 'ရေကူးကန်ပြုပြင်ထိန်းသိမ်းမှုချိန်းပါ',
    'ai.suggestion3': 'မနက်ဖြန်အတွက် ကျွန်ုပ်၏ဧည့်သည်ကိုခွင့်ပြုပါ',
    'ai.suggestion4': 'ဘယ်ဝန်ဆောင်မှုတွေရနိုင်သလဲ?',
    'roleSimulator.title': 'အခန်းကဏ္ဍ ပုံစံချခြင်း',
    'roleSimulator.description': 'အတွေ့အကြုံပုံစံချရန် အခန်းကဏ္ဍရွေးပါ',
    'roleSimulator.godMode': 'အားလုံးကြီးကြပ်မှု',
    'roleSimulator.godModeDesc': 'ပလက်ဖောင်းတစ်ခုလုံးဝင်ခွင့်',
    'roleSimulator.admin': 'အသိုင်းအဝိုင်းစီမံခန့်ခွဲသူ',
    'roleSimulator.adminDesc': 'အသိုင်းအဝိုင်းအိမ်ခြံနှင့်အသုံးပြုသူများစီမံခန့်ခွဲပါ',
    'roleSimulator.homeowner': 'အိမ်ပိုင်ရှင်',
    'roleSimulator.homeownerDesc': 'အိမ်ခြံပိုင်ရှင်အတွေ့အကြုံ',
    'roleSimulator.guest': 'ဧည့်သည်',
    'roleSimulator.guestDesc': 'အချိန်ကန့်သတ်ဧည့်သည်ဝင်ခွင့်',
    'roleSimulator.active': 'အသုံးပြုနေ',
    'roleSimulator.exitSimulation': 'ပုံစံချမှုမှထွက်ပါ',
    
    // Simulation Banner
    'simulation.simulating': 'ပုံစံချနေသည်',
    'simulation.exit': 'ထွက်ပါ',
    
    // Cookie Consent
    'cookies.message': 'သင့်အတွေ့အကြုံကို မြှင့်တင်ရန် cookies အသုံးပြုပါသည်။',
    'cookies.learnMore': 'ကျွန်ုပ်တို့၏ ကိုယ်ရေးလုံခြုံမှု မူဝါဒကို ဖတ်ရှုပါ',
    'cookies.accept': 'လက်ခံပါ',
    'cookies.dismiss': 'ပယ်ပါ',
    'common.close': 'ပိတ်ပါ',
    
    // Notification Center
    'notificationCenter.new': 'အသစ်',
    'notificationCenter.noNotifications': 'အကြောင်းကြားချက်မရှိသေးပါ',
    'notificationCenter.noNotificationsDesc': 'ငွေပေးချေမှုသတိပေးချက်များနှင့် အပ်ဒိတ်များ ဤနေရာတွင် မြင်ရပါမည်',
    
    // Toast Messages
    'toast.loadFailed': 'ဒေတာဖတ်ရန်မအောင်မြင်ပါ',
    'toast.emailRequired': 'အီးမေးလ်လိုအပ်သည်',
    'toast.inviteSent': 'ဖိတ်ကြားစာပို့လိုက်ပါပြီ',
    'toast.inviteFailed': 'ဖိတ်ကြားစာပို့ရန်မအောင်မြင်ပါ',
    'toast.addressRequired': 'လိပ်စာလိုအပ်သည်',
    'toast.propertyAdded': 'အိမ်ခြံထည့်လိုက်ပါပြီ',
    'toast.propertyAddFailed': 'အိမ်ခြံထည့်ရန်မအောင်မြင်ပါ',
    'toast.rentalModeEnabled': 'ငှားရမ်းမုဒ်ဖွင့်ပါပြီ',
    'toast.rentalModeDisabled': 'ငှားရမ်းမုဒ်ပိတ်ပါပြီ',
    'toast.rentalModeUpdateFailed': 'ငှားရမ်းမုဒ်အပ်ဒိတ်မအောင်မြင်ပါ',
    'toast.rentalListingSaved': 'ငှားရမ်းစာရင်းသိမ်းဆည်းပါပြီ',
    'toast.rentalListingSaveFailed': 'ငှားရမ်းစာရင်းသိမ်းဆည်းမအောင်မြင်ပါ',
    'toast.communityNameRequired': 'အသိုင်းအဝိုင်းအမည်လိုအပ်သည်',
    'toast.communityCreated': 'အသိုင်းအဝိုင်းဖန်တီးပါပြီ',
    'toast.communityCreateFailed': 'အသိုင်းအဝိုင်းဖန်တီးရန်မအောင်မြင်ပါ',
    'toast.adminInviteSent': 'စီမံခန့်ခွဲသူဖိတ်ကြားစာပို့ပါပြီ',
    'toast.inviteDeleted': 'ဖိတ်ကြားစာဖျက်ပါပြီ',
    'toast.deleteFailed': 'ဖျက်ရန်မအောင်မြင်ပါ',
    'toast.listingSaved': 'စာရင်းသိမ်းပါပြီ',
    'toast.listingSaveFailed': 'စာရင်းသိမ်းရန်မအောင်မြင်ပါ',
    'toast.calendarUpdated': 'ပြက္ခဒိန်အပ်ဒိတ်ပါပြီ',
    'toast.calendarUpdateFailed': 'ပြက္ခဒိန်အပ်ဒိတ်မအောင်မြင်ပါ',
    'toast.ownerInviteSent': 'ပိုင်ရှင်ဖိတ်ကြားစာ အောင်မြင်စွာ ပို့ပြီးပါပြီ',
    'toast.updateFailed': 'အပ်ဒိတ်မအောင်မြင်ပါ',
    'common.error': 'အမှား',
    'common.unknown': 'မသိ',
    
    // Smart Home (Phase 5)
    'smartHome.title': 'စမတ်အိမ် အသုံးပြုခွင့်',
    'smartHome.shareTitle': 'ဧည့်သည်များနှင့် စမတ်အိမ် မျှဝေပါ',
    'smartHome.shareDesc': 'Google Home သုံး၍ ဧည့်သည်များအား စမတ်ပစ္စည်းများ အသုံးပြုခွင့်ပေးပါ',
    'smartHome.quickSteps': 'မြန်ဆန်သော အဆင့်များ',
    'smartHome.step1': 'Google Home အက်ပ် ဖွင့်ပါ',
    'smartHome.step2': 'ဆက်တင် → အိမ်ထောင်စု သို့သွားပါ',
    'smartHome.step3': 'ဧည့်သည်အား အီးမေးလ်ဖြင့် ဖိတ်ခေါ်ပါ',
    'smartHome.remember': 'သတိရပါ',
    'smartHome.reminderText': 'ဝင်ရောက်မှုမတိုင်မီ ဧည့်သည်အသုံးပြုခွင့် ထည့်၍ ထွက်ခွာပြီးနောက် ဖယ်ရှားပါ',
    'smartHome.viewGuide': 'လမ်းညွှန်အပြည့် ကြည့်ပါ',
    'smartHome.worksWith': 'အလုပ်လုပ်သည်:',
    'smartHome.devicesList': 'Nest, Ring, Yale, August, Philips Hue နှင့် 50,000+ Google Home သဟဇာတပစ္စည်းများ',
    
    // Rental Widget (Phase 5)
    'rental.status': 'ငှားရမ်းအခြေအနေ',
    'rental.bookings': 'ကြိုတင်မှာယူမှုများ',
    'rental.pending': 'စောင့်ဆိုင်းဆဲ',
    'rental.revenue': 'ဝင်ငွေ',
    'rental.rating': 'အဆင့်သတ်မှတ်',
    'rental.view': 'ကြည့်ရန်',
    'rental.edit': 'ပြင်ရန်',
    'rental.calendar': 'ပြက္ခဒိန်',
    'rental.completeListing': 'ငွေရှာရန် သင်၏စာရင်းကို ပြည့်စုံအောင်လုပ်ပါ',
    'rental.enableMode': 'သင်၏အိမ်ခြံကို စာရင်းတင်၍ ငွေရှာရန် ငှားရမ်းမုဒ်ဖွင့်ပါ',
    'rental.enableButton': 'ငှားရမ်းမုဒ် ဖွင့်ပါ',
    'rental.modeEnabled': 'ငှားရမ်းမုဒ် ဖွင့်ပြီး',
    'rental.modeDisabled': 'ငှားရမ်းမုဒ် ပိတ်ပြီး',
    'rental.updateFailed': 'ငှားရမ်းမုဒ် အပ်ဒိတ်မအောင်မြင်ပါ',
    
    // Homeowner Dashboard Widget (Phase 5)
    'homeowner.upcomingHOA': 'လာမည့် HOA ကြေး',
    'homeowner.payNow': 'ယခုပေးချေရန်',
    'homeowner.noHOADues': 'HOA ကြေး မသတ်မှတ်ရသေးပါ',
    'homeowner.dueToday': 'ယနေ့ပေးရမည်',
    'homeowner.daysLeft': '{days} ရက်',
    'homeowner.communityUpdates': 'အသိုင်းအဝိုင်း အပ်ဒိတ်များ',
    'homeowner.new': 'အသစ်',
    'homeowner.noAnnouncements': 'မကြာသေးမီ ကြေငြာချက်မရှိပါ',
    
    // Transactions
    'transaction.pending': 'စောင့်ဆိုင်းဆဲ',
    'transaction.processing': 'လုပ်ဆောင်နေသည်',
    'transaction.completed': 'ပြီးစီးပြီ',
    'transaction.failed': 'မအောင်မြင်ပါ',
    'transaction.refunded': 'ပြန်အမ်းပြီး',
    'transaction.type.hoa_dues': 'HOA ကြေး',
    'transaction.type.service_payment': 'ဝန်ဆောင်မှုငွေပေးချေမှု',
    'transaction.type.refund': 'ပြန်အမ်းငွေ',
    'transaction.type.deposit': 'စပ်ငွေ',
    
    // HOA Payments
    'hoa.monthlyDues': 'လစဉ် HOA ကြေး',
    'hoa.dueOnDay': 'လစဉ် {day} ရက်နေ့ ပေးရမည်',
    'hoa.perMonth': '/လ',
    'hoa.nextPayment': 'နောက်ငွေပေးချေမှု',
    'hoa.payNow': 'ယခုပေးချေရန်',
    'hoa.allCaughtUp': 'အားလုံးပြည့်စုံပြီ!',
    'hoa.notConfigured': 'ဤအိမ်ခြံအတွက် HOA ကြေးမသတ်မှတ်ရသေးပါ',
    'hoa.paymentHistory': 'ငွေပေးချေမှုမှတ်တမ်း',
    'hoa.noProperties': 'အိမ်ခြံမတွေ့ပါ',
    'hoa.noPropertiesDesc': 'HOA ကြေးပေးချေရန် အိမ်ခြံပိုင်ဆိုင်ရန်လိုအပ်သည်',
    'hoa.choosePaymentMethod': 'ငွေပေးချေနည်းရွေးချယ်ပါ',
    'hoa.paid': 'ပေးချေပြီး',
    'hoa.verifying': 'အတည်ပြုနေသည်',
    
    // Concierge Page
    'concierge.title': 'ဧည့်ခံဝန်ဆောင်မှု',
    'concierge.subtitle': 'သင့်အမိန့်ဖြင့် အထူးဝန်ဆောင်မှုများ',
    'concierge.askAether': 'Aether ကိုမေးပါ',
    'concierge.services': 'ဝန်ဆောင်မှုများ',
    'concierge.myBookings': 'ကျွန်ုပ်၏ကြိုတင်မှာယူမှုများ',
    'concierge.serviceCategories': 'ဝန်ဆောင်မှုအမျိုးအစားများ',
    'concierge.yourBookings': 'သင့်ကြိုတင်မှာယူမှုများ',
    'concierge.viewAllBookings': 'ကြိုတင်မှာယူမှုအားလုံးကြည့်ရန်',
    'concierge.manageBookings': 'သင့်လာမည့်ဝန်ဆောင်မှုကြိုတင်မှာယူမှုများကိုကြည့်ရှုစီမံခန့်ခွဲပါ',
    'concierge.notAvailable': 'ဧည့်ခံဝန်ဆောင်မှုမရရှိနိုင်ပါ',
    'concierge.notAvailableDesc': 'သင့်ရပ်ကွက်အတွက်ဧည့်ခံဝန်ဆောင်မှုများကိုဖွင့်မထားပါ။ အသေးစိတ်အချက်အလက်များအတွက် စီမံခန့်ခွဲသူကို ဆက်သွယ်ပါ။',
    'concierge.noCommunity': 'ရပ်ကွက်ဝင်ရောက်ခွင့်မရှိပါ',
    'concierge.noCommunityDesc': 'ရပ်ကွက်တစ်ခုနှင့်ချိတ်ဆက်ပြီးနောက်ဧည့်ခံဝန်ဆောင်မှုများရရှိမည်။',
    'concierge.createCommunity': 'သင့်ပထမဆုံးရပ်ကွက်ဖန်တီးရန်',
    'concierge.noServices': 'ဝန်ဆောင်မှုများမကြာမီလာမည်',
    'concierge.noServicesDesc': 'သင့်ရပ်ကွက်စီမံခန့်ခွဲသူကဧည့်ခံဝန်ဆောင်မှုများကိုစီစဉ်နေသည်။ မကြာမီပြန်လာကြည့်ပါ!',
    
    // Service Categories
    'service.housekeeping': 'အိမ်သန့်ရှင်းရေး',
    'service.housekeepingDesc': 'အရည်အသွေးမြင့်သန့်ရှင်းရေးနှင့်အဝတ်လျှော်ခြင်း',
    'service.wellness': 'ကျန်းမာရေး',
    'service.wellnessDesc': 'စပါ၊ ကျန်းမာရေးနှင့်ပြန်လည်နုပျိုရေး',
    'service.maintenance': 'ပြုပြင်ထိန်းသိမ်းရေး',
    'service.maintenanceDesc': 'အိမ်ခြံစောင့်ရှောက်ရေးနှင့်ပြုပြင်ရေး',
    'service.dining': 'သီးသန့်စားသောက်ခြင်း',
    'service.diningDesc': 'ရွေးချယ်ထားသော အစားအစာအတွေ့အကြုံများ',
    'service.transport': 'သယ်ယူပို့ဆောင်ရေး',
    'service.transportDesc': 'ဇိမ်ခံယာဉ်ဝန်ဆောင်မှုများ',
    
    // Service Marketplace
    'marketplace.title': 'ဝန်ဆောင်မှုများ',
    'marketplace.subtitle': 'အရည်အသွေးမြင့်ဝန်ဆောင်မှုများကြိုတင်မှာယူပါ',
    'marketplace.all': 'အားလုံး',
    'marketplace.noServices': 'ဝန်ဆောင်မှုများမရှိပါ',
    'marketplace.checkBack': 'နောက်မှပြန်လာကြည့်ပါ',
    'marketplace.book': 'မှာယူရန်',
    'marketplace.estimatedPrice': 'ခန့်မှန်းစျေးနှုန်း',
    'marketplace.priceVaries': '*နောက်ဆုံးစျေးနှုန်းသည်အိမ်ခြံအရွယ်အစားပေါ်မူတည်၍ကွဲပြားနိုင်သည်',
    'marketplace.scheduleDateTime': 'ရက်စွဲနှင့်အချိန်စီစဉ်ပါ',
    'marketplace.specialRequests': 'အထူးတောင်းဆိုချက်များ (ရွေးချယ်နိုင်သည်)',
    'marketplace.specialRequestsPlaceholder': 'အထူးညွှန်ကြားချက်များ...',
    'marketplace.payWithCard': 'ကတ်ဖြင့်ပေးချေရန် (USD)',
    
    // Sovereign Dashboard
    'sovereign.propertyManager': 'အိမ်ခြံစီမံခန့်ခွဲသူ',
    'sovereign.properties': 'အိမ်ခြံများ',
    'sovereign.selectProperty': 'အိမ်ခြံရွေးချယ်ပါ',
    'sovereign.noPropertyTitle': 'အိမ်ခြံမတွေ့ပါ',
    'sovereign.noPropertyDesc': 'သင့်အကောင့်တွင်အိမ်ခြံများသတ်မှတ်ထားခြင်းမရှိပါ။',
    'sovereign.goToDashboard': 'ဒက်ရှ်ဘုတ်သို့သွားရန်',
    'sovereign.standaloneProperty': 'သီးသန့်အိမ်ခြံ',
    'sovereign.community': 'အသိုင်းအဝိုင်း',
    'sovereign.rentalMode': 'ငှားရမ်းမုဒ်',
    'sovereign.rentalModeDesc': 'ရေတိုငှားရန်အိမ်ခြံစာရင်းသွင်းပါ',
    'sovereign.totalBookings': 'စုစုပေါင်းကြိုတင်မှာယူမှုများ',
    'sovereign.pending': 'စောင့်ဆိုင်းဆဲ',
    'sovereign.revenue': 'ဝင်ငွေ',
    'sovereign.rating': 'အဆင့်သတ်မှတ်ချက်',
    'sovereign.bookings': 'ကြိုတင်မှာယူမှုများ',
    'sovereign.calendar': 'ပြက္ခဒိန်',
    'sovereign.listing': 'စာရင်း',
    'sovereign.reviews': 'သုံးသပ်ချက်များ',
    'sovereign.smartHome': 'စမတ်အိမ်',
    'sovereign.guestReviews': 'ဧည့်သည်သုံးသပ်ချက်များ',
    'sovereign.enableRentalMode': 'ငှားရမ်းမုဒ်ဖွင့်ရန်',
    'sovereign.enableRentalModeDesc': 'ရေတိုငှားရမ်းရန်စျေးကွက်တွင်သင့်အိမ်ခြံကိုစာရင်းသွင်းပါ။သင့်ကိုယ်ပိုင်စျေးနှုန်းများသတ်မှတ်ပါ၊ရရှိနိုင်မှုကိုစီမံခန့်ခွဲပါ၊သင့်အိမ်ခြံမှဝင်ငွေရရှိပါ။',
    'sovereign.setRates': 'ညစဉ်စျေးနှုန်းနှင့်သန့်ရှင်းရေးကြေးများသတ်မှတ်ပါ',
    'sovereign.controlCalendar': 'သင့်ရရှိနိုင်မှုပြက္ခဒိန်ကိုထိန်းချုပ်ပါ',
    'sovereign.manageGuests': 'ဧည့်သည်များကိုအတည်ပြုပြီးကြိုတင်မှာယူမှုများကိုစီမံခန့်ခွဲပါ',
    
    // Admin Dashboard
    'admin.title': 'ဒက်ရှ်ဘုတ်',
    'admin.role': 'အက်ဒမင်',
    'admin.godMode': 'ဘုရားမုဒ်',
    'admin.properties': 'အိမ်ခြံများ',
    'admin.owners': 'ပိုင်ရှင်များ',
    'admin.collected': 'ကောက်ခံပြီး',
    'admin.overview': 'ခြုံငုံသုံးသပ်ချက်',
    'admin.forSale': 'ရောင်းရန်',
    'admin.announcements': 'သတင်း',
    'admin.bookings': 'ကြိုတင်မှာယူမှုများ',
    'admin.services': 'ဝန်ဆောင်မှုများ',
    'admin.vendors': 'ရောင်းချသူများ',
    'admin.earnings': 'ဝင်ငွေများ',
    'admin.reviews': 'သုံးသပ်ချက်များ',
    'admin.more': 'နောက်ထပ်',
    'admin.hoaStatus': 'HOA ကောက်ခံမှုအခြေအနေ',
    'admin.paid': 'ပေးချေပြီး',
    'admin.pending': 'စောင့်ဆိုင်းဆဲ',
    'admin.overdue': 'သတ်မှတ်ချိန်လွန်',
    'admin.invite': 'ဖိတ်ကြားရန်',
    'admin.inviteHomeowner': 'အိမ်ပိုင်ရှင်ဖိတ်ကြားရန်',
    'admin.addProperty': 'အိမ်ခြံထည့်ရန်',
    'admin.sendInvitation': 'ဖိတ်ကြားစာပို့ရန်',
    'admin.noProperties': 'အိမ်ခြံများမရှိသေးပါ',
    'admin.addFirstProperty': 'စတင်ရန်သင့်ပထမဆုံးအိမ်ခြံထည့်ပါ',
    'admin.moreProperties': 'အိမ်ခြံများပိုမို',
    'admin.unassigned': 'မသတ်မှတ်ရသေး',
    'admin.rental': 'ငှားရမ်းရန်',
    'admin.rentalMode': 'ငှားရမ်းမုဒ်',
    'admin.manageListing': 'စီမံခန့်ခွဲရန်',
    'admin.rentalListingTitle': 'ငှားရမ်းစာရင်းစီမံခန့်ခွဲရန်',
    'admin.streetAddress': 'လမ်းလိပ်စာ',
    'admin.unitNumber': 'ယူနစ်နံပါတ် (ရွေးချယ်နိုင်)',
    'admin.bedrooms': 'အိပ်ခန်းများ',
    'admin.bathrooms': 'ရေချိုးခန်းများ',
    'admin.manageAnnouncements': 'ကြေညာချက်များစီမံခန့်ခွဲရန်',
    'admin.dragToReschedule': 'ပြန်စီစဉ်ရန်ဆွဲပါ',
    'admin.allReservations': 'ကြိုတင်မှာယူမှုအားလုံး',
    'admin.createCommunity': 'အသိုင်းအဝိုင်းအသစ်ဖန်တီးရန်',
    'admin.createCommunityDesc': 'အိမ်ခြံများနှင့်အိမ်ပိုင်ရှင်များကိုစီမံခန့်ခွဲရန်အသိုင်းအဝိုင်းအသစ်တည်ဆောက်ပါ',
    'admin.communityMap': 'အသိုင်းအဝိုင်းမြေပုံ',
    'admin.welcome': 'ကြိုဆိုပါသည်',
    'admin.createFirstCommunity': 'သင့်ပထမဆုံးအသိုင်းအဝိုင်းကိုဖန်တီးပါ',
    'admin.createFirstCommunityDesc': 'အိမ်ခြံများနှင့်အိမ်ပိုင်ရှင်များကိုစီမံခန့်ခွဲရန်အသိုင်းအဝိုင်းတစ်ခုဖန်တီးခြင်းဖြင့်စတင်ပါ။',
    
    // Browse Rentals
    'browse.title': 'ငှားရမ်းရန်ရှာဖွေရန်',
    'browse.luxuryStays': 'ဇိမ်ခံတည်းခို',
    'browse.heroTitle': 'အထူးကောင်းမွန်သောအိမ်ခြံများကိုရှာဖွေပါ',
    'browse.heroSubtitle': 'ကျွန်ုပ်တို့၏သီးသန့်အသိုင်းအဝိုင်းများတွင် ကမ္ဘာ့အဆင့်မီအဆောက်အအုံများနှင့် ကိုယ်ပိုင်ဧည့်ခံဝန်ဆောင်မှုများကို ခံစားပါ',
    'browse.searchPlaceholder': 'တည်နေရာ သို့မဟုတ် အသိုင်းအဝိုင်းဖြင့်ရှာဖွေပါ...',
    'browse.filters': 'စစ်ထုတ်မှုများ',
    'browse.priceRange': 'စျေးနှုန်းအပိုင်းအခြား (တစ်ညလျှင်)',
    'browse.minBedrooms': 'အနည်းဆုံးအိပ်ခန်း',
    'browse.amenities': 'အဆောက်အအုံများ',
    'browse.any': 'မည်သည်မဆို',
    'browse.clearFilters': 'စစ်ထုတ်မှုအားလုံးရှင်းပါ',
    'browse.propertiesFound': 'အိမ်ခြံ {count} ခုတွေ့ရှိ',
    'browse.propertyFound': 'အိမ်ခြံ ၁ ခုတွေ့ရှိ',
    'browse.noReviews': 'သုံးသပ်ချက်များမရှိသေးပါ',
    'browse.luxuryProperty': 'ဇိမ်ခံအိမ်ခြံ',
    'browse.privateCommunity': 'ပုဂ္ဂလိကအသိုင်းအဝိုင်း',
    'browse.beds': 'အိပ်ခန်း',
    'browse.baths': 'ရေချိုးခန်း',
    'browse.moreAmenities': '+{count} နောက်ထပ်',
    'browse.additionalAmenities': 'နောက်ထပ်အဆောက်အအုံများ:',
    'browse.viewDetails': 'အသေးစိတ်ကြည့်ရန်',
    'browse.bookNow': 'ယခုဘွတ်ကင်လုပ်ရန်',
    'browse.bookYourStay': 'သင့်တည်းခိုမှုကိုဘွတ်ကင်လုပ်ပါ',
    'browse.booking': 'ဘွတ်ကင်',
    'browse.reviews': 'သုံးသပ်ချက်များ',
    'browse.addressRevealed': 'ဘွတ်ကင်အတည်ပြုပြီးနောက် အပြည့်အစုံလိပ်စာပေါ်လာပါမည်',
    'browse.checkIn': 'ဝင်ရောက်ရန်',
    'browse.checkOut': 'ထွက်ခွာရန်',
    'browse.selectDate': 'ရက်စွဲရွေးပါ',
    'browse.guests': 'ဧည့်သည်များ',
    'browse.nights': 'ည',
    'browse.cleaningFee': 'သန့်ရှင်းရေးကြေး',
    'browse.total': 'စုစုပေါင်း',
    'browse.confirmPay': 'အတည်ပြုပြီးပေးချေပါ',
    'browse.signInToBook': 'ဘွတ်ကင်လုပ်ရန်ဝင်ရောက်ပါ',
    'browse.processing': 'လုပ်ဆောင်နေပါသည်...',
    'browse.minNights': 'အနည်းဆုံး {min} ည',
    'browse.maxNights': 'အများဆုံး {max} ည',
    'browse.noPropertiesMatch': 'စစ်ထုတ်မှုနှင့်ကိုက်ညီသောအိမ်ခြံမရှိပါ',
    'browse.tryAdjusting': 'ရှာဖွေမှုစံနှုန်းကိုပြင်ဆင်ကြည့်ပါ',
    
    // Listing Detail Page (new)
    'listing.notFound': 'စာရင်းမတွေ့ပါ',
    'listing.notFoundDesc': 'ဤအိမ်ခြံသည်မရှိတော့နိုင်ပါ',
    'listing.browseRentals': 'ငှားရမ်းရန်ရှာဖွေပါ',
    'listing.viewAllPhotos': 'ဓာတ်ပုံ {count} ခုအားလုံးကြည့်ရန်',
    'listing.allPhotos': 'ဓာတ်ပုံအားလုံး',
    'listing.about': 'ဤအိမ်ခြံအကြောင်း',
    'listing.aboutProperty': 'ဤအိမ်ခြံအကြောင်း',
    'listing.amenities': 'အဆောက်အအုံများ',
    'listing.houseRules': 'အိမ်စည်းကမ်းများ',
    'listing.sqft': 'စတုရန်းပေ',
    'listing.night': 'ည',
    'listing.image': 'ဓာတ်ပုံ',
    'listing.payWithCard': 'ကတ်ဖြင့်ပေးချေရန်',
    'listing.payWithKBZ': 'KBZPayဖြင့်ပေးချေရန်',
    'listing.payWithWave': 'Wave Moneyဖြင့်ပေးချေရန်',
    'listing.signInRequired': 'ဤအိမ်ခြံကိုဘွတ်ကင်လုပ်ရန်ဝင်ရောက်ပါ',
    'listing.pleaseSignIn': 'ဤအိမ်ခြံကိုဘွတ်ကင်လုပ်ရန်ဝင်ရောက်ပါ',
    'listing.selectDates': 'ဝင်ရောက်ရန်နှင့်ထွက်ခွာရန်ရက်စွဲများရွေးချယ်ပါ',
    'listing.minStay': 'အနည်းဆုံး {min} ညတည်းခိုရပါမည်',
    'listing.maxStay': 'အများဆုံး {max} ညတည်းခိုနိုင်သည်',
    
    // Booking Confirmation Page (new)
    'confirmation.invalid': 'မမှန်ကန်သောဘွတ်ကင်',
    'confirmation.invalidDesc': 'ဘွတ်ကင်IDမပေးထားပါ',
    'confirmation.browseProperties': 'အိမ်ခြံများရှာဖွေရန်',
    'confirmation.notFound': 'ဘွတ်ကင်မတွေ့ပါ',
    'confirmation.notFoundDesc': 'ဤဘွတ်ကင်ကိုရှာမတွေ့ပါ',
    'confirmation.confirmed': 'ဘွတ်ကင်အတည်ပြုပြီး!',
    'confirmation.pending': 'ဘွတ်ကင်စောင့်ဆိုင်းဆဲ',
    'confirmation.accessPass': 'အိမ်ခြံဝင်ရောက်ခွင့်ပါစ်',
    'confirmation.propertyDetails': 'အိမ်ခြံအသေးစိတ်',
    'confirmation.stayDetails': 'တည်းခိုမှုအသေးစိတ်',
    'confirmation.paymentSummary': 'ငွေပေးချေမှုအကျဉ်းချုပ်',
    'confirmation.leaveReview': 'သုံးသပ်ချက်ရေးပါ',
    'confirmation.viewDirections': 'အိမ်ခြံလမ်းညွှန်နှင့်မြေပုံကြည့်ရန်',
    'confirmation.messageOwner': 'အိမ်ပိုင်ရှင်ထံမက်ဆေ့ချ်ပို့ရန်',
    'confirmation.viewAllBookings': 'ဘွတ်ကင်အားလုံးကြည့်ရန်',
    'confirmation.goToDashboard': 'ဒက်ရှ်ဘုတ်သို့သွားရန်',
    'confirmation.checkIn': 'ဝင်ရောက်ရန်',
    'confirmation.checkOut': 'ထွက်ခွာရန်',
    'confirmation.guests': 'ဧည့်သည်များ',
    'confirmation.nights': 'ည',
    'confirmation.cleaningFee': 'သန့်ရှင်းရေးကြေး',
    'confirmation.total': 'စုစုပေါင်း',
    'confirmation.paymentMethod': 'ငွေပေးချေနည်း',
    'confirmation.verificationPending': 'ငွေပေးချေမှုအတည်ပြုမှုစောင့်ဆိုင်းဆဲ',
    
    // Wallet Page (new)
    'wallet.title': 'ပိုက်ဆံအိတ်',
    'wallet.subtitle': 'ငွေပေးချေမှုများနှင့်ငွေလွှဲမှုများ',
    'wallet.paymentMethods': 'ငွေပေးချေနည်းများ',
    'wallet.hoaDues': 'HOAကြေး',
    'wallet.serviceHistory': 'ဝန်ဆောင်မှုမှတ်တမ်း',
    'wallet.viewAll': 'အားလုံးကြည့်ရန်',
    'wallet.noTransactions': 'ငွေလွှဲမှုများမရှိသေးပါ',
    'wallet.noTransactionsDesc': 'ဝန်ဆောင်မှုမှာယူပြီးနောက်ဤနေရာတွင်ပြမည်',
    'wallet.paymentSuccess': 'ငွေပေးချေမှုအောင်မြင်',
    'wallet.paymentSuccessDesc': 'သင့်ငွေပေးချေမှုကိုလုပ်ဆောင်ပြီးပါပြီ။',
    'wallet.paymentCanceled': 'ငွေပေးချေမှုပယ်ဖျက်',
    'wallet.paymentCanceledDesc': 'သင့်ငွေပေးချေမှုကိုပယ်ဖျက်ပြီးပါပြီ။',
    'wallet.cardAdded': 'ကတ်ထည့်ပြီး',
    'wallet.cardAddedDesc': 'သင့်ငွေပေးချေနည်းကိုသိမ်းဆည်းပြီးပါပြီ။',
    'wallet.noPaymentMethods': 'သိမ်းဆည်းထားသောငွေပေးချေနည်းများမရှိပါ',
    'wallet.addCardDesc': 'ဝန်ဆောင်မှုများနှင့်ကြေးများပေးချေရန်ကတ်ထည့်ပါ',
    'wallet.addCard': 'ကတ်ထည့်ရန်',
    'wallet.continueAddCard': 'ကတ်ထည့်ရန်ဆက်လက်လုပ်ဆောင်ပါ',
    'wallet.addPaymentMethod': 'ငွေပေးချေနည်းထည့်ရန်',
    'wallet.default': 'ပုံသေ',
    'wallet.manage': 'စီမံရန်',
    'wallet.securePayment': 'လုံခြုံသောငွေပေးချေမှု',
    'wallet.cardSecure': 'သင့်ကတ်အချက်အလက်များကိုကုဒ်ဝှက်ထားပြီးလုံခြုံပါသည်',
    'wallet.stripeRedirect': 'သင့်ကတ်ထည့်ရန်Stripeလုံခြုံသောငွေပေးချေစာမျက်နှာသို့ပြန်လည်လမ်းညွှန်ပါမည်။',
    'wallet.openingStripe': 'သင့်ကတ်ထည့်ရန်Stripeကိုဖွင့်နေသည်...',
    'wallet.failedAddMethod': 'ငွေပေးချေနည်းထည့်ရန်မအောင်မြင်ပါ',
    'wallet.failedOpenPortal': 'ငွေတောင်းခံလွှာပို့တယ်ကိုဖွင့်ရန်မအောင်မြင်ပါ',
    'wallet.expires': 'သက်တမ်းကုန်',
    
    // Payments Page (Burmese)
    'payments.title': 'ငွေပေးချေမှုများ',
    'payments.subtitle': 'ငွေပေးချေမှုများနှင့်ငွေလွှဲမှုများကိုစီမံရန်',
    'payments.noCommunity': 'ရပ်ကွက်ဝင်ရောက်ခွင့်မရှိပါ',
    'payments.noCommunityDesc': 'ရပ်ကွက်တစ်ခုနှင့်ချိတ်ဆက်ပြီးနောက်ငွေပေးချေမှုအင်္ဂါရပ်များရရှိမည်။',
    'payments.createCommunity': 'သင့်ပထမဆုံးရပ်ကွက်ဖန်တီးရန်',
    
    // Amenity translations (Burmese)
    'amenity.privatePool': 'ကိုယ်ပိုင်ရေကူးကန်',
    'amenity.pool': 'ရေကူးကန်',
    'amenity.hotTub': 'ရေပူကန်',
    'amenity.oceanView': 'ပင်လယ်ရှုခင်း',
    'amenity.sunsetViews': 'နေဝင်ရှုခင်း',
    'amenity.beachAccess': 'ကမ်းခြေဝင်ရောက်ခွင့်',
    'amenity.smartHome': 'စမတ်အိမ်',
    'amenity.wineCellar': 'ဝိုင်သိုလှောင်ခန်း',
    'amenity.homeTheater': 'အိမ်ရုပ်ရှင်ရုံ',
    'amenity.gym': 'ကျန်းမာရေးခန်း',
    'amenity.spa': 'စပါ',
    'amenity.rooftopTerrace': 'အမိုးပေါ်ထရပ်စ်',
    'amenity.concierge': 'ကွန်စီယာ့ဂျ်',
    'amenity.wifi': 'ဝိုင်ဖိုင်',
    'amenity.parking': 'ကားပါကင်',
    'amenity.airConditioning': 'လေအေး',
    'amenity.heating': 'အပူပေးစနစ်',
    'amenity.kitchen': 'မီးဖိုချောင်',
    'amenity.washer': 'အဝတ်လျှော်စက်',
    'amenity.dryer': 'အခြောက်ခံစက်',
    'amenity.washerDryer': 'အဝတ်လျှော်/အခြောက်ခံစက်',
    'amenity.petFriendly': 'အိမ်မွေးတိရစ္ဆာန်ခွင့်ပြု',
    'amenity.garden': 'ဥယျာဉ်',
    'amenity.fireplace': 'မီးလင်းဖို',
    'amenity.balcony': 'ဝရံတာ',
    
    // Guest Pass (new)
    'guestPass.title': 'ဧည့်သည်ပါစ်များ',
    'guestPass.subtitle': 'ယာယီဧည့်သည်ဝင်ရောက်ခွင့်စီမံခန့်ခွဲရန်',
    'guestPass.header': 'ဧည့်သည်ပါစ်များ',
    'guestPass.newPass': 'ပါစ်အသစ်',
    'guestPass.createPass': 'ဧည့်သည်ပါစ်ဖန်တီးရန်',
    'guestPass.property': 'အိမ်ခြံ',
    'guestPass.selectProperty': 'အိမ်ခြံရွေးချယ်ပါ',
    'guestPass.guestEmail': 'ဧည့်သည်အီးမေးလ်',
    'guestPass.guestName': 'ဧည့်သည်အမည် (ရွေးချယ်နိုင်)',
    'guestPass.accessStart': 'ဝင်ရောက်ခွင့်စတင်',
    'guestPass.accessEnd': 'ဝင်ရောက်ခွင့်ကုန်ဆုံး',
    'guestPass.selectStartDate': 'စတင်ရက်ရွေးပါ',
    'guestPass.selectEndDate': 'ကုန်ဆုံးရက်ရွေးပါ',
    'guestPass.duration': 'ကာလ: {days} ရက်',
    'guestPass.sendPass': 'ဧည့်သည်ပါစ်ပို့ရန်',
    'guestPass.sending': 'ပို့နေသည်...',
    'guestPass.noProperties': 'ပါစ်ဖန်တီး၍မရပါ',
    'guestPass.noPropertiesDesc': 'ဧည့်သည်ပါစ်ဖန်တီးရန်သင်အိမ်ခြံပိုင်ဆိုင်ရန်လိုအပ်သည်။',
    'guestPass.activePasses': 'လက်ရှိပါစ်များ',
    'guestPass.expiredPasses': 'သက်တမ်းကုန်ပါစ်များ',
    'guestPass.noActivePasses': 'လက်ရှိဧည့်သည်ပါစ်များမရှိပါ',
    'guestPass.noExpiredPasses': 'သက်တမ်းကုန်ပါစ်များမရှိပါ',
    'guestPass.guest': 'ဧည့်သည်',
    'guestPass.expires': 'ကုန်ဆုံးရက်',
    'guestPass.expired': 'ကုန်ဆုံးပြီး',
    'guestPass.showQR': 'QRပြရန်',
    'guestPass.revoke': 'ရုပ်သိမ်းရန်',
    'guestPass.revokeConfirm': 'ဤဧည့်သည်ပါစ်ကိုရုပ်သိမ်းလိုသည်မှာသေချာပါသလား?ဤလုပ်ဆောင်ချက်ကိုပြန်လည်ပြုလုပ်၍မရပါ။',
    
    // Reviews (new)
    'reviews.title': 'သုံးသပ်ချက်များ',
    'reviews.noReviews': 'သုံးသပ်ချက်များမရှိသေးပါ',
    'reviews.noReviewsDesc': 'ဤအိမ်ခြံအတွက်သုံးသပ်ချက်များမရှိသေးပါ',
    'reviews.overallRating': 'အလုံးစုံအဆင့်သတ်မှတ်ချက်',
    'reviews.cleanliness': 'သန့်ရှင်းမှု',
    'reviews.accuracy': 'တိကျမှု',
    'reviews.communication': 'ဆက်သွယ်ရေး',
    'reviews.location': 'တည်နေရာ',
    'reviews.value': 'တန်ဖိုး',
    'reviews.ownerResponse': 'ပိုင်ရှင်တုံ့ပြန်ချက်',
    'reviews.basedOn': 'သုံးသပ်ချက် {count} ခုအပေါ်အခြေခံ',
    
    // Chat (new)
    'chat.title': 'Aether AI အကူအညီ',
    'chat.placeholder': 'မက်ဆေ့ချ်ရိုက်ပါ သို့မဟုတ် "မင်္ဂလာပါ"ပြောပါ...',
    'chat.send': 'ပို့ရန်',
    'chat.voiceInput': 'အသံထည့်သွင်းမှု',
    'chat.listening': 'နားထောင်နေသည်...',
    'chat.clearHistory': 'မှတ်တမ်းရှင်းလင်းရန်',
    'chat.suggestedPrompts': 'အကြံပြုချက်များ',
    'chat.findRental': 'ငါ့အတွက်သင့်တော်တဲ့ငှားရမ်းရန်ရှာပေးပါ',
    'chat.bookService': 'ဝန်ဆောင်မှုတစ်ခုမှာယူချင်တယ်',
    'chat.checkPayment': 'ငါ့ငွေပေးချေမှုအခြေအနေစစ်ဆေးပါ',
    'chat.getDirections': 'ငါ့အိမ်ခြံသို့လမ်းညွှန်ရယူပါ',
    
    // Auth Extended (new)
    'auth.fullName': 'အမည်အပြည့်အစုံ',
    'auth.phone': 'ဖုန်းနံပါတ်',
    'auth.signingIn': 'ဝင်ရောက်နေသည်...',
    'auth.signingUp': 'စာရင်းသွင်းနေသည်...',
    'auth.resetting': 'ပြန်သတ်မှတ်နေသည်...',
    'auth.checkEmail': 'ပြန်သတ်မှတ်ရန်လင့်ခ်အတွက်အီးမေးလ်စစ်ဆေးပါ',
    'auth.emailRequired': 'အီးမေးလ်ထည့်ပါ',
    'auth.passwordRequired': 'စကားဝှက်ထည့်ပါ',
    'auth.passwordMismatch': 'စကားဝှက်များမကိုက်ညီပါ',
    'auth.termsAgree': 'ဝန်ဆောင်မှုစည်းမျဉ်းနှင့်ကိုယ်ရေးကိုယ်တာမူဝါဒကိုသဘောတူပါသည်',
    
    // Booking History (new)
    'bookingHistory.title': 'ဘွတ်ကင်မှတ်တမ်း',
    'bookingHistory.totalBookings': 'ဘွတ်ကင် {count} ခု',
    'bookingHistory.upcoming': 'လာမည့်',
    'bookingHistory.past': 'ပြီးခဲ့သော',
    'bookingHistory.noUpcoming': 'လာမည့်ဘွတ်ကင်များမရှိပါ',
    'bookingHistory.noPast': 'ပြီးခဲ့သောဘွတ်ကင်များမရှိပါ',
    'bookingHistory.bookService': 'ဝန်ဆောင်မှုမှာယူပြီးနောက်ဤနေရာတွင်ပြမည်',
    'bookingHistory.willAppear': 'သင်ပြီးမြောက်သောဘွတ်ကင်များဤနေရာတွင်ပြမည်',
    'bookingHistory.browseServices': 'ဝန်ဆောင်မှုများရှာဖွေရန်',
    'bookingHistory.reschedule': 'ပြန်စီစဉ်ရန်',
    'bookingHistory.cancel': 'ပယ်ဖျက်ရန်',
    'bookingHistory.viewReceipt': 'ပြေစာကြည့်ရန်',
    'bookingHistory.rescheduleBooking': 'ဘွတ်ကင်ပြန်စီစဉ်ရန်',
    'bookingHistory.service': 'ဝန်ဆောင်မှု',
    'bookingHistory.newDateTime': 'ရက်စွဲနှင့်အချိန်အသစ်',
    'bookingHistory.confirmReschedule': 'ပြန်စီစဉ်မှုအတည်ပြုရန်',
    'bookingHistory.filterStatus': 'အခြေအနေစစ်ထုတ်ရန်',
    'bookingHistory.allStatuses': 'အခြေအနေအားလုံး',
    'bookingHistory.inProgress': 'လုပ်ဆောင်ဆဲ',
    
    // Payment History (new)
    'paymentHistory.title': 'ငွေပေးချေမှုမှတ်တမ်း',
    'paymentHistory.subtitle': 'ငွေပေးချေမှုများနှင့်ပြေစာများအားလုံးကြည့်ရန်',
    'paymentHistory.all': 'အားလုံး',
    'paymentHistory.hoa': 'HOA',
    'paymentHistory.rentals': 'ငှားရမ်းခြင်း',
    'paymentHistory.services': 'ဝန်ဆောင်မှုများ',
    'paymentHistory.noPayments': 'ငွေပေးချေမှုမတွေ့ပါ',
    'paymentHistory.willAppear': 'သင့်ငွေပေးချေမှုမှတ်တမ်းဤနေရာတွင်ပြမည်',
    'paymentHistory.paymentDetails': 'ငွေပေးချေမှုအသေးစိတ်',
    'paymentHistory.amount': 'ပမာဏ',
    'paymentHistory.type': 'အမျိုးအစား',
    'paymentHistory.status': 'အခြေအနေ',
    'paymentHistory.date': 'ရက်စွဲ',
    'paymentHistory.paymentMethod': 'ငွေပေးချေနည်း',
    'paymentHistory.property': 'အိမ်ခြံ',
    'paymentHistory.verified': 'အတည်ပြုပြီး',
    'paymentHistory.paymentProof': 'ငွေပေးချေမှုအထောက်အထား',
    'paymentHistory.downloadReceipt': 'ပြေစာဒေါင်းလုဒ်ရယူရန်',
    'paymentHistory.paid': 'ပေးချေပြီး',
    'paymentHistory.pending': 'စောင့်ဆိုင်းဆဲ',
    'paymentHistory.rejected': 'ငြင်းပယ်ပြီး',
    
    // Onboarding (new)
    'onboarding.welcome': 'Aetherမှကြိုဆိုပါသည်',
    'onboarding.profile': 'ပရိုဖိုင်',
    'onboarding.profileDesc': 'သင့်အကြောင်းပြောပြပါ',
    'onboarding.properties': 'အိမ်ခြံများ',
    'onboarding.propertiesDesc': 'သင့်အိမ်ခြံများထည့်ပါ',
    'onboarding.preferences': 'ဦးစားပေးမှုများ',
    'onboarding.preferencesDesc': 'သင့်အတွေ့အကြုံကိုစိတ်ကြိုက်ပြင်ဆင်ပါ',
    'onboarding.skip': 'ကျော်ရန်',
    'onboarding.continue': 'ဆက်လက်',
    'onboarding.complete': 'စတင်ပြင်ဆင်မှုပြီးမြောက်',
    'onboarding.userType': 'သင်ဘယ်သူလဲ?',
    'onboarding.homeowner': 'အိမ်ပိုင်ရှင်',
    'onboarding.homeownerDesc': 'ငါအိမ်ခြံပိုင်ဆိုင်ပြီးAetherဝန်ဆောင်မှုများသုံးချင်တယ်',
    'onboarding.guest': 'ဧည့်သည်',
    'onboarding.guestDesc': 'ငါအိမ်ခြံငှားချင်သို့မဟုတ်တည်းခိုချင်တယ်',
    'onboarding.community': 'အသိုင်းအဝိုင်း',
    'onboarding.selectCommunity': 'အသိုင်းအဝိုင်းရွေးချယ်ပါ',
    'onboarding.propertyAddress': 'အိမ်ခြံလိပ်စာ',
    'onboarding.unitNumber': 'ယူနစ်နံပါတ်',
    'onboarding.propertyType': 'အိမ်ခြံအမျိုးအစား',
    'onboarding.rentalMode': 'ငှားရမ်းမုဒ်ဖွင့်ရန်',
    'onboarding.addProperty': 'အိမ်ခြံထည့်ရန်',
    'onboarding.removeProperty': 'ဖယ်ရှားရန်',
    'onboarding.nightlyRate': 'ညစဉ်နှုန်း',
    'onboarding.cleaningFee': 'သန့်ရှင်းရေးကြေး',
    'onboarding.selectAmenities': 'အဆောက်အအုံများရွေးချယ်ပါ',
    'onboarding.uploadImages': 'ပုံများတင်ပါ',
    'onboarding.notifyBookings': 'ဘွတ်ကင်အကြောင်းကြားချက်များ',
    'onboarding.notifyMessages': 'မက်ဆေ့ချ်အကြောင်းကြားချက်များ',
    
    // Support (new)
    'support.title': 'ပံ့ပိုးမှုစင်တာ',
    'support.heroTitle': 'ဘာကူညီပေးရမလဲ?',
    'support.heroSubtitle': 'ကျွန်ုပ်တို့အဖွဲ့သည်သင့်မေးခွန်းများသို့မဟုတ်ပြဿနာများကိုကူညီရန်ရှိပါသည်။',
    'support.liveChat': 'တိုက်ရိုက်ချတ်',
    'support.liveChatDesc': 'ကျွန်ုပ်တို့ပံ့ပိုးမှုအဖွဲ့နှင့်တိုက်ရိုက်ချတ်လုပ်ပါ',
    'support.emailSupport': 'အီးမေးလ်ပံ့ပိုးမှု',
    'support.emailSupportDesc': '၂၄နာရီအတွင်းပြန်ကြားပေးမည်',
    'support.phoneSupport': 'ဖုန်းပံ့ပိုးမှု',
    'support.phoneSupportDesc': 'လုပ်ငန်းသုံးသမားများသာ',
    'support.startChat': 'ချတ်စတင်ရန်',
    'support.sendEmail': 'အီးမေးလ်ပို့ရန်',
    'support.callNow': 'ယခုခေါ်ရန်',
    'support.quickAnswers': 'အမြန်အဖြေများ',
    'support.commonQuestions': 'အသုံးပြုသူများ၏ဘုံမေးခွန်းများ',
    'support.viewAllFAQs': 'FAQအားလုံးကြည့်ရန်',
    'support.sendMessage': 'မက်ဆေ့ချ်ပို့ရန်',
    'support.name': 'အမည်',
    'support.email': 'အီးမေးလ်',
    'support.subject': 'အကြောင်းအရာ',
    'support.message': 'မက်ဆေ့ချ်',
    'support.yourName': 'သင့်အမည်',
    'support.yourEmail': 'သင့်အီးမေးလ်',
    'support.howCanWeHelp': 'ဘာကူညီပေးရမလဲ?',
    'support.describeIssue': 'သင့်ပြဿနာသို့မဟုတ်မေးခွန်းကိုဖော်ပြပါ...',
    'support.sending': 'ပို့နေသည်...',
    
    // Notifications (new)
    'notifications.title': 'အကြောင်းကြားချက်များ',
    'notifications.markAllRead': 'အားလုံးဖတ်ပြီးဟုမှတ်သားရန်',
    'notifications.all': 'အားလုံး',
    'notifications.urgent': 'အရေးပေါ်',
    'notifications.unread': 'မဖတ်ရသေး',
    'notifications.noNotifications': 'အကြောင်းကြားချက်မရှိပါ',
    'notifications.allCaughtUp': 'အားလုံးဖတ်ပြီးပါပြီ!',
    'notifications.checkBack': 'နောက်မှပြန်လာကြည့်ပါ',
    'notifications.today': 'ဒီနေ့',
    'notifications.yesterday': 'မနေ့က',
    'notifications.thisWeek': 'ဤအပတ်',
    'notifications.earlier': 'အစောပိုင်း',
    
    // Rental Editor (Burmese)
    'rentalEditor.pricing': 'စျေးနှုန်း',
    'rentalEditor.nightlyRate': 'တစ်ညအိပ်နှုန်း',
    'rentalEditor.cleaningFee': 'သန့်ရှင်းရေးကြေး',
    'rentalEditor.minNights': 'အနည်းဆုံးည',
    'rentalEditor.maxNights': 'အများဆုံးည',
    'rentalEditor.description': 'ဖော်ပြချက်',
    'rentalEditor.descriptionPlaceholder': 'သင့်အိမ်ခြံကိုဖော်ပြပါ...',
    'rentalEditor.amenities': 'အဆောက်အအုံများ',
    'rentalEditor.houseRules': 'အိမ်စည်းကမ်း',
    'rentalEditor.houseRulesPlaceholder': 'အိမ်စည်းကမ်းများထည့်ပါ (ဥပမာ - ဆေးလိပ်မသောက်ရ၊ ပါတီမလုပ်ရ...)',
    'rentalEditor.smartHomeInstructions': 'စမတ်အိမ်လမ်းညွှန်',
    'rentalEditor.smartHomeDesc': 'ဘွတ်ကင်အတည်ပြုပြီးနောက် ဧည့်သည်များကိုပြသမည်',
    'rentalEditor.tip': 'အကြံပြုချက်',
    'rentalEditor.smartHomeTip': 'WiFi စကားဝှက်၊ တံခါးကုဒ်များ၊ အသံအမိန့်များနှင့် စက်တည်နေရာများ ဤနေရာတွင်ထည့်ပါ။',
    'rentalEditor.publishListing': 'စာရင်းတင်ရန်',
    'rentalEditor.publishDesc': 'ဧည့်သည်များမြင်နိုင်အောင်ပြုလုပ်ပါ',
    'rentalEditor.saveListing': 'စာရင်းသိမ်းရန်',
    'rentalEditor.rentalType': 'ငှားရမ်းမှုအမျိုးအစား',
    'rentalEditor.shortTerm': 'ရေတိုငှားရမ်းခြင်း',
    'rentalEditor.shortTermDesc': 'အားလပ်ရက်နှင့် ရေတိုတည်းခိုခြင်းအတွက် ည တစ်ညလျှင် (၁-၃၀ ည)',
    'rentalEditor.longTerm': 'ရေရှည်ငှားရမ်းခြင်း',
    'rentalEditor.longTermDesc': 'ရေရှည်တည်းခိုခြင်းအတွက် လစဉ်ငှားရမ်းခြင်း (၆ လနှင့်အထက်)',
    'rentalEditor.bothTypes': 'ရေတိုနှင့် ရေရှည် နှစ်မျိုးလုံး',
    'rentalEditor.bothTypesDesc': 'ည တစ်ညလျှင်နှင့် လစဉ် ဘွတ်ကင်များ လက်ခံသည်',
    'rentalEditor.shortTermPricing': 'ရေတိုစျေးနှုန်း',
    'rentalEditor.longTermPricing': 'ရေရှည်စျေးနှုန်း',
    'rentalEditor.monthlyRate': 'လစဉ်နှုန်း',
    'rentalEditor.monthlyRateHint': 'ရေရှည်ငှားရမ်းသူများအတွက် လစဉ်စျေးနှုန်း',
    'rentalEditor.minLeaseDuration': 'အနည်းဆုံးငှားရမ်းကာလ (လ)',
    
    // Admin Dashboard (Burmese)
    'admin.rentals': 'ငှားရမ်းခြင်း',
    'admin.news': 'သတင်း',
    'admin.inviteOwner': 'ပိုင်ရှင်ဖိတ်ကြားရန်',
    'admin.inviteOwnerTitle': 'အိမ်ခြံပိုင်ရှင်ကို ဖိတ်ကြားရန်',
    'admin.inviteOwnerDesc': 'ပိုင်ရှင်သည် အကောင့်ဖွင့်ရန်နှင့် ဤအိမ်ခြံကို စီမံခန့်ခွဲရန် အီးမေးလ် ဖိတ်ကြားစာ လက်ခံရရှိမည်။',
    
    // Community Switcher (Burmese)
    'community.selectCommunity': 'ရပ်ကွက်ရွေးပါ',
    'community.createNew': 'အသစ်အသိုင်းအဝိုင်းဖန်တီးရန်',
    
    // Map Page (Burmese)
    'map.title': 'ရပ်ကွက်မြေပုံ',
    'map.amenities': 'အဆောက်အအုံများ',
    'map.properties': 'အိမ်ခြံများ',
    
    // Service Marketplace additional (Burmese)
    'service.services': 'ဝန်ဆောင်မှုများ',
    'service.bookPremium': 'အထူးဝန်ဆောင်မှုများဘွတ်ကင်',
    'service.all': 'အားလုံး',
    'service.noServices': 'ဝန်ဆောင်မှုမရှိပါ',
    'service.checkBackLater': 'နောက်မှပြန်လာကြည့်ပါ',
    'service.by': 'မှ',
    'service.book': 'ဘွတ်ကင်',
    'service.service': 'ဝန်ဆောင်မှု',
    'service.estimatedPrice': 'ခန့်မှန်းစျေး',
    'service.priceVaryNote': 'နောက်ဆုံးစျေးနှုန်းသည် အိမ်အရွယ်အစားပေါ်မူတည်၍ ကွဲပြားနိုင်သည်',
    'service.scheduleDateTime': 'ရက်စွဲနှင့်အချိန်',
    'service.specialRequests': 'အထူးတောင်းဆိုချက် (ရွေးချယ်ခွင့်)',
    'service.specialInstructions': 'အထူးညွှန်ကြားချက်များ...',
    'service.payWithCard': 'ကတ်ဖြင့်ပေးချေ (USD)',
    'service.category.housekeeping': 'အိမ်သန့်ရှင်းရေး',
    'service.category.wellness': 'ကျန်းမာရေး',
    'service.category.maintenance': 'ပြုပြင်ထိန်းသိမ်းမှု',
    'service.category.dining': 'စားသောက်',
    'service.category.transport': 'သယ်ယူပို့ဆောင်ရေး',
    
    // Chat (additional - Burmese)
    'chat.fallbackError': 'တောင်းပန်ပါသည်၊ သင့်တောင်းဆိုချက်ကို လုပ်ဆောင်၍မရပါ။ ထပ်မံကြိုးစားပါ။',
    'chat.bookingSuccess': 'ဘွတ်ကင်အတည်ပြုပြီးပါပြီ။',
    'chat.bookingIssue': 'ဘွတ်ကင်ဖန်တီးရာတွင် ပြဿနာရှိပါသည်။ ထပ်မံကြိုးစားပါ။',
    'chat.temporaryError': 'တောင်းပန်ပါသည်၊ ယာယီချိတ်ဆက်မှုပြဿနာ ကြုံနေပါသည်။ ခဏစောင့်ပြီး ထပ်မံကြိုးစားပါ။',
    
    // Map (additional - Burmese)
    'map.loading': 'မြေပုံတင်နေသည်...',
    
    // God Mode Dashboard (Burmese)
    'godMode.title': 'အားလုံးကြီးကြပ်မှု',
    'godMode.commandCenter': 'စီမံခန့်ခွဲမှုဘောင်',
    'godMode.adminPanel': 'စီမံခန့်ခွဲမှုဘောင်',
    'godMode.communities': 'ရပ်ကွက်များ',
    'godMode.homes': 'အိမ်များ',
    'godMode.mrr': 'လစဉ်ဝင်ငွေ',
    'godMode.propertyDevelopers': 'အိမ်ခြံမြေ ဖွံ့ဖြိုးရေးသမားများ',
    'godMode.developerInviteDesc': 'ပလက်ဖောင်းပေါ်တွင် ရပ်ကွက်များဖန်တီးရန် ဖွံ့ဖြိုးရေးသမားများကို ဖိတ်ကြားပါ။',
    'godMode.add': 'ထည့်ရန်',
    'godMode.createNewCommunity': 'ရပ်ကွက်အသစ်ဖန်တီးရန်',
    'godMode.communityName': 'ရပ်ကွက်အမည်',
    'godMode.city': 'မြို့',
    'godMode.state': 'ပြည်နယ်',
    'godMode.baseFee': 'အခြေခံကြေး/လ',
    'godMode.perHomeFee': 'အိမ်တစ်လုံးကြေး',
    'godMode.createCommunity': 'ရပ်ကွက်ဖန်တီးရန်',
    'godMode.noCommunities': 'ရပ်ကွက်မရှိသေးပါ',
    'godMode.createFirstCommunity': 'စတင်ရန် ပထမဆုံးရပ်ကွက်ကို ဖန်တီးပါ',
    'godMode.active': 'အသုံးပြုနေ',
    'godMode.inactive': 'ရပ်ဆိုင်း',
    'godMode.noLocation': 'တည်နေရာမရှိ',
    
    // Admin additional
    'admin.owner': 'ပိုင်ရှင်',
    'admin.homeownerEmail': 'အိမ်ပိုင်ရှင်အီးမေးလ်',
    'admin.property': 'အိမ်ခြံ',
    'admin.totalProperties': 'စုစုပေါင်းအိမ်ခြံ',
    'admin.rentalModeOn': 'ငှားရမ်းမုဒ်ဖွင့်ထား',
    'admin.activeListings': 'အသုံးပြုနေသောစာရင်း',
    'admin.upcomingArrivals': 'လာမည့်ဧည့်သည်များ',
    'admin.rentalProperties': 'ငှားရမ်းအိမ်ခြံများ',
    'admin.rentalOn': 'ငှားရမ်းမုဒ်ဖွင့်',
    'admin.rentalOff': 'ငှားရမ်းမုဒ်ပိတ်',
    'admin.upcomingBookings': 'လာမည့်ကြိုတင်စာရင်းများ',
    'admin.listingInactive': 'စာရင်းအသုံးမပြုပါ',
    
    // Rental types
    'rental.shortTerm': 'ရေတိုငှား',
    'rental.longTerm': 'ရေရှည်ငှား',
    'rental.bothTypes': 'နှစ်မျိုးလုံး',
    
    // Review Form
    'reviewForm.howWasStay': 'သင့်တည်းခိုမှုကဘယ်လိုလဲ?',
    'reviewForm.overallExperience': 'ခြုံငုံသုံးသပ်ချက်',
    'reviewForm.ratingRequired': 'ခြုံငုံအမှတ်ပေးပါ',
    'reviewForm.submitSuccess': 'သုံးသပ်ချက်တင်သွင်းပြီးပါပြီ!',
    'reviewForm.submitFailed': 'သုံးသပ်ချက်တင်သွင်းရန်မအောင်မြင်ပါ',
    'reviewForm.titleLabel': 'သုံးသပ်ချက်ခေါင်းစဉ် (ရွေးချယ်နိုင်)',
    'reviewForm.titlePlaceholder': 'သင့်အတွေ့အကြုံကိုအကျဉ်းချုပ်ပါ',
    'reviewForm.contentLabel': 'သင့်သုံးသပ်ချက် (ရွေးချယ်နိုင်)',
    'reviewForm.contentPlaceholder': 'သင့်တည်းခိုမှုအသေးစိတ်ကိုမျှဝေပါ...',
    'reviewForm.submit': 'သုံးသပ်ချက်တင်ရန်',
    
    // Review Moderation
    'moderation.title': 'သုံးသပ်ချက်စီမံခန့်ခွဲမှု',
    'moderation.total': 'စုစုပေါင်း',
    'moderation.pending': 'စောင့်ဆိုင်းဆဲ',
    'moderation.flagged': 'အမှတ်အသားပြုထား',
    'moderation.all': 'အားလုံး',
    'moderation.published': 'ထုတ်ဝေပြီး',
    'moderation.hidden': 'ဝှက်ထား',
    'moderation.lowRating': 'အမှတ်နိမ့်',
    'moderation.unknownProperty': 'မသိအိမ်ခြံ',
    'moderation.anonymous': 'အမည်ဝှက်',
    'moderation.commShort': 'ဆက်သွယ်ရေး',
    'moderation.hide': 'ဝှက်ရန်',
    'moderation.publish': 'ထုတ်ဝေရန်',
    'moderation.edit': 'တည်းဖြတ်',
    'moderation.respond': 'တုံ့ပြန်ရန်',
    'moderation.statusUpdated': 'သုံးသပ်ချက်အခြေအနေပြောင်းလဲပြီး',
    'moderation.updateFailed': 'သုံးသပ်ချက်ပြောင်းလဲရန်မအောင်မြင်ပါ',
    'moderation.responseAdded': 'တုံ့ပြန်ချက်ထည့်သွင်းပြီး',
    'moderation.responseFailed': 'တုံ့ပြန်ချက်ထည့်သွင်းရန်မအောင်မြင်ပါ',
    'moderation.noReviews': 'သုံးသပ်ချက်မရှိသေးပါ',
    'moderation.noPending': 'စောင့်ဆိုင်းဆဲသုံးသပ်ချက်မရှိပါ',
    'moderation.noPublished': 'ထုတ်ဝေထားသောသုံးသပ်ချက်မရှိပါ',
    'moderation.noFlagged': 'အမှတ်အသားပြုထားသောသုံးသပ်ချက်မရှိပါ',
    'moderation.editResponse': 'တုံ့ပြန်ချက်တည်းဖြတ်ရန်',
    'moderation.addResponse': 'တုံ့ပြန်ချက်ထည့်ရန်',
    'moderation.by': 'မှ',
    'moderation.responsePlaceholder': 'ဤသုံးသပ်ချက်အတွက်သင့်တုံ့ပြန်ချက်ကိုရေးပါ...',
    'moderation.saveResponse': 'တုံ့ပြန်ချက်သိမ်းရန်',
    
    // Audit Log (Burmese)
    'audit.title': 'စစ်ဆေးမှတ်တမ်း',
    'audit.subtitle': 'စနစ်လုပ်ဆောင်ချက်မှတ်တမ်း',
    'audit.noLogs': 'စစ်ဆေးမှတ်တမ်းမတွေ့ပါ',
    'audit.noLogsDesc': 'စနစ်လုပ်ဆောင်ချက်များဤနေရာတွင်ပြမည်',
    'audit.filter': 'လုပ်ဆောင်ချက်ဖြင့်စစ်ထုတ်ပါ',
    'audit.allActions': 'လုပ်ဆောင်ချက်အားလုံး',
    'audit.allResources': 'အရင်းအမြစ်အားလုံး',
    'audit.searchPlaceholder': 'လုပ်ဆောင်ချက်၊ အရင်းအမြစ်ရှာဖွေပါ...',
    'audit.loadMore': 'နောက်ထပ်တင်ပါ',
    'common.refresh': 'ပြန်လည်စတင်ရန်',
    
    // Services Manager (Burmese)
    'services.title': 'ဝန်ဆောင်မှုစီမံခန့်ခွဲမှု',
    'services.addService': 'ဝန်ဆောင်မှုထည့်ရန်',
    'services.editService': 'ဝန်ဆောင်မှုတည်းဖြတ်ရန်',
    'services.serviceName': 'ဝန်ဆောင်မှုအမည်',
    'services.serviceNamePlaceholder': 'ဥပမာ - အတွင်းကျကျသန့်ရှင်းရေး',
    'services.description': 'ဖော်ပြချက်',
    'services.descriptionPlaceholder': 'ဝန်ဆောင်မှုကိုဖော်ပြပါ...',
    'services.category': 'အမျိုးအစား',
    'services.selectCategory': 'အမျိုးအစားရွေးပါ',
    'services.vendor': 'ရောင်းချသူ (ရွေးချယ်နိုင်)',
    'services.selectVendor': 'ရောင်းချသူရွေးပါ',
    'services.noVendor': 'ရောင်းချသူမရှိ',
    'services.basePrice': 'အခြေခံစျေးနှုန်း',
    'services.pricePerBedroom': 'အိပ်ခန်းတစ်ခန်းစျေး',
    'services.pricePerSqft': 'စတုရန်းပေတစ်ခုစျေး',
    'services.duration': 'ကြာချိန် (မိနစ်)',
    'services.isGlobal': 'အသိုင်းအဝိုင်းအားလုံးအတွက်ရရှိနိုင်',
    'services.isActive': 'အသုံးပြုနိုင်',
    'services.saveService': 'ဝန်ဆောင်မှုသိမ်းရန်',
    'services.deleteConfirm': 'ဤဝန်ဆောင်မှုကိုဖျက်မည်လား?',
    'services.deleteSuccess': 'ဝန်ဆောင်မှုဖျက်ပြီးပါပြီ',
    'services.deleteFailed': 'ဝန်ဆောင်မှုဖျက်ရန်မအောင်မြင်ပါ',
    'services.saveSuccess': 'ဝန်ဆောင်မှုသိမ်းပြီးပါပြီ',
    'services.saveFailed': 'ဝန်ဆောင်မှုသိမ်းရန်မအောင်မြင်ပါ',
    'services.noServices': 'ဝန်ဆောင်မှုမရှိသေးပါ',
    'services.addFirst': 'စတင်ရန်ပထမဆုံးဝန်ဆောင်မှုထည့်ပါ',
    'services.global': 'ကမ္ဘာလုံးဆိုင်ရာ',
    'services.housekeeping': 'အိမ်သန့်ရှင်းရေး',
    'services.wellness': 'ကျန်းမာရေး',
    'services.maintenance': 'ပြုပြင်ထိန်းသိမ်းမှု',
    'services.dining': 'စားသောက်ဆိုင်',
    'services.transport': 'သယ်ယူပို့ဆောင်ရေး',
    
    // Vendor Manager (Burmese)
    'vendor.title': 'ရောင်းချသူစီမံခန့်ခွဲမှု',
    'vendor.addVendor': 'ရောင်းချသူထည့်ရန်',
    'vendor.editVendor': 'ရောင်းချသူတည်းဖြတ်ရန်',
    'vendor.vendorName': 'ရောင်းချသူအမည်',
    'vendor.vendorNamePlaceholder': 'ဥပမာ - အရည်အသွေးမြင့်သန့်ရှင်းရေးကုမ္ပဏီ',
    'vendor.email': 'အီးမေးလ်',
    'vendor.emailPlaceholder': 'vendor@example.com',
    'vendor.description': 'ဖော်ပြချက်',
    'vendor.descriptionPlaceholder': 'ရောင်းချသူကိုဖော်ပြပါ...',
    'vendor.category': 'အမျိုးအစား',
    'vendor.selectCategory': 'အမျိုးအစားရွေးပါ',
    'vendor.commissionRate': 'ကော်မရှင်နှုန်း (%)',
    'vendor.isGlobal': 'အသိုင်းအဝိုင်းအားလုံးအတွက်ရရှိနိုင်',
    'vendor.isActive': 'အသုံးပြုနိုင်',
    'vendor.saveVendor': 'ရောင်းချသူသိမ်းရန်',
    'vendor.deleteConfirm': 'ဤရောင်းချသူကိုဖျက်မည်လား?',
    'vendor.deleteSuccess': 'ရောင်းချသူဖျက်ပြီးပါပြီ',
    'vendor.deleteFailed': 'ရောင်းချသူဖျက်ရန်မအောင်မြင်ပါ',
    'vendor.saveSuccess': 'ရောင်းချသူသိမ်းပြီးပါပြီ',
    'vendor.saveFailed': 'ရောင်းချသူသိမ်းရန်မအောင်မြင်ပါ',
    'vendor.noVendors': 'ရောင်းချသူမရှိသေးပါ',
    'vendor.addFirst': 'စတင်ရန်ပထမဆုံးရောင်းချသူထည့်ပါ',
    'vendor.global': 'ကမ္ဘာလုံးဆိုင်ရာ',
    'vendor.commission': 'ကော်မရှင်',
    'vendor.paymentSettings': 'ငွေပေးချေမှုဆက်တင်များ',
    'vendor.stripeConnected': 'Stripe ချိတ်ဆက်ပြီး',
    'vendor.stripeNotConnected': 'Stripe မချိတ်ဆက်ရသေး',
    'vendor.mobilePayment': 'မိုဘိုင်းငွေပေးချေမှု',
    'vendor.accountName': 'အကောင့်အမည်',
    'vendor.phoneNumber': 'ဖုန်းနံပါတ်',
    
    // Payment Verification (Burmese)
    'paymentVerification.title': 'အတည်ပြုရန်စောင့်ဆိုင်းနေသောငွေပေးချေမှုများ',
    'paymentVerification.noPayments': 'အတည်ပြုရန်ငွေပေးချေမှုမရှိပါ',
    'paymentVerification.allProcessed': 'ငွေပေးချေမှုအားလုံးလုပ်ဆောင်ပြီးပါပြီ',
    'paymentVerification.verifyPayment': 'ငွေပေးချေမှုအတည်ပြုရန်',
    'paymentVerification.amount': 'ပမာဏ',
    'paymentVerification.paymentMethod': 'ငွေပေးချေနည်း',
    'paymentVerification.customer': 'ဖောက်သည်',
    'paymentVerification.date': 'ရက်စွဲ',
    'paymentVerification.property': 'အိမ်ခြံ',
    'paymentVerification.paymentProof': 'ငွေပေးချေမှုအထောက်အထား',
    'paymentVerification.fullSize': 'အပြည့်အစုံ',
    'paymentVerification.noProof': 'ငွေပေးချေမှုအထောက်အထားမတင်ရသေးပါ',
    'paymentVerification.verifyManually': 'သင့်ဘဏ်အက်ပ်တွင်ဤငွေပေးချေမှုကိုလက်ဖြင့်အတည်ပြုပါ',
    'paymentVerification.rejectionReason': 'ငြင်းပယ်ရသည့်အကြောင်းရင်း (ငြင်းပယ်ပါက)',
    'paymentVerification.rejectionPlaceholder': 'ဥပမာ - ပမာဏမတူညီပါ၊ မှန်ကန်သောစခရင်ရှော့မဟုတ်ပါ...',
    'paymentVerification.approve': 'အတည်ပြုရန်',
    'paymentVerification.reject': 'ငြင်းပယ်ရန်',
    'paymentVerification.approved': 'ငွေပေးချေမှုအတည်ပြုပြီးပါပြီ',
    'paymentVerification.rejected': 'ငွေပေးချေမှုငြင်းပယ်ပြီးပါပြီ',
    'paymentVerification.approveFailed': 'ငွေပေးချေမှုအတည်ပြုရန်မအောင်မြင်ပါ',
    'paymentVerification.rejectFailed': 'ငွေပေးချေမှုငြင်းပယ်ရန်မအောင်မြင်ပါ',
    'paymentVerification.provideReason': 'ငြင်းပယ်ရသည့်အကြောင်းရင်းပေးပါ',
    'paymentVerification.hasProof': 'အထောက်အထားရှိ',
    'paymentVerification.hoa': 'HOA',
    'paymentVerification.rental': 'ငှားရမ်းခြင်း',
    'paymentVerification.loadFailed': 'စောင့်ဆိုင်းနေသောငွေပေးချေမှုများတင်ရန်မအောင်မြင်ပါ',
    
    // Vendor Payouts (Burmese)
    'vendorPayouts.totalRevenue': 'စုစုပေါင်းဝင်ငွေ',
    'vendorPayouts.platformFees': 'ပလက်ဖောင်းကြေး',
    'vendorPayouts.vendorPayouts': 'ရောင်းချသူငွေထုတ်ပေးမှုများ',
    'vendorPayouts.pendingPayouts': 'စောင့်ဆိုင်းနေသောငွေထုတ်ပေးမှုများ',
    'vendorPayouts.completedPayouts': 'ပြီးမြောက်သောငွေထုတ်ပေးမှုများ',
    'vendorPayouts.noVendors': 'ရောင်းချသူမရှိသေးပါ',
    'vendorPayouts.addVendorsFirst': 'ဝင်ငွေကြည့်ရန်ရောင်းချသူထည့်ပါ',
    'vendorPayouts.vendor': 'ရောင်းချသူ',
    'vendorPayouts.category': 'အမျိုးအစား',
    'vendorPayouts.earnings': 'ဝင်ငွေ',
    'vendorPayouts.commission': 'ကော်မရှင်',
    'vendorPayouts.payout': 'ငွေထုတ်ပေးမှု',
    'vendorPayouts.status': 'အခြေအနေ',
    'vendorPayouts.actions': 'လုပ်ဆောင်ချက်များ',
    'vendorPayouts.markPaid': 'ပေးချေပြီးဟုမှတ်သားရန်',
    'vendorPayouts.viewHistory': 'မှတ်တမ်းကြည့်ရန်',
    'vendorPayouts.paid': 'ပေးချေပြီး',
    'vendorPayouts.pending': 'စောင့်ဆိုင်းဆဲ',
    
    // Calendar (Burmese)
    'calendar.title': 'ကြိုတင်မှာယူမှုပြက္ခဒိန်',
    'calendar.today': 'ဒီနေ့',
    'calendar.week': 'အပတ်',
    'calendar.month': 'လ',
    'calendar.noBookings': 'ကြိုတင်မှာယူမှုမရှိသေးပါ',
    'calendar.dragToReschedule': 'ပြန်စီစဉ်ရန်ဆွဲပါ',
    'calendar.reschedule': 'ပြန်စီစဉ်ရန်',
    'calendar.newDate': 'ရက်စွဲအသစ်',
    'calendar.newTime': 'အချိန်အသစ်',
    'calendar.from': 'မှ:',
    'calendar.to': 'သို့:',
    'calendar.at': 'တွင်',
    'calendar.confirm': 'အတည်ပြုရန်',
    'calendar.rescheduled': 'ကြိုတင်မှာယူမှုပြန်စီစဉ်ပြီးပါပြီ',
    'calendar.rescheduleFailed': 'ကြိုတင်မှာယူမှုပြန်စီစဉ်ရန်မအောင်မြင်ပါ',
    'calendar.availabilityCalendar': 'ရရှိနိုင်မှုပြက္ခဒိန်',
    
    // Relative Dates (Burmese)
    'dates.today': 'ဒီနေ့',
    'dates.yesterday': 'မနေ့က',
    'dates.tomorrow': 'မနက်ဖြန်',
    'dates.daysAgo': '{count} ရက်အကြာက',
    'dates.inDays': '{count} ရက်အတွင်း',
    'dates.justNow': 'ခုလေးတင်',
    'dates.dueToday': 'ဒီနေ့ သတ်မှတ်',
    
    // Settings Page (Phase 4 additions - Burmese)
    'settings.manageAccount': 'သင့်အကောင့်ကို စီမံခန့်ခွဲပါ',
    'settings.developerIntegrations': 'ဆော့ဖ်ဝဲရေးသားသူနှင့် ပေါင်းစည်းမှုများ',
    'settings.yourProperties': 'သင့်အိမ်ခြံများ',
    'settings.setupProfile': 'ပရိုဖိုင်ကို သတ်မှတ်ပါ',
    'settings.profileUpdated': 'ပရိုဖိုင် အပ်ဒိတ်လုပ်ပြီးပါပြီ',
    'settings.profileUpdateFailed': 'ပရိုဖိုင် အပ်ဒိတ်မအောင်မြင်ပါ',
    'settings.pushNotSupported': 'ဤဘရောက်ဇာတွင် push အကြောင်းကြားချက်များ ပံ့ပိုးမထားပါ',
    'settings.pushEnabled': 'Push အကြောင်းကြားချက်များ ဖွင့်ထားပါပြီ',
    'settings.pushDenied': 'Push အကြောင်းကြားချက်များ ခွင့်ပြုချက် ငြင်းပယ်ခံရပါသည်',
    'settings.pushEnableFailed': 'Push အကြောင်းကြားချက်များ ဖွင့်ရာတွင် မအောင်မြင်ပါ',
    'settings.openingPortal': 'ငွေပေးချေမှု portal ဖွင့်နေသည်...',
    'settings.portalFailed': 'ငွေပေးချေမှု portal ဖွင့်ရာတွင် မအောင်မြင်ပါ',
    'settings.editProfile': 'ပရိုဖိုင် ပြင်ဆင်ရန်',
    'settings.fullName': 'အမည်အပြည့်အစုံ',
    'settings.enterName': 'သင့်အမည်ကို ရိုက်ထည့်ပါ',
    'settings.email': 'အီးမေးလ်',
    'settings.phoneNumber': 'ဖုန်းနံပါတ်',
    'settings.enterPhone': 'သင့်ဖုန်းနံပါတ်ကို ရိုက်ထည့်ပါ',
    'settings.saveChanges': 'ပြောင်းလဲမှုများ သိမ်းရန်',
    'settings.emailNotifications': 'အီးမေးလ် အကြောင်းကြားချက်များ',
    'settings.bookingUpdates': 'ကြိုတင်မှာယူမှု အပ်ဒိတ်များ',
    'settings.bookingUpdatesDesc': 'ကြိုတင်မှာယူမှု အပ်ဒိတ်များကို အီးမေးလ်ဖြင့် လက်ခံရယူပါ',
    'settings.paymentAlerts': 'ငွေပေးချေမှု သတိပေးချက်များ',
    'settings.paymentAlertsDesc': 'ငွေပေးချေမှု လုပ်ဆောင်ချက်များအတွက် အီးမေးလ် သတိပေးချက်များ လက်ခံရယူပါ',
    'settings.guestPassActivity': 'ဧည့်သည် Pass လုပ်ဆောင်ချက်',
    'settings.guestPassActivityDesc': 'ဧည့်သည်များ pass အသုံးပြုသောအခါ အကြောင်းကြားချက်များ လက်ခံရယူပါ',
    'settings.pushNotifications': 'Push အကြောင်းကြားချက်များ',
    'settings.enablePush': 'Push အကြောင်းကြားချက်များ ဖွင့်ရန်',
    'settings.enablePushDesc': 'သင့်စက်ပစ္စည်းပေါ်တွင် အချိန်နှင့်တပြေးညီ သတိပေးချက်များ လက်ခံရယူပါ',
    'settings.bookingNotifications': 'ကြိုတင်မှာယူမှု အကြောင်းကြားချက်များ',
    'settings.bookingNotificationsDesc': 'ကြိုတင်မှာယူမှု အသစ်များနှင့် ပြောင်းလဲမှုများ',
    'settings.messageNotifications': 'မက်ဆေ့ခ်ျ အကြောင်းကြားချက်များ',
    'settings.messageNotificationsDesc': 'ဧည့်သည်များထံမှ မက်ဆေ့ခ်ျ အသစ်များ',
    'settings.announcementNotifications': 'ကြေညာချက် အကြောင်းကြားချက်များ',
    'settings.announcementNotificationsDesc': 'အသိုင်းအဝိုင်း ကြေညာချက်များ',
    'settings.managePaymentMethods': 'ငွေပေးချေမှုနည်းလမ်းများ စီမံခန့်ခွဲရန်',
    'settings.managePaymentMethodsDesc': 'လုံခြုံသော portal မှတဆင့် သင့်ငွေပေးချေမှုနည်းလမ်းများကို အပ်ဒိတ်လုပ်ပါ',
    'settings.openPaymentPortal': 'ငွေပေးချေမှု Portal ဖွင့်ရန်',
    'settings.paymentSecurityNote': 'သင့်ငွေပေးချေမှု အချက်အလက်များကို Stripe မှ လုံခြုံစွာ ကိုင်တွယ်ပါသည်',
    'settings.preferences': 'နှစ်သက်မှုများ',
    'settings.selectTimezone': 'အချိန်ဇုန် ရွေးချယ်ပါ',
    'settings.timezoneSet': 'အချိန်ဇုန် သတ်မှတ်ပြီး',
    'settings.selectLanguage': 'ဘာသာစကား ရွေးချယ်ပါ',
    'settings.languageChanged': 'ဘာသာစကား ပြောင်းပြီး',
    'settings.selectCurrency': 'ငွေကြေး ရွေးချယ်ပါ',
    'settings.currencyChanged': 'ငွေကြေး ပြောင်းပြီး',
    'settings.preferencesSaved': 'သင့်နှစ်သက်မှုများကို အလိုအလျောက် သိမ်းဆည်းထားပါသည်',
    
    // Service Marketplace (Phase 4 - Burmese)
    'service.selectDateTime': 'ကျေးဇူးပြု၍ ရက်စွဲနှင့် အချိန် ရွေးချယ်ပါ',
    'service.bookingCreated': 'ကြိုတင်မှာယူမှု ဖန်တီးပြီးပါပြီ! အတည်ပြုရန် ငွေပေးချေမှု ပြီးဆုံးပါ။',
    'service.bookingFailed': 'ဝန်ဆောင်မှု ကြိုတင်မှာယူရာတွင် မအောင်မြင်ပါ',
    'service.paymentSubmitted': 'ငွေပေးချေမှု တင်သွင်းပြီးပါပြီ! အတည်ပြုမှု စောင့်ဆိုင်းနေပါသည်။',
    
    // Browse Rentals (Phase 4 - Burmese)
    'browse.pleaseSignIn': 'ငှားရမ်းခြင်း ကြိုတင်မှာယူရန် ကျေးဇူးပြု၍ အကောင့်ဝင်ပါ',
    'browse.selectDates': 'ကျေးဇူးပြု၍ ဝင်ရောက်ရက်နှင့် ထွက်ခွာရက် ရွေးချယ်ပါ',
    'browse.minStayError': 'အနည်းဆုံး {nights} ည တည်းခိုရပါမည်',
    'browse.maxStayError': 'အများဆုံး {nights} ည တည်းခိုနိုင်ပါသည်',
    'browse.redirectingPayment': 'ငွေပေးချေမှုသို့ ညွှန်းနေသည်...',
    'browse.bookingFailed': 'ကြိုတင်မှာယူမှု ဖန်တီးရာတွင် မအောင်မြင်ပါ',
    
    // Booking History (Phase 4 - Burmese additions)
    'bookingHistory.cancelSuccess': 'ကြိုတင်မှာယူမှု အောင်မြင်စွာ ပယ်ဖျက်ပြီးပါပြီ',
    'bookingHistory.cancelFailed': 'ကြိုတင်မှာယူမှု ပယ်ဖျက်ရာတွင် မအောင်မြင်ပါ',
    'bookingHistory.rescheduleSuccess': 'ကြိုတင်မှာယူမှု အောင်မြင်စွာ ပြန်စီစဉ်ပြီးပါပြီ',
    'bookingHistory.rescheduleFailed': 'ကြိုတင်မှာယူမှု ပြန်စီစဉ်ရာတွင် မအောင်မြင်ပါ',
    'bookingHistory.paymentPaid': 'ပေးချေပြီး',
    'bookingHistory.paymentProcessing': 'လုပ်ဆောင်နေသည်',
    'bookingHistory.paymentFailed': 'မအောင်မြင်ပါ',
    'bookingHistory.paymentPending': 'ငွေပေးချေရန် လိုအပ်သည်',
    
    // Rental Management (Burmese)
    'rentalManagement.title': 'ငှားရမ်းခြင်း စီမံခန့်ခွဲမှု',
    'rentalManagement.notEnabled': 'ငှားရမ်းခြင်းမုဒ် မဖွင့်ထားပါ',
    'rentalManagement.enableDesc': 'စာရင်းတင်ခြင်း စတင်ရန် သင့်အိမ်ခြံတွင် ငှားရမ်းခြင်းမုဒ်ကို ဒက်ရှ်ဘုတ်မှ ဖွင့်ပါ',
    'rentalManagement.live': 'တိုက်ရိုက်',
    'rentalManagement.draft': 'မူကြမ်း',
    'rentalManagement.perNight': 'တစ်ည',
    'rentalManagement.nights': 'ည',
    'rentalManagement.bedrooms': 'အိပ်ခန်း',
    'rentalManagement.listing': 'စာရင်း',
    'rentalManagement.calendar': 'ပြက္ခဒိန်',
    'rentalManagement.analytics': 'ခွဲခြမ်းစိတ်ဖြာချက်',
    'rentalManagement.bookings': 'ကြိုတင်မှာယူမှုများ',
    'rentalManagement.createFirst': 'ရရှိနိုင်မှုကို စီမံခန့်ခွဲရန် ဦးစွာ စာရင်းတင်ပါ',
    'rentalManagement.saveSuccess': 'စာရင်း အောင်မြင်စွာ သိမ်းပြီးပါပြီ',
    'rentalManagement.saveFailed': 'စာရင်း သိမ်းရာတွင် မအောင်မြင်ပါ',
    'rentalManagement.availabilityUpdated': 'ရရှိနိုင်မှု အပ်ဒိတ်ပြီးပါပြီ',
    'rentalManagement.availabilityFailed': 'ရရှိနိုင်မှု အပ်ဒိတ်ရာတွင် မအောင်မြင်ပါ',
    
    // Messaging (Burmese)
    'messaging.noMessages': 'မက်ဆေ့ခ်ျ မရှိသေးပါ',
    'messaging.startConversation': 'စကားပြောဆိုမှု စတင်ပါ!',
    'messaging.typeMessage': 'မက်ဆေ့ခ်ျ ရိုက်ပါ...',
    'messaging.loadFailed': 'မက်ဆေ့ခ်ျများ တင်ရာတွင် မအောင်မြင်ပါ',
    'messaging.sendFailed': 'မက်ဆေ့ခ်ျ ပေးပို့ရာတွင် မအောင်မြင်ပါ',
    
    // Form Validation (Burmese)
    'validation.nameRequired': 'အမည် လိုအပ်ပါသည်',
    'validation.nameMaxLength': 'အမည် စာလုံး ၁၀၀ ထက် မပိုရပါ',
    'validation.emailInvalid': 'မှန်ကန်သော အီးမေးလ်လိပ်စာ မဟုတ်ပါ',
    'validation.emailMaxLength': 'အီးမေးလ် စာလုံး ၂၅၅ ထက် မပိုရပါ',
    'validation.subjectRequired': 'အကြောင်းအရာ လိုအပ်ပါသည်',
    'validation.subjectMaxLength': 'အကြောင်းအရာ စာလုံး ၂၀၀ ထက် မပိုရပါ',
    'validation.messageRequired': 'မက်ဆေ့ခ်ျ လိုအပ်ပါသည်',
    'validation.messageMaxLength': 'မက်ဆေ့ခ်ျ စာလုံး ၂၀၀၀ ထက် မပိုရပါ',
    
    // QR Payment Dialog
    'qrPayment.notAvailable': 'ငွေပေးချေမှု မရနိုင်ပါ',
    'qrPayment.notSetUp': 'ဤအိမ်ခြံမြေအတွက် {method} ငွေပေးချေမှု မဖွင့်ထားပါ။',
    'qrPayment.contactManager': 'အိမ်ခြံမြေမန်နေဂျာကို ဆက်သွယ်ပါ သို့မဟုတ် အခြားငွေပေးချေနည်းကို အသုံးပြုပါ။',
    'qrPayment.payWith': '{method} ဖြင့် ပေးချေရန်',
    'qrPayment.amountToPay': 'ပေးချေရမည့်ပမာဏ',
    'qrPayment.copyAmount': 'ပမာဏကူးယူရန်',
    'qrPayment.howToPay': 'ပေးချေနည်း:',
    'qrPayment.step1': 'သင့် {method} အက်ပ်ကို ဖွင့်ပါ',
    'qrPayment.step2': '"QR စကင်" ကိုနှိပ်ပြီး အထက်ပါကုဒ်ကို စကင်ဖတ်ပါ',
    'qrPayment.step3': 'တိကျသောပမာဏ ထည့်ပါ: {amount}',
    'qrPayment.step4': 'ငွေပေးချေမှုကို ပြီးဆုံးပါ',
    'qrPayment.step5': 'အတည်ပြုချက်၏ ဓာတ်ပုံရိုက်ထားပါ',
    'qrPayment.madePayment': 'ငွေပေးချေပြီးပါပြီ',
    'qrPayment.confirmPayment': 'သင့်ငွေပေးချေမှုကို အတည်ပြုပါ',
    'qrPayment.uploadScreenshot': 'ပိုမြန်ဆန်စွာ အတည်ပြုရန် ငွေပေးချေမှုအတည်ပြုချက် ဓာတ်ပုံကို တင်ပါ။',
    'qrPayment.paymentScreenshot': 'ငွေပေးချေမှုဓာတ်ပုံ (အကြံပြုချက်)',
    'qrPayment.tapToUpload': 'ဓာတ်ပုံတင်ရန် နှိပ်ပါ',
    'qrPayment.remove': 'ဖယ်ရှားရန်',
    'qrPayment.submitting': 'တင်သွင်းနေသည်...',
    'qrPayment.confirm': 'ငွေပေးချေမှုအတည်ပြုပါ',
    'qrPayment.submitted': 'ငွေပေးချေမှု တင်သွင်းပြီးပါပြီ!',
    'qrPayment.beingVerified': 'သင့်ငွေပေးချေမှုကို စစ်ဆေးနေပါသည်။ အတည်ပြုပြီးပါက အကြောင်းကြားပါမည်။',
    'qrPayment.verificationTime': 'ရုံးဖွင့်ရက်များတွင် ပုံမှန်အားဖြင့် ၁-၂၄ နာရီကြာပါသည်။',
    'qrPayment.done': 'ပြီးပါပြီ',
    'qrPayment.phoneCopied': 'ဖုန်းနံပါတ် ကူးယူပြီးပါပြီ',
    'qrPayment.amountCopied': 'ပမာဏ ကူးယူပြီးပါပြီ',
    'qrPayment.uploadImage': 'ဓာတ်ပုံဖိုင်ကို တင်ပါ',
    'qrPayment.screenshotUploaded': 'ငွေပေးချေမှုဓာတ်ပုံ တင်ပြီးပါပြီ',
    'qrPayment.uploadFailed': 'ဓာတ်ပုံတင်ခြင်း မအောင်မြင်ပါ',
    'qrPayment.submitFailed': 'ငွေပေးချေမှုတင်သွင်းခြင်း မအောင်မြင်ပါ',
    
    // Community Creator
    'communityCreator.title': 'အသစ်အသိုင်းအဝိုင်းဖန်တီးရန်',
    'communityCreator.completePaymentSetup': 'ငွေပေးချေမှုစနစ်ပြီးဆုံးရန်',
    'communityCreator.uploadQrCodes': 'ငွေပေးချေမှု QR ကုဒ်များတင်ရန်',
    'communityCreator.createButton': 'အသိုင်းအဝိုင်းဖန်တီးရန်',
    'communityCreator.communityName': 'အသိုင်းအဝိုင်းအမည်',
    'communityCreator.communityNamePlaceholder': 'ဥပမာ: ပင်လယ်မြင်ကွင်း',
    'communityCreator.streetAddress': 'လမ်းလိပ်စာ',
    'communityCreator.streetAddressPlaceholder': 'လမ်းလိပ်စာထည့်ပါ...',
    'communityCreator.city': 'မြို့',
    'communityCreator.cityPlaceholder': 'မြို့',
    'communityCreator.state': 'ပြည်နယ်',
    'communityCreator.statePlaceholder': 'ပြည်နယ်',
    'communityCreator.zipCode': 'စာပို့သင်္ကေတ',
    'communityCreator.zipCodePlaceholder': 'စာပို့သင်္ကေတ',
    'communityCreator.description': 'ဖော်ပြချက်',
    'communityCreator.descriptionPlaceholder': 'သင့်အသိုင်းအဝိုင်းအကြောင်း အကျဉ်းချုပ်...',
    'communityCreator.detailsTab': 'အသေးစိတ်',
    'communityCreator.paymentsTab': 'ငွေပေးချေမှုများ',
    'communityCreator.nextPaymentSettings': 'နောက်တစ်ဆင့်: ငွေပေးချေမှုဆက်တင်များ',
    'communityCreator.defaultCurrency': 'မူရင်းငွေကြေး',
    'communityCreator.currencyHintMMK': 'ပြည်တွင်းငွေပေးချေမှုအတွက် မြန်မာကျပ်',
    'communityCreator.currencyHintUSD': 'နိုင်ငံတကာငွေပေးချေမှုအတွက် အမေရိကန်ဒေါ်လာ',
    'communityCreator.acceptedPaymentMethods': 'လက်ခံသော ငွေပေးချေနည်းများ',
    'communityCreator.selectPaymentMethod': 'အနည်းဆုံး ငွေပေးချေနည်းတစ်ခု ရွေးချယ်ပါ',
    'communityCreator.qrCodePrompt': 'အသိုင်းအဝိုင်းဖန်တီးပြီးနောက် မိုဘိုင်းပိုက်ဆံအိတ်ငွေပေးချေမှုအတွက် QR ကုဒ်များ တင်ရန် တောင်းဆိုပါမည်။',
    'communityCreator.creating': 'ဖန်တီးနေသည်...',
    'communityCreator.completeStripeSetup': 'Stripe စနစ်ပြီးဆုံးရန်',
    'communityCreator.stripeOnboardingDescription': 'သင့်အသိုင်းအဝိုင်း ဖန်တီးပြီးပါပြီ! HOA ငွေပေးချေမှုများ လက်ခံရန် Stripe ဝန်ဆောင်မှုကို ပြီးဆုံးပါ။',
    'communityCreator.doLater': 'နောက်မှလုပ်မည်',
    'communityCreator.qrUploadDescription': 'အိမ်ရှင်များ ကြေးပေးချေနိုင်ရန် KBZPay နှင့် Wave Money QR ကုဒ်များ တင်ပါ။',
    'communityCreator.skipForNow': 'အခုတော့ ကျော်မည်',
    'communityCreator.success': 'အသိုင်းအဝိုင်း အောင်မြင်စွာ ဖန်တီးပြီးပါပြီ!',
    'communityCreator.error': 'အသိုင်းအဝိုင်းဖန်တီးခြင်း မအောင်မြင်ပါ',
    
    // Property Delete Dialog
    'propertyDelete.title': 'အိမ်ခြံမြေဖျက်ရန်',
    'propertyDelete.confirm': 'ဖျက်လိုသည်မှာ သေချာပါသလား',
    'propertyDelete.willRemove': 'ဤလုပ်ဆောင်ချက်က:',
    'propertyDelete.removeProperty': 'သင့်အကောင့်မှ အိမ်ခြံမြေကို ဖယ်ရှားပါမည်',
    'propertyDelete.deactivateListings': 'လက်ရှိ ငှားရမ်းစာရင်းများကို ပိတ်ပါမည်',
    'propertyDelete.cancelBookings': 'ဆိုင်းငံ့ထားသော ကြိုတင်စာရင်းသွင်းမှုများကို ပယ်ဖျက်ပါမည်',
    'propertyDelete.cannotUndo': 'ဤလုပ်ဆောင်ချက်ကို ပြန်ပြင်၍ မရပါ။',
    'propertyDelete.deleting': 'ဖျက်နေသည်...',
    'propertyDelete.success': 'အိမ်ခြံမြေ အောင်မြင်စွာ ဖျက်ပြီးပါပြီ',
    'propertyDelete.failed': 'အိမ်ခြံမြေဖျက်ခြင်း မအောင်မြင်ပါ',
    
    // Sale Listing Manager
    'saleListing.title': 'ရောင်းချစာရင်း',
    'saleListing.edit': 'ရောင်းချစာရင်း တည်းဖြတ်ရန်',
    'saleListing.create': 'ရောင်းချစာရင်း ဖန်တီးရန်',
    'saleListing.noProperties': 'အိမ်ခြံမြေ မရှိပါ',
    'saleListing.addPropertiesFirst': 'ဤအသိုင်းအဝိုင်းတွင် အိမ်ခြံမြေများ အရင်ထည့်ပါ၊ ထို့နောက် ရောင်းချစာရင်းသွင်းနိုင်ပါသည်။',
    'saleListing.listForSale': 'ရောင်းရန်စာရင်းသွင်း',
    'saleListing.published': 'ထုတ်ဝေပြီး',
    'saleListing.available': 'ရနိုင်သည်',
    'saleListing.pending': 'ဆိုင်းငံ့',
    'saleListing.sold': 'ရောင်းပြီး',
    'saleListing.priceRange': 'စျေးနှုန်းအပိုင်းအခြား',
    'saleListing.description': 'ခန့်မှန်းစျေးနှုန်းအပိုင်းအခြားဖြင့် ရောင်းမည့်အိမ်ခြံမြေများ စာရင်းသွင်းပါ။ အမှန်တကယ်ရောင်းချမှုများကို အော့ဖ်လိုင်းတွင် လုပ်ဆောင်ပါသည်။',
    'saleListing.updated': 'ရောင်းချစာရင်း အပ်ဒိတ်ပြီးပါပြီ',
    'saleListing.created': 'ရောင်းချစာရင်း ဖန်တီးပြီးပါပြီ',
    'saleListing.saveFailed': 'စာရင်းသိမ်းခြင်း မအောင်မြင်ပါ',
    
    // Amenity Booking Dialog
    'amenityBooking.book': 'ကြိုတင်စာရင်းသွင်း',
    'amenityBooking.reserveSpace': 'ဧည့်သည် {capacity} ဦးအထိ ဤနေရာကို ကြိုတင်စာရင်းသွင်းပါ',
    'amenityBooking.selectDate': 'ရက်စွဲရွေးချယ်ပါ',
    'amenityBooking.startTime': 'စတင်ချိန်',
    'amenityBooking.duration': 'ကြာချိန်',
    'amenityBooking.minutes': '{minutes} မိနစ် ({endTime} အထိ)',
    'amenityBooking.guestCount': 'ဧည့်သည်အရေအတွက် (အများဆုံး {max})',
    'amenityBooking.specialRequests': 'အထူးတောင်းဆိုချက်များ (ရွေးချယ်စရာ)',
    'amenityBooking.specialPlaceholder': 'အထူးလိုအပ်ချက် သို့မဟုတ် တောင်းဆိုချက်များ...',
    'amenityBooking.confirmBooking': 'ကြိုတင်စာရင်းသွင်းမှု အတည်ပြုပါ',
    'amenityBooking.booking': 'စာရင်းသွင်းနေသည်...',
    'amenityBooking.confirmed': 'ကြိုတင်စာရင်းသွင်းမှု အတည်ပြုပြီး!',
    'amenityBooking.on': 'တွင်',
    'amenityBooking.success': '{name} အောင်မြင်စွာ ကြိုတင်စာရင်းသွင်းပြီးပါပြီ!',
    'amenityBooking.selectDateError': 'ရက်စွဲတစ်ခု ရွေးချယ်ပါ',
    'amenityBooking.fullyBooked': 'ဤအချိန် အပြည့်ရှိပြီးပါပြီ။ အများဆုံးဆံ့နိုင်မှု: {capacity}',
    'amenityBooking.spotsAvailable': 'ဤအချိန်တွင် {spots} နေရာသာ ကျန်ရှိပါသည်။ ဧည့်သည်အရေအတွက် လျှော့ပါ။',
    'amenityBooking.failed': 'သာယာစရာ ကြိုတင်စာရင်းသွင်းခြင်း မအောင်မြင်ပါ။ ထပ်ကြိုးစားပါ။',
    
    // Common additions
    'common.notAuthenticated': 'အထောက်အထားမပြနိုင်ပါ',
    'common.note': 'မှတ်ချက်',
    
    // Admin - Developer Invite
    'admin.developer.invite': 'Developer ဖိတ်ကြားရန်',
    'admin.developer.inviteTitle': 'အိမ်ခြံမြေ Developer ဖိတ်ကြားရန်',
    'admin.developer.description': 'အိမ်ခြံမြေ Developer များသည် အသိုင်းအဝိုင်းများ ဖန်တီးခြင်းနှင့် စီမံခန့်ခွဲခြင်း၊ အိမ်ခြံမြေများ၊ ဝန်ဆောင်မှုများနှင့် ရောင်းချသူများ ထည့်သွင်းနိုင်ပါသည်။',
    'admin.developer.emailLabel': 'အီးမေးလ်လိပ်စာ',
    'admin.developer.emailPlaceholder': 'developer@company.com',
    'admin.developer.nameLabel': 'အမည်အပြည့်အစုံ (ရွေးချယ်စရာ)',
    'admin.developer.namePlaceholder': 'ဂျွန် Developer',
    'admin.developer.note': 'Developer သည် အကောင့်တည်ဆောက်ရန် ညွှန်ကြားချက်များပါသော အီးမေးလ်ရရှိပါမည်။ သူတို့သည် အသိုင်းအဝိုင်းများဖန်တီးရန် admin အဆင့် ဝင်ရောက်ခွင့်ရှိပါမည်။',
    'admin.developer.sendInvite': 'ဖိတ်ကြားစာပို့ရန်',
    'admin.developer.emailRequired': 'အီးမေးလ် လိုအပ်ပါသည်',
    'admin.developer.inviteSent': 'Developer ဖိတ်ကြားစာ အောင်မြင်စွာ ပို့ပြီးပါပြီ',
    'admin.developer.inviteFailed': 'ဖိတ်ကြားစာပို့ခြင်း မအောင်မြင်ပါ',
    
    // Admin - Amenity Manager
    'admin.amenity.title': 'အသိုင်းအဝိုင်း သာယာစရာများ',
    'admin.amenity.add': 'သာယာစရာ ထည့်ရန်',
    'admin.amenity.addTitle': 'သာယာစရာ ထည့်ရန်',
    'admin.amenity.namePlaceholder': 'သာယာစရာ အမည်',
    'admin.amenity.selectCategory': 'အမျိုးအစား ရွေးချယ်ပါ',
    'admin.amenity.descriptionPlaceholder': 'ဖော်ပြချက် (ရွေးချယ်စရာ)',
    'admin.amenity.latitudePlaceholder': 'လတ္တီကျု (ဥပမာ 25.7617)',
    'admin.amenity.longitudePlaceholder': 'လောင်ဂျီကျု (ဥပမာ -80.1918)',
    'admin.amenity.coordsTip': 'အကြံပြုချက်: Google Maps တွင် တည်နေရာပေါ်တွင် right-click နှိပ်ပြီး ကိုဩဒိနိတ်များ ရှာပါ',
    'admin.amenity.addButton': 'သာယာစရာ ထည့်ရန်',
    'admin.amenity.noAmenities': 'သာယာစရာများ မရှိသေးပါ',
    'admin.amenity.addFirst': 'သင့်ပထမဆုံး အသိုင်းအဝိုင်း သာယာစရာ ထည့်ပါ',
    'admin.amenity.loadFailed': 'သာယာစရာများ ထည့်သွင်းခြင်း မအောင်မြင်ပါ',
    'admin.amenity.nameAndCoordsRequired': 'အမည်နှင့် ကိုဩဒိနိတ်များ လိုအပ်ပါသည်',
    'admin.amenity.addSuccess': 'သာယာစရာ အောင်မြင်စွာ ထည့်ပြီးပါပြီ',
    'admin.amenity.addFailed': 'သာယာစရာ ထည့်ခြင်း မအောင်မြင်ပါ',
    'admin.amenity.confirmDelete': 'ဤသာယာစရာကို ဖျက်ရန် သေချာပါသလား?',
    'admin.amenity.deleteSuccess': 'သာယာစရာ ဖျက်ပြီးပါပြီ',
    'admin.amenity.deleteFailed': 'သာယာစရာ ဖျက်ခြင်း မအောင်မြင်ပါ',
    'admin.amenity.category.pool': 'ရေကူးကန်',
    'admin.amenity.category.gym': 'ကျန်းမာရေးစင်တာ',
    'admin.amenity.category.restaurant': 'စားသောက်ဆိုင်',
    'admin.amenity.category.valet': 'ကားရပ်ဝန်ဆောင်မှု',
    'admin.amenity.category.spa': 'စပါနှင့် ကျန်းမာရေး',
    'admin.amenity.category.park': 'ပန်းခြံ / ဥယျာဉ်',
    'admin.amenity.category.clubhouse': 'ကလပ်ဟောက်စ်',
    'admin.amenity.category.other': 'အခြား',
    
    // Admin - Announcement Manager
    'admin.announcement.title': 'ကြေညာချက်များ',
    'admin.announcement.new': 'အသစ်',
    'admin.announcement.newTitle': 'ကြေညာချက်အသစ်',
    'admin.announcement.editTitle': 'ကြေညာချက် တည်းဖြတ်ရန်',
    'admin.announcement.titleLabel': 'ခေါင်းစဉ်',
    'admin.announcement.titlePlaceholder': 'ကြေညာချက် ခေါင်းစဉ်',
    'admin.announcement.contentLabel': 'အကြောင်းအရာ',
    'admin.announcement.contentPlaceholder': 'သင့်ကြေညာချက်ကို ရေးပါ...',
    'admin.announcement.priorityLabel': 'ဦးစားပေးအဆင့်',
    'admin.announcement.priority.normal': 'ပုံမှန်',
    'admin.announcement.priority.important': 'အရေးကြီး',
    'admin.announcement.priority.urgent': 'အရေးပေါ်',
    'admin.announcement.publishImmediately': 'ချက်ချင်း ထုတ်ဝေရန်',
    'admin.announcement.visibleToResidents': 'နေထိုင်သူအားလုံးအတွက် မြင်ရအောင်လုပ်ရန်',
    'admin.announcement.create': 'ကြေညာချက် ဖန်တီးရန်',
    'admin.announcement.update': 'ကြေညာချက် အပ်ဒိတ်လုပ်ရန်',
    'admin.announcement.publish': 'ထုတ်ဝေရန်',
    'admin.announcement.unpublish': 'ထုတ်ဝေမှု ပြန်ရုပ်သိမ်းရန်',
    'admin.announcement.status.published': 'ထုတ်ဝေပြီး',
    'admin.announcement.status.draft': 'မူကြမ်း',
    'admin.announcement.noAnnouncements': 'ကြေညာချက်များ မရှိပါ',
    'admin.announcement.createToInform': 'နေထိုင်သူများကို အသိပေးရန် ကြေညာချက်များ ဖန်တီးပါ',
    'admin.announcement.titleContentRequired': 'ခေါင်းစဉ်နှင့် အကြောင်းအရာ လိုအပ်ပါသည်',
    'admin.announcement.createSuccess': 'ကြေညာချက် ဖန်တီးပြီးပါပြီ',
    'admin.announcement.updateSuccess': 'ကြေညာချက် အပ်ဒိတ်လုပ်ပြီးပါပြီ',
    'admin.announcement.saveFailed': 'ကြေညာချက် သိမ်းဆည်းခြင်း မအောင်မြင်ပါ',
    'admin.announcement.deleteSuccess': 'ကြေညာချက် ဖျက်ပြီးပါပြီ',
    'admin.announcement.deleteFailed': 'ကြေညာချက် ဖျက်ခြင်း မအောင်မြင်ပါ',
    'admin.announcement.published': 'ကြေညာချက် ထုတ်ဝေပြီးပါပြီ',
    'admin.announcement.unpublished': 'ကြေညာချက် ထုတ်ဝေမှု ပြန်ရုပ်သိမ်းပြီးပါပြီ',
    'admin.announcement.updateFailed': 'ကြေညာချက် အပ်ဒိတ်လုပ်ခြင်း မအောင်မြင်ပါ',
    
    // Admin - Guest Pass Manager
    'admin.guestPass.title': 'ဧည့်သည်ပါစ် စီမံခန့်ခွဲမှု',
    'admin.guestPass.subtitle': 'လာရောက်သူအားလုံး၏ ဝင်ရောက်ခွင့် စီမံခန့်ခွဲပါ',
    'admin.guestPass.status.active': 'အသက်ဝင်',
    'admin.guestPass.status.expired': 'သက်တမ်းကုန်',
    'admin.guestPass.status.revoked': 'ပြန်ရုပ်သိမ်းပြီး',
    'admin.guestPass.noGuestPasses': 'ဧည့်သည်ပါစ်များ မတွေ့ပါ',
    'admin.guestPass.unknownGuest': 'မသိသော ဧည့်သည်',
    'admin.guestPass.expires': 'သက်တမ်းကုန်',
    'admin.guestPass.extend': 'သက်တမ်းတိုး',
    'admin.guestPass.reactivate': 'ပြန်ဖွင့်ရန် (၇ ရက်)',
    'admin.guestPass.extendTitle': 'ဧည့်သည် ဝင်ရောက်ခွင့် သက်တမ်းတိုးရန်',
    'admin.guestPass.guestLabel': 'ဧည့်သည်',
    'admin.guestPass.newEndDate': 'ပြီးဆုံးရက် အသစ်',
    'admin.guestPass.selectEndDate': 'ပြီးဆုံးရက် အသစ် ရွေးချယ်ပါ',
    'admin.guestPass.extendAccess': 'ဝင်ရောက်ခွင့် သက်တမ်းတိုးရန်',
    'admin.guestPass.loadFailed': 'ဧည့်သည်ပါစ်များ ထည့်သွင်းခြင်း မအောင်မြင်ပါ',
    'admin.guestPass.revoked': 'ဧည့်သည်ပါစ် ပြန်ရုပ်သိမ်းပြီးပါပြီ',
    'admin.guestPass.revokeFailed': 'ပါစ် ပြန်ရုပ်သိမ်းခြင်း မအောင်မြင်ပါ',
    'admin.guestPass.extendedTo': 'ဝင်ရောက်ခွင့် သက်တမ်းတိုးပြီးပါပြီ',
    'admin.guestPass.extendFailed': 'ဝင်ရောက်ခွင့် သက်တမ်းတိုးခြင်း မအောင်မြင်ပါ',
    'admin.guestPass.reactivated': 'ဧည့်သည်ပါစ် ၇ ရက်အတွက် ပြန်ဖွင့်ပြီးပါပြီ',
    'admin.guestPass.reactivateFailed': 'ပါစ် ပြန်ဖွင့်ခြင်း မအောင်မြင်ပါ',
    
    // Date/Time Picker
    'datePicker.pickDate': 'ရက်စွဲရွေးပါ',
    'datePicker.selectDate': 'ရက်စွဲနှင့်အချိန်ရွေးပါ',
    'datePicker.time': 'အချိန်',
    
    // Services Manager Additional
    'services.addNewService': 'ဝန်ဆောင်မှုအသစ်ထည့်ပါ',
    'services.serviceUpdated': 'ဝန်ဆောင်မှုပြင်ဆင်ပြီး',
    'services.hasBeenUpdated': 'ပြင်ဆင်ပြီးပါပြီ',
    'services.serviceCreated': 'ဝန်ဆောင်မှုဖန်တီးပြီး',
    'services.hasBeenAdded': 'ထည့်သွင်းပြီးပါပြီ',
    'services.failedToSave': 'ဝန်ဆောင်မှုသိမ်းဆည်းခြင်းမအောင်မြင်ပါ',
    'services.noVendorAssigned': 'ရောင်းချသူမသတ်မှတ်ထားပါ',
    'services.unknownVendor': 'မသိသောရောင်းချသူ',
    'services.loadingServices': 'ဝန်ဆောင်မှုများဖတ်နေသည်...',
    'services.perBedroom': 'အိပ်ခန်းတစ်ခန်းလျှင်',
    'services.perSqft': 'စတုရန်းပေတစ်ပေလျှင်',
    'services.pricing': 'စျေးနှုန်း',
    'services.min': 'မိနစ်',
    'services.active': 'အသုံးပြုနိုင်',
    'services.inactive': 'မသုံးနိုင်',
    'services.createService': 'ဝန်ဆောင်မှုဖန်တီးပါ',
    'services.updateService': 'ဝန်ဆောင်မှုပြင်ဆင်ပါ',
    
    // Service Names (from database) - Burmese
    'serviceName.poolService': 'ရေကူးကန်ဝန်ဆောင်မှု',
    'serviceName.poolMaintenance': 'ရေကူးကန်ပြုပြင်ထိန်းသိမ်းခြင်း',
    'serviceName.poolSpaMaintenance': 'ရေကူးကန်နှင့်စပါထိန်းသိမ်းခြင်း',
    'serviceName.deepCleaning': 'အဆင့်မြင့်သန့်ရှင်းရေး',
    'serviceName.deepClean': 'အဆင့်မြင့်သန့်ရှင်းရေး',
    'serviceName.regularCleaning': 'ပုံမှန်သန့်ရှင်းရေး',
    'serviceName.housekeeping': 'အိမ်သန့်ရှင်းရေး',
    'serviceName.expressHousekeeping': 'အမြန်အိမ်သန့်ရှင်းရေး',
    'serviceName.signatureHousekeeping': 'အထူးအိမ်သန့်ရှင်းရေး',
    'serviceName.fullEstateCleaning': 'အိမ်ကြီးသန့်ရှင်းရေး',
    'serviceName.weeklyMaintenanceClean': 'အပတ်စဉ်ထိန်းသိမ်းရေးသန့်ရှင်းခြင်း',
    'serviceName.turndownService': 'အိပ်ခန်းပြင်ဆင်ဝန်ဆောင်မှု',
    'serviceName.laundryService': 'အဝတ်လျှော်ဝန်ဆောင်မှု',
    'serviceName.laundry': 'အဝတ်လျှော်',
    'serviceName.laundryPressing': 'အဝတ်လျှော်နှင့်မီးပူတိုက်',
    'serviceName.carpetCleaning': 'ကော်ဇောသန့်ရှင်းရေး',
    'serviceName.windowCleaning': 'ပြတင်းပေါက်သန့်ရှင်းရေး',
    'serviceName.inHomeSpa': 'အိမ်တွင်စပါနှင့်ကျန်းမာရေး',
    'serviceName.spaWellness': 'စပါနှင့်ကျန်းမာရေး',
    'serviceName.couplesSpaDay': 'စုံတွဲစပါနေ့',
    'serviceName.couplesSpaPackage': 'စုံတွဲစပါပက်ကေ့ဂျ်',
    'serviceName.inVillaMassage': 'အိမ်တွင်အနှိပ်',
    'serviceName.swedishMassage': 'ဆွီဒင်အနှိပ်',
    'serviceName.hotStoneTherapy': 'ကျောက်ပူကုထုံး',
    'serviceName.personalTraining': 'ကိုယ်ပိုင်လေ့ကျင့်ရေး',
    'serviceName.personalTrainingSession': 'ကိုယ်ပိုင်လေ့ကျင့်ရေးသင်တန်း',
    'serviceName.privateYogaClass': 'ပုဂ္ဂလိကယောဂသင်တန်း',
    'serviceName.privateYogaSession': 'ပုဂ္ဂလိကယောဂ',
    'serviceName.massageTherapy': 'အနှိပ်ကုထုံး',
    'serviceName.massage': 'အနှိပ်',
    'serviceName.yogaSession': 'ယောဂသင်တန်း',
    'serviceName.yoga': 'ယောဂ',
    'serviceName.meditation': 'တရားထိုင်',
    'serviceName.beautyServices': 'အလှအပဝန်ဆောင်မှု',
    'serviceName.salonServices': 'ဆံပင်ဝန်ဆောင်မှု',
    'serviceName.generalMaintenance': 'အထွေထွေပြုပြင်မှု',
    'serviceName.plumbing': 'ပိုက်ပြင်',
    'serviceName.emergencyPlumbing': 'အရေးပေါ်ပိုက်ပြင်',
    'serviceName.electrical': 'လျှပ်စစ်ပြင်',
    'serviceName.hvacService': 'လေအေးစက်ဝန်ဆောင်မှု',
    'serviceName.hvac': 'လေအေးစက်',
    'serviceName.hvacMaintenance': 'လေအေးစက်ထိန်းသိမ်းခြင်း',
    'serviceName.handyman': 'အိမ်ပြင်ဆရာ',
    'serviceName.handymanServices': 'အိမ်ပြင်ဝန်ဆောင်မှု',
    'serviceName.landscaping': 'ဥယျာဉ်ပြင်ဆင်',
    'serviceName.landscapingService': 'ဥယျာဉ်ပြင်ဆင်ဝန်ဆောင်မှု',
    'serviceName.gardenLandscaping': 'ဥယျာဉ်အလှဆင်ခြင်း',
    'serviceName.gardenMaintenance': 'ဥယျာဉ်ထိန်းသိမ်းမှု',
    'serviceName.pestControl': 'ပိုးသတ်',
    'serviceName.privateChef': 'ကိုယ်ပိုင်စားဖိုမှူး',
    'serviceName.privateChefDinner': 'ကိုယ်ပိုင်စားဖိုမှူးညစာ',
    'serviceName.privateChefExperience': 'ကိုယ်ပိုင်စားဖိုမှူးအတွေ့အကြုံ',
    'serviceName.italianDinnerExperience': 'အီတလီညစာအတွေ့အကြုံ',
    'serviceName.sushiOmakase': 'ဆူရှီအိုမာကာဆဲ',
    'serviceName.winePairingDinner': 'ဝိုင်တွဲညစာ',
    'serviceName.wineSommelierService': 'ဝိုင်ကျွမ်းကျင်ဝန်ဆောင်မှု',
    'serviceName.breakfastInBed': 'အိပ်ရာပေါ်နံနက်စာ',
    'serviceName.cocktailPartyCatering': 'ကော့တေးပါတီအစားအသောက်',
    'serviceName.catering': 'အစားအသောက်ဝန်ဆောင်မှု',
    'serviceName.mealDelivery': 'အစားအစာပို့ဆောင်ခြင်း',
    'serviceName.groceryShopping': 'ကုန်စုံဝယ်ပေးခြင်း',
    'serviceName.bartenderService': 'ဘားသမားဝန်ဆောင်မှု',
    'serviceName.bartender': 'ဘားသမား',
    'serviceName.airportTransfer': 'လေဆိပ်ကြိုပို့',
    'serviceName.airportLimousine': 'လေဆိပ်လီမိုဆင်း',
    'serviceName.carRental': 'ကားငှားရမ်း',
    'serviceName.golfCartRental': 'ဂေါက်ကားငှား',
    'serviceName.chauffeurService': 'ယာဉ်မောင်းဝန်ဆောင်မှု',
    'serviceName.chauffeur': 'ယာဉ်မောင်း',
    'serviceName.valetParking': 'ကားရပ်ပေးဝန်ဆောင်မှု',
    'serviceName.valetParkingService': 'ကားရပ်ပေးဝန်ဆောင်မှု',
    'serviceName.valet': 'ကားရပ်ပေး',
    'serviceName.boatCharter': 'လှေငှား',
    'serviceName.yachtCharter': 'ရွက်သင်္ဘောငှား',
    'serviceName.yachtCharterHalfDay': 'ရွက်သင်္ဘောငှား - တစ်ဝက်နေ့',
    'serviceName.yachtDetailing': 'ရွက်သင်္ဘောအလှပြင်',
    
    // Vendor Manager Additional
    'vendor.failedToLoad': 'ရောင်းချသူများဖတ်ခြင်းမအောင်မြင်ပါ',
    'vendor.nameRequired': 'ရောင်းချသူအမည်လိုအပ်သည်',
    'vendor.addedSuccess': 'ရောင်းချသူထည့်သွင်းပြီး',
    'vendor.failedToAdd': 'ရောင်းချသူထည့်သွင်းခြင်းမအောင်မြင်ပါ',
    'vendor.updatedSuccess': 'ရောင်းချသူပြင်ဆင်ပြီး',
    'vendor.failedToUpdate': 'ရောင်းချသူပြင်ဆင်ခြင်းမအောင်မြင်ပါ',
    'vendor.confirmDelete': 'ဤရောင်းချသူကိုဖျက်မည်မှာသေချာပါသလား?',
    'vendor.deleted': 'ရောင်းချသူပိတ်ထားပြီး',
    'vendor.failedToDelete': 'ရောင်းချသူဖျက်ခြင်းမအောင်မြင်ပါ',
    'vendor.stripeOnboardingOpened': 'Stripe စာရင်းသွင်းခြင်းစာမျက်နှာဖွင့်ပြီး',
    'vendor.failedToStartOnboarding': 'Stripe စာရင်းသွင်းခြင်းစတင်ခြင်းမအောင်မြင်ပါ',
    'vendor.vendorsAndProviders': 'ရောင်းချသူများနှင့် ဝန်ဆောင်မှုပေးသူများ',
    'vendor.addNewVendor': 'ရောင်းချသူအသစ်ထည့်ပါ',
    'vendor.enterVendorName': 'ရောင်းချသူအမည်ရိုက်ထည့်ပါ',
    'vendor.contactEmail': 'ဆက်သွယ်ရန်အီးမေးလ်',
    'vendor.emailNotification': 'ငွေပေးချေမှုနှင့် ကြိုတင်မှာယူမှုအကြောင်းကြားချက်များအတွက်',
    'vendor.platformKeeps': 'ပလက်ဖောင်းက ထိန်းသိမ်းသည်',
    'vendor.notConnected': 'မချိတ်ဆက်ထားပါ',
    'vendor.fullSetup': 'အပြည့်အဝတပ်ဆင်ပြီး',
    'vendor.stripeOnly': 'Stripe သာ',
    'vendor.qrOnly': 'QR သာ',
    'vendor.noVendorsDesc': 'ဝန်ဆောင်မှုများပေးရန် ရောင်းချသူများထည့်ပါ',
    'vendor.updateStripeAccount': 'Stripe အကောင့်ပြင်ဆင်ပါ',
    'vendor.connectStripeAccount': 'Stripe အကောင့်ချိတ်ဆက်ပါ',
    'vendor.editDetails': 'ရောင်းချသူပြင်ဆင်ပါ',
    'vendor.qrCodes': 'QR ကုဒ်များ',
    
    // Guest Preferences Quiz (Burmese)
    'quiz.saved': 'ဦးစားပေးချက်များ သိမ်းဆည်းပြီးပါပြီ!',
    'quiz.savedDesc': 'သင့်ဦးစားပေးချက်များအပေါ် အခြေခံ၍ ငှားရမ်းမှုရှာဖွေခြင်းကို ကိုယ်ပိုင်ပြုလုပ်ပေးပါမည်။',
    'quiz.saveFailed': 'ဦးစားပေးချက်များ သိမ်းဆည်းခြင်း မအောင်မြင်ပါ',
    'quiz.howTravel': 'ခရီးသွားရာတွင် သင်ဘာကို နှစ်သက်ပါသလဲ?',
    'quiz.selectAll': 'သက်ဆိုင်သမျှ ရွေးချယ်ပါ',
    'quiz.whatBrings': 'ဘာကြောင့် ဒီကို လာတာပါလဲ?',
    'quiz.whoTraveling': 'သင်နှင့်အတူ ဘယ်သူတွေ ခရီးသွားပါသလဲ?',
    'quiz.selectGroupSize': 'သင့်ပုံမှန် အုပ်စုအရွယ်အစား ရွေးချယ်ပါ',
    'quiz.stayLength': 'ပုံမှန် တည်းခို ကြာချိန်',
    'quiz.nights': 'ည',
    'quiz.whatBudget': 'သင့်ဘတ်ဂျက် ဘယ်လောက်ပါလဲ?',
    'quiz.perNightPref': 'တစ်ညလျှင် နှုန်း ဦးစားပေးချက်',
    'quiz.mustHaveAmenities': 'မရှိမဖြစ် အဆင်ပြေမှုများ',
    'quiz.selectEssentials': 'သင့် မရှိမဖြစ်များ ရွေးချယ်ပါ',
    'quiz.petFriendly': 'အိမ်မွေးတိရစ္ဆာန် ခွင့်ပြု',
    'quiz.petFriendlyDesc': 'အိမ်မွေးတိရစ္ဆာန်များနှင့် ခရီးသွားခြင်း',
    'quiz.accessibility': 'သုံးစွဲနိုင်မှု',
    'quiz.accessibilityDesc': 'သုံးစွဲနိုင်မှု အင်္ဂါရပ်များ လိုအပ်သည်',
    'quiz.personalizeExperience': 'သင့်အတွေ့အကြုံကို ကိုယ်ပိုင်ပြုလုပ်ပါ',
    'quiz.updatePreferences': 'ဦးစားပေးချက်များ အပ်ဒိတ်လုပ်ပါ',
    'quiz.helpFind': 'သင့်အတွက် ပြည့်စုံသော အိမ်ခြံမြေများ ရှာဖွေရန် ကူညီပါ',
    'quiz.skipForNow': 'အခုတော့ ကျော်မည်',
    'quiz.savePreferences': 'ဦးစားပေးချက်များ သိမ်းဆည်းပါ',
    'quiz.style.short_trip': 'ရေတိုခရီးများ',
    'quiz.style.short_tripDesc': '၁-၃ ည တည်းခိုမှု',
    'quiz.style.long_term': 'ရေရှည် တည်းခိုမှု',
    'quiz.style.long_termDesc': 'ရက်သတ္တပတ် သို့မဟုတ် လများ',
    'quiz.style.luxury': 'ဇိမ်ခံ အပန်းဖြေခရီး',
    'quiz.style.luxuryDesc': 'ပရီမီယံ အတွေ့အကြုံများ',
    'quiz.style.budget': 'ဘတ်ဂျက်သက်သာ',
    'quiz.style.budgetDesc': 'တန်ဖိုးကို ဦးစားပေး',
    'quiz.purpose.vacation': 'အပန်းဖြေခရီး',
    'quiz.purpose.business': 'စီးပွားရေး',
    'quiz.purpose.relocation': 'နေရာပြောင်းခြင်း',
    'quiz.purpose.remote_work': 'အဝေးမှ အလုပ်',
    'quiz.group.solo': 'တစ်ယောက်ထဲ',
    'quiz.group.couple': 'စုံတွဲ',
    'quiz.group.family': 'မိသားစု',
    'quiz.group.group': 'အုပ်စု',
    'quiz.budget.budget': 'ဘတ်ဂျက်',
    'quiz.budget.moderate': 'အလယ်အလတ်',
    'quiz.budget.luxury': 'ဇိမ်ခံ',
    'quiz.budget.ultra_luxury': 'အထွတ်အထိပ် ဇိမ်ခံ',
    'quiz.amenity.wifi': 'WiFi',
    'quiz.amenity.parking': 'ကားရပ်နားစရာ',
    'quiz.amenity.pool': 'ရေကူးကန်',
    'quiz.amenity.gym': 'ကိုယ်ကာယလေ့ကျင့်ခန်း',
    'quiz.amenity.kitchen': 'မီးဖိုချောင်',
    'quiz.amenity.air_conditioning': 'လေအေး',
    'quiz.amenity.tv': 'စမတ်TV',
    'quiz.amenity.hot_tub': 'ရေပူကန်',
    'quiz.amenity.garden': 'ဥယျာဉ်',
    'quiz.amenity.security': 'လုံခြုံရေး',
    
    // Payment Method Selector (Burmese)
    'payment.totalAmount': 'စုစုပေါင်း ပမာဏ',
    'payment.myanmarOptions': 'မြန်မာ ငွေပေးချေမှု ရွေးချယ်စရာများ',
    'payment.localPayment': 'ပြည်တွင်း ငွေပေးချေမှု',
    'payment.internationalCards': 'နိုင်ငံတကာ ကတ်များ',
    'payment.processing': 'လုပ်ဆောင်နေသည်...',
    'payment.payWith': '{method} ဖြင့် ပေးချေပါ',
    'payment.selectMethod': 'ငွေပေးချေနည်း ရွေးချယ်ပါ',
    'payment.securityNote': 'သင့်ငွေပေးချေမှုကို အစအဆုံး ကုဒ်ဝှက်ခြင်းဖြင့် လုံခြုံစေသည်',
    
    // Amenity Booking Manager - Admin
    'amenityBooking.reservations': 'အဆောက်အအုံ ကြိုတင်စာရင်းများ',
    'amenityBooking.allStatus': 'အခြေအနေအားလုံး',
    'amenityBooking.statusConfirmed': 'အတည်ပြုပြီး',
    'amenityBooking.statusCancelled': 'ပယ်ဖျက်ပြီး',
    'amenityBooking.statusCompleted': 'ပြီးဆုံးပြီး',
    'amenityBooking.statusPending': 'စောင့်ဆိုင်းဆဲ',
    'amenityBooking.noBookings': 'ကြိုတင်စာရင်း မတွေ့ပါ',
    'amenityBooking.noBookingsDesc': 'နေထိုင်သူများ ကြိုတင်စာရင်းသွင်းသောအခါ ဤနေရာတွင် ပေါ်လာမည်',
    'amenityBooking.failedToLoad': 'ကြိုတင်စာရင်းများ ဖွင့်မရပါ',
    'amenityBooking.failedToUpdate': 'ကြိုတင်စာရင်း အပ်ဒိတ်မအောင်မြင်ပါ',
    'amenityBooking.bookingUpdated': 'ကြိုတင်စာရင်း အပ်ဒိတ်ပြီးပါပြီ',
    'amenityBooking.amenity': 'အဆောက်အအုံ',
    'amenityBooking.dateTime': 'ရက်စွဲနှင့်အချိန်',
    'amenityBooking.guest': 'ဧည့်သည်',
    'amenityBooking.status': 'အခြေအနေ',
    'amenityBooking.actions': 'လုပ်ဆောင်ချက်များ',
    'amenityBooking.filterStatus': 'အခြေအနေ စစ်ထုတ်ရန်',
    
    // Common additional
    'common.guest': 'ဧည့်သည်',
    'common.guests': 'ဧည့်သည်များ',
    
    // New Pricing Plans - Explorer (Burmese)
    'pricing.explorer.name': 'Explorer',
    'pricing.explorer.desc': 'ထူးခြားသော တည်းခိုခန်းများ ရှာဖွေနေသော ခရီးသွားများအတွက်',
    'pricing.explorer.feature1': 'ဇိမ်ခံ ရေတိုငှားရမ်းခြင်း ကြိုတင်စာရင်းသွင်းပါ',
    'pricing.explorer.feature2': 'ရေရှည် ငှားရမ်းခြင်း ကြိုတင်စာရင်းသွင်းပါ',
    'pricing.explorer.feature3': 'AI ဧည့်ခံဝန်ဆောင်မှု',
    'pricing.explorer.feature4': 'အိမ်ရှင်များနှင့် လုံခြုံစွာ ဆက်သွယ်ပါ',
    'pricing.explorer.feature5': 'ဦးစားပေး ကြိုတင်စာရင်းသွင်းခြင်း ပံ့ပိုးမှု',
    
    // New Pricing Plans - Sovereign (Burmese)
    'pricing.sovereign.name': 'Sovereign',
    'pricing.sovereign.desc': 'သူတို့၏ အိမ်ခြံမြေများ ငှားရမ်းရန် အဆင်သင့်ဖြစ်သော ပိုင်ရှင်များအတွက်',
    'pricing.sovereign.feature1': 'ရေတိုငှားရမ်းခြင်းအတွက် အိမ်ခြံမြေစာရင်းသွင်းပါ',
    'pricing.sovereign.feature2': 'ရေရှည်ငှားရမ်းခြင်းအတွက် အိမ်ခြံမြေစာရင်းသွင်းပါ',
    'pricing.sovereign.feature3': 'ပြောင်းလဲနိုင်သော ဈေးနှုန်းကိရိယာများ',
    'pricing.sovereign.feature4': 'ဧည့်သည်စီမံခန့်ခွဲမှု ဒက်ရှ်ဘုတ်',
    'pricing.sovereign.feature5': 'ဝင်ငွေခွဲခြမ်းစိတ်ဖြာမှု',
    'pricing.sovereign.feature6': 'ဦးစားပေး အိမ်ရှင်ပံ့ပိုးမှု',
    
    // New Pricing Plans - Architect (Burmese)
    'pricing.architect.name': 'Architect',
    'pricing.architect.desc': 'ရပ်ကွက်များ စီမံခန့်ခွဲနေသော ဖွံ့ဖြိုးရေးသမားများအတွက်',
    'pricing.architect.feature1': 'ရပ်ကွက်များ ဖန်တီးပြီး စီမံခန့်ခွဲပါ',
    'pricing.architect.feature2': 'အိမ်ပိုင်ရှင်များကို ဖိတ်ကြားပြီး စီမံခန့်ခွဲပါ',
    'pricing.architect.feature3': 'HOA ငွေပေးချေမှု ကောက်ခံခြင်း',
    'pricing.architect.feature4': 'ရောင်းချသူစျေးကွက် စီမံခန့်ခွဲမှု',
    'pricing.architect.feature5': 'ရပ်ကွက်ကြေညာချက်များ',
    'pricing.architect.feature6': 'အဆင့်မြင့် ခွဲခြမ်းစိတ်ဖြာမှု ဒက်ရှ်ဘုတ်',
    'pricing.architect.feature7': 'စိတ်ကြိုက် အမှတ်တံဆိပ်',
    
    // Pricing Modal & Common (Burmese)
    'pricing.monthly': 'လစဉ်',
    'pricing.annual': 'နှစ်စဉ်',
    'pricing.save': '{percent}% သက်သာ',
    'pricing.trialDays': '{days} ရက် အခမဲ့ စမ်းသုံးခြင်း',
    'pricing.startTrial': 'အခမဲ့ စမ်းသုံးရန် စတင်ပါ',
    'pricing.subscribe': 'ယခု စာရင်းသွင်းပါ',
    'pricing.securePayment': 'Stripe မှ လုံခြုံစွာ ငွေပေးချေမှု',
    'pricing.redirecting': 'ငွေပေးချေခြင်းသို့ ညွှန်းနေသည်...',
    'pricing.error': 'စာရင်းသွင်းခြင်း မအောင်မြင်ပါ။ ထပ်မံ ကြိုးစားပါ။',
    
    // Pricing Triggers (Burmese)
    'pricing.trigger.rentalMode': 'သင့်အိမ်ခြံမြေ ငှားရန် စာရင်းသွင်းပါ',
    'pricing.trigger.booking': 'ဤအိမ်ခြံမြေကို ကြိုတင်စာရင်းသွင်းရန် စာရင်းသွင်းပါ',
    'pricing.trigger.community': 'ရပ်ကွက်များ ဖန်တီးပြီး စီမံခန့်ခွဲရန် စာရင်းသွင်းပါ',
    'pricing.trigger.default': 'သင့်အကောင့် အဆင့်မြှင့်ပါ',
    'landing.pricing.perYear': 'နှစ်',
    'landing.pricing.billingMonthly': 'လစဉ်',
    'landing.pricing.billingAnnual': 'နှစ်စဉ်',
    
    // Pricing Page (Burmese)
    'pricingPage.backToHome': 'ပင်မစာမျက်နှာသို့',
    'pricingPage.title': 'ရိုးရှင်း ပွင့်လင်းသော စျေးနှုန်း',
    'pricingPage.subtitle': 'သင့်လိုအပ်ချက်နှင့် ကိုက်ညီသော အစီအစဉ်ကို ရွေးပါ။ ဝှက်ထားသော ကုန်ကျစရိတ် မရှိပါ။',
    'pricingPage.saveUpTo': '၁၇% သက်သာ',
    'pricingPage.compareFeatures': 'လုပ်ဆောင်ချက်များ နှိုင်းယှဉ်ပါ',
    'pricingPage.feature': 'လုပ်ဆောင်ချက်',
    'pricingPage.feature.bookRentals': 'အဆင့်မြင့် ငှားရမ်းမှုများ ကြိုတင်မှာကြားပါ',
    'pricingPage.feature.aiConcierge': 'AI ဧည့်ခံ လက်ထောက်',
    'pricingPage.feature.secureMessaging': 'လုံခြုံသော မက်ဆေ့ခ်ျ',
    'pricingPage.feature.listProperty': 'အိမ်ခြံမြေ ငှားရန် တင်ပါ',
    'pricingPage.feature.guestManagement': 'ဧည့်သည် စီမံခန့်ခွဲမှု ဒက်ရှ်ဘုတ်',
    'pricingPage.feature.revenueAnalytics': 'ဝင်ငွေ ခွဲခြမ်းစိတ်ဖြာမှု',
    'pricingPage.feature.createCommunities': 'ရပ်ကွက်များ ဖန်တီးပါ',
    'pricingPage.feature.inviteHomeowners': 'အိမ်ပိုင်ရှင်များကို ဖိတ်ကြားပြီး စီမံခန့်ခွဲပါ',
    'pricingPage.feature.hoaCollection': 'HOA ငွေပေးချေမှု ကောက်ခံခြင်း',
    'pricingPage.feature.vendorManagement': 'ရောင်းချသူ စျေးကွက်',
    'pricingPage.feature.customBranding': 'စိတ်ကြိုက် အမှတ်တံဆိပ်',
    'pricingPage.contactSalesTitle': 'အရောင်းဌာန ဆက်သွယ်ပါ',
    'pricingPage.contactSalesSubtitle': 'Sovereign သို့မဟုတ် Architect အစီအစဉ်တွင် စိတ်ဝင်စားပါသလား။ ကျွန်ုပ်တို့အဖွဲ့နှင့် ဆက်သွယ်ပါ။',
    'pricingPage.yourName': 'သင့်အမည်',
    'pricingPage.namePlaceholder': 'ကို/မ အမည်',
    'pricingPage.yourEmail': 'သင့်အီးမေးလ်',
    'pricingPage.emailPlaceholder': 'name@example.com',
    'pricingPage.companyOptional': 'ကုမ္ပဏီ (ရွေးချယ်နိုင်)',
    'pricingPage.companyPlaceholder': 'သင့်ကုမ္ပဏီအမည်',
    'pricingPage.planInterest': 'စိတ်ဝင်စားသော အစီအစဉ်',
    'pricingPage.homeownerPlan': 'Sovereign (အိမ်ပိုင်ရှင်)',
    'pricingPage.developerPlan': 'Architect (တိုးတက်ရေးကုမ္ပဏီ)',
    'pricingPage.tellUsMore': 'ပိုပြောပြပါ',
    'pricingPage.messagePlaceholder': 'သင့်လိုအပ်ချက်များ၊ အိမ်ခြံမြေ ပိုင်ဆိုင်မှု သို့မဟုတ် မေးခွန်းများ ပြောပြပါ...',
    'pricingPage.sending': 'ပို့နေသည်...',
    'pricingPage.sendInquiry': 'စုံစမ်းမှု ပို့ပါ',
    'pricingPage.inquirySent': 'သင့်စုံစမ်းမှု ပို့လိုက်ပြီ! ကျွန်ုပ်တို့ အရောင်းအဖွဲ့မှ မကြာမီ ဆက်သွယ်ပါမည်။',
    'pricingPage.inquiryFailed': 'စုံစမ်းမှု ပို့၍ မရပါ။ ထပ်ကြိုးစားပါ။',
    'pricing.inviteRequired': 'ရပ်ကွက် စီမံခန့်ခွဲသူထံမှ ဖိတ်ကြားစာ လိုအပ်သည်',
    'pricing.enterpriseOnboarding': 'လုပ်ငန်း ဝင်ရောက်ခြင်း လိုအပ်သည်',
    
    // Support Page - Sales Inquiry (Burmese)
    'support.salesInquiryTitle': 'အိမ်ပိုင်ရှင်နှင့် တိုးတက်ရေးကုမ္ပဏီ စာရင်းသွင်းခြင်း',
    'support.salesInquirySubtitle': 'သင့်အိမ်ခြံမြေ တင်ရန် သို့မဟုတ် ရပ်ကွက် စီမံခန့်ခွဲရန် စိတ်ဝင်စားပါသလား။ ကျွန်ုပ်တို့ အရောင်းအဖွဲ့နှင့် ဆက်သွယ်ပါ။',
    'support.homeownerPlan': 'အိမ်ပိုင်ရှင် (Sovereign)',
    'support.homeownerPlanDesc': 'သင့်အိမ်ခြံမြေကို ရေတို သို့မဟုတ် ရေရှည် ငှားရန် တင်ပါ',
    'support.developerPlan': 'တိုးတက်ရေးကုမ္ပဏီ (Architect)',
    'support.developerPlanDesc': 'အိမ်ခြံမြေ အများအပြား ပါဝင်သော ရပ်ကွက်များ ဖန်တီးပြီး စီမံခန့်ခွဲပါ',
    'support.companyOptional': 'ကုမ္ပဏီ (ရွေးချယ်နိုင်)',
    'support.companyPlaceholder': 'သင့်ကုမ္ပဏီအမည်',
    'support.tellUsMore': 'ပိုပြောပြပါ',
    'support.salesMessagePlaceholder': 'သင့်အိမ်ခြံမြေ၊ ရပ်ကွက်အရွယ်အစား သို့မဟုတ် မေးခွန်းများ ပြောပြပါ...',
    'support.submitSalesInquiry': 'အရောင်း စုံစမ်းမှု တင်ပါ',
    'support.salesInquirySent': 'ကျေးဇူးတင်ပါသည်! ကျွန်ုပ်တို့ အရောင်းအဖွဲ့မှ ၂၄ နာရီအတွင်း ဆက်သွယ်ပါမည်။',
    'support.salesInquiryFailed': 'စုံစမ်းမှု တင်၍ မရပါ။ ထပ်ကြိုးစားပါ။',
    'support.otherWaysToReach': 'အခြား ဆက်သွယ်နည်းများ',
    
    // SmartHome Guide Page (Burmese)
    'smartHome.guide.lighting': 'မီး',
    'smartHome.guide.lightingDesc': 'စမတ်မီးများနှင့် ဖွဲ့စည်းပုံများကို ထိန်းချုပ်ပါ',
    'smartHome.guide.smartLocks': 'စမတ်သော့ခလောက်များ',
    'smartHome.guide.smartLocksDesc': 'တံခါးများကို သော့ခတ်/ဖွင့်ပါ (ဖွင့်ထားလျှင်)',
    'smartHome.guide.doorbell': 'တံခါးခေါင်းလောင်း',
    'smartHome.guide.doorbellDesc': 'တံခါးမှာ ဘယ်သူရှိလဲ ကြည့်ပါ',
    'smartHome.guide.voiceCommands': 'အသံ ညွှန်ကြားချက်များ',
    'smartHome.guide.voiceCommandsNote': 'အိမ်ခြံမြေတွင် Google Nest စပီကာများ ရှိပါက ဤညွှန်ကြားချက်များကို စမ်းကြည့်ပါ:',
    'smartHome.guide.appleHomeKit': 'Apple HomeKit အခြားရွေးချယ်စရာ',
    'smartHome.guide.appleHomeKitDesc': 'Apple စနစ်ကိုသာ အသုံးပြုသော အိမ်ခြံမြေများအတွက်',
    'smartHome.guide.howToShareHomeKit': 'ဧည့်သည်များနှင့် HomeKit ဝင်ရောက်ခွင့် မျှဝေနည်း',
    'smartHome.guide.helpfulResources': 'အသုံးဝင်သော အရင်းအမြစ်များ',
    'smartHome.guide.homeKitNote': 'HomeKit အတွက် ဧည့်သည်များတွင် Apple ID နှင့် iOS/macOS စက်ပစ္စည်း ရှိရန် လိုအပ်သည်။ Google Home သည် ပိုမိုများပြားသော စက်ပစ္စည်းအမျိုးအစားများကို ပံ့ပိုးပြီး ပလက်ဖောင်းများတွင် အလုပ်လုပ်သည်။',
    
    // AI Page (Burmese)
    'aiPage.title': 'Aether AI',
    'aiPage.subtitle': 'သင်၏ ကိုယ်ပိုင်လက်ထောက်',
    'aiPage.welcome': 'မင်္ဂလာပါ! ကျွန်ုပ်သည် သင်၏ AI ဧည့်ခံဖြစ်သည်။ ကျွန်ုပ် ကူညီနိုင်သည်:',
    'aiPage.help.booking': 'ဝန်ဆောင်မှုများနှင့် ချိန်းဆိုမှုများ ကြိုတင်မှာယူခြင်း',
    'aiPage.help.guestAccess': 'ဧည့်သည် ဝင်ရောက်ခွင့် စီမံခန့်ခွဲခြင်း',
    'aiPage.help.maintenance': 'အိမ်ခြံမြေ ပြုပြင်ထိန်းသိမ်းမှု တောင်းဆိုချက်များ',
    'aiPage.help.lifestyle': 'လူနေမှုပုံစံ အကြံပြုချက်များ',
    'aiPage.trySaying': 'ဒီလို ပြောကြည့်ပါ',
    'aiPage.placeholder': 'ဘာမဆို မေးပါ...',
    'aiPage.suggestion1': 'စနေနေ့အတွက် ကိုယ်ပိုင်စားဖိုမှူးကို ကြိုတင်မှာယူပါ',
    'aiPage.suggestion2': 'ရေကူးကန် ထိန်းသိမ်းမှု အချိန်ဇယားဆွဲပါ',
    'aiPage.suggestion3': 'မနက်ဖြန်အတွက် ကျွန်ုပ်၏ ဧည့်သည်ကို ခွင့်ပြုပါ',
    'aiPage.suggestion4': 'ဘာ ဝန်ဆောင်မှုများ ရနိုင်သလဲ?',
    
    // Public Sales Page (Burmese)
    'sales.priceRange': 'စျေးနှုန်း အပိုင်းအခြား',
    'sales.minBedrooms': 'အနည်းဆုံး အိပ်ခန်း',
    'sales.any': 'မည်သည့်',
    'sales.features': 'အင်္ဂါရပ်များ',
    'sales.clearFilters': 'စစ်ထုတ်မှုအားလုံး ရှင်းပါ',
    'sales.exclusiveProperties': 'သီးသန့် အိမ်ခြံမြေများ',
    'sales.propertiesForSale': 'ရောင်းရန် အိမ်ခြံမြေများ',
    'sales.discoverProperties': 'ကျွန်ုပ်တို့၏ သီးသန့် ဇိမ်ခံ အသိုင်းအဝိုင်းများတွင် ထူးခြားသော အိမ်ခြံမြေများကို ရှာဖွေပါ',
    'sales.forSale': 'ရောင်းရန်',
    'sales.sqft': 'စတုရန်းပေ',
    'sales.keyFeatures': 'အဓိက အင်္ဂါရပ်များ',
    'sales.propertyDirections': 'အိမ်ခြံမြေ လမ်းညွှန်',
    'sales.searchPlaceholder': 'တည်နေရာ သို့မဟုတ် အသိုင်းအဝိုင်းဖြင့် ရှာပါ...',
    'sales.filters': 'စစ်ထုတ်မှုများ',
    'sales.propertiesFound': 'အိမ်ခြံမြေ {count} ခု တွေ့ရှိ',
    'sales.propertyFound': 'အိမ်ခြံမြေ ၁ ခု တွေ့ရှိ',
    'sales.viewRentals': 'ငှားရမ်းမှုများ ကြည့်ရှုပါ',
    'sales.noProperties': 'သင်၏ စစ်ထုတ်မှုများနှင့် ကိုက်ညီသော အိမ်ခြံမြေ မရှိပါ',
    'sales.tryAdjusting': 'ရှာဖွေမှု သတ်မှတ်ချက်များကို ချိန်ညှိကြည့်ပါ',
    'sales.luxuryProperty': 'ဇိမ်ခံ အိမ်ခြံမြေ',
    'sales.exclusiveCommunity': 'သီးသန့် အသိုင်းအဝိုင်း',
    'sales.bed': 'အိပ်ခန်း',
    'sales.bath': 'ရေချိုးခန်း',
    'sales.parking': 'ကားပါကင်',
    'sales.built': 'တည်ဆောက်',
    'sales.moreFeatures': '+{count} ပိုမို',
    'sales.contactForDetails': 'အသေးစိတ်အတွက် ဆက်သွယ်ပါ',
    
    // GodMode Dashboard (Burmese)
    'godMode.tenantAnalytics': 'ငှားရမ်းသူ ခွဲခြမ်းစိတ်ဖြာမှု',
    'godMode.tenantAnalyticsDesc': 'အသုံးပြုမှု မက်ထရစ်များနှင့် ဝင်ငွေ ခွဲခြမ်းမှုကို ကြည့်ရှုပါ',
    'godMode.communityAdmins': 'အသိုင်းအဝိုင်း စီမံခန့်ခွဲသူများ',
    'godMode.revenue': 'ဝင်ငွေ',
    'godMode.configure': 'ပြင်ဆင်သတ်မှတ်ပါ',
    'godMode.admins': 'စီမံခန့်ခွဲသူများ',
    
    // SaleDetailPage (Burmese)
    'saleDetail.back': 'နောက်သို့',
    'saleDetail.forSale': 'ရောင်းရန်',
    'saleDetail.bedrooms': 'အိပ်ခန်းများ',
    'saleDetail.bathrooms': 'ရေချိုးခန်းများ',
    'saleDetail.parking': 'ကားပါကင်',
    'saleDetail.builtIn': 'တည်ဆောက်',
    'saleDetail.floor': 'ထပ်',
    'saleDetail.sqft': 'စတုရန်းပေ',
    'saleDetail.aboutProperty': 'ဤအိမ်ခြံမြေအကြောင်း',
    'saleDetail.keyFeatures': 'အဓိက အင်္ဂါရပ်များ',
    'saleDetail.amenities': 'အဆောင်အယုံများ',
    'saleDetail.requestInfo': 'အချက်အလက် တောင်းဆိုပါ',
    'saleDetail.name': 'အမည်',
    'saleDetail.email': 'အီးမေးလ်',
    'saleDetail.phone': 'ဖုန်း',
    'saleDetail.message': 'မက်ဆေ့ခ်ျ',
    'saleDetail.interestedMessage': 'ဤအိမ်ခြံမြေကို စိတ်ဝင်စားပါသည်...',
    'saleDetail.sendInquiry': 'စုံစမ်းမှု ပေးပို့ပါ',
    'saleDetail.sending': 'ပေးပို့နေသည်...',
    'saleDetail.listingNotFound': 'စာရင်းမတွေ့ရှိပါ',
    'saleDetail.propertyUnavailable': 'ဤအိမ်ခြံမြေ မရရှိနိုင်တော့ပါ။',
    'saleDetail.browseProperties': 'အိမ်ခြံမြေများ ကြည့်ရှုပါ',
    'saleDetail.exclusiveLocation': 'သီးသန့် တည်နေရာ',
    'saleDetail.fillNameEmail': 'သင့်အမည်နှင့် အီးမေးလ်ကို ဖြည့်ပါ',
    'saleDetail.inquirySubmitted': 'သင့်စုံစမ်းမှု တင်ပြပြီးပါပြီ! ကိုယ်စားလှယ်က မကြာမီ ဆက်သွယ်ပါမည်။',
    'saleDetail.inquiryFailed': 'စုံစမ်းမှု တင်ပြရန် မအောင်မြင်ပါ။ ထပ်ကြိုးစားပါ။',
    'saleDetail.yourFullName': 'သင့်အမည်အပြည့်အစုံ',
    'saleDetail.yourEmail': 'your@email.com',
    'saleDetail.contactWithin24h': 'အရောင်းကိုယ်စားလှယ်က ၂၄ နာရီအတွင်း ဆက်သွယ်ပါမည်။',
    
    // PropertyDirectionsPage (Burmese)
    'directions.propertyDirections': 'အိမ်ခြံမြေ လမ်းညွှန်',
    'directions.booking': 'ဘွတ်ကင်',
    'directions.confirmed': 'အတည်ပြုပြီး',
    'directions.fullPropertyAddress': 'အိမ်ခြံမြေ လိပ်စာအပြည့်အစုံ',
    'directions.copyAddress': 'လိပ်စာကူးပါ',
    'directions.openInMaps': 'မြေပုံတွင်ဖွင့်ပါ',
    'directions.getDirections': 'လမ်းညွှန်ရယူပါ',
    'directions.stayDetails': 'တည်းခို အသေးစိတ်',
    'directions.checkIn': 'ချက်အင်',
    'directions.checkOut': 'ချက်အောက်',
    'directions.after3pm': 'ညနေ ၃:၀၀ နောက်',
    'directions.before11am': 'နံနက် ၁၁:၀၀ မတိုင်မီ',
    'directions.guests': 'ဧည့်သည်များ',
    'directions.guest': 'ဧည့်သည်',
    'directions.houseRules': 'အိမ်စည်းကမ်းများ',
    'directions.smartHomeAccess': 'စမတ်ဟုမ်း အသုံးပြုခွင့်',
    'directions.confidentialInfo': 'ဤအချက်အလက်သည် လျှို့ဝှက်ဖြစ်ပြီး အခြားသူများနှင့် မျှဝေမသင့်ပါ။',
    'directions.needHelp': 'အကူအညီလိုပါသလား?',
    'directions.messageHost': 'အိမ်ရှင်ထံ မက်ဆေ့ပါ',
    'directions.contactSupport': 'ပံ့ပိုးမှုကို ဆက်သွယ်ပါ',
    'directions.bookingNotFound': 'ဘွတ်ကင်မတွေ့ရှိပါ',
    'directions.noAccess': 'ဤဘွတ်ကင်မရှိပါ သို့မဟုတ် သင့်တွင် အသုံးပြုခွင့်မရှိပါ။',
    'directions.goToBookings': 'ဘွတ်ကင်များသို့ သွားပါ',
    'directions.addressCopied': 'လိပ်စာကို ကလစ်ဘုတ်သို့ ကူးပြီးပါပြီ',
    'directions.addressOnlyConfirmed': 'အိမ်ခြံမြေလိပ်စာသည် အတည်ပြုပြီးသော ဘွတ်ကင်များအတွက်သာ ရရှိနိုင်ပါသည်',
    'directions.failedToLoad': 'အိမ်ခြံမြေ အသေးစိတ် တင်ရန် မအောင်မြင်ပါ',
    
    // Privacy Page (Burmese)
    'privacy.backToHome': 'ပင်မစာမျက်နှာသို့ ပြန်သွားရန်',
    'privacy.title': 'ကိုယ်ရေးကိုယ်တာ မူဝါဒ',
    'privacy.lastUpdated': 'နောက်ဆုံး အပ်ဒိတ်: ဒီဇင်ဘာ 2025',
    'privacy.footer': '© 2025 Aether. အိမ်ခြံမြေ စီမံခန့်ခွဲမှု ပိုမို ရိုးရှင်းစွာ။',
    'privacy.section1.title': '1. ကျွန်ုပ်တို့ စုဆောင်းသော အချက်အလက်',
    'privacy.section1.intro': 'Aether တွင်၊ သင် အကောင့် ဖန်တီးသောအခါ၊ ငွေပေးချေသောအခါ သို့မဟုတ် ပံ့ပိုးကူညီမှု တောင်းခံသောအခါ ကဲ့သို့ သင် တိုက်ရိုက် ပေးသော အချက်အလက်များကို ကျွန်ုပ်တို့ စုဆောင်းပါသည်။ ဤတွင် ပါဝင်နိုင်သည်:',
    'privacy.section1.item1': 'အမည်နှင့် ဆက်သွယ်ရန် အချက်အလက် (အီးမေးလ်၊ ဖုန်းနံပါတ်)',
    'privacy.section1.item2': 'အိမ်ခြံမြေ အသေးစိတ်နှင့် လိပ်စာများ',
    'privacy.section1.item3': 'ငွေပေးချေမှု အချက်အလက် (Stripe မှတစ်ဆင့် လုံခြုံစွာ လုပ်ဆောင်)',
    'privacy.section1.item4': 'ဆက်သွယ်ရေး ဦးစားပေးမှုများ',
    'privacy.section1.item5': 'အသုံးပြုမှု ဒေတာနှင့် ခွဲခြမ်းစိတ်ဖြာမှု',
    'privacy.section2.title': '2. သင့် အချက်အလက်ကို ကျွန်ုပ်တို့ မည်သို့ အသုံးပြုသနည်း',
    'privacy.section2.intro': 'ကျွန်ုပ်တို့ စုဆောင်းသော အချက်အလက်များကို အောက်ပါအတိုင်း အသုံးပြုပါသည်:',
    'privacy.section2.item1': 'ကျွန်ုပ်တို့ ဝန်ဆောင်မှုများကို ပံ့ပိုးရန်၊ ထိန်းသိမ်းရန်နှင့် တိုးတက်စေရန်',
    'privacy.section2.item2': 'ငွေပေးငွေယူများကို လုပ်ဆောင်ပြီး ဆက်စပ် အကြောင်းကြားချက်များ ပို့ရန်',
    'privacy.section2.item3': 'သင့် မှတ်ချက်များ၊ မေးခွန်းများနှင့် ပံ့ပိုးမှု တောင်းဆိုချက်များကို တုံ့ပြန်ရန်',
    'privacy.section2.item4': 'နည်းပညာ အသိပေးချက်များ၊ အပ်ဒိတ်များနှင့် လုံခြုံရေး သတိပေးချက်များ ပို့ရန်',
    'privacy.section2.item5': 'ထုတ်ကုန်များ၊ ဝန်ဆောင်မှုများနှင့် ပွဲများ အကြောင်း သင်နှင့် ဆက်သွယ်ရန်',
    'privacy.section3.title': '3. စာရင်းသွင်း အဆင့်များနှင့် ဒေတာ ကိုင်တွယ်မှု',
    'privacy.section3.intro': 'သင့် စာရင်းသွင်း အဆင့်အပေါ် မူတည်၍ ဒေတာကို ကျွန်ုပ်တို့ ကိုင်တွယ်ပါသည်:',
    'privacy.section3.explorer': 'Explorer (ဧည့်သည်များ)',
    'privacy.section3.explorerDesc': '15 ရက် အစမ်းသုံးကာလနှင့် နောက်ပိုင်း ကြိုတင်မှာယူမှု မှတ်တမ်း၊ ဦးစားပေးမှုများ၊ ငွေပေးချေမှု မှတ်တမ်းများ',
    'privacy.section3.sovereign': 'Sovereign (အိမ်ပိုင်ရှင်များ)',
    'privacy.section3.sovereignDesc': '30 ရက် အစမ်းသုံးကာလနှင့် နောက်ပိုင်း အိမ်ခြံမြေ ဒေတာ၊ ငှားရမ်းစာရင်းများ၊ ဧည့်သည် စီမံခန့်ခွဲမှု၊ ဝင်ငွေ ခွဲခြမ်းစိတ်ဖြာမှု',
    'privacy.section3.architect': 'Architect (တိုးတက်ရေးကုမ္ပဏီများ)',
    'privacy.section3.architectDesc': 'အသိုင်းအဝိုင်း ဒေတာ၊ အိမ်ပိုင်ရှင် စီမံခန့်ခွဲမှု၊ HOA ငွေပေးချေမှုများ၊ ရောင်းချသူ ဆက်ဆံရေးများ',
    'privacy.section4.title': '4. ဖိတ်ကြားချက်ဖြင့်သာ ဝင်ရောက်ခွင့်',
    'privacy.section4.intro': 'အချို့ စာရင်းသွင်း အဆင့်များအတွက်၊ ဝင်ရောက်ခွင့်သည် ဖိတ်ကြားချက်ဖြင့်သာ ဖြစ်ပါသည်:',
    'privacy.section4.item1': 'အိမ်ပိုင်ရှင်များ (Sovereign) ကို အသိုင်းအဝိုင်း စီမံခန့်ခွဲသူများက ဖိတ်ကြားပါသည်',
    'privacy.section4.item2': 'အိမ်ခြံမြေ တိုးတက်ရေးကုမ္ပဏီများ (Architect) ကို အရောင်း တိုင်ပင်ပြီးနောက် Aether ဝန်ထမ်းများက ဖိတ်ကြားပါသည်',
    'privacy.section5.title': '5. ဒေတာ လုံခြုံရေး',
    'privacy.section5.content': 'ဒေတာ ဖလှယ်မှု အားလုံးအတွက် ဘဏ်အဆင့် 256-bit SSL ကုဒ်ဝှက်ခြင်းကို ကျွန်ုပ်တို့ အသုံးပြုပါသည်။ သင့် ဒေတာကို 99.99% uptime ရှိသော SOC 2 Type II အသိအမှတ်ပြု ဒေတာစင်တာများတွင် သိမ်းဆည်းပါသည်။ ခွင့်ပြုချက်မရှိဘဲ ဝင်ရောက်ခြင်း၊ ပြောင်းလဲခြင်း၊ ထုတ်ဖော်ခြင်း သို့မဟုတ် ဖျက်ဆီးခြင်းမှ သင့် ကိုယ်ရေးကိုယ်တာ အချက်အလက်ကို ကာကွယ်ရန် သင့်လျော်သော နည်းပညာနှင့် အဖွဲ့အစည်းဆိုင်ရာ အစီအမံများကို ကျွန်ုပ်တို့ အကောင်အထည်ဖော်ပါသည်။',
    'privacy.section6.title': '6. တတိယပါတီ ဝန်ဆောင်မှုများ',
    'privacy.section6.intro': 'ကျွန်ုပ်တို့ ပလက်ဖောင်းကို ပံ့ပိုးရန် ယုံကြည်ရသော တတိယပါတီ ဝန်ဆောင်မှုများကို အသုံးပြုပါသည်:',
    'privacy.section6.stripe': 'စာရင်းသွင်းမှုများ၊ HOA ကြေးများနှင့် ငှားရမ်းမှု ငွေပေးချေမှုများအတွက် လုံခြုံသော ငွေပေးချေမှု လုပ်ဆောင်ခြင်း',
    'privacy.section6.mapbox': 'အိမ်ခြံမြေ တည်နေရာများနှင့် အသိုင်းအဝိုင်း အဆောက်အအုံများအတွက် အပြန်အလှန် တုံ့ပြန်နိုင်သော မြေပုံများ',
    'privacy.section6.openai': 'ဝန်ဆောင်မှု ကြိုတင်မှာယူခြင်းနှင့် အကြံပြုချက်များအတွက် AI မောင်းနှင်သော ဧည့်ခံ ဝန်ဆောင်မှု',
    'privacy.section7.title': '7. ဒေတာ သိမ်းဆည်းခြင်း',
    'privacy.section7.content': 'သင့် အကောင့် အသက်ဝင်နေသရွေ့ သို့မဟုတ် ဝန်ဆောင်မှုများ ပံ့ပိုးရန် လိုအပ်သရွေ့ သင့် ဒေတာကို ကျွန်ုပ်တို့ သိမ်းဆည်းပါသည်။ စာရင်းသွင်းမှုနှင့် ငွေပေးငွေယူ ဒေတာကို အခွန်နှင့် ဥပဒေ လိုက်နာမှုအတွက် 7 နှစ် သိမ်းဆည်းပါသည်။ သင့် အကောင့်နှင့် ကိုယ်ရေးကိုယ်တာ ဒေတာကို ဖျက်ပစ်ရန် အချိန်မရွေး တောင်းဆိုနိုင်ပါသည်။',
    'privacy.section8.title': '8. သင့် အခွင့်အရေးများ',
    'privacy.section8.intro': 'သင့်တွင် အခွင့်အရေး ရှိသည်:',
    'privacy.section8.item1': 'သင့် ကိုယ်ရေးကိုယ်တာ ဒေတာ၏ မိတ္တူကို ဝင်ရောက်ကြည့်ရှုပြီး ရယူရန်',
    'privacy.section8.item2': 'မှားယွင်းသော ကိုယ်ရေးကိုယ်တာ ဒေတာကို ပြင်ဆင်ရန်',
    'privacy.section8.item3': 'သင့် ကိုယ်ရေးကိုယ်တာ ဒေတာကို ဖျက်ပစ်ရန် တောင်းဆိုရန်',
    'privacy.section8.item4': 'သင့် ကိုယ်ရေးကိုယ်တာ ဒေတာကို လုပ်ဆောင်ခြင်းကို ကန့်ကွက်ရန်',
    'privacy.section8.item5': 'သင့် ကိုယ်ရေးကိုယ်တာ ဒေတာ လုပ်ဆောင်ခြင်းကို ကန့်သတ်ရန် တောင်းဆိုရန်',
    'privacy.section8.item6': 'ဒေတာ သယ်ယူနိုင်မှု',
    'privacy.section9.title': '9. ကျွန်ုပ်တို့ကို ဆက်သွယ်ပါ',
    'privacy.section9.content': 'ဤ ကိုယ်ရေးကိုယ်တာ မူဝါဒ အကြောင်း မေးခွန်းများ ရှိပါက ကျွန်ုပ်တို့ကို ဆက်သွယ်ပါ:',
    
    // Terms Page (Burmese)
    'terms.backToHome': 'ပင်မစာမျက်နှာသို့ ပြန်သွားရန်',
    'terms.title': 'ဝန်ဆောင်မှု စည်းမျဉ်းများ',
    'terms.lastUpdated': 'နောက်ဆုံး အပ်ဒိတ်: ဒီဇင်ဘာ 2025',
    'terms.footer': '© 2025 Aether. အိမ်ခြံမြေ စီမံခန့်ခွဲမှု ပိုမို ရိုးရှင်းစွာ။',
    'terms.section1.title': '1. စည်းမျဉ်းများ လက်ခံခြင်း',
    'terms.section1.content': 'Aether ("ဝန်ဆောင်မှု") ကို ဝင်ရောက် သို့မဟုတ် အသုံးပြုခြင်းဖြင့်၊ ဤ ဝန်ဆောင်မှု စည်းမျဉ်းများကို လိုက်နာရန် သင် သဘောတူပါသည်။ ဤ စည်းမျဉ်းများကို သင် သဘောမတူပါက ကျွန်ုပ်တို့ ဝန်ဆောင်မှုကို အသုံးမပြုပါနှင့်။ ဤ စည်းမျဉ်းများကို အချိန်မရွေး အပ်ဒိတ်လုပ်ရန် ကျွန်ုပ်တို့တွင် အခွင့်အရေး ရှိပြီး ဝန်ဆောင်မှုကို ဆက်လက် အသုံးပြုခြင်းသည် မည်သည့် ပြောင်းလဲမှုများကိုမဆို လက်ခံခြင်း ဖြစ်ပါသည်။',
    'terms.section2.title': '2. ဝန်ဆောင်မှု ဖော်ပြချက်',
    'terms.section2.content': 'Aether သည် အသိုင်းအဝိုင်း စီမံခန့်ခွဲသူများ၊ အိမ်ခြံမြေ ပိုင်ရှင်များနှင့် ဧည့်သည်များအတွက် ကိရိယာများ ပံ့ပိုးပေးသော အိမ်ခြံမြေ စီမံခန့်ခွဲမှု ပလက်ဖောင်း ဖြစ်ပါသည်။ ကျွန်ုပ်တို့ ဝန်ဆောင်မှုများတွင် ငွေပေးချေမှု လုပ်ဆောင်ခြင်း၊ ဧည့်သည် ပါစ် စီမံခန့်ခွဲခြင်း၊ ဝန်ဆောင်မှု ကြိုတင်မှာယူခြင်း၊ အသိုင်းအဝိုင်း ကြေညာချက်များ၊ ငှားရမ်း စာရင်းများနှင့် AI မောင်းနှင်သော ဧည့်ခံ ဝန်ဆောင်မှုများ ပါဝင်သော်လည်း ကန့်သတ်မထားပါ။',
    'terms.section3.title': '3. စာရင်းသွင်း အစီအစဉ်များ',
    'terms.section3.intro': 'Aether သည် စာရင်းသွင်း အဆင့် သုံးဆင့် ပေးပါသည်:',
    'terms.section3.explorer': 'Explorer ($9.99/လ)',
    'terms.section3.explorerDesc': 'ဇိမ်ခံ တည်းခိုခန်းများ ရှာဖွေသော ခရီးသွားများအတွက်။ ရေတို ငှားရမ်းမှုများ၊ ရေရှည် ငှားရမ်းမှုများ ကြိုတင်မှာယူပြီး AI ဧည့်ခံ ဝန်ဆောင်မှုများ အသုံးပြုပါ။',
    'terms.section3.sovereign': 'Sovereign ($19.99/လ)',
    'terms.section3.sovereignDesc': 'အိမ်ပိုင်ရှင်များအတွက်။ အိမ်ခြံမြေများ စာရင်းတင်ပါ၊ ဧည့်သည်များ စီမံခန့်ခွဲပါ၊ ခွဲခြမ်းစိတ်ဖြာမှုများ ကြည့်ရှုပြီး ငှားရမ်းမှုများမှ ဝင်ငွေ ရယူပါ။',
    'terms.section3.architect': 'Architect (စိတ်ကြိုက် စျေးနှုန်း)',
    'terms.section3.architectDesc': 'အိမ်ခြံမြေ တိုးတက်ရေးကုမ္ပဏီများအတွက်။ အသိုင်းအဝိုင်းများ ဖန်တီးပါ၊ အိမ်ပိုင်ရှင်များ စီမံခန့်ခွဲပါ၊ HOA ငွေပေးချေမှုများ ကောက်ခံပြီး လုပ်ငန်း အင်္ဂါရပ်များ အသုံးပြုပါ။',
    'terms.section4.title': '4. အစမ်းသုံး ကာလများ',
    'terms.section4.intro': 'ရွေးချယ်ထားသော အစီအစဉ်များအတွက် အခမဲ့ အစမ်းသုံး ရရှိနိုင်ပါသည်:',
    'terms.section4.explorer': 'Explorer',
    'terms.section4.explorerTrial': '15 ရက် အခမဲ့ အစမ်းသုံး။ စတင်ရန် ခရက်ဒစ်ကတ် မလိုအပ်ပါ။',
    'terms.section4.sovereign': 'Sovereign',
    'terms.section4.sovereignTrial': 'စီမံခန့်ခွဲသူထံမှ အသိုင်းအဝိုင်း ဖိတ်ကြားစာ ရရှိပြီးနောက် 30 ရက် အခမဲ့ အစမ်းသုံး။',
    'terms.section4.architect': 'Architect',
    'terms.section4.architectTrial': 'အစမ်းသုံး မရှိပါ။ သရုပ်ပြနှင့် စိတ်ကြိုက် စျေးနှုန်းအတွက် အရောင်းဌာနကို ဆက်သွယ်ပါ။',
    'terms.section5.title': '5. ဖိတ်ကြားချက်ဖြင့်သာ ဝင်ရောက်ခွင့်',
    'terms.section5.intro': 'အချို့ အစီအစဉ်များအတွက် ဖိတ်ကြားချက် လိုအပ်ပါသည်:',
    'terms.section5.item1': 'အိမ်ပိုင်ရှင်များ (Sovereign အစီအစဉ်) ကို အသိုင်းအဝိုင်း စီမံခန့်ခွဲသူက ဖိတ်ကြားရပါမည်',
    'terms.section5.item2': 'အိမ်ခြံမြေ တိုးတက်ရေးကုမ္ပဏီများ (Architect အစီအစဉ်) ကို အရောင်း တိုင်ပင်ပြီးနောက် Aether ဝန်ထမ်းများက ဖိတ်ကြားရပါမည်',
    'terms.section5.item3': 'ဖိတ်ကြားချက်များသည် လွှဲပြောင်း၍မရပါ၊ သတ်မှတ်ထားသော လက်ခံသူအတွက်သာ ရည်ရွယ်ပါသည်',
    'terms.section6.title': '6. အသုံးပြုသူ အကောင့်များ',
    'terms.section6.intro': 'ဝန်ဆောင်မှု၏ အချို့ အင်္ဂါရပ်များကို ဝင်ရောက်ရန် အကောင့် ဖန်တီးရပါမည်။ သင် သဘောတူသည်:',
    'terms.section6.item1': 'တိကျ၊ လက်ရှိနှင့် ပြည့်စုံသော အချက်အလက် ပေးရန်',
    'terms.section6.item2': 'သင့် အကောင့် အချက်အလက်ကို ထိန်းသိမ်းပြီး အချိန်မီ အပ်ဒိတ်လုပ်ရန်',
    'terms.section6.item3': 'သင့် စကားဝှက်နှင့် အကောင့်၏ လုံခြုံရေးကို ထိန်းသိမ်းရန်',
    'terms.section6.item4': 'သင့် အကောင့်အောက်တွင် လုပ်ဆောင်မှု အားလုံးအတွက် တာဝန်ယူရန်',
    'terms.section6.item5': 'ခွင့်ပြုချက်မရှိ အသုံးပြုမှု ရှိပါက ကျွန်ုပ်တို့ကို ချက်ချင်း အကြောင်းကြားရန်',
    'terms.section7.title': '7. အခန်းကဏ္ဍအပေါ် အခြေခံသော ဝင်ရောက်ခွင့် ထိန်းချုပ်မှု',
    'terms.section7.intro': 'Aether သည် ခွင့်ပြုချက်များကို စီမံခန့်ခွဲရန် အခန်းကဏ္ဍအပေါ် အခြေခံသော ဝင်ရောက်ခွင့်ကို အသုံးပြုပါသည်:',
    'terms.section7.admin': 'Admin (အသိုင်းအဝိုင်း စီမံခန့်ခွဲသူ)',
    'terms.section7.adminDesc': 'အိမ်ခြံမြေများ၊ အိမ်ပိုင်ရှင်များ၊ ရောင်းချသူများနှင့် အသိုင်းအဝိုင်း ဆက်တင်များကို စီမံခန့်ခွဲရန် အပြည့်အဝ ဝင်ရောက်ခွင့်',
    'terms.section7.sovereign': 'Sovereign (အိမ်ခြံမြေ ပိုင်ရှင်)',
    'terms.section7.sovereignDesc': 'ပိုင်ဆိုင်သော အိမ်ခြံမြေများ၊ HOA ငွေပေးချေမှုများ၊ ငှားရမ်းမှု စီမံခန့်ခွဲမှုနှင့် ဧည့်ခံ ဝန်ဆောင်မှုများ ဝင်ရောက်ခွင့်',
    'terms.section7.demiUser': 'Demi-User (ဧည့်သည်)',
    'terms.section7.demiUserDesc': 'မြေပုံများ၊ အခြေခံ ဝန်ဆောင်မှုများနှင့် အိမ်ရှင်များနှင့် ဆက်သွယ်ရေး အချိန်ကန့်သတ် ဝင်ရောက်ခွင့်',
    'terms.section8.title': '8. လက်ခံနိုင်သော အသုံးပြုမှု',
    'terms.section8.intro': 'သင် သဘောတူသည်:',
    'terms.section8.item1': 'မည်သည့် ဥပဒေမဲ့ ရည်ရွယ်ချက်အတွက်မျှ ဝန်ဆောင်မှုကို အသုံးမပြုရန်',
    'terms.section8.item2': 'ဝန်ဆောင်မှု၏ မည်သည့် အစိတ်အပိုင်းကိုမျှ ခွင့်ပြုချက်မရှိ ဝင်ရောက်ရန် မကြိုးစားရန်',
    'terms.section8.item3': 'ဝန်ဆောင်မှု သို့မဟုတ် ဆာဗာများကို ဝင်ရောက်စွက်ဖက် သို့မဟုတ် အနှောင့်အယှက်ပေးခြင်း မပြုရန်',
    'terms.section8.item4': 'အန္တရာယ်ရှိသော ကုဒ် သို့မဟုတ် အကြောင်းအရာ တင်ခြင်း မပြုရန်',
    'terms.section8.item5': 'မည်သူကိုမျှ သို့မဟုတ် အဖွဲ့အစည်းကိုမျှ အယောင်ဆောင်ခြင်း မပြုရန်',
    'terms.section8.item6': 'သက်ဆိုင်ရာ ဥပဒေများ သို့မဟုတ် စည်းမျဉ်းများကို ချိုးဖောက်ခြင်း မပြုရန်',
    'terms.section9.title': '9. ငွေတောင်းခံခြင်းနှင့် ပယ်ဖျက်ခြင်း',
    'terms.section9.content': 'စာရင်းသွင်း အခကြေးငွေများကို လစဉ် သို့မဟုတ် နှစ်စဉ် ကြိုတင်ပေးချေရပါသည်။ ဥပဒေအရ လိုအပ်မှုမှလွဲ၍ အခကြေးငွေ အားလုံးကို ပြန်အမ်းမည် မဟုတ်ပါ။ ဆက်တင်များရှိ Stripe Customer Portal မှတစ်ဆင့် သင့် စာရင်းသွင်းမှုကို အချိန်မရွေး ပယ်ဖျက်နိုင်ပါသည်။ သင့် ဝင်ရောက်ခွင့်သည် ငွေတောင်းခံ ကာလ အဆုံးအထိ ဆက်လက် ရှိနေပါမည်။',
    'terms.section10.title': '10. တာဝန်ယူမှု ကန့်သတ်ချက်',
    'terms.section10.content': 'ဥပဒေ ခွင့်ပြုသော အမြင့်ဆုံး အတိုင်းအတာအထိ၊ ဝန်ဆောင်မှုကို သင် အသုံးပြုခြင်း သို့မဟုတ် အသုံးပြု၍မရခြင်းကြောင့် ဖြစ်ပေါ်သော မည်သည့် သွယ်ဝိုက်၊ မတော်တဆ၊ အထူး၊ ရလဒ်ဆိုင်ရာ သို့မဟုတ် ပြစ်ဒဏ်ခံ ပျက်စီးမှုများအတွက် Aether သည် တာဝန်မရှိပါ။ ကျွန်ုပ်တို့၏ စုစုပေါင်း တာဝန်ယူမှုသည် တောင်းဆိုမှု မပြုမီ 12 လအတွင်း သင် ကျွန်ုပ်တို့ထံ ပေးချေခဲ့သော ပမာဏထက် ပိုမည် မဟုတ်ပါ။',
    'terms.section11.title': '11. ဆက်သွယ်ရန်',
    'terms.section11.content': 'ဤ စည်းမျဉ်းများ အကြောင်း မေးခွန်းများအတွက် ကျွန်ုပ်တို့ကို ဆက်သွယ်ပါ:',
    
    // Support FAQ (Burmese - Updated)
    'support.faq1Question': 'Aether တွင် မည်သို့ စာရင်းသွင်းရမည်နည်း။',
    'support.faq1Answer': 'ဧည့်သည်များသည် ကျွန်ုပ်တို့ ဝဘ်ဆိုဒ်တွင် တိုက်ရိုက် စာရင်းသွင်းပြီး 15 ရက် အခမဲ့ အစမ်းသုံးနိုင်ပါသည်။ အိမ်ပိုင်ရှင်များသည် ပါဝင်ရန် ၎င်းတို့၏ အသိုင်းအဝိုင်း စီမံခန့်ခွဲသူထံမှ ဖိတ်ကြားစာ လိုအပ်ပါသည် (30 ရက် အစမ်းသုံး)။ အိမ်ခြံမြေ တိုးတက်ရေးကုမ္ပဏီများသည် ကျွန်ုပ်တို့ အရောင်းအဖွဲ့ကို ဆက်သွယ်သင့်ပါသည်။',
    'support.faq2Question': 'စာရင်းသွင်း အစီအစဉ် တစ်ခုစီတွင် ဘာတွေ ပါဝင်သနည်း။',
    'support.faq2Answer': 'Explorer ($9.99/လ) သည် ခရီးသွားများအတွက် - ငှားရမ်းမှုများ ကြိုတင်မှာယူခြင်း၊ AI ဧည့်ခံ အသုံးပြုခြင်း။ Sovereign ($19.99/လ) သည် အိမ်ပိုင်ရှင်များအတွက် - အိမ်ခြံမြေများ စာရင်းတင်ခြင်း၊ ဧည့်သည်များ စီမံခန့်ခွဲခြင်း။ Architect (စိတ်ကြိုက် စျေးနှုန်း) သည် အသိုင်းအဝိုင်းများ စီမံခန့်ခွဲသော တိုးတက်ရေးကုမ္ပဏီများအတွက်။',
    'support.faq3Question': 'အခမဲ့ အစမ်းသုံး မည်သို့ အလုပ်လုပ်သနည်း။',
    'support.faq3Answer': 'Explorer အစီအစဉ်: 15 ရက် အခမဲ့ အစမ်းသုံး၊ ခရက်ဒစ်ကတ် မလိုအပ်ပါ။ Sovereign အစီအစဉ်: အသိုင်းအဝိုင်း ဖိတ်ကြားစာ ရရှိပြီးနောက် 30 ရက် အခမဲ့ အစမ်းသုံး။ Architect အစီအစဉ်: အစမ်းသုံး မရှိပါ - သရုပ်ပြနှင့် စိတ်ကြိုက် စျေးနှုန်းအတွက် အရောင်းဌာနကို ဆက်သွယ်ပါ။',
    'support.faq4Question': 'အိမ်ပိုင်ရှင်အဖြစ် ဖိတ်ကြားခံရန် မည်သို့ လုပ်ရမည်နည်း။',
    'support.faq4Answer': 'သင့် အသိုင်းအဝိုင်း စီမံခန့်ခွဲသူ သို့မဟုတ် အိမ်ခြံမြေ တိုးတက်ရေးကုမ္ပဏီကို ဆက်သွယ်ပြီး Aether မှတစ်ဆင့် သင့်ထံ ဖိတ်ကြားစာ ပို့ရန် တောင်းဆိုပါ။ သင့် အကောင့် ဖန်တီးရန် ညွှန်ကြားချက်များ ပါသော အီးမေးလ် ရရှိပါမည်။',
    'support.faq5Question': 'Aether တွင် အိမ်ခြံမြေ တိုးတက်ရေးကုမ္ပဏီ ဖြစ်လာရန် မည်သို့ လုပ်ရမည်နည်း။',
    'support.faq5Answer': 'ပံ့ပိုးမှု စာမျက်နှာမှ သို့မဟုတ် sales@aether.app သို့ အီးမေးလ် ပို့၍ ကျွန်ုပ်တို့ အရောင်းအဖွဲ့ကို ဆက်သွယ်ပါ။ သင့် အိမ်ခြံမြေ ပိုင်ဆိုင်မှု အရွယ်အစားပေါ် မူတည်၍ လုပ်ငန်း ဝင်ရောက်ခြင်းနှင့် စိတ်ကြိုက် စျေးနှုန်း ပေးပါသည်။',
    'support.faq6Question': 'စာရင်းသွင်းမှုကို ပယ်ဖျက် သို့မဟုတ် ပြောင်းလဲရန် မည်သို့ လုပ်ရမည်နည်း။',
    'support.faq6Answer': 'ဆက်တင်များ > စာရင်းသွင်းမှုများ သို့သွားပြီး Stripe Customer Portal ဝင်ရောက်ရန် "စာရင်းသွင်းမှု စီမံခန့်ခွဲရန်" ကို နှိပ်ပါ။ အချိန်မရွေး ပယ်ဖျက်နိုင်ပါသည် - သင့် ဝင်ရောက်ခွင့်သည် ငွေတောင်းခံ ကာလ အဆုံးအထိ ဆက်လက် ရှိနေပါမည်။',
  },
  th: {
    // Common
    'common.signIn': 'เข้าสู่ระบบ',
    'common.signUp': 'ลงทะเบียน',
    'common.signOut': 'ออกจากระบบ',
    'common.loading': 'กำลังโหลด...',
    'common.save': 'บันทึก',
    'common.cancel': 'ยกเลิก',
    'common.delete': 'ลบ',
    'common.edit': 'แก้ไข',
    'common.search': 'ค้นหา',
    'common.filter': 'กรอง',
    'common.clear': 'ล้าง',
    'common.submit': 'ส่ง',
    'common.back': 'กลับ',
    'common.next': 'ถัดไป',
    'common.viewAll': 'ดูทั้งหมด',
    'common.learnMore': 'เรียนรู้เพิ่มเติม',
    
    // Role Simulator
    'roleSimulator.title': 'เครื่องจำลองบทบาท',
    'roleSimulator.description': 'ทดสอบแอปจากมุมมองของผู้ใช้ที่แตกต่างกัน',
    'roleSimulator.godMode': 'God Mode',
    'roleSimulator.admin': 'แอดมินชุมชน',
    'roleSimulator.homeowner': 'เจ้าของบ้าน',
    'roleSimulator.guest': 'แขก',
    'roleSimulator.godModeDesc': 'การเข้าถึงระบบทั้งหมด',
    'roleSimulator.adminDesc': 'จัดการชุมชน',
    'roleSimulator.homeownerDesc': 'จัดการทรัพย์สิน',
    'roleSimulator.guestDesc': 'การเข้าถึงจำกัด',
    'roleSimulator.active': 'ใช้งานอยู่',
    'roleSimulator.exitSimulation': 'ออกจากการจำลอง',
    
    // Simulation Banner
    'simulation.simulating': 'กำลังจำลอง',
    'simulation.exit': 'ออกจากการจำลอง',
    
    // Cookie Consent
    'cookies.message': 'เราใช้คุกกี้เพื่อเพิ่มประสบการณ์ของคุณ',
    'cookies.learnMore': 'อ่านนโยบายความเป็นส่วนตัวของเรา',
    'cookies.accept': 'ยอมรับ',
    'cookies.dismiss': 'ปิด',
    'common.close': 'ปิด',
    
    // Notification Center
    'notificationCenter.new': 'ใหม่',
    'notificationCenter.noNotifications': 'ยังไม่มีการแจ้งเตือน',
    'notificationCenter.noNotificationsDesc': 'การแจ้งเตือนการชำระเงินและการอัปเดตจะแสดงที่นี่',
    
    // Toast Messages
    'toast.loadFailed': 'โหลดข้อมูลไม่สำเร็จ',
    'toast.emailRequired': 'กรุณากรอกอีเมล',
    'toast.inviteSent': 'ส่งคำเชิญสำเร็จ',
    'toast.inviteFailed': 'ส่งคำเชิญไม่สำเร็จ',
    'toast.addressRequired': 'กรุณากรอกที่อยู่',
    'toast.propertyAdded': 'เพิ่มทรัพย์สินสำเร็จ',
    'toast.propertyAddFailed': 'เพิ่มทรัพย์สินไม่สำเร็จ',
    'toast.rentalModeEnabled': 'เปิดโหมดเช่าแล้ว',
    'toast.rentalModeDisabled': 'ปิดโหมดเช่าแล้ว',
    'toast.rentalModeUpdateFailed': 'อัปเดตโหมดเช่าไม่สำเร็จ',
    'toast.rentalListingSaved': 'บันทึกรายการให้เช่าสำเร็จ',
    'toast.rentalListingSaveFailed': 'บันทึกรายการให้เช่าไม่สำเร็จ',
    'toast.communityNameRequired': 'กรุณากรอกชื่อชุมชน',
    'toast.communityCreated': 'สร้างชุมชนสำเร็จ',
    'toast.communityCreateFailed': 'สร้างชุมชนไม่สำเร็จ',
    'toast.adminInviteSent': 'ส่งคำเชิญแอดมินแล้ว',
    'toast.inviteDeleted': 'ลบคำเชิญแล้ว',
    'toast.deleteFailed': 'ลบไม่สำเร็จ',
    'toast.listingSaved': 'บันทึกรายการสำเร็จ',
    'toast.listingSaveFailed': 'บันทึกรายการไม่สำเร็จ',
    'toast.calendarUpdated': 'อัปเดตปฏิทินแล้ว',
    'toast.calendarUpdateFailed': 'อัปเดตปฏิทินไม่สำเร็จ',
    'toast.ownerInviteSent': 'ส่งคำเชิญเจ้าของสำเร็จ',
    'toast.updateFailed': 'อัปเดตไม่สำเร็จ',
    'common.error': 'ข้อผิดพลาด',
    'common.unknown': 'ไม่ทราบ',
    
    // Smart Home & Rental & Homeowner (Phase 5 - Thai)
    'smartHome.title': 'การเข้าถึงสมาร์ทโฮม',
    'smartHome.shareTitle': 'แชร์สมาร์ทโฮมกับแขก',
    'smartHome.shareDesc': 'ใช้ Google Home เพื่อให้แขกเข้าถึงอุปกรณ์สมาร์ท',
    'smartHome.quickSteps': 'ขั้นตอนด่วน',
    'smartHome.step1': 'เปิดแอป Google Home',
    'smartHome.step2': 'ไปที่ การตั้งค่า → ครอบครัว',
    'smartHome.step3': 'เชิญแขกโดยใช้อีเมล',
    'smartHome.remember': 'จำไว้',
    'smartHome.reminderText': 'เพิ่มการเข้าถึงก่อนเช็คอินและลบหลังเช็คเอาท์',
    'smartHome.viewGuide': 'ดูคู่มือฉบับเต็ม',
    'smartHome.worksWith': 'ใช้ได้กับ:',
    'smartHome.devicesList': 'Nest, Ring, Yale, August, Philips Hue และอุปกรณ์ที่รองรับ Google Home 50,000+',
    'rental.status': 'สถานะการเช่า',
    'rental.bookings': 'การจอง',
    'rental.pending': 'รอดำเนินการ',
    'rental.revenue': 'รายได้',
    'rental.rating': 'คะแนน',
    'rental.view': 'ดู',
    'rental.edit': 'แก้ไข',
    'rental.calendar': 'ปฏิทิน',
    'rental.completeListing': 'ทำรายการให้สมบูรณ์เพื่อเริ่มสร้างรายได้',
    'rental.enableMode': 'เปิดโหมดเช่าเพื่อลงประกาศอสังหาริมทรัพย์',
    'rental.enableButton': 'เปิดโหมดเช่า',
    'rental.modeEnabled': 'เปิดโหมดเช่าแล้ว',
    'rental.modeDisabled': 'ปิดโหมดเช่าแล้ว',
    'rental.updateFailed': 'อัปเดตโหมดเช่าไม่สำเร็จ',
    'homeowner.upcomingHOA': 'ค่า HOA ที่กำลังจะถึงกำหนด',
    'homeowner.payNow': 'ชำระเลย',
    'homeowner.noHOADues': 'ไม่มีค่า HOA ที่กำหนด',
    'homeowner.dueToday': 'ครบกำหนดวันนี้',
    'homeowner.daysLeft': '{days} วัน',
    'homeowner.communityUpdates': 'อัปเดตชุมชน',
    'homeowner.new': 'ใหม่',
    'homeowner.noAnnouncements': 'ไม่มีประกาศล่าสุด',
    
    // Navigation
    'nav.home': 'แดชบอร์ด',
    'nav.dashboard': 'แดชบอร์ด',
    'nav.services': 'บริการ',
    'nav.rentals': 'เช่า',
    'nav.map': 'แผนที่',
    'nav.wallet': 'การชำระเงิน',
    'nav.settings': 'ตั้งค่า',
    'nav.messages': 'ข้อความ',
    'nav.notifications': 'การแจ้งเตือน',
    'nav.concierge': 'บริการคอนเซียร์จ',
    
    // Auth
    'auth.welcomeBack': 'ยินดีต้อนรับกลับ',
    'auth.createAccount': 'สร้างบัญชี',
    'auth.resetPassword': 'รีเซ็ตรหัสผ่าน',
    'auth.setPassword': 'ตั้งรหัสผ่าน',
    'auth.email': 'อีเมล',
    'auth.password': 'รหัสผ่าน',
    'auth.confirmPassword': 'ยืนยันรหัสผ่าน',
    'auth.forgotPassword': 'ลืมรหัสผ่าน?',
    'auth.noAccount': 'ยังไม่มีบัญชี?',
    'auth.hasAccount': 'มีบัญชีอยู่แล้ว?',
    'auth.backToHome': 'กลับหน้าแรก',
    
    // Dashboard
    'dashboard.welcome': 'ยินดีต้อนรับกลับ',
    'dashboard.properties': 'อสังหาริมทรัพย์',
    'dashboard.bookings': 'การจอง',
    'dashboard.payments': 'การชำระเงิน',
    'dashboard.guests': 'แขก',
    
    // Rentals
    'rentals.title': 'ค้นพบอสังหาริมทรัพย์พิเศษ',
    'rentals.subtitle': 'สัมผัสสิ่งอำนวยความสะดวกระดับโลกและบริการคอนเซียร์จส่วนตัว',
    'rentals.searchPlaceholder': 'ค้นหาตามสถานที่...',
    'rentals.pricePerNight': 'ต่อคืน',
    'rentals.bedrooms': 'ห้องนอน',
    'rentals.bathrooms': 'ห้องน้ำ',
    'rentals.bookNow': 'จองเลย',
    'rentals.checkIn': 'เช็คอิน',
    'rentals.checkOut': 'เช็คเอาท์',
    'rentals.guests': 'แขก',
    'rentals.noResults': 'ไม่มีอสังหาริมทรัพย์ที่ตรงกับตัวกรอง',
    'rentals.askAI': 'ให้ AI ช่วยหาที่พัก',
    
    // AI Assistant
    'ai.greeting': 'สวัสดี! ฉันเป็นคอนเซียร์จ Aether ของคุณ มีอะไรให้ช่วยไหม?',
    'ai.placeholder': 'ถามอะไรก็ได้...',
    'ai.listening': 'กำลังฟัง...',
    'ai.findRentals': 'ค้นหาที่พักว่าง',
    
    // Settings
    'settings.title': 'ตั้งค่า',
    'settings.language': 'ภาษา',
    'settings.currency': 'สกุลเงิน',
    'settings.timezone': 'เขตเวลา',
    'settings.notifications': 'การแจ้งเตือน',
    'settings.appearance': 'รูปลักษณ์',
    'settings.darkMode': 'โหมดมืด',
    'settings.profile': 'ข้อมูลโปรไฟล์',
    'settings.profileDesc': 'ชื่อ, โทรศัพท์, รูปโปรไฟล์',
    'settings.notificationsDesc': 'การตั้งค่าอีเมลและการแจ้งเตือน',
    'settings.payment': 'วิธีการชำระเงิน',
    'settings.paymentDesc': 'จัดการบัตรและการเรียกเก็บเงิน',
    'settings.subscriptions': 'การสมัครสมาชิก',
    'settings.subscriptionsDesc': 'แผน SaaS และ HOA',
    'settings.guestPasses': 'บัตรผ่านแขก',
    'settings.guestPassesDesc': 'จัดการการเข้าถึงของผู้เยี่ยมชม',
    'settings.languageCurrency': 'ภาษาและสกุลเงิน',
    'settings.languageCurrencyDesc': 'การตั้งค่าภูมิภาค',
    'settings.smartHome': 'การแชร์สมาร์ทโฮม',
    'settings.smartHomeDesc': 'แชร์การเข้าถึง Google Home กับแขก',
    'settings.viewAs': 'ดูในฐานะ (จำลองบทบาท)',
    'settings.viewAsDesc': 'ทดสอบประสบการณ์ผู้ใช้ที่แตกต่างกัน',
    'settings.signOut': 'ออกจากระบบ',
    
    // Messages
    'messages.title': 'ข้อความ',
    'messages.subtitle': 'การสนทนา, AI และการแจ้งเตือน',
    'messages.chats': 'แชท',
    'messages.aether': 'Aether',
    'messages.alerts': 'การแจ้งเตือน',
    'messages.noConversations': 'ยังไม่มีการสนทนา',
    'messages.noConversationsDesc': 'ข้อความจากแขกและเจ้าของทรัพย์สินจะปรากฏที่นี่',
    'messages.noNotifications': 'ไม่มีการแจ้งเตือน',
    'messages.noNotificationsDesc': 'ประกาศชุมชนจะปรากฏที่นี่',
    'messages.markAllRead': 'ทำเครื่องหมายว่าอ่านแล้วทั้งหมด',
    'messages.startConversation': 'เริ่มการสนทนา',
    'messages.aetherTitle': 'Aether AI คอนเซียร์จ',
    'messages.aetherDesc': 'ผู้ช่วย AI ส่วนตัวของคุณสำหรับการจองบริการ รับคำแนะนำ และจัดการความต้องการทรัพย์สินของคุณ',
    
    // Browse
    'nav.browse': 'สำรวจ',
    
    // Dashboard specific
    'dashboard.guestAccess': 'การเข้าถึงของแขก',
    'dashboard.accessExpires': 'การเข้าถึงหมดอายุใน',
    'dashboard.daysRemaining': 'วันที่เหลือของการเข้าถึงแขก',
    'dashboard.browseRentals': 'เรียกดูที่พักให้เช่า',
    'dashboard.services': 'บริการ',
    'dashboard.personalizeSearch': 'ปรับแต่งการค้นหาของคุณ',
    'dashboard.updatePreferences': 'อัปเดตความชอบในการเช่าของคุณ',
    'dashboard.availableServices': 'บริการที่มี',
    'dashboard.communityMap': 'แผนที่ชุมชน',
    'dashboard.exploreAmenities': 'สำรวจสิ่งอำนวยความสะดวก',
    'dashboard.dining': 'อาหาร',
    'dashboard.requestMeals': 'ขออาหาร',
    'dashboard.transport': 'การขนส่ง',
    'dashboard.requestValet': 'ขอบริการรับฝากรถ',
    'dashboard.housekeeping': 'แม่บ้าน',
    'dashboard.ownerOnly': 'เฉพาะเจ้าของ',
    'dashboard.guestGuidelines': 'แนวทางปฏิบัติสำหรับแขก',
    'dashboard.propertyManager': 'ผู้จัดการทรัพย์สิน',
    'dashboard.guestAtProperty': 'แขกที่อสังหาริมทรัพย์นี้',
    'dashboard.guidelineQuietHours': 'เวลาเงียบตั้งแต่ 22:00 ถึง 07:00',
    'dashboard.guidelinePoolGym': 'สระว่ายน้ำและยิมเปิดให้บริการ 06:00 - 22:00',
    'dashboard.guidelineConcierge': 'ติดต่อคอนเซียร์จสำหรับความช่วยเหลือใดๆ',
    'dashboard.guidelineRespect': 'เคารพสิ่งอำนวยความสะดวกทั้งหมดของชุมชน',
    'dashboard.greetingMorning': 'สวัสดีตอนเช้า',
    'dashboard.greetingAfternoon': 'สวัสดีตอนบ่าย',
    'dashboard.greetingEvening': 'สวัสดีตอนเย็น',
    'dashboard.loading': 'กำลังโหลดแดชบอร์ดของคุณ...',
    'dashboard.welcomeAether': 'ยินดีต้อนรับสู่ Aether',
    'dashboard.yourSmartHome': 'สมาร์ทโฮมของคุณ',
    'dashboard.allSystemsOperational': 'ระบบทั้งหมดทำงานปกติ',
    'dashboard.quickActions': 'การดำเนินการด่วน',
    'dashboard.propertyStatus': 'สถานะอสังหาริมทรัพย์',
    'dashboard.communityUpdates': 'อัปเดตชุมชน',
    'dashboard.upcoming': 'กำลังจะมาถึง',
    'dashboard.confirmed': 'ยืนยันแล้ว',
    'dashboard.rentalMode': 'โหมดเช่า',
    'dashboard.rentalModeDesc': 'ลงประกาศสำหรับการเช่าระยะสั้น',
    'dashboard.rentalModeEnabled': 'เปิดใช้งานโหมดเช่าแล้ว',
    'dashboard.rentalModeDisabled': 'ปิดใช้งานโหมดเช่าแล้ว',
    'dashboard.createGuestPass': 'สร้างบัตรผ่านแขก',
    'dashboard.guestEmail': 'อีเมลแขก',
    'dashboard.accessDuration': 'ระยะเวลาการเข้าถึง (วัน)',
    'dashboard.sendGuestPass': 'ส่งบัตรผ่านแขก',
    'dashboard.needProperty': 'คุณต้องเป็นเจ้าของอสังหาริมทรัพย์เพื่อสร้างบัตรผ่านแขก',
    'dashboard.privateDinner': 'อาหารค่ำส่วนตัว',
    
    // AI Page
    'ai.title': 'Aether AI',
    'ai.subtitle': 'ผู้ช่วยส่วนตัวของคุณ',
    'ai.welcomeMessage': 'สวัสดี! ฉันเป็นคอนเซียร์จ AI ของคุณ ฉันสามารถช่วยคุณได้:',
    'ai.helpBooking': 'จองบริการและนัดหมาย',
    'ai.helpGuest': 'จัดการการเข้าถึงของแขก',
    'ai.helpMaintenance': 'คำขอบำรุงรักษาอสังหาริมทรัพย์',
    'ai.helpLifestyle': 'คำแนะนำไลฟ์สไตล์',
    'ai.trySaying': 'ลองพูดว่า',
    'ai.suggestion1': 'จองเชฟส่วนตัวสำหรับวันเสาร์',
    'ai.suggestion2': 'นัดหมายบำรุงรักษาสระว่ายน้ำ',
    'ai.suggestion3': 'อนุมัติแขกของฉันสำหรับพรุ่งนี้',
    'ai.suggestion4': 'มีบริการอะไรบ้าง?',
    // Transactions
    'transaction.pending': 'รอดำเนินการ',
    'transaction.processing': 'กำลังประมวลผล',
    'transaction.completed': 'เสร็จสิ้น',
    'transaction.failed': 'ล้มเหลว',
    'transaction.refunded': 'คืนเงินแล้ว',
    'transaction.type.hoa_dues': 'ค่าธรรมเนียม HOA',
    'transaction.type.service_payment': 'การชำระค่าบริการ',
    'transaction.type.refund': 'การคืนเงิน',
    'transaction.type.deposit': 'เงินมัดจำ',
    
    // HOA Payments
    'hoa.monthlyDues': 'ค่าธรรมเนียม HOA รายเดือน',
    'hoa.dueOnDay': 'ครบกำหนดวันที่ {day} ของทุกเดือน',
    'hoa.perMonth': '/เดือน',
    'hoa.nextPayment': 'การชำระเงินครั้งถัดไป',
    'hoa.payNow': 'จ่ายเลย',
    'hoa.allCaughtUp': 'ชำระครบแล้ว!',
    'hoa.notConfigured': 'ยังไม่ได้กำหนดค่าธรรมเนียม HOA สำหรับทรัพย์สินนี้',
    'hoa.paymentHistory': 'ประวัติการชำระเงิน',
    'hoa.noProperties': 'ไม่พบทรัพย์สิน',
    'hoa.noPropertiesDesc': 'คุณต้องเป็นเจ้าของทรัพย์สินเพื่อชำระค่าธรรมเนียม HOA',
    'hoa.choosePaymentMethod': 'เลือกวิธีการชำระเงิน',
    'hoa.paid': 'ชำระแล้ว',
    'hoa.verifying': 'กำลังตรวจสอบ',
    
    // Concierge Page
    'concierge.title': 'คอนเซียร์จ',
    'concierge.subtitle': 'บริการระดับพรีเมียมตามคำสั่งของคุณ',
    'concierge.askAether': 'ถาม Aether',
    'concierge.services': 'บริการ',
    'concierge.myBookings': 'การจองของฉัน',
    'concierge.serviceCategories': 'หมวดหมู่บริการ',
    'concierge.yourBookings': 'การจองของคุณ',
    'concierge.viewAllBookings': 'ดูการจองทั้งหมด',
    'concierge.manageBookings': 'ดูและจัดการการจองบริการที่กำลังจะมาถึง',
    'concierge.notAvailable': 'คอนเซียร์จไม่พร้อมให้บริการ',
    'concierge.notAvailableDesc': 'บริการคอนเซียร์จยังไม่ได้เปิดใช้งานสำหรับชุมชนของคุณ ติดต่อผู้ดูแลระบบสำหรับข้อมูลเพิ่มเติม',
    'concierge.noCommunity': 'ไม่มีสิทธิ์เข้าถึงชุมชน',
    'concierge.noCommunityDesc': 'บริการคอนเซียร์จจะใช้ได้หลังจากเข้าร่วมชุมชน',
    'concierge.createCommunity': 'สร้างชุมชนแรกของคุณ',
    'concierge.noServices': 'บริการกำลังจะมาเร็วๆ นี้',
    'concierge.noServicesDesc': 'ผู้ดูแลชุมชนของคุณกำลังตั้งค่าบริการคอนเซียร์จ กลับมาตรวจสอบเร็วๆ นี้!',
    
    // Service Categories
    'service.housekeeping': 'แม่บ้าน',
    'service.housekeepingDesc': 'บริการทำความสะอาดและซักรีดระดับพรีเมียม',
    'service.wellness': 'สุขภาพ',
    'service.wellnessDesc': 'สปา ฟิตเนส และการผ่อนคลาย',
    'service.maintenance': 'ซ่อมบำรุง',
    'service.maintenanceDesc': 'การดูแลและซ่อมแซมทรัพย์สิน',
    'service.dining': 'อาหารส่วนตัว',
    'service.diningDesc': 'ประสบการณ์อาหารที่คัดสรร',
    'service.transport': 'การขนส่ง',
    'service.transportDesc': 'บริการยานพาหนะหรูหรา',
    
    // Service Marketplace
    'marketplace.title': 'บริการ',
    'marketplace.subtitle': 'จองบริการพรีเมียม',
    'marketplace.all': 'ทั้งหมด',
    'marketplace.noServices': 'ไม่มีบริการที่พร้อมให้บริการ',
    'marketplace.checkBack': 'กลับมาตรวจสอบในภายหลัง',
    'marketplace.book': 'จอง',
    'marketplace.estimatedPrice': 'ราคาประมาณ',
    'marketplace.priceVaries': '*ราคาสุดท้ายอาจแตกต่างกันตามขนาดทรัพย์สิน',
    'marketplace.scheduleDateTime': 'กำหนดวันและเวลา',
    'marketplace.specialRequests': 'ความต้องการพิเศษ (ไม่บังคับ)',
    'marketplace.specialRequestsPlaceholder': 'คำแนะนำพิเศษ...',
    'marketplace.payWithCard': 'ชำระด้วยบัตร (USD)',
    
    // Sovereign Dashboard
    'sovereign.propertyManager': 'ผู้จัดการทรัพย์สิน',
    'sovereign.properties': 'ทรัพย์สิน',
    'sovereign.selectProperty': 'เลือกทรัพย์สิน',
    'sovereign.noPropertyTitle': 'ไม่พบทรัพย์สิน',
    'sovereign.noPropertyDesc': 'คุณไม่มีทรัพย์สินที่มอบหมายให้กับบัญชีของคุณ',
    'sovereign.goToDashboard': 'ไปที่แดชบอร์ด',
    'sovereign.standaloneProperty': 'ทรัพย์สินเดี่ยว',
    'sovereign.community': 'ชุมชน',
    'sovereign.rentalMode': 'โหมดเช่า',
    'sovereign.rentalModeDesc': 'ลงทรัพย์สินสำหรับเช่าระยะสั้น',
    'sovereign.totalBookings': 'การจองทั้งหมด',
    'sovereign.pending': 'รอดำเนินการ',
    'sovereign.revenue': 'รายได้',
    'sovereign.rating': 'คะแนน',
    'sovereign.bookings': 'การจอง',
    'sovereign.calendar': 'ปฏิทิน',
    'sovereign.listing': 'รายการ',
    'sovereign.reviews': 'รีวิว',
    'sovereign.smartHome': 'สมาร์ทโฮม',
    'sovereign.guestReviews': 'รีวิวจากแขก',
    'sovereign.enableRentalMode': 'เปิดใช้โหมดเช่า',
    'sovereign.enableRentalModeDesc': 'ลงทรัพย์สินของคุณในตลาดสำหรับเช่าระยะสั้น กำหนดราคาของคุณเอง จัดการความพร้อม และสร้างรายได้',
    'sovereign.setRates': 'กำหนดราคาต่อคืนและค่าทำความสะอาด',
    'sovereign.controlCalendar': 'ควบคุมปฏิทินความพร้อม',
    'sovereign.manageGuests': 'อนุมัติแขกและจัดการการจอง',
    
    // Admin Dashboard
    'admin.title': 'แดชบอร์ด',
    'admin.role': 'แอดมิน',
    'admin.godMode': 'โหมดพระเจ้า',
    'admin.properties': 'ทรัพย์สิน',
    'admin.owners': 'เจ้าของ',
    'admin.collected': 'เก็บได้',
    'admin.overview': 'ภาพรวม',
    'admin.forSale': 'ขาย',
    'admin.announcements': 'ข่าว',
    'admin.bookings': 'การจอง',
    'admin.services': 'บริการ',
    'admin.vendors': 'ผู้ขาย',
    'admin.earnings': 'รายได้',
    'admin.reviews': 'รีวิว',
    'admin.more': 'เพิ่มเติม',
    'admin.hoaStatus': 'สถานะการเก็บค่า HOA',
    'admin.paid': 'ชำระแล้ว',
    'admin.pending': 'รอดำเนินการ',
    'admin.overdue': 'เกินกำหนด',
    'admin.invite': 'เชิญ',
    'admin.inviteHomeowner': 'เชิญเจ้าของบ้าน',
    'admin.addProperty': 'เพิ่มทรัพย์สิน',
    'admin.sendInvitation': 'ส่งคำเชิญ',
    'admin.noProperties': 'ยังไม่มีทรัพย์สิน',
    'admin.addFirstProperty': 'เพิ่มทรัพย์สินแรกเพื่อเริ่มต้น',
    'admin.moreProperties': 'ทรัพย์สินเพิ่มเติม',
    'admin.unassigned': 'ไม่ได้มอบหมาย',
    'admin.rental': 'เช่า',
    'admin.rentalMode': 'โหมดให้เช่า',
    'admin.manageListing': 'จัดการ',
    'admin.rentalListingTitle': 'จัดการรายการให้เช่า',
    'admin.streetAddress': 'ที่อยู่ถนน',
    'admin.unitNumber': 'เลขยูนิต (ไม่บังคับ)',
    'admin.bedrooms': 'ห้องนอน',
    'admin.bathrooms': 'ห้องน้ำ',
    'admin.manageAnnouncements': 'จัดการประกาศ',
    'admin.dragToReschedule': 'ลากเพื่อจัดกำหนดการใหม่',
    'admin.allReservations': 'การจองทั้งหมด',
    'admin.createCommunity': 'สร้างชุมชนใหม่',
    'admin.createCommunityDesc': 'ตั้งค่าชุมชนใหม่เพื่อจัดการทรัพย์สินและเจ้าของบ้าน',
    'admin.communityMap': 'แผนที่ชุมชน',
    'admin.welcome': 'ยินดีต้อนรับ',
    'admin.createFirstCommunity': 'สร้างชุมชนแรกของคุณ',
    'admin.createFirstCommunityDesc': 'เริ่มต้นด้วยการสร้างชุมชนเพื่อจัดการทรัพย์สินและเจ้าของบ้าน',
    
    // Browse Rentals
    'browse.title': 'เรียกดูที่พักให้เช่า',
    'browse.luxuryStays': 'ที่พักหรูหรา',
    'browse.heroTitle': 'ค้นพบอสังหาริมทรัพย์พิเศษ',
    'browse.heroSubtitle': 'สัมผัสสิ่งอำนวยความสะดวกระดับโลกและบริการคอนเซียร์จส่วนตัวในชุมชนพิเศษของเรา',
    'browse.searchPlaceholder': 'ค้นหาตามสถานที่หรือชุมชน...',
    'browse.filters': 'ตัวกรอง',
    'browse.priceRange': 'ช่วงราคา (ต่อคืน)',
    'browse.minBedrooms': 'ห้องนอนขั้นต่ำ',
    'browse.amenities': 'สิ่งอำนวยความสะดวก',
    'browse.any': 'ใดก็ได้',
    'browse.clearFilters': 'ล้างตัวกรองทั้งหมด',
    'browse.propertiesFound': 'พบ {count} อสังหาริมทรัพย์',
    'browse.propertyFound': 'พบ 1 อสังหาริมทรัพย์',
    'browse.noReviews': 'ยังไม่มีรีวิว',
    'browse.luxuryProperty': 'อสังหาริมทรัพย์หรูหรา',
    'browse.privateCommunity': 'ชุมชนส่วนตัว',
    'browse.beds': 'ห้องนอน',
    'browse.baths': 'ห้องน้ำ',
    'browse.moreAmenities': '+{count} เพิ่มเติม',
    'browse.additionalAmenities': 'สิ่งอำนวยความสะดวกเพิ่มเติม:',
    'browse.viewDetails': 'ดูรายละเอียด',
    'browse.bookNow': 'จองเลย',
    'browse.bookYourStay': 'จองที่พักของคุณ',
    'browse.booking': 'การจอง',
    'browse.reviews': 'รีวิว',
    'browse.addressRevealed': 'ที่อยู่ฉบับเต็มจะแสดงหลังยืนยันการจอง',
    'browse.checkIn': 'เช็คอิน',
    'browse.checkOut': 'เช็คเอาท์',
    'browse.selectDate': 'เลือกวันที่',
    'browse.guests': 'แขก',
    'browse.nights': 'คืน',
    'browse.cleaningFee': 'ค่าทำความสะอาด',
    'browse.total': 'รวม',
    'browse.confirmPay': 'ยืนยันและชำระเงิน',
    'browse.signInToBook': 'ลงชื่อเข้าใช้เพื่อจอง',
    'browse.processing': 'กำลังดำเนินการ...',
    'browse.minNights': 'ขั้นต่ำ {min} คืน',
    'browse.maxNights': 'สูงสุด {max} คืน',
    'browse.noPropertiesMatch': 'ไม่มีอสังหาริมทรัพย์ที่ตรงกับตัวกรอง',
    'browse.tryAdjusting': 'ลองปรับเกณฑ์การค้นหาของคุณ',
    
    // Payment History (Thai)
    'paymentHistory.title': 'ประวัติการชำระเงิน',
    'paymentHistory.subtitle': 'ดูการชำระเงินและใบเสร็จทั้งหมด',
    'paymentHistory.all': 'ทั้งหมด',
    'paymentHistory.hoa': 'HOA',
    'paymentHistory.rentals': 'เช่า',
    'paymentHistory.services': 'บริการ',
    'paymentHistory.noPayments': 'ไม่พบการชำระเงิน',
    'paymentHistory.noPaymentsSubtitle': 'ประวัติการชำระเงินจะปรากฏที่นี่',
    'paymentHistory.hoaDues': 'ค่า HOA',
    'paymentHistory.rentalBooking': 'การจองที่พัก',
    'paymentHistory.servicePayment': 'ค่าบริการ',
    'paymentHistory.paid': 'ชำระแล้ว',
    'paymentHistory.pending': 'รอดำเนินการ',
    'paymentHistory.rejected': 'ถูกปฏิเสธ',
    'paymentHistory.period': 'งวด',
    'paymentHistory.paymentDetails': 'รายละเอียดการชำระเงิน',
    'paymentHistory.amount': 'จำนวนเงิน',
    'paymentHistory.type': 'ประเภท',
    'paymentHistory.status': 'สถานะ',
    'paymentHistory.date': 'วันที่',
    'paymentHistory.paymentMethod': 'วิธีการชำระเงิน',
    'paymentHistory.property': 'อสังหาริมทรัพย์',
    'paymentHistory.verified': 'ยืนยันแล้ว',
    'paymentHistory.paymentProof': 'หลักฐานการชำระเงิน',
    'paymentHistory.downloadReceipt': 'ดาวน์โหลดใบเสร็จ',
    'paymentHistory.bankTransfer': 'โอนเงินผ่านธนาคาร',
    'paymentHistory.card': 'บัตร',
    'common.notAvailable': 'ไม่มีข้อมูล',
    
    // Onboarding (Thai)
    'onboarding.homeowner': 'เจ้าของบ้าน',
    'onboarding.homeownerDesc': 'ฉันเป็นเจ้าของอสังหาริมทรัพย์และต้องการใช้บริการ Aether',
    'onboarding.guest': 'แขก',
    'onboarding.guestDesc': 'ฉันกำลังหาที่พักเช่าหรือพักอาศัย',
    'onboarding.profile': 'โปรไฟล์',
    'onboarding.profileDesc': 'บอกเราเกี่ยวกับตัวคุณ',
    'onboarding.properties': 'อสังหาริมทรัพย์',
    'onboarding.propertiesDesc': 'เพิ่มอสังหาริมทรัพย์ของคุณ',
    'onboarding.preferences': 'การตั้งค่า',
    'onboarding.preferencesDesc': 'ปรับแต่งประสบการณ์ของคุณ',
    'onboarding.fullName': 'ชื่อเต็ม',
    'onboarding.fullNamePlaceholder': 'กรอกชื่อเต็มของคุณ',
    'onboarding.phoneNumber': 'หมายเลขโทรศัพท์',
    'onboarding.phonePlaceholder': 'กรอกหมายเลขโทรศัพท์',
    'onboarding.iAmA': 'ฉันเป็น...',
    'onboarding.selectRole': 'เลือกบทบาทของคุณ',
    'onboarding.community': 'ชุมชน',
    'onboarding.loadingCommunities': 'กำลังโหลดชุมชน...',
    'onboarding.noCommunities': 'ไม่มีชุมชนที่พร้อมใช้งาน',
    'onboarding.noCommunitiesDesc': 'กรุณาติดต่อผู้พัฒนาอสังหาริมทรัพย์หรือกลับมาตรวจสอบภายหลัง',
    'onboarding.selectCommunity': 'เลือกชุมชน',
    'onboarding.propertyAddress': 'ที่อยู่อสังหาริมทรัพย์',
    'onboarding.addressPlaceholder': 'เริ่มพิมพ์ที่อยู่ของคุณ...',
    'onboarding.unitNumber': 'หมายเลขยูนิต',
    'onboarding.propertyType': 'ประเภทอสังหาริมทรัพย์',
    'onboarding.singleFamilyHome': 'บ้านเดี่ยว',
    'onboarding.condominium': 'คอนโดมิเนียม',
    'onboarding.townhouse': 'ทาวน์เฮาส์',
    'onboarding.villa': 'วิลล่า',
    'onboarding.penthouse': 'เพนต์เฮาส์',
    'onboarding.gatedCommunity': 'หมู่บ้านจัดสรร',
    'onboarding.bedrooms': 'ห้องนอน',
    'onboarding.bathrooms': 'ห้องน้ำ',
    'onboarding.sqft': 'ตร.ฟุต',
    'onboarding.enableRentalMode': 'เปิดโหมดเช่า',
    'onboarding.rentalModeDesc': 'ลงประกาศอสังหาริมทรัพย์นี้สำหรับเช่าระยะสั้น',
    'onboarding.rentalSettings': 'การตั้งค่าการเช่า',
    'onboarding.nightlyRate': 'ราคาต่อคืน ($)',
    'onboarding.cleaningFee': 'ค่าทำความสะอาด ($)',
    'onboarding.amenities': 'สิ่งอำนวยความสะดวก',
    'onboarding.wifi': 'WiFi',
    'onboarding.parking': 'ที่จอดรถ',
    'onboarding.pool': 'สระว่ายน้ำ',
    'onboarding.gym': 'ยิม',
    'onboarding.kitchen': 'ห้องครัว',
    'onboarding.ac': 'แอร์',
    'onboarding.smartTv': 'สมาร์ททีวี',
    'onboarding.hotTub': 'อ่างน้ำร้อน',
    'onboarding.garden': 'สวน',
    'onboarding.security': 'รปภ. 24 ชม.',
    'onboarding.property': 'อสังหาริมทรัพย์',
    'onboarding.add': 'เพิ่ม',
    'onboarding.language': 'ภาษา',
    'onboarding.currency': 'สกุลเงิน',
    'onboarding.timezone': 'เขตเวลา',
    'onboarding.notifications': 'การแจ้งเตือน',
    'onboarding.bookingUpdates': 'อัปเดตการจอง',
    'onboarding.messageAlerts': 'การแจ้งเตือนข้อความ',
    'onboarding.skipForNow': 'ข้ามไปก่อน',
    'onboarding.welcomeToAether': 'ยินดีต้อนรับสู่ Aether',
    'onboarding.letsSetup': 'มาตั้งค่าบัญชีของคุณกัน',
    'onboarding.back': 'กลับ',
    'onboarding.continue': 'ดำเนินการต่อ',
    'onboarding.getStarted': 'เริ่มต้นใช้งาน',
    'onboarding.skipped': 'ข้ามการตั้งค่าเริ่มต้น',
    'onboarding.skippedDesc': 'คุณสามารถกรอกข้อมูลโปรไฟล์ได้ทุกเมื่อในการตั้งค่า',
    'onboarding.propertyAdded': 'เพิ่มอสังหาริมทรัพย์สำเร็จ!',
    'onboarding.propertiesAdded': 'เพิ่มอสังหาริมทรัพย์สำเร็จ!',
    'onboarding.setupComplete': 'บัญชีของคุณถูกตั้งค่าเรียบร้อยแล้ว',
    'onboarding.setupFailed': 'ไม่สามารถตั้งค่าได้ กรุณาลองอีกครั้ง',
    
    // Listing Detail Page (Thai)
    'listing.notFound': 'ไม่พบรายการ',
    'listing.notFoundDesc': 'อสังหาริมทรัพย์นี้อาจไม่มีให้บริการแล้ว',
    'listing.browseRentals': 'เรียกดูที่พัก',
    'listing.viewAllPhotos': 'ดูรูปภาพทั้งหมด {count} ภาพ',
    'listing.allPhotos': 'รูปภาพทั้งหมด',
    'listing.about': 'เกี่ยวกับอสังหาริมทรัพย์นี้',
    'listing.aboutProperty': 'เกี่ยวกับอสังหาริมทรัพย์นี้',
    'listing.amenities': 'สิ่งอำนวยความสะดวก',
    'listing.houseRules': 'กฎบ้าน',
    'listing.sqft': 'ตร.ฟุต',
    'listing.night': 'คืน',
    'listing.image': 'รูปภาพ',
    'listing.payWithCard': 'ชำระด้วยบัตร',
    'listing.payWithKBZ': 'ชำระด้วย KBZPay',
    'listing.payWithWave': 'ชำระด้วย Wave Money',
    'listing.signInRequired': 'กรุณาเข้าสู่ระบบเพื่อจองอสังหาริมทรัพย์นี้',
    'listing.pleaseSignIn': 'กรุณาเข้าสู่ระบบเพื่อจองอสังหาริมทรัพย์นี้',
    'listing.selectDates': 'กรุณาเลือกวันเช็คอินและเช็คเอาท์',
    'listing.minStay': 'พักขั้นต่ำ {min} คืน',
    'listing.maxStay': 'พักสูงสุด {max} คืน',
    
    // Wallet Page (Thai)
    'wallet.title': 'กระเป๋าเงิน',
    'wallet.subtitle': 'การชำระเงินและธุรกรรม',
    'wallet.paymentMethods': 'วิธีการชำระเงิน',
    'wallet.hoaDues': 'ค่า HOA',
    'wallet.serviceHistory': 'ประวัติบริการ',
    'wallet.viewAll': 'ดูทั้งหมด',
    'wallet.noTransactions': 'ยังไม่มีธุรกรรม',
    'wallet.noTransactionsDesc': 'จองบริการเพื่อดูที่นี่',
    'wallet.paymentSuccess': 'ชำระเงินสำเร็จ',
    'wallet.paymentSuccessDesc': 'การชำระเงินของคุณได้รับการดำเนินการแล้ว',
    'wallet.paymentCanceled': 'ยกเลิกการชำระเงิน',
    'wallet.paymentCanceledDesc': 'การชำระเงินของคุณถูกยกเลิก',
    'wallet.cardAdded': 'เพิ่มบัตรแล้ว',
    'wallet.cardAddedDesc': 'บันทึกวิธีการชำระเงินของคุณแล้ว',
    'wallet.noPaymentMethods': 'ไม่มีวิธีการชำระเงินที่บันทึกไว้',
    'wallet.addCardDesc': 'เพิ่มบัตรเพื่อชำระค่าบริการและค่าธรรมเนียม',
    'wallet.addCard': 'เพิ่มบัตร',
    'wallet.continueAddCard': 'ดำเนินการเพิ่มบัตร',
    'wallet.addPaymentMethod': 'เพิ่มวิธีการชำระเงิน',
    'wallet.default': 'ค่าเริ่มต้น',
    'wallet.manage': 'จัดการ',
    'wallet.securePayment': 'การชำระเงินที่ปลอดภัย',
    'wallet.cardSecure': 'ข้อมูลบัตรของคุณถูกเข้ารหัสและปลอดภัย',
    'wallet.stripeRedirect': 'คุณจะถูกนำไปยังหน้าชำระเงินที่ปลอดภัยของ Stripe เพื่อเพิ่มบัตร',
    'wallet.openingStripe': 'กำลังเปิด Stripe เพื่อเพิ่มบัตรของคุณ...',
    'wallet.failedAddMethod': 'ไม่สามารถเพิ่มวิธีการชำระเงินได้',
    'wallet.failedOpenPortal': 'ไม่สามารถเปิดพอร์ทัลการเรียกเก็บเงินได้',
    'wallet.expires': 'หมดอายุ',
    
    // Payments Page (Thai)
    'payments.title': 'การชำระเงิน',
    'payments.subtitle': 'จัดการการชำระเงินและธุรกรรม',
    'payments.noCommunity': 'ไม่มีสิทธิ์เข้าถึงชุมชน',
    'payments.noCommunityDesc': 'คุณสมบัติการชำระเงินจะใช้ได้หลังจากเข้าร่วมชุมชน',
    'payments.createCommunity': 'สร้างชุมชนแรกของคุณ',
    
    // Amenity translations (Thai)
    'amenity.privatePool': 'สระว่ายน้ำส่วนตัว',
    'amenity.pool': 'สระว่ายน้ำ',
    'amenity.hotTub': 'อ่างน้ำร้อน',
    'amenity.oceanView': 'วิวทะเล',
    'amenity.sunsetViews': 'วิวพระอาทิตย์ตก',
    'amenity.beachAccess': 'ทางเข้าชายหาด',
    'amenity.smartHome': 'สมาร์ทโฮม',
    'amenity.wineCellar': 'ห้องเก็บไวน์',
    'amenity.homeTheater': 'โฮมเธียเตอร์',
    'amenity.gym': 'ยิม',
    'amenity.spa': 'สปา',
    'amenity.rooftopTerrace': 'ลานดาดฟ้า',
    'amenity.concierge': 'บริการคอนเซียร์จ',
    'amenity.wifi': 'WiFi',
    'amenity.parking': 'ที่จอดรถ',
    'amenity.airConditioning': 'แอร์',
    'amenity.heating': 'เครื่องทำความร้อน',
    'amenity.kitchen': 'ครัว',
    'amenity.washer': 'เครื่องซักผ้า',
    'amenity.dryer': 'เครื่องอบผ้า',
    'amenity.washerDryer': 'เครื่องซัก/อบผ้า',
    'amenity.petFriendly': 'อนุญาตสัตว์เลี้ยง',
    'amenity.garden': 'สวน',
    'amenity.fireplace': 'เตาผิง',
    'amenity.balcony': 'ระเบียง',
    
    // Support (Thai)
    'support.liveChat': 'แชทสด',
    'support.liveChatDesc': 'แชทกับทีมสนับสนุนของเราแบบเรียลไทม์',
    'support.liveChatAvailability': 'เปิดให้บริการ 9.00 - 18.00 น.',
    'support.startChat': 'เริ่มแชท',
    'support.emailSupport': 'สนับสนุนทางอีเมล',
    'support.emailSupportDesc': 'ได้รับการตอบกลับภายใน 24 ชั่วโมง',
    'support.sendEmail': 'ส่งอีเมล',
    'support.phoneSupport': 'สนับสนุนทางโทรศัพท์',
    'support.phoneSupportDesc': 'สำหรับลูกค้าองค์กรเท่านั้น',
    'support.callNow': 'โทรเลย',
    'support.backToHome': 'กลับไปหน้าแรก',
    'support.supportCenter': 'ศูนย์ช่วยเหลือ',
    'support.howCanWeHelp': 'เราช่วยอะไรได้บ้าง?',
    'support.heroSubtitle': 'ทีมของเราพร้อมช่วยเหลือคุณเกี่ยวกับคำถามหรือปัญหาใด ๆ',
    'support.quickAnswers': 'คำตอบด่วน',
    'support.commonQuestions': 'คำถามที่พบบ่อยจากผู้ใช้',
    'support.viewAllFAQs': 'ดูคำถามทั้งหมด',
    'support.sendUsMessage': 'ส่งข้อความถึงเรา',
    'support.name': 'ชื่อ',
    'support.namePlaceholder': 'ชื่อของคุณ',
    'support.email': 'อีเมล',
    'support.emailPlaceholder': 'your@email.com',
    'support.subject': 'หัวข้อ',
    'support.subjectPlaceholder': 'เราช่วยอะไรได้บ้าง?',
    'support.message': 'ข้อความ',
    'support.messagePlaceholder': 'อธิบายปัญหาหรือคำถามของคุณ...',
    'support.sendMessage': 'ส่งข้อความ',
    'support.sending': 'กำลังส่ง...',
    'support.messageSent': 'ข้อความของคุณถูกส่งแล้ว! เราจะติดต่อกลับเร็ว ๆ นี้',
    'support.messageFailed': 'ไม่สามารถส่งข้อความได้ กรุณาลองอีกครั้ง',
    'support.footer': '© 2025 Aether. การจัดการอสังหาริมทรัพย์ ง่ายขึ้น',
    
    // Notifications (Thai)
    'notifications.title': 'การแจ้งเตือน',
    'notifications.markAllRead': 'ทำเครื่องหมายว่าอ่านทั้งหมด',
    'notifications.all': 'ทั้งหมด',
    'notifications.urgent': 'เร่งด่วน',
    'notifications.unread': 'ยังไม่ได้อ่าน',
    'notifications.info': 'ข้อมูล',
    'notifications.noNotifications': 'ไม่มีการแจ้งเตือน',
    'notifications.allCaughtUp': 'ไม่มีการแจ้งเตือนใหม่!',
    'notifications.checkBackLater': 'กลับมาตรวจสอบภายหลังสำหรับการอัปเดต',
    'notifications.today': 'วันนี้',
    'notifications.yesterday': 'เมื่อวาน',
    'notifications.thisWeek': 'สัปดาห์นี้',
    'notifications.earlier': 'ก่อนหน้านี้',
    
    // Rental Editor (Thai)
    'rentalEditor.pricing': 'ราคา',
    'rentalEditor.nightlyRate': 'ราคาต่อคืน',
    'rentalEditor.cleaningFee': 'ค่าทำความสะอาด',
    'rentalEditor.minNights': 'คืนขั้นต่ำ',
    'rentalEditor.maxNights': 'คืนสูงสุด',
    'rentalEditor.description': 'คำอธิบาย',
    'rentalEditor.descriptionPlaceholder': 'อธิบายอสังหาริมทรัพย์ของคุณ...',
    'rentalEditor.amenities': 'สิ่งอำนวยความสะดวก',
    'rentalEditor.houseRules': 'กฎของบ้าน',
    'rentalEditor.houseRulesPlaceholder': 'ใส่กฎของบ้าน (เช่น ห้ามสูบบุหรี่ ห้ามปาร์ตี้...)',
    'rentalEditor.smartHomeInstructions': 'คำแนะนำสมาร์ทโฮม',
    'rentalEditor.smartHomeDesc': 'แสดงให้แขกเห็นหลังยืนยันการจอง',
    'rentalEditor.tip': 'เคล็ดลับ',
    'rentalEditor.smartHomeTip': 'ใส่รหัสผ่าน WiFi รหัสประตู คำสั่งเสียง และตำแหน่งอุปกรณ์ที่นี่',
    'rentalEditor.publishListing': 'เผยแพร่รายการ',
    'rentalEditor.publishDesc': 'ให้แขกมองเห็นรายการของคุณ',
    'rentalEditor.saveListing': 'บันทึกรายการ',
    'rentalEditor.rentalType': 'ประเภทการเช่า',
    'rentalEditor.shortTerm': 'เช่าระยะสั้น',
    'rentalEditor.shortTermDesc': 'การจองรายคืนสำหรับวันหยุดและที่พักระยะสั้น (1-30 คืน)',
    'rentalEditor.longTerm': 'เช่าระยะยาว',
    'rentalEditor.longTermDesc': 'สัญญาเช่ารายเดือนสำหรับที่พักระยะยาว (6 เดือนขึ้นไป)',
    'rentalEditor.bothTypes': 'ทั้งระยะสั้นและระยะยาว',
    'rentalEditor.bothTypesDesc': 'รับจองทั้งรายคืนและรายเดือน',
    'rentalEditor.shortTermPricing': 'ราคาระยะสั้น',
    'rentalEditor.longTermPricing': 'ราคาระยะยาว',
    'rentalEditor.monthlyRate': 'ราคาต่อเดือน',
    'rentalEditor.monthlyRateHint': 'ราคาต่อเดือนสำหรับผู้เช่าระยะยาว',
    'rentalEditor.minLeaseDuration': 'ระยะเวลาเช่าขั้นต่ำ (เดือน)',
    
    // Admin Dashboard (Thai)
    'admin.rentals': 'การเช่า',
    'admin.news': 'ข่าว',
    'admin.inviteOwner': 'เชิญเจ้าของ',
    'admin.inviteOwnerTitle': 'เชิญเจ้าของอสังหาริมทรัพย์',
    'admin.inviteOwnerDesc': 'เจ้าของจะได้รับอีเมลเชิญเพื่อตั้งค่าบัญชีและจัดการอสังหาริมทรัพย์นี้',
    
    // Community Switcher (Thai)
    'community.selectCommunity': 'เลือกชุมชน',
    'community.createNew': 'สร้างชุมชนใหม่',
    
    // Map Page (Thai)
    'map.title': 'แผนที่ชุมชน',
    'map.amenities': 'สิ่งอำนวยความสะดวก',
    'map.properties': 'อสังหาริมทรัพย์',
    
    // Service Marketplace additional (Thai)
    'service.services': 'บริการ',
    'service.bookPremium': 'จองบริการพรีเมียม',
    'service.all': 'ทั้งหมด',
    'service.noServices': 'ไม่มีบริการที่พร้อมใช้งาน',
    'service.checkBackLater': 'กลับมาตรวจสอบภายหลัง',
    'service.by': 'โดย',
    'service.book': 'จอง',
    'service.service': 'บริการ',
    'service.estimatedPrice': 'ราคาโดยประมาณ',
    'service.priceVaryNote': 'ราคาสุดท้ายอาจแตกต่างกันตามขนาดอสังหาริมทรัพย์',
    'service.scheduleDateTime': 'กำหนดวันและเวลา',
    'service.specialRequests': 'คำขอพิเศษ (ตัวเลือก)',
    'service.specialInstructions': 'คำแนะนำพิเศษใดๆ...',
    'service.payWithCard': 'ชำระด้วยบัตร (USD)',
    'service.category.housekeeping': 'ทำความสะอาด',
    'service.category.wellness': 'สุขภาพ',
    'service.category.maintenance': 'ซ่อมบำรุง',
    'service.category.dining': 'อาหาร',
    'service.category.transport': 'ขนส่ง',
    
    // Chat (additional - Thai)
    'chat.fallbackError': 'ขออภัย ไม่สามารถประมวลผลคำขอของคุณได้ โปรดลองอีกครั้ง',
    'chat.bookingSuccess': 'ยืนยันการจองเรียบร้อยแล้ว',
    'chat.bookingIssue': 'มีปัญหาในการสร้างการจอง โปรดลองอีกครั้ง',
    'chat.temporaryError': 'ขออภัย เกิดปัญหาการเชื่อมต่อชั่วคราว โปรดลองอีกครั้งในอีกสักครู่',
    
    // Map (additional - Thai)
    'map.loading': 'กำลังโหลดแผนที่...',
    
    // God Mode Dashboard (Thai)
    'godMode.title': 'โหมดผู้ดูแลสูงสุด',
    'godMode.commandCenter': 'แผงควบคุมผู้ดูแล',
    'godMode.adminPanel': 'แผงควบคุมผู้ดูแล',
    'godMode.communities': 'ชุมชน',
    'godMode.homes': 'บ้าน',
    'godMode.mrr': 'รายได้ต่อเดือน',
    'godMode.propertyDevelopers': 'ผู้พัฒนาอสังหาริมทรัพย์',
    'godMode.developerInviteDesc': 'เชิญผู้พัฒนาอสังหาริมทรัพย์มาสร้างและจัดการชุมชนบนแพลตฟอร์ม',
    'godMode.add': 'เพิ่ม',
    'godMode.createNewCommunity': 'สร้างชุมชนใหม่',
    'godMode.communityName': 'ชื่อชุมชน',
    'godMode.city': 'เมือง',
    'godMode.state': 'จังหวัด',
    'godMode.baseFee': 'ค่าธรรมเนียมพื้นฐาน/เดือน',
    'godMode.perHomeFee': 'ค่าธรรมเนียมต่อบ้าน',
    'godMode.createCommunity': 'สร้างชุมชน',
    'godMode.noCommunities': 'ยังไม่มีชุมชน',
    'godMode.createFirstCommunity': 'สร้างชุมชนแรกของคุณเพื่อเริ่มต้น',
    'godMode.active': 'ใช้งาน',
    'godMode.inactive': 'ไม่ใช้งาน',
    'godMode.noLocation': 'ไม่มีตำแหน่ง',
    
    // Admin additional
    'admin.owner': 'เจ้าของ',
    'admin.homeownerEmail': 'อีเมลเจ้าของบ้าน',
    'admin.property': 'อสังหาริมทรัพย์',
    'admin.totalProperties': 'ทรัพย์สินทั้งหมด',
    'admin.rentalModeOn': 'โหมดให้เช่าเปิด',
    'admin.activeListings': 'ประกาศที่ใช้งาน',
    'admin.upcomingArrivals': 'ผู้เข้าพักที่จะมาถึง',
    'admin.rentalProperties': 'ทรัพย์สินให้เช่า',
    'admin.rentalOn': 'เปิดให้เช่า',
    'admin.rentalOff': 'ปิดให้เช่า',
    'admin.upcomingBookings': 'การจองที่จะมาถึง',
    'admin.listingInactive': 'ประกาศไม่ใช้งาน',
    
    // Rental types
    'rental.shortTerm': 'เช่าระยะสั้น',
    'rental.longTerm': 'เช่าระยะยาว',
    'rental.bothTypes': 'ทั้งสองแบบ',
    
    // Review Form
    'reviewForm.howWasStay': 'การเข้าพักเป็นอย่างไร?',
    'reviewForm.overallExperience': 'ประสบการณ์โดยรวม',
    'reviewForm.ratingRequired': 'กรุณาให้คะแนนโดยรวม',
    'reviewForm.submitSuccess': 'ส่งรีวิวสำเร็จ!',
    'reviewForm.submitFailed': 'ส่งรีวิวไม่สำเร็จ',
    'reviewForm.titleLabel': 'หัวข้อรีวิว (ไม่บังคับ)',
    'reviewForm.titlePlaceholder': 'สรุปประสบการณ์ของคุณ',
    'reviewForm.contentLabel': 'รีวิวของคุณ (ไม่บังคับ)',
    'reviewForm.contentPlaceholder': 'แชร์รายละเอียดการเข้าพัก...',
    'reviewForm.submit': 'ส่งรีวิว',
    
    // Review Moderation
    'moderation.title': 'การจัดการรีวิว',
    'moderation.total': 'ทั้งหมด',
    'moderation.pending': 'รอดำเนินการ',
    'moderation.flagged': 'ถูกแจ้ง',
    'moderation.all': 'ทั้งหมด',
    'moderation.published': 'เผยแพร่แล้ว',
    'moderation.hidden': 'ซ่อนแล้ว',
    'moderation.lowRating': 'คะแนนต่ำ',
    'moderation.unknownProperty': 'ไม่ทราบอสังหาริมทรัพย์',
    'moderation.anonymous': 'ไม่ระบุชื่อ',
    'moderation.commShort': 'สื่อสาร',
    'moderation.hide': 'ซ่อน',
    'moderation.publish': 'เผยแพร่',
    'moderation.edit': 'แก้ไข',
    'moderation.respond': 'ตอบกลับ',
    'moderation.statusUpdated': 'อัปเดตสถานะรีวิวแล้ว',
    'moderation.updateFailed': 'อัปเดตรีวิวไม่สำเร็จ',
    'moderation.responseAdded': 'เพิ่มคำตอบสำเร็จ',
    'moderation.responseFailed': 'เพิ่มคำตอบไม่สำเร็จ',
    'moderation.noReviews': 'ยังไม่มีรีวิว',
    'moderation.noPending': 'ไม่มีรีวิวที่รอดำเนินการ',
    'moderation.noPublished': 'ไม่มีรีวิวที่เผยแพร่',
    'moderation.noFlagged': 'ไม่มีรีวิวที่ถูกแจ้ง',
    'moderation.editResponse': 'แก้ไขคำตอบ',
    'moderation.addResponse': 'เพิ่มคำตอบ',
    'moderation.by': 'โดย',
    'moderation.responsePlaceholder': 'เขียนคำตอบของคุณสำหรับรีวิวนี้...',
    'moderation.saveResponse': 'บันทึกคำตอบ',
    
    // Audit Log (Thai)
    'audit.title': 'บันทึกการตรวจสอบ',
    'audit.subtitle': 'บันทึกกิจกรรมของระบบ',
    'audit.noLogs': 'ไม่พบบันทึกการตรวจสอบ',
    'audit.noLogsDesc': 'กิจกรรมของระบบจะแสดงที่นี่',
    'audit.filter': 'กรองตามการกระทำ',
    'audit.allActions': 'การกระทำทั้งหมด',
    'audit.allResources': 'ทรัพยากรทั้งหมด',
    'audit.searchPlaceholder': 'ค้นหาการกระทำ, ทรัพยากร...',
    'audit.loadMore': 'โหลดเพิ่มเติม',
    'common.refresh': 'รีเฟรช',
    
    // Services Manager (Thai)
    'services.title': 'การจัดการบริการ',
    'services.addService': 'เพิ่มบริการ',
    'services.editService': 'แก้ไขบริการ',
    'services.serviceName': 'ชื่อบริการ',
    'services.serviceNamePlaceholder': 'เช่น ทำความสะอาดล้ำลึก',
    'services.description': 'คำอธิบาย',
    'services.descriptionPlaceholder': 'อธิบายบริการ...',
    'services.category': 'หมวดหมู่',
    'services.selectCategory': 'เลือกหมวดหมู่',
    'services.vendor': 'ผู้ขาย (ตัวเลือก)',
    'services.selectVendor': 'เลือกผู้ขาย',
    'services.noVendor': 'ไม่มีผู้ขาย',
    'services.basePrice': 'ราคาพื้นฐาน',
    'services.pricePerBedroom': 'ราคาต่อห้องนอน',
    'services.pricePerSqft': 'ราคาต่อตารางฟุต',
    'services.duration': 'ระยะเวลา (นาที)',
    'services.isGlobal': 'พร้อมใช้งานสำหรับทุกชุมชน',
    'services.isActive': 'ใช้งาน',
    'services.saveService': 'บันทึกบริการ',
    'services.deleteConfirm': 'ลบบริการนี้?',
    'services.deleteSuccess': 'ลบบริการแล้ว',
    'services.deleteFailed': 'ลบบริการไม่สำเร็จ',
    'services.saveSuccess': 'บันทึกบริการแล้ว',
    'services.saveFailed': 'บันทึกบริการไม่สำเร็จ',
    'services.noServices': 'ยังไม่มีบริการ',
    'services.addFirst': 'เพิ่มบริการแรกเพื่อเริ่มต้น',
    'services.global': 'ทั่วโลก',
    'services.housekeeping': 'งานแม่บ้าน',
    'services.wellness': 'สุขภาพ',
    'services.maintenance': 'การบำรุงรักษา',
    'services.dining': 'ร้านอาหาร',
    'services.transport': 'การขนส่ง',
    
    // Vendor Manager (Thai)
    'vendor.title': 'การจัดการผู้ขาย',
    'vendor.addVendor': 'เพิ่มผู้ขาย',
    'vendor.editVendor': 'แก้ไขผู้ขาย',
    'vendor.vendorName': 'ชื่อผู้ขาย',
    'vendor.vendorNamePlaceholder': 'เช่น บริษัททำความสะอาดพรีเมียม',
    'vendor.email': 'อีเมล',
    'vendor.emailPlaceholder': 'vendor@example.com',
    'vendor.description': 'คำอธิบาย',
    'vendor.descriptionPlaceholder': 'อธิบายผู้ขาย...',
    'vendor.category': 'หมวดหมู่',
    'vendor.selectCategory': 'เลือกหมวดหมู่',
    'vendor.commissionRate': 'อัตราค่าคอมมิชชั่น (%)',
    'vendor.isGlobal': 'พร้อมใช้งานสำหรับทุกชุมชน',
    'vendor.isActive': 'ใช้งาน',
    'vendor.saveVendor': 'บันทึกผู้ขาย',
    'vendor.deleteConfirm': 'ลบผู้ขายนี้?',
    'vendor.deleteSuccess': 'ลบผู้ขายแล้ว',
    'vendor.deleteFailed': 'ลบผู้ขายไม่สำเร็จ',
    'vendor.saveSuccess': 'บันทึกผู้ขายแล้ว',
    'vendor.saveFailed': 'บันทึกผู้ขายไม่สำเร็จ',
    'vendor.noVendors': 'ยังไม่มีผู้ขาย',
    'vendor.addFirst': 'เพิ่มผู้ขายแรกเพื่อเริ่มต้น',
    'vendor.global': 'ทั่วโลก',
    'vendor.commission': 'ค่าคอมมิชชั่น',
    'vendor.paymentSettings': 'การตั้งค่าการชำระเงิน',
    'vendor.stripeConnected': 'เชื่อมต่อ Stripe แล้ว',
    'vendor.stripeNotConnected': 'ยังไม่ได้เชื่อมต่อ Stripe',
    'vendor.mobilePayment': 'การชำระเงินผ่านมือถือ',
    'vendor.accountName': 'ชื่อบัญชี',
    'vendor.phoneNumber': 'หมายเลขโทรศัพท์',
    
    // Payment Verification (Thai)
    'paymentVerification.title': 'รอการยืนยัน',
    'paymentVerification.noPayments': 'ไม่มีการชำระเงินที่รอยืนยัน',
    'paymentVerification.allProcessed': 'การชำระเงินทั้งหมดได้รับการประมวลผลแล้ว',
    'paymentVerification.verifyPayment': 'ยืนยันการชำระเงิน',
    'paymentVerification.amount': 'จำนวนเงิน',
    'paymentVerification.paymentMethod': 'วิธีการชำระเงิน',
    'paymentVerification.customer': 'ลูกค้า',
    'paymentVerification.date': 'วันที่',
    'paymentVerification.property': 'อสังหาริมทรัพย์',
    'paymentVerification.paymentProof': 'หลักฐานการชำระเงิน',
    'paymentVerification.fullSize': 'ขนาดเต็ม',
    'paymentVerification.noProof': 'ไม่ได้อัปโหลดหลักฐานการชำระเงิน',
    'paymentVerification.verifyManually': 'กรุณายืนยันการชำระเงินนี้ด้วยตนเองในแอปธนาคารของคุณ',
    'paymentVerification.rejectionReason': 'เหตุผลในการปฏิเสธ (ถ้าปฏิเสธ)',
    'paymentVerification.rejectionPlaceholder': 'เช่น จำนวนเงินไม่ตรง, ภาพหน้าจอไม่ถูกต้อง...',
    'paymentVerification.approve': 'อนุมัติ',
    'paymentVerification.reject': 'ปฏิเสธ',
    'paymentVerification.approved': 'อนุมัติการชำระเงินสำเร็จ',
    'paymentVerification.rejected': 'ปฏิเสธการชำระเงินแล้ว',
    'paymentVerification.approveFailed': 'อนุมัติการชำระเงินไม่สำเร็จ',
    'paymentVerification.rejectFailed': 'ปฏิเสธการชำระเงินไม่สำเร็จ',
    'paymentVerification.provideReason': 'กรุณาระบุเหตุผลในการปฏิเสธ',
    'paymentVerification.hasProof': 'มีหลักฐาน',
    'paymentVerification.hoa': 'HOA',
    'paymentVerification.rental': 'เช่า',
    'paymentVerification.loadFailed': 'ไม่สามารถโหลดการชำระเงินที่รอดำเนินการได้',
    
    // Vendor Payouts (Thai)
    'vendorPayouts.totalRevenue': 'รายได้รวม',
    'vendorPayouts.platformFees': 'ค่าธรรมเนียมแพลตฟอร์ม',
    'vendorPayouts.vendorPayouts': 'การจ่ายเงินให้ผู้ขาย',
    'vendorPayouts.pendingPayouts': 'การจ่ายเงินที่รอดำเนินการ',
    'vendorPayouts.completedPayouts': 'การจ่ายเงินที่เสร็จสิ้น',
    'vendorPayouts.noVendors': 'ยังไม่มีผู้ขาย',
    'vendorPayouts.addVendorsFirst': 'เพิ่มผู้ขายเพื่อดูรายได้',
    'vendorPayouts.vendor': 'ผู้ขาย',
    'vendorPayouts.category': 'หมวดหมู่',
    'vendorPayouts.earnings': 'รายได้',
    'vendorPayouts.commission': 'ค่าคอมมิชชั่น',
    'vendorPayouts.payout': 'การจ่ายเงิน',
    'vendorPayouts.status': 'สถานะ',
    'vendorPayouts.actions': 'การดำเนินการ',
    'vendorPayouts.markPaid': 'ทำเครื่องหมายว่าชำระแล้ว',
    'vendorPayouts.viewHistory': 'ดูประวัติ',
    'vendorPayouts.paid': 'ชำระแล้ว',
    'vendorPayouts.pending': 'รอดำเนินการ',
    
    // Calendar (Thai)
    'calendar.title': 'ปฏิทินการจอง',
    'calendar.today': 'วันนี้',
    'calendar.week': 'สัปดาห์',
    'calendar.month': 'เดือน',
    'calendar.noBookings': 'ยังไม่มีการจอง',
    'calendar.dragToReschedule': 'ลากเพื่อจัดกำหนดการใหม่',
    'calendar.reschedule': 'จัดกำหนดการใหม่',
    'calendar.newDate': 'วันที่ใหม่',
    'calendar.newTime': 'เวลาใหม่',
    'calendar.from': 'จาก:',
    'calendar.to': 'ถึง:',
    'calendar.at': 'เวลา',
    'calendar.confirm': 'ยืนยัน',
    'calendar.rescheduled': 'จัดกำหนดการใหม่สำเร็จ',
    'calendar.rescheduleFailed': 'จัดกำหนดการใหม่ไม่สำเร็จ',
    'calendar.availabilityCalendar': 'ปฏิทินความพร้อม',
    
    // Relative Dates (Thai)
    'dates.today': 'วันนี้',
    'dates.yesterday': 'เมื่อวาน',
    'dates.tomorrow': 'พรุ่งนี้',
    'dates.daysAgo': '{count} วันที่แล้ว',
    'dates.inDays': 'อีก {count} วัน',
    'dates.justNow': 'เมื่อสักครู่',
    'dates.dueToday': 'ครบกำหนดวันนี้',
    
    // Settings Page (Phase 4 additions - Thai)
    'settings.manageAccount': 'จัดการบัญชีของคุณ',
    'settings.developerIntegrations': 'นักพัฒนาและการผสานรวม',
    'settings.yourProperties': 'อสังหาริมทรัพย์ของคุณ',
    'settings.setupProfile': 'ตั้งค่าโปรไฟล์ของคุณ',
    'settings.profileUpdated': 'อัปเดตโปรไฟล์สำเร็จ',
    'settings.profileUpdateFailed': 'ไม่สามารถอัปเดตโปรไฟล์ได้',
    'settings.pushNotSupported': 'การแจ้งเตือนแบบพุชไม่รองรับในเบราว์เซอร์นี้',
    'settings.pushEnabled': 'เปิดใช้งานการแจ้งเตือนแบบพุชแล้ว',
    'settings.pushDenied': 'ปฏิเสธการอนุญาตการแจ้งเตือนแบบพุช',
    'settings.pushEnableFailed': 'ไม่สามารถเปิดใช้งานการแจ้งเตือนแบบพุชได้',
    'settings.openingPortal': 'กำลังเปิดพอร์ทัลชำระเงิน...',
    'settings.portalFailed': 'ไม่สามารถเปิดพอร์ทัลชำระเงินได้',
    'settings.editProfile': 'แก้ไขโปรไฟล์',
    'settings.fullName': 'ชื่อเต็ม',
    'settings.enterName': 'ใส่ชื่อของคุณ',
    'settings.email': 'อีเมล',
    'settings.phoneNumber': 'หมายเลขโทรศัพท์',
    'settings.enterPhone': 'ใส่หมายเลขโทรศัพท์ของคุณ',
    'settings.saveChanges': 'บันทึกการเปลี่ยนแปลง',
    'settings.emailNotifications': 'การแจ้งเตือนทางอีเมล',
    'settings.bookingUpdates': 'การอัปเดตการจอง',
    'settings.bookingUpdatesDesc': 'รับการอัปเดตการจองทางอีเมล',
    'settings.paymentAlerts': 'การแจ้งเตือนการชำระเงิน',
    'settings.paymentAlertsDesc': 'รับการแจ้งเตือนกิจกรรมการชำระเงินทางอีเมล',
    'settings.guestPassActivity': 'กิจกรรมบัตรผ่านแขก',
    'settings.guestPassActivityDesc': 'รับการแจ้งเตือนเมื่อแขกใช้บัตรผ่าน',
    'settings.pushNotifications': 'การแจ้งเตือนแบบพุช',
    'settings.enablePush': 'เปิดใช้งานการแจ้งเตือนแบบพุช',
    'settings.enablePushDesc': 'รับการแจ้งเตือนแบบเรียลไทม์บนอุปกรณ์ของคุณ',
    'settings.bookingNotifications': 'การแจ้งเตือนการจอง',
    'settings.bookingNotificationsDesc': 'การจองใหม่และการเปลี่ยนแปลง',
    'settings.messageNotifications': 'การแจ้งเตือนข้อความ',
    'settings.messageNotificationsDesc': 'ข้อความใหม่จากแขก',
    'settings.announcementNotifications': 'การแจ้งเตือนประกาศ',
    'settings.announcementNotificationsDesc': 'ประกาศของชุมชน',
    'settings.managePaymentMethods': 'จัดการวิธีการชำระเงิน',
    'settings.managePaymentMethodsDesc': 'อัปเดตวิธีการชำระเงินผ่านพอร์ทัลที่ปลอดภัย',
    'settings.openPaymentPortal': 'เปิดพอร์ทัลชำระเงิน',
    'settings.paymentSecurityNote': 'ข้อมูลการชำระเงินของคุณได้รับการจัดการอย่างปลอดภัยโดย Stripe',
    'settings.preferences': 'การตั้งค่า',
    'settings.selectTimezone': 'เลือกเขตเวลา',
    'settings.timezoneSet': 'ตั้งค่าเขตเวลาเป็น',
    'settings.selectLanguage': 'เลือกภาษา',
    'settings.languageChanged': 'เปลี่ยนภาษาเป็น',
    'settings.selectCurrency': 'เลือกสกุลเงิน',
    'settings.currencyChanged': 'เปลี่ยนสกุลเงินเป็น',
    'settings.preferencesSaved': 'การตั้งค่าของคุณถูกบันทึกโดยอัตโนมัติ',
    
    // Service Marketplace (Phase 4 - Thai)
    'service.selectDateTime': 'กรุณาเลือกวันที่และเวลา',
    'service.bookingCreated': 'สร้างการจองแล้ว! ชำระเงินเพื่อยืนยัน',
    'service.bookingFailed': 'ไม่สามารถจองบริการได้',
    'service.paymentSubmitted': 'ส่งการชำระเงินแล้ว! รอการตรวจสอบ',
    
    // Browse Rentals (Phase 4 - Thai)
    'browse.pleaseSignIn': 'กรุณาเข้าสู่ระบบเพื่อจองที่พัก',
    'browse.selectDates': 'กรุณาเลือกวันเช็คอินและเช็คเอาท์',
    'browse.minStayError': 'ขั้นต่ำ {nights} คืน',
    'browse.maxStayError': 'สูงสุด {nights} คืน',
    'browse.redirectingPayment': 'กำลังเปลี่ยนเส้นทางไปยังการชำระเงิน...',
    'browse.bookingFailed': 'ไม่สามารถสร้างการจองได้',
    
    // Booking History (Phase 4 - Thai)
    'bookingHistory.title': 'ประวัติการจอง',
    'bookingHistory.totalBookings': 'ทั้งหมด {count} การจอง',
    'bookingHistory.upcoming': 'กำลังจะมาถึง',
    'bookingHistory.past': 'ที่ผ่านมา',
    'bookingHistory.filterStatus': 'กรองตามสถานะ',
    'bookingHistory.allStatuses': 'ทุกสถานะ',
    'bookingHistory.inProgress': 'กำลังดำเนินการ',
    'bookingHistory.noUpcoming': 'ไม่มีการจองที่กำลังจะมาถึง',
    'bookingHistory.noPast': 'ไม่มีการจองที่ผ่านมา',
    'bookingHistory.bookService': 'จองบริการเพื่อดูที่นี่',
    'bookingHistory.willAppear': 'การจองที่เสร็จสิ้นจะปรากฏที่นี่',
    'bookingHistory.browseServices': 'เรียกดูบริการ',
    'bookingHistory.viewReceipt': 'ดูใบเสร็จ',
    'bookingHistory.reschedule': 'เลื่อนนัดหมาย',
    'bookingHistory.cancel': 'ยกเลิก',
    'bookingHistory.rescheduleBooking': 'เลื่อนการจอง',
    'bookingHistory.service': 'บริการ',
    'bookingHistory.newDateTime': 'วันที่และเวลาใหม่',
    'bookingHistory.confirmReschedule': 'ยืนยันการเลื่อน',
    'bookingHistory.cancelSuccess': 'ยกเลิกการจองสำเร็จ',
    'bookingHistory.cancelFailed': 'ยกเลิกการจองไม่สำเร็จ',
    'bookingHistory.rescheduleSuccess': 'เลื่อนการจองสำเร็จ',
    'bookingHistory.rescheduleFailed': 'เลื่อนการจองไม่สำเร็จ',
    'bookingHistory.paymentPaid': 'ชำระแล้ว',
    'bookingHistory.paymentProcessing': 'กำลังดำเนินการ',
    'bookingHistory.paymentFailed': 'ล้มเหลว',
    'bookingHistory.paymentPending': 'รอการชำระเงิน',
    'rentalManagement.title': 'จัดการการเช่า',
    'rentalManagement.notEnabled': 'ไม่ได้เปิดใช้งานโหมดเช่า',
    'rentalManagement.enableDesc': 'เปิดใช้งานโหมดเช่าจากแดชบอร์ด',
    'rentalManagement.live': 'เปิดใช้งาน',
    'rentalManagement.draft': 'แบบร่าง',
    'rentalManagement.perNight': 'ต่อคืน',
    'rentalManagement.nights': 'คืน',
    'rentalManagement.bedrooms': 'ห้องนอน',
    'rentalManagement.listing': 'รายการ',
    'rentalManagement.calendar': 'ปฏิทิน',
    'rentalManagement.analytics': 'การวิเคราะห์',
    'rentalManagement.bookings': 'การจอง',
    'rentalManagement.createFirst': 'สร้างรายการก่อนเพื่อจัดการ',
    'rentalManagement.saveSuccess': 'บันทึกรายการสำเร็จ',
    'rentalManagement.saveFailed': 'บันทึกไม่สำเร็จ',
    'rentalManagement.availabilityUpdated': 'อัปเดตความพร้อมแล้ว',
    'rentalManagement.availabilityFailed': 'อัปเดตไม่สำเร็จ',
    'messaging.noMessages': 'ยังไม่มีข้อความ',
    'messaging.startConversation': 'เริ่มการสนทนา!',
    'messaging.typeMessage': 'พิมพ์ข้อความ...',
    'messaging.loadFailed': 'โหลดข้อความไม่สำเร็จ',
    'messaging.sendFailed': 'ส่งข้อความไม่สำเร็จ',
    'validation.nameRequired': 'กรุณากรอกชื่อ',
    'validation.nameMaxLength': 'ชื่อต้องไม่เกิน 100 ตัวอักษร',
    'validation.emailInvalid': 'อีเมลไม่ถูกต้อง',
    'validation.emailMaxLength': 'อีเมลต้องไม่เกิน 255 ตัวอักษร',
    'validation.subjectRequired': 'กรุณากรอกหัวข้อ',
    'validation.subjectMaxLength': 'หัวข้อต้องไม่เกิน 200 ตัวอักษร',
    'validation.messageRequired': 'กรุณากรอกข้อความ',
    'validation.messageMaxLength': 'ข้อความต้องไม่เกิน 2000 ตัวอักษร',
    
    // QR Payment Dialog
    'qrPayment.notAvailable': 'ไม่สามารถชำระเงินได้',
    'qrPayment.notSetUp': 'ไม่ได้ตั้งค่าการชำระเงิน {method} สำหรับอสังหาริมทรัพย์นี้',
    'qrPayment.contactManager': 'กรุณาติดต่อผู้จัดการอสังหาริมทรัพย์หรือใช้วิธีชำระเงินอื่น',
    'qrPayment.payWith': 'ชำระด้วย {method}',
    'qrPayment.amountToPay': 'จำนวนเงินที่ต้องชำระ',
    'qrPayment.copyAmount': 'คัดลอกจำนวนเงิน',
    'qrPayment.howToPay': 'วิธีชำระเงิน:',
    'qrPayment.step1': 'เปิดแอป {method} ของคุณ',
    'qrPayment.step2': 'แตะ "สแกน QR" และสแกนรหัสด้านบน',
    'qrPayment.step3': 'กรอกจำนวนเงินที่ถูกต้อง: {amount}',
    'qrPayment.step4': 'ดำเนินการชำระเงินให้เสร็จสิ้น',
    'qrPayment.step5': 'ถ่ายภาพหน้าจอการยืนยัน',
    'qrPayment.madePayment': 'ฉันชำระเงินแล้ว',
    'qrPayment.confirmPayment': 'ยืนยันการชำระเงินของคุณ',
    'qrPayment.uploadScreenshot': 'อัปโหลดภาพหน้าจอการยืนยันการชำระเงินเพื่อการตรวจสอบที่รวดเร็วขึ้น',
    'qrPayment.paymentScreenshot': 'ภาพหน้าจอการชำระเงิน (แนะนำ)',
    'qrPayment.tapToUpload': 'แตะเพื่ออัปโหลดภาพหน้าจอ',
    'qrPayment.remove': 'ลบ',
    'qrPayment.submitting': 'กำลังส่ง...',
    'qrPayment.confirm': 'ยืนยันการชำระเงิน',
    'qrPayment.submitted': 'ส่งการชำระเงินแล้ว!',
    'qrPayment.beingVerified': 'กำลังตรวจสอบการชำระเงินของคุณ คุณจะได้รับแจ้งเมื่อยืนยันแล้ว',
    'qrPayment.verificationTime': 'โดยปกติใช้เวลา 1-24 ชั่วโมงในวันทำการ',
    'qrPayment.done': 'เสร็จสิ้น',
    'qrPayment.phoneCopied': 'คัดลอกหมายเลขโทรศัพท์แล้ว',
    'qrPayment.amountCopied': 'คัดลอกจำนวนเงินแล้ว',
    'qrPayment.uploadImage': 'กรุณาอัปโหลดไฟล์รูปภาพ',
    'qrPayment.screenshotUploaded': 'อัปโหลดภาพหน้าจอการชำระเงินแล้ว',
    'qrPayment.uploadFailed': 'อัปโหลดภาพหน้าจอไม่สำเร็จ',
    'qrPayment.submitFailed': 'ส่งการชำระเงินไม่สำเร็จ',
    
    // Community Creator (Thai)
    'communityCreator.title': 'สร้างชุมชนใหม่',
    'communityCreator.completePaymentSetup': 'ตั้งค่าการชำระเงินให้เสร็จสมบูรณ์',
    'communityCreator.uploadQrCodes': 'อัปโหลด QR Code การชำระเงิน',
    'communityCreator.createButton': 'สร้างชุมชน',
    'communityCreator.communityName': 'ชื่อชุมชน',
    'communityCreator.communityNamePlaceholder': 'เช่น โอเชียนวิว เอสเตท',
    'communityCreator.streetAddress': 'ที่อยู่',
    'communityCreator.streetAddressPlaceholder': 'กรอกที่อยู่...',
    'communityCreator.city': 'เมือง',
    'communityCreator.cityPlaceholder': 'เมือง',
    'communityCreator.state': 'จังหวัด',
    'communityCreator.statePlaceholder': 'จังหวัด',
    'communityCreator.zipCode': 'รหัสไปรษณีย์',
    'communityCreator.zipCodePlaceholder': 'รหัสไปรษณีย์',
    'communityCreator.description': 'คำอธิบาย',
    'communityCreator.descriptionPlaceholder': 'คำอธิบายสั้นๆ เกี่ยวกับชุมชนของคุณ...',
    'communityCreator.detailsTab': 'รายละเอียด',
    'communityCreator.paymentsTab': 'การชำระเงิน',
    'communityCreator.nextPaymentSettings': 'ถัดไป: การตั้งค่าการชำระเงิน',
    'communityCreator.defaultCurrency': 'สกุลเงินเริ่มต้น',
    'communityCreator.currencyHintMMK': 'จ๊าตเมียนมาร์สำหรับการชำระเงินในประเทศ',
    'communityCreator.currencyHintUSD': 'ดอลลาร์สหรัฐสำหรับการชำระเงินระหว่างประเทศ',
    'communityCreator.acceptedPaymentMethods': 'วิธีการชำระเงินที่รับ',
    'communityCreator.selectPaymentMethod': 'กรุณาเลือกวิธีการชำระเงินอย่างน้อยหนึ่งวิธี',
    'communityCreator.qrCodePrompt': 'คุณจะถูกขอให้อัปโหลด QR Code สำหรับการชำระเงินผ่านมือถือหลังจากสร้างชุมชน',
    'communityCreator.creating': 'กำลังสร้าง...',
    'communityCreator.completeStripeSetup': 'ตั้งค่า Stripe ให้เสร็จสมบูรณ์',
    'communityCreator.stripeOnboardingDescription': 'ชุมชนของคุณถูกสร้างแล้ว! เพื่อรับการชำระเงิน HOA กรุณาเสร็จสิ้นการลงทะเบียน Stripe',
    'communityCreator.doLater': 'ทำภายหลัง',
    'communityCreator.qrUploadDescription': 'อัปโหลด QR Code ของ KBZPay และ Wave Money เพื่อให้เจ้าของบ้านสามารถชำระค่าธรรมเนียมได้',
    'communityCreator.skipForNow': 'ข้ามไปก่อน',
    'communityCreator.success': 'สร้างชุมชนสำเร็จแล้ว!',
    'communityCreator.error': 'ไม่สามารถสร้างชุมชนได้',

    // Landing Page (Thai)
    'landing.hero.badge': 'สำหรับนักพัฒนาอสังหาริมทรัพย์และ HOA',
    'landing.hero.title': 'จัดการทรัพย์สินของคุณ',
    'landing.hero.titleHighlight': 'โดยไม่ต้องวุ่นวาย',
    'landing.hero.subtitle': 'แพลตฟอร์มเดียวสำหรับการชำระเงิน การเข้าถึงของแขก การจองบริการ และการสื่อสารกับผู้พักอาศัย ลดงานธุรการ ผู้พักอาศัยมีความสุขมากขึ้น',
    'landing.hero.getStarted': 'เริ่มต้นใช้งาน',
    'landing.hero.watchDemo': 'ดูตัวอย่าง',
    'landing.hero.trustSecurity': 'ความปลอดภัยระดับธนาคาร',
    'landing.hero.trustProperties': '500+ ทรัพย์สิน',
    'landing.hero.trustResidents': '10,000+ ผู้พักอาศัย',
    'landing.howItWorks.title': 'เริ่มต้นใน 3 ขั้นตอนง่ายๆ',
    'landing.howItWorks.subtitle': 'จากการลงทะเบียนถึงการใช้งานจริงในเวลาน้อยกว่า 10 นาที',
    'landing.howItWorks.step1.title': 'สร้างบัญชีของคุณ',
    'landing.howItWorks.step1.desc': 'ลงทะเบียนด้วยอีเมล เพิ่มรายละเอียดชุมชนและเชิญทีมของคุณ',
    'landing.howItWorks.step2.title': 'เพิ่มทรัพย์สินและผู้พักอาศัย',
    'landing.howItWorks.step2.desc': 'นำเข้ารายการทรัพย์สินหรือเพิ่มด้วยตนเอง ส่งคำเชิญให้ผู้พักอาศัย',
    'landing.howItWorks.step3.title': 'เริ่มใช้งาน',
    'landing.howItWorks.step3.desc': 'เริ่มรับการชำระเงิน จัดการบัตรผ่านแขก และจองบริการได้ทันที',
    'landing.howItWorks.cta': 'เริ่มทดลองใช้ฟรี',
    'landing.personas.title': 'สร้างมาสำหรับทุกคนในชุมชนของคุณ',
    'landing.personas.subtitle': 'ไม่ว่าคุณจะจัดการ เป็นเจ้าของ หรือมาเยี่ยมชม—Aether ทำงานให้คุณ',
    'landing.personas.manager': 'ผู้จัดการชุมชน',
    'landing.personas.managerTagline': 'จัดการการดำเนินงาน ไม่ใช่งานจุกจิก',
    'landing.personas.managerBenefit1': 'แดชบอร์ดครบวงจรเห็นทุกทรัพย์สิน',
    'landing.personas.managerBenefit2': 'เก็บเงินและแจ้งเตือนอัตโนมัติ',
    'landing.personas.managerBenefit3': 'จัดการผู้ขายและผู้ให้บริการ',
    'landing.personas.managerBenefit4': 'ส่งประกาศถึงผู้พักอาศัยทันที',
    'landing.personas.managerQuote': '"เมื่อก่อนใช้เวลา 3 ชั่วโมงต่อวันกับงานธุรการ ตอนนี้แค่ 30 นาที"',
    'landing.personas.owner': 'เจ้าของทรัพย์สิน',
    'landing.personas.ownerTagline': 'ทรัพย์สินของคุณ วิธีของคุณ',
    'landing.personas.ownerBenefit1': 'จ่ายค่า HOA ด้วยการแตะเดียว',
    'landing.personas.ownerBenefit2': 'จองบริการ concierge สำหรับบ้านของคุณ',
    'landing.personas.ownerBenefit3': 'ลงประกาศทรัพย์สินเพื่อเช่าระยะสั้น',
    'landing.personas.ownerBenefit4': 'ออกบัตรผ่านแขกอย่างปลอดภัย',
    'landing.personas.ownerQuote': '"ในที่สุดก็มีแอพที่ดูแลทรัพย์สินของฉันเหมือนสินทรัพย์มีค่า"',
    'landing.personas.guest': 'แขก',
    'landing.personas.guestTagline': 'มาถึงเหมือนเป็นส่วนหนึ่ง',
    'landing.personas.guestBenefit1': 'เข้าด้วย QR code—ไม่ต้องรอที่ประตู',
    'landing.personas.guestBenefit2': 'สำรวจแผนที่และสิ่งอำนวยความสะดวก',
    'landing.personas.guestBenefit3': 'ส่งข้อความถึงเจ้าของโดยตรงในแอพ',
    'landing.personas.guestBenefit4': 'เข้าถึงได้เฉพาะช่วงเวลาพักของคุณ',
    'landing.personas.guestQuote': '"การเช็คอินที่ราบรื่นที่สุดที่เคยสัมผัส"',
    'landing.communities.title': 'สร้างมาสำหรับชุมชนพรีเมียม',
    'landing.communities.subtitle': 'ไม่ว่าคุณจะจัดการคอนโด ชุมชนปิด หรือเช่าหรู—Aether ขยายตามพอร์ตโฟลิโอของคุณ',
    'landing.communities.penthouses': 'เพนท์เฮาส์ในเมือง',
    'landing.communities.penthousesDesc': 'วิวเมือง ระเบียงดาดฟ้า',
    'landing.communities.estates': 'เอสเตทสมัยใหม่',
    'landing.communities.estatesDesc': 'ผลงานสถาปัตยกรรมชั้นเลิศ',
    'landing.communities.beachfront': 'วิลล่าริมหาด',
    'landing.communities.beachfrontDesc': 'ชายหาดส่วนตัว ขอบฟ้าไม่สิ้นสุด',
    'landing.features.title': 'ทุกอย่างที่คุณต้องการ ไม่มีสิ่งที่ไม่จำเป็น',
    'landing.features.subtitle': 'แทนที่ 5+ เครื่องมือด้วยแพลตฟอร์มเดียว ประหยัดเวลาและลดต้นทุนตั้งแต่วันแรก',
    'landing.features.portfolio': 'ควบคุมพอร์ตโฟลิโอจากศูนย์กลาง',
    'landing.features.portfolioDesc': 'จัดการทุกทรัพย์สินจากแดชบอร์ดเดียว ไม่ต้องจัดการสเปรดชีต อีเมล และแอพหลายตัว',
    'landing.features.bookings': 'จองบริการอัตโนมัติ',
    'landing.features.bookingsDesc': 'ให้ผู้พักอาศัยจองบริการทำความสะอาด บำรุงรักษา และ concierge ด้วยตนเอง—ลดงานธุรการ 80%',
    'landing.features.response': 'ตอบสนองเร็วขึ้น',
    'landing.features.responseDesc': 'การส่งข้อความในตัวเชื่อมต่อเจ้าของ แขก และผู้ขาย—ไม่พลาดสายหรืออีเมลหาย',
    'landing.features.payments': 'เก็บเงินตรงเวลา',
    'landing.features.paymentsDesc': 'เก็บค่า HOA และบริการอัตโนมัติ ลดการจ่ายล่าช้า 60%',
    'landing.features.maps': 'แสดงชุมชนของคุณ',
    'landing.features.mapsDesc': 'แผนที่โต้ตอบช่วยผู้พักอาศัยและแขกค้นพบสิ่งอำนวยความสะดวก เพิ่มความพึงพอใจ',
    'landing.features.access': 'เข้าถึงแขกอย่างปลอดภัย',
    'landing.features.accessDesc': 'บัตรผ่าน QR จำกัดเวลา รู้ว่าใครเข้าทรัพย์สินของคุณและเมื่อไหร่',
    'landing.pricing.title': 'ราคาเรียบง่าย คาดเดาได้',
    'landing.pricing.subtitle': 'ไม่มีค่าธรรมเนียมซ่อน ไม่มีค่าธรรมเนียมต่อธุรกรรม แค่อัตราคงที่',
    'landing.pricing.sovereign': 'Sovereign',
    'landing.pricing.sovereignDesc': 'สำหรับเจ้าของทรัพย์สินที่ต้องการความเป็นเลิศ',
    'landing.pricing.elite': 'Elite',
    'landing.pricing.eliteDesc': 'สำหรับผู้มีพอร์ตโฟลิโอขนาดใหญ่',
    'landing.pricing.enterprise': 'Enterprise',
    'landing.pricing.enterpriseDesc': 'สำหรับชุมชนและ HOA',
    'landing.pricing.month': 'เดือน',
    'landing.pricing.custom': 'กำหนดเอง',
    'landing.pricing.mostPopular': 'นิยมที่สุด',
    'landing.pricing.getStarted': 'เริ่มต้นใช้งาน',
    'landing.pricing.contactSales': 'ติดต่อฝ่ายขาย',
    'landing.pricing.upTo3': 'สูงสุด 3 ทรัพย์สิน',
    'landing.pricing.aiConcierge': 'เข้าถึง AI Concierge',
    'landing.pricing.guestPass': 'จัดการบัตรผ่านแขก',
    'landing.pricing.hoaPortal': 'พอร์ทัลชำระ HOA',
    'landing.pricing.notifications': 'การแจ้งเตือนเรียลไทม์',
    'landing.pricing.basicAnalytics': 'วิเคราะห์ขั้นพื้นฐาน',
    'landing.pricing.unlimited': 'ทรัพย์สินไม่จำกัด',
    'landing.pricing.priorityAi': 'AI Concierge ลำดับความสำคัญ',
    'landing.pricing.advancedGuest': 'จัดการแขกขั้นสูง',
    'landing.pricing.rentalMarketplace': 'เข้าถึงตลาดเช่า',
    'landing.pricing.revenueAnalytics': 'แดชบอร์ดวิเคราะห์รายได้',
    'landing.pricing.whiteGlove': 'บริการระดับพิเศษ',
    'landing.pricing.customIntegrations': 'การรวมระบบตามต้องการ',
    'landing.pricing.communityWide': 'ติดตั้งทั้งชุมชน',
    'landing.pricing.dedicatedManager': 'ผู้จัดการบัญชีเฉพาะ',
    'landing.pricing.customBranding': 'แบรนด์ดิ้งตามต้องการ',
    'landing.pricing.apiAccess': 'เข้าถึง API',
    'landing.pricing.sla': 'การรับประกัน SLA',
    'landing.pricing.onSiteTraining': 'อบรมในสถานที่',
    'landing.pricing.multiCommunity': 'รองรับหลายชุมชน',
    'landing.testimonials.title': 'ผู้จัดการทรัพย์สินรัก Aether',
    'landing.testimonials.subtitle': 'ดูว่าทีมต่างๆ ประหยัดเวลาและทำให้ผู้พักอาศัยมีความสุขได้อย่างไร',
    'landing.testimonials.quote1': '"เราลดเวลาธุรการลงครึ่งหนึ่ง ผู้พักอาศัยจองบริการเอง และการชำระเงินเข้ามาอัตโนมัติ"',
    'landing.testimonials.author1': 'Alexander Chen',
    'landing.testimonials.role1': 'นักพัฒนาอสังหาริมทรัพย์ เบเวอร์ลีฮิลส์',
    'landing.testimonials.quote2': '"การเข้าถึงของแขกเคยเป็นฝันร้าย ตอนนี้ผู้มาเยี่ยมได้ QR code และเรามีบันทึกครบ"',
    'landing.testimonials.author2': 'Victoria Sterling',
    'landing.testimonials.role2': 'ประธานคณะกรรมการ HOA ไมอามี',
    'landing.testimonials.quote3': '"ผู้พักอาศัยชอบแอพนี้ คำร้องขอบริการลดลง 40% เพราะพวกเขาหาคำตอบได้เอง"',
    'landing.testimonials.author3': 'Marcus Williams',
    'landing.testimonials.role3': 'ผู้จัดการชุมชน 200+ ยูนิต',
    'landing.cta.title': 'พร้อมทำให้การดำเนินงานง่ายขึ้นหรือยัง?',
    'landing.cta.subtitle': 'ตั้งค่าในไม่กี่นาที เห็นผลในไม่กี่วัน ไม่มีสัญญา ยกเลิกได้ทุกเมื่อ',
    'landing.faq.title': 'คำถามที่พบบ่อย',
    'landing.faq.subtitle': 'ทุกอย่างที่คุณต้องรู้เกี่ยวกับการเริ่มต้นใช้งาน Aether',
    'landing.faq.q1': 'ใช้เวลานานแค่ไหนในการตั้งค่า Aether สำหรับชุมชนของฉัน?',
    'landing.faq.a1': 'ชุมชนส่วนใหญ่พร้อมใช้งานเต็มรูปแบบภายใน 10-15 นาที เพียงสร้างบัญชี เพิ่มทรัพย์สินและผู้พักอาศัย แล้วคุณก็พร้อมใช้งาน ตัวช่วยเริ่มต้นจะแนะนำทุกขั้นตอน และทีมซัพพอร์ตพร้อมให้บริการตลอด 24 ชั่วโมง',
    'landing.faq.q2': 'ข้อมูลของฉันปลอดภัยกับ Aether หรือไม่?',
    'landing.faq.a2': 'แน่นอน เราใช้การเข้ารหัส SSL 256-bit ระดับธนาคารสำหรับการส่งข้อมูลทั้งหมด ข้อมูลของคุณจัดเก็บในศูนย์ข้อมูลที่ได้รับการรับรอง SOC 2 Type II พร้อมเวลาทำงาน 99.99% เรายังมีการยืนยันตัวตนสองปัจจัยและการควบคุมการเข้าถึงตามบทบาท',
    'landing.faq.q3': 'ฉันสามารถทดลองใช้ Aether ก่อนสมัครแพ็คเกจได้หรือไม่?',
    'landing.faq.a3': 'ได้! เราให้ทดลองใช้ฟรี 14 วันพร้อมเข้าถึงฟีเจอร์ทั้งหมด ไม่ต้องใส่บัตรเครดิตเพื่อเริ่มต้น คุณสามารถสำรวจแพลตฟอร์ม เพิ่มทรัพย์สินทดสอบ และดูว่า Aether ช่วยปรับปรุงการดำเนินงานของคุณอย่างไร',
    'landing.faq.q4': 'การประมวลผลการชำระเงินทำงานอย่างไร?',
    'landing.faq.a4': 'Aether รวมกับ Stripe สำหรับการประมวลผลการชำระเงินที่ปลอดภัย ผู้พักอาศัยสามารถจ่ายค่า HOA ค่าบริการ และค่าเช่าโดยตรงผ่านแอพ เงินจะโอนเข้าบัญชีของคุณภายใน 2-3 วันทำการ เราเก็บค่าธรรมเนียมแพลตฟอร์มคงที่—ไม่มีค่าธรรมเนียมต่อธุรกรรมซ่อน',
    'landing.faq.q5': 'ฉันสามารถรวม Aether กับเครื่องมือที่มีอยู่ได้หรือไม่?',
    'landing.faq.a5': 'แพ็คเกจ Elite และ Enterprise รวมการเข้าถึง API และการรวมระบบตามต้องการ เรารวมกับซอฟต์แวร์บัญชี ระบบจัดการทรัพย์สิน และเครื่องมือสื่อสารยอดนิยม ทีมของเราสามารถทำงานร่วมกับคุณเพื่อเชื่อมต่อ Aether กับระบบที่มีอยู่',
    'landing.faq.q6': 'มีการสนับสนุนอะไรบ้างหากฉันมีปัญหา?',
    'landing.faq.a6': 'ทุกแพ็คเกจรวมการสนับสนุนทางอีเมลตอบกลับภายใน 24 ชั่วโมง แพ็คเกจ Pro ได้รับการสนับสนุนลำดับความสำคัญตอบกลับใน 4 ชั่วโมง ลูกค้า Enterprise ได้รับผู้จัดการบัญชีเฉพาะและการสนับสนุนทางโทรศัพท์ตลอด 24 ชั่วโมง เรายังมีเอกสารครบถ้วนและวิดีโอสอนการใช้งาน',
    'landing.faq.stillQuestions': 'ยังมีคำถามอยู่?',
    'landing.faq.contactSupport': 'ติดต่อฝ่ายสนับสนุน',
    'landing.footer.tagline': 'Sovereign Living',
    'landing.footer.copyright': '© {{year}} Aether. การจัดการทรัพย์สิน ทำให้ง่าย',
    'landing.footer.privacy': 'ความเป็นส่วนตัว',
    'landing.footer.terms': 'เงื่อนไข',
    'landing.footer.support': 'สนับสนุน',
    
    // Google Auth
    'auth.orContinueWith': 'หรือดำเนินการต่อด้วย',
    'auth.signInWithGoogle': 'เข้าสู่ระบบด้วย Google',
    'auth.googleSignInError': 'ไม่สามารถเข้าสู่ระบบด้วย Google ได้',
    'landing.nav.howItWorks': 'วิธีการทำงาน',
    'landing.nav.features': 'ฟีเจอร์',
    'landing.nav.pricing': 'ราคา',
    'landing.nav.signIn': 'เข้าสู่ระบบ',
    
    // Property Delete Dialog
    'propertyDelete.title': 'ลบอสังหาริมทรัพย์',
    'propertyDelete.confirm': 'คุณแน่ใจหรือไม่ว่าต้องการลบ',
    'propertyDelete.willRemove': 'การดำเนินการนี้จะ:',
    'propertyDelete.removeProperty': 'ลบอสังหาริมทรัพย์ออกจากบัญชีของคุณ',
    'propertyDelete.deactivateListings': 'ปิดใช้งานรายการเช่าที่ใช้งานอยู่',
    'propertyDelete.cancelBookings': 'ยกเลิกการจองที่รอดำเนินการ',
    'propertyDelete.cannotUndo': 'การดำเนินการนี้ไม่สามารถยกเลิกได้',
    'propertyDelete.deleting': 'กำลังลบ...',
    'propertyDelete.success': 'ลบอสังหาริมทรัพย์สำเร็จแล้ว',
    'propertyDelete.failed': 'ลบอสังหาริมทรัพย์ไม่สำเร็จ',
    
    // Sale Listing Manager
    'saleListing.title': 'รายการขาย',
    'saleListing.edit': 'แก้ไขรายการขาย',
    'saleListing.create': 'สร้างรายการขาย',
    'saleListing.noProperties': 'ไม่มีอสังหาริมทรัพย์',
    'saleListing.addPropertiesFirst': 'เพิ่มอสังหาริมทรัพย์ในชุมชนนี้ก่อน จากนั้นคุณสามารถลงรายการขายได้',
    'saleListing.listForSale': 'ลงรายการขาย',
    'saleListing.published': 'เผยแพร่แล้ว',
    'saleListing.available': 'พร้อมขาย',
    'saleListing.pending': 'รอดำเนินการ',
    'saleListing.sold': 'ขายแล้ว',
    'saleListing.priceRange': 'ช่วงราคา',
    'saleListing.description': 'ลงรายการอสังหาริมทรัพย์เพื่อขายพร้อมช่วงราคาโดยประมาณ การขายจริงดำเนินการแบบออฟไลน์',
    'saleListing.updated': 'อัปเดตรายการขายแล้ว',
    'saleListing.created': 'สร้างรายการขายแล้ว',
    'saleListing.saveFailed': 'บันทึกรายการไม่สำเร็จ',
    
    // Amenity Booking Dialog
    'amenityBooking.book': 'จอง',
    'amenityBooking.reserveSpace': 'จองพื้นที่นี้สำหรับผู้เข้าพักสูงสุด {capacity} คน',
    'amenityBooking.selectDate': 'เลือกวันที่',
    'amenityBooking.startTime': 'เวลาเริ่มต้น',
    'amenityBooking.duration': 'ระยะเวลา',
    'amenityBooking.minutes': '{minutes} นาที (ถึง {endTime})',
    'amenityBooking.guestCount': 'จำนวนผู้เข้าพัก (สูงสุด {max})',
    'amenityBooking.specialRequests': 'คำขอพิเศษ (ไม่บังคับ)',
    'amenityBooking.specialPlaceholder': 'ข้อกำหนดหรือคำขอพิเศษ...',
    'amenityBooking.confirmBooking': 'ยืนยันการจอง',
    'amenityBooking.booking': 'กำลังจอง...',
    'amenityBooking.confirmed': 'ยืนยันการจองแล้ว!',
    'amenityBooking.on': 'เมื่อ',
    'amenityBooking.success': 'จอง {name} สำเร็จแล้ว!',
    'amenityBooking.selectDateError': 'กรุณาเลือกวันที่',
    'amenityBooking.fullyBooked': 'ช่วงเวลานี้เต็มแล้ว ความจุสูงสุด: {capacity}',
    'amenityBooking.spotsAvailable': 'ช่วงเวลานี้เหลือเพียง {spots} ที่ ลดจำนวนผู้เข้าพัก',
    'amenityBooking.failed': 'จองสิ่งอำนวยความสะดวกไม่สำเร็จ กรุณาลองอีกครั้ง',
    
    // Common additions
    'common.notAuthenticated': 'ไม่ได้รับการยืนยันตัวตน',
    'common.note': 'หมายเหตุ',
    
    // Admin - Developer Invite
    'admin.developer.invite': 'เชิญนักพัฒนา',
    'admin.developer.inviteTitle': 'เชิญนักพัฒนาอสังหาริมทรัพย์',
    'admin.developer.description': 'นักพัฒนาอสังหาริมทรัพย์สามารถสร้างและจัดการชุมชน เพิ่มอสังหาริมทรัพย์ บริการ และผู้ขาย',
    'admin.developer.emailLabel': 'ที่อยู่อีเมล',
    'admin.developer.emailPlaceholder': 'developer@company.com',
    'admin.developer.nameLabel': 'ชื่อเต็ม (ไม่บังคับ)',
    'admin.developer.namePlaceholder': 'จอห์น นักพัฒนา',
    'admin.developer.note': 'นักพัฒนาจะได้รับอีเมลพร้อมคำแนะนำในการตั้งค่าบัญชี พวกเขาจะมีสิทธิ์ระดับผู้ดูแลในการสร้างชุมชน',
    'admin.developer.sendInvite': 'ส่งคำเชิญ',
    'admin.developer.emailRequired': 'ต้องระบุอีเมล',
    'admin.developer.inviteSent': 'ส่งคำเชิญนักพัฒนาสำเร็จแล้ว',
    'admin.developer.inviteFailed': 'ส่งคำเชิญไม่สำเร็จ',
    
    // Admin - Amenity Manager
    'admin.amenity.title': 'สิ่งอำนวยความสะดวกของชุมชน',
    'admin.amenity.add': 'เพิ่มสิ่งอำนวยความสะดวก',
    'admin.amenity.addTitle': 'เพิ่มสิ่งอำนวยความสะดวก',
    'admin.amenity.namePlaceholder': 'ชื่อสิ่งอำนวยความสะดวก',
    'admin.amenity.selectCategory': 'เลือกหมวดหมู่',
    'admin.amenity.descriptionPlaceholder': 'คำอธิบาย (ไม่บังคับ)',
    'admin.amenity.latitudePlaceholder': 'ละติจูด (เช่น 25.7617)',
    'admin.amenity.longitudePlaceholder': 'ลองจิจูด (เช่น -80.1918)',
    'admin.amenity.coordsTip': 'เคล็ดลับ: ค้นหาพิกัดใน Google Maps โดยคลิกขวาที่ตำแหน่ง',
    'admin.amenity.addButton': 'เพิ่มสิ่งอำนวยความสะดวก',
    'admin.amenity.noAmenities': 'ยังไม่มีสิ่งอำนวยความสะดวก',
    'admin.amenity.addFirst': 'เพิ่มสิ่งอำนวยความสะดวกแรกของชุมชน',
    'admin.amenity.loadFailed': 'โหลดสิ่งอำนวยความสะดวกไม่สำเร็จ',
    'admin.amenity.nameAndCoordsRequired': 'ต้องระบุชื่อและพิกัด',
    'admin.amenity.addSuccess': 'เพิ่มสิ่งอำนวยความสะดวกสำเร็จแล้ว',
    'admin.amenity.addFailed': 'เพิ่มสิ่งอำนวยความสะดวกไม่สำเร็จ',
    'admin.amenity.confirmDelete': 'คุณแน่ใจหรือไม่ว่าต้องการลบสิ่งอำนวยความสะดวกนี้?',
    'admin.amenity.deleteSuccess': 'ลบสิ่งอำนวยความสะดวกแล้ว',
    'admin.amenity.deleteFailed': 'ลบสิ่งอำนวยความสะดวกไม่สำเร็จ',
    'admin.amenity.category.pool': 'สระว่ายน้ำ',
    'admin.amenity.category.gym': 'ศูนย์ฟิตเนส',
    'admin.amenity.category.restaurant': 'ร้านอาหาร',
    'admin.amenity.category.valet': 'บริการจอดรถ',
    'admin.amenity.category.spa': 'สปาและเวลเนส',
    'admin.amenity.category.park': 'สวนสาธารณะ / สวน',
    'admin.amenity.category.clubhouse': 'คลับเฮาส์',
    'admin.amenity.category.other': 'อื่นๆ',
    
    // Admin - Announcement Manager
    'admin.announcement.title': 'ประกาศ',
    'admin.announcement.new': 'ใหม่',
    'admin.announcement.newTitle': 'ประกาศใหม่',
    'admin.announcement.editTitle': 'แก้ไขประกาศ',
    'admin.announcement.titleLabel': 'หัวข้อ',
    'admin.announcement.titlePlaceholder': 'หัวข้อประกาศ',
    'admin.announcement.contentLabel': 'เนื้อหา',
    'admin.announcement.contentPlaceholder': 'เขียนประกาศของคุณ...',
    'admin.announcement.priorityLabel': 'ความสำคัญ',
    'admin.announcement.priority.normal': 'ปกติ',
    'admin.announcement.priority.important': 'สำคัญ',
    'admin.announcement.priority.urgent': 'เร่งด่วน',
    'admin.announcement.publishImmediately': 'เผยแพร่ทันที',
    'admin.announcement.visibleToResidents': 'แสดงให้ผู้พักอาศัยทุกคนเห็น',
    'admin.announcement.create': 'สร้างประกาศ',
    'admin.announcement.update': 'อัปเดตประกาศ',
    'admin.announcement.publish': 'เผยแพร่',
    'admin.announcement.unpublish': 'ยกเลิกการเผยแพร่',
    'admin.announcement.status.published': 'เผยแพร่แล้ว',
    'admin.announcement.status.draft': 'ร่าง',
    'admin.announcement.noAnnouncements': 'ไม่มีประกาศ',
    'admin.announcement.createToInform': 'สร้างประกาศเพื่อแจ้งผู้พักอาศัย',
    'admin.announcement.titleContentRequired': 'ต้องระบุหัวข้อและเนื้อหา',
    'admin.announcement.createSuccess': 'สร้างประกาศแล้ว',
    'admin.announcement.updateSuccess': 'อัปเดตประกาศแล้ว',
    'admin.announcement.saveFailed': 'บันทึกประกาศไม่สำเร็จ',
    'admin.announcement.deleteSuccess': 'ลบประกาศแล้ว',
    'admin.announcement.deleteFailed': 'ลบประกาศไม่สำเร็จ',
    'admin.announcement.published': 'เผยแพร่ประกาศแล้ว',
    'admin.announcement.unpublished': 'ยกเลิกการเผยแพร่ประกาศแล้ว',
    'admin.announcement.updateFailed': 'อัปเดตประกาศไม่สำเร็จ',
    
    // Admin - Guest Pass Manager
    'admin.guestPass.title': 'การจัดการบัตรผ่านแขก',
    'admin.guestPass.subtitle': 'จัดการการเข้าถึงของผู้เยี่ยมชมทั้งหมด',
    'admin.guestPass.status.active': 'ใช้งานอยู่',
    'admin.guestPass.status.expired': 'หมดอายุ',
    'admin.guestPass.status.revoked': 'ถูกเพิกถอน',
    'admin.guestPass.noGuestPasses': 'ไม่พบบัตรผ่านแขก',
    'admin.guestPass.unknownGuest': 'แขกไม่ทราบชื่อ',
    'admin.guestPass.expires': 'หมดอายุ',
    'admin.guestPass.extend': 'ขยายเวลา',
    'admin.guestPass.reactivate': 'เปิดใช้งานอีกครั้ง (7 วัน)',
    'admin.guestPass.extendTitle': 'ขยายเวลาการเข้าถึงของแขก',
    'admin.guestPass.guestLabel': 'แขก',
    'admin.guestPass.newEndDate': 'วันสิ้นสุดใหม่',
    'admin.guestPass.selectEndDate': 'เลือกวันสิ้นสุดใหม่',
    'admin.guestPass.extendAccess': 'ขยายเวลาการเข้าถึง',
    'admin.guestPass.loadFailed': 'โหลดบัตรผ่านแขกไม่สำเร็จ',
    'admin.guestPass.revoked': 'เพิกถอนบัตรผ่านแขกแล้ว',
    'admin.guestPass.revokeFailed': 'เพิกถอนบัตรผ่านไม่สำเร็จ',
    'admin.guestPass.extendedTo': 'ขยายเวลาการเข้าถึงถึง',
    'admin.guestPass.extendFailed': 'ขยายเวลาการเข้าถึงไม่สำเร็จ',
    'admin.guestPass.reactivated': 'เปิดใช้งานบัตรผ่านแขกอีกครั้งเป็นเวลา 7 วัน',
    'admin.guestPass.reactivateFailed': 'เปิดใช้งานบัตรผ่านอีกครั้งไม่สำเร็จ',
    
    // Date/Time Picker
    'datePicker.pickDate': 'เลือกวันที่',
    'datePicker.selectDate': 'เลือกวันที่และเวลา',
    'datePicker.time': 'เวลา',
    
    // Services Manager Additional
    'services.addNewService': 'เพิ่มบริการใหม่',
    'services.serviceUpdated': 'อัปเดตบริการแล้ว',
    'services.hasBeenUpdated': 'ได้รับการอัปเดตแล้ว',
    'services.serviceCreated': 'สร้างบริการแล้ว',
    'services.hasBeenAdded': 'ได้ถูกเพิ่มแล้ว',
    'services.failedToSave': 'บันทึกบริการไม่สำเร็จ',
    'services.noVendorAssigned': 'ไม่ได้กำหนดผู้ให้บริการ',
    'services.unknownVendor': 'ผู้ให้บริการไม่ทราบ',
    'services.loadingServices': 'กำลังโหลดบริการ...',
    'services.perBedroom': 'ต่อห้องนอน',
    'services.perSqft': 'ต่อตารางฟุต',
    'services.pricing': 'ราคา',
    'services.min': 'นาที',
    'services.active': 'ใช้งานอยู่',
    'services.inactive': 'ไม่ได้ใช้งาน',
    'services.createService': 'สร้างบริการ',
    'services.updateService': 'อัปเดตบริการ',
    
    // Service Names (from database) - Thai
    'serviceName.poolService': 'บริการสระว่ายน้ำ',
    'serviceName.poolMaintenance': 'ดูแลสระว่ายน้ำ',
    'serviceName.poolSpaMaintenance': 'ดูแลสระและสปา',
    'serviceName.deepCleaning': 'ทำความสะอาดเชิงลึก',
    'serviceName.deepClean': 'ทำความสะอาดเชิงลึก',
    'serviceName.regularCleaning': 'ทำความสะอาดปกติ',
    'serviceName.housekeeping': 'แม่บ้าน',
    'serviceName.expressHousekeeping': 'แม่บ้านด่วน',
    'serviceName.signatureHousekeeping': 'แม่บ้านพรีเมียม',
    'serviceName.fullEstateCleaning': 'ทำความสะอาดทั้งบ้าน',
    'serviceName.weeklyMaintenanceClean': 'ทำความสะอาดรายสัปดาห์',
    'serviceName.turndownService': 'บริการจัดเตียงนอน',
    'serviceName.laundryService': 'บริการซักรีด',
    'serviceName.laundry': 'ซักรีด',
    'serviceName.laundryPressing': 'ซักรีดและรีด',
    'serviceName.carpetCleaning': 'ทำความสะอาดพรม',
    'serviceName.windowCleaning': 'ทำความสะอาดหน้าต่าง',
    'serviceName.inHomeSpa': 'สปาและเวลเนสที่บ้าน',
    'serviceName.spaWellness': 'สปาและเวลเนส',
    'serviceName.couplesSpaDay': 'วันสปาคู่รัก',
    'serviceName.couplesSpaPackage': 'แพ็คเกจสปาคู่รัก',
    'serviceName.inVillaMassage': 'นวดในวิลล่า',
    'serviceName.swedishMassage': 'นวดสวีดิช',
    'serviceName.hotStoneTherapy': 'การบำบัดด้วยหินร้อน',
    'serviceName.personalTraining': 'เทรนเนอร์ส่วนตัว',
    'serviceName.personalTrainingSession': 'คลาสเทรนเนอร์ส่วนตัว',
    'serviceName.privateYogaClass': 'คลาสโยคะส่วนตัว',
    'serviceName.privateYogaSession': 'เซสชั่นโยคะส่วนตัว',
    'serviceName.massageTherapy': 'นวดบำบัด',
    'serviceName.massage': 'นวด',
    'serviceName.yogaSession': 'คลาสโยคะ',
    'serviceName.yoga': 'โยคะ',
    'serviceName.meditation': 'สมาธิ',
    'serviceName.beautyServices': 'บริการความงาม',
    'serviceName.salonServices': 'บริการเสริมสวย',
    'serviceName.generalMaintenance': 'ซ่อมบำรุงทั่วไป',
    'serviceName.plumbing': 'ช่างประปา',
    'serviceName.emergencyPlumbing': 'ช่างประปาฉุกเฉิน',
    'serviceName.electrical': 'ช่างไฟ',
    'serviceName.hvacService': 'บริการแอร์',
    'serviceName.hvac': 'แอร์',
    'serviceName.hvacMaintenance': 'ดูแลแอร์',
    'serviceName.handyman': 'ช่างซ่อมทั่วไป',
    'serviceName.handymanServices': 'บริการช่างซ่อม',
    'serviceName.landscaping': 'จัดสวน',
    'serviceName.landscapingService': 'บริการจัดสวน',
    'serviceName.gardenLandscaping': 'จัดภูมิทัศน์สวน',
    'serviceName.gardenMaintenance': 'ดูแลสวน',
    'serviceName.pestControl': 'กำจัดแมลง',
    'serviceName.privateChef': 'เชฟส่วนตัว',
    'serviceName.privateChefDinner': 'มื้อค่ำเชฟส่วนตัว',
    'serviceName.privateChefExperience': 'ประสบการณ์เชฟส่วนตัว',
    'serviceName.italianDinnerExperience': 'ประสบการณ์อาหารค่ำอิตาลี',
    'serviceName.sushiOmakase': 'ซูชิโอมากาเสะ',
    'serviceName.winePairingDinner': 'มื้อค่ำจับคู่ไวน์',
    'serviceName.wineSommelierService': 'บริการซอมเมอลิเย่ร์',
    'serviceName.breakfastInBed': 'อาหารเช้าบนเตียง',
    'serviceName.cocktailPartyCatering': 'จัดเลี้ยงปาร์ตี้ค็อกเทล',
    'serviceName.catering': 'จัดเลี้ยง',
    'serviceName.mealDelivery': 'ส่งอาหาร',
    'serviceName.groceryShopping': 'ซื้อของชำ',
    'serviceName.bartenderService': 'บริการบาร์เทนเดอร์',
    'serviceName.bartender': 'บาร์เทนเดอร์',
    'serviceName.airportTransfer': 'รับส่งสนามบิน',
    'serviceName.airportLimousine': 'ลีมูซีนสนามบิน',
    'serviceName.carRental': 'เช่ารถ',
    'serviceName.golfCartRental': 'เช่ารถกอล์ฟ',
    'serviceName.chauffeurService': 'บริการคนขับรถ',
    'serviceName.chauffeur': 'คนขับรถ',
    'serviceName.valetParking': 'บริการจอดรถ',
    'serviceName.valetParkingService': 'บริการจอดรถ',
    'serviceName.valet': 'บริการจอดรถ',
    'serviceName.boatCharter': 'เช่าเรือ',
    'serviceName.yachtCharter': 'เช่าเรือยอร์ช',
    'serviceName.yachtCharterHalfDay': 'เช่าเรือยอร์ชครึ่งวัน',
    'serviceName.yachtDetailing': 'ดีเทลลิ่งเรือยอร์ช',
    
    // Vendor Manager Additional
    'vendor.failedToLoad': 'โหลดผู้ให้บริการไม่สำเร็จ',
    'vendor.nameRequired': 'ต้องระบุชื่อผู้ให้บริการ',
    'vendor.addedSuccess': 'เพิ่มผู้ให้บริการสำเร็จ',
    'vendor.failedToAdd': 'เพิ่มผู้ให้บริการไม่สำเร็จ',
    'vendor.updatedSuccess': 'อัปเดตผู้ให้บริการสำเร็จ',
    'vendor.failedToUpdate': 'อัปเดตผู้ให้บริการไม่สำเร็จ',
    'vendor.confirmDelete': 'คุณแน่ใจหรือไม่ที่จะลบผู้ให้บริการนี้?',
    'vendor.deleted': 'ปิดใช้งานผู้ให้บริการแล้ว',
    'vendor.failedToDelete': 'ลบผู้ให้บริการไม่สำเร็จ',
    'vendor.stripeOnboardingOpened': 'เปิดหน้าลงทะเบียน Stripe ในแท็บใหม่',
    'vendor.failedToStartOnboarding': 'เริ่มลงทะเบียน Stripe ไม่สำเร็จ',
    'vendor.vendorsAndProviders': 'ผู้ให้บริการ',
    'vendor.addNewVendor': 'เพิ่มผู้ให้บริการใหม่',
    'vendor.enterVendorName': 'ใส่ชื่อผู้ให้บริการ',
    'vendor.contactEmail': 'อีเมลติดต่อ',
    'vendor.emailNotification': 'สำหรับการแจ้งเตือนการชำระเงินและการจอง',
    'vendor.platformKeeps': 'แพลตฟอร์มเก็บ',
    'vendor.notConnected': 'ไม่ได้เชื่อมต่อ',
    'vendor.fullSetup': 'ตั้งค่าเต็มรูปแบบ',
    'vendor.stripeOnly': 'Stripe เท่านั้น',
    'vendor.qrOnly': 'QR เท่านั้น',
    'vendor.noVendorsDesc': 'เพิ่มผู้ให้บริการเพื่อให้บริการ',
    'vendor.updateStripeAccount': 'อัปเดตบัญชี Stripe',
    'vendor.connectStripeAccount': 'เชื่อมต่อบัญชี Stripe',
    'vendor.editDetails': 'แก้ไขผู้ให้บริการ',
    'vendor.qrCodes': 'QR โค้ด',
    
    'calendar.viewBookings': 'ดูการจองและจัดการวันที่บล็อก',
    'calendar.booked': 'จองแล้ว',
    'calendar.blocked': 'บล็อกแล้ว',
    'calendar.available': 'ว่าง',
    'calendar.upcomingBookings': 'การจองที่กำลังจะมาถึง',
    'calendar.noUpcomingBookings': 'ไม่มีการจองที่กำลังจะมาถึง',
    'calendar.blockedDates': 'วันที่ถูกบล็อก',
    'calendar.clickToBlock': 'คลิกวันที่ว่างเพื่อบล็อก',
    'calendar.saveAvailability': 'บันทึกความพร้อม',
    'calendar.guests': 'แขก',
    'calendar.guest': 'แขก',
    
    // Guest Preferences Quiz (Thai)
    'quiz.saved': 'บันทึกความชอบแล้ว!',
    'quiz.savedDesc': 'เราจะปรับแต่งการค้นหาที่พักตามความชอบของคุณ',
    'quiz.saveFailed': 'บันทึกความชอบไม่สำเร็จ',
    'quiz.howTravel': 'คุณชอบเดินทางแบบไหน?',
    'quiz.selectAll': 'เลือกทั้งหมดที่ตรง',
    'quiz.whatBrings': 'อะไรพาคุณมาที่นี่?',
    'quiz.whoTraveling': 'ใครเดินทางไปด้วย?',
    'quiz.selectGroupSize': 'เลือกขนาดกลุ่มทั่วไปของคุณ',
    'quiz.stayLength': 'ระยะเวลาพักทั่วไป',
    'quiz.nights': 'คืน',
    'quiz.whatBudget': 'งบประมาณของคุณเท่าไหร่?',
    'quiz.perNightPref': 'ราคาต่อคืนที่ต้องการ',
    'quiz.mustHaveAmenities': 'สิ่งอำนวยความสะดวกที่ต้องมี',
    'quiz.selectEssentials': 'เลือกสิ่งจำเป็นของคุณ',
    'quiz.petFriendly': 'เลี้ยงสัตว์ได้',
    'quiz.petFriendlyDesc': 'เดินทางกับสัตว์เลี้ยง',
    'quiz.accessibility': 'การเข้าถึง',
    'quiz.accessibilityDesc': 'ต้องการสิ่งอำนวยความสะดวกสำหรับผู้พิการ',
    'quiz.personalizeExperience': 'ปรับแต่งประสบการณ์ของคุณ',
    'quiz.updatePreferences': 'อัปเดตความชอบ',
    'quiz.helpFind': 'ช่วยเราหาที่พักที่เหมาะสมสำหรับคุณ',
    'quiz.skipForNow': 'ข้ามไปก่อน',
    'quiz.savePreferences': 'บันทึกความชอบ',
    'quiz.style.short_trip': 'ทริปสั้น',
    'quiz.style.short_tripDesc': 'พัก 1-3 คืน',
    'quiz.style.long_term': 'พักระยะยาว',
    'quiz.style.long_termDesc': 'หลายสัปดาห์หรือหลายเดือน',
    'quiz.style.luxury': 'พักผ่อนหรูหรา',
    'quiz.style.luxuryDesc': 'ประสบการณ์พรีเมียม',
    'quiz.style.budget': 'ประหยัด',
    'quiz.style.budgetDesc': 'เน้นความคุ้มค่า',
    'quiz.purpose.vacation': 'พักร้อน',
    'quiz.purpose.business': 'ธุรกิจ',
    'quiz.purpose.relocation': 'ย้ายที่อยู่',
    'quiz.purpose.remote_work': 'ทำงานระยะไกล',
    'quiz.group.solo': 'คนเดียว',
    'quiz.group.couple': 'คู่รัก',
    'quiz.group.family': 'ครอบครัว',
    'quiz.group.group': 'กลุ่ม',
    'quiz.budget.budget': 'ประหยัด',
    'quiz.budget.moderate': 'ปานกลาง',
    'quiz.budget.luxury': 'หรูหรา',
    'quiz.budget.ultra_luxury': 'หรูหราสุดขีด',
    'quiz.amenity.wifi': 'WiFi',
    'quiz.amenity.parking': 'ที่จอดรถ',
    'quiz.amenity.pool': 'สระว่ายน้ำ',
    'quiz.amenity.gym': 'ฟิตเนส',
    'quiz.amenity.kitchen': 'ครัว',
    'quiz.amenity.air_conditioning': 'แอร์',
    'quiz.amenity.tv': 'สมาร์ททีวี',
    'quiz.amenity.hot_tub': 'อ่างน้ำร้อน',
    'quiz.amenity.garden': 'สวน',
    'quiz.amenity.security': 'ระบบรักษาความปลอดภัย',
    
    // Payment Method Selector (Thai)
    'payment.totalAmount': 'ยอดรวม',
    'payment.myanmarOptions': 'ตัวเลือกการชำระเงินเมียนมา',
    'payment.localPayment': 'ชำระเงินในประเทศ',
    'payment.internationalCards': 'บัตรต่างประเทศ',
    'payment.processing': 'กำลังดำเนินการ...',
    'payment.payWith': 'ชำระด้วย {method}',
    'payment.selectMethod': 'เลือกวิธีชำระเงิน',
    'payment.securityNote': 'การชำระเงินของคุณได้รับการเข้ารหัสตั้งแต่ต้นจนจบ',
    
    // Amenity Booking Manager - Admin
    'amenityBooking.reservations': 'การจองสิ่งอำนวยความสะดวก',
    'amenityBooking.allStatus': 'ทุกสถานะ',
    'amenityBooking.statusConfirmed': 'ยืนยันแล้ว',
    'amenityBooking.statusCancelled': 'ยกเลิกแล้ว',
    'amenityBooking.statusCompleted': 'เสร็จสิ้นแล้ว',
    'amenityBooking.statusPending': 'รอดำเนินการ',
    'amenityBooking.noBookings': 'ไม่พบการจอง',
    'amenityBooking.noBookingsDesc': 'การจองจะปรากฏที่นี่เมื่อผู้อยู่อาศัยจองสิ่งอำนวยความสะดวก',
    'amenityBooking.failedToLoad': 'โหลดการจองไม่สำเร็จ',
    'amenityBooking.failedToUpdate': 'อัปเดตการจองไม่สำเร็จ',
    'amenityBooking.bookingUpdated': 'อัปเดตการจองแล้ว',
    'amenityBooking.amenity': 'สิ่งอำนวยความสะดวก',
    'amenityBooking.dateTime': 'วันและเวลา',
    'amenityBooking.guest': 'ผู้เข้าพัก',
    'amenityBooking.status': 'สถานะ',
    'amenityBooking.actions': 'การดำเนินการ',
    'amenityBooking.filterStatus': 'กรองสถานะ',
    
    // Common additional
    'common.guest': 'คน',
    'common.guests': 'คน',
    
    // New Pricing Plans - Explorer (Thai)
    'pricing.explorer.name': 'Explorer',
    'pricing.explorer.desc': 'สำหรับนักเดินทางที่มองหาที่พักพิเศษ',
    'pricing.explorer.feature1': 'จองที่พักระยะสั้นหรูหรา',
    'pricing.explorer.feature2': 'จองเช่าระยะยาว',
    'pricing.explorer.feature3': 'บริการคอนเซียร์จ AI',
    'pricing.explorer.feature4': 'ส่งข้อความกับเจ้าของอย่างปลอดภัย',
    'pricing.explorer.feature5': 'สนับสนุนการจองแบบพิเศษ',
    
    // New Pricing Plans - Sovereign (Thai)
    'pricing.sovereign.name': 'Sovereign',
    'pricing.sovereign.desc': 'สำหรับเจ้าของบ้านที่พร้อมให้เช่าอสังหาริมทรัพย์',
    'pricing.sovereign.feature1': 'ลงประกาศอสังหาริมทรัพย์สำหรับเช่าระยะสั้น',
    'pricing.sovereign.feature2': 'ลงประกาศอสังหาริมทรัพย์สำหรับเช่าระยะยาว',
    'pricing.sovereign.feature3': 'เครื่องมือกำหนดราคาแบบไดนามิก',
    'pricing.sovereign.feature4': 'แดชบอร์ดจัดการผู้เข้าพัก',
    'pricing.sovereign.feature5': 'วิเคราะห์รายได้',
    'pricing.sovereign.feature6': 'สนับสนุนเจ้าบ้านแบบพิเศษ',
    
    // New Pricing Plans - Architect (Thai)
    'pricing.architect.name': 'Architect',
    'pricing.architect.desc': 'สำหรับผู้พัฒนาที่จัดการชุมชน',
    'pricing.architect.feature1': 'สร้างและจัดการชุมชน',
    'pricing.architect.feature2': 'เชิญและจัดการเจ้าของบ้าน',
    'pricing.architect.feature3': 'เก็บค่า HOA',
    'pricing.architect.feature4': 'จัดการตลาดผู้ให้บริการ',
    'pricing.architect.feature5': 'ประกาศชุมชน',
    'pricing.architect.feature6': 'แดชบอร์ดวิเคราะห์ขั้นสูง',
    'pricing.architect.feature7': 'แบรนด์ที่กำหนดเอง',
    
    // Pricing Modal & Common (Thai)
    'pricing.monthly': 'รายเดือน',
    'pricing.annual': 'รายปี',
    'pricing.save': 'ประหยัด {percent}%',
    'pricing.trialDays': 'ทดลองใช้ฟรี {days} วัน',
    'pricing.startTrial': 'เริ่มทดลองใช้ฟรี',
    'pricing.subscribe': 'สมัครสมาชิกเลย',
    'pricing.securePayment': 'การชำระเงินที่ปลอดภัยโดย Stripe',
    'pricing.redirecting': 'กำลังเปลี่ยนเส้นทางไปชำระเงิน...',
    'pricing.error': 'ไม่สามารถสร้างการสมัครสมาชิก กรุณาลองอีกครั้ง',
    
    // Pricing Triggers (Thai)
    'pricing.trigger.rentalMode': 'สมัครสมาชิกเพื่อลงประกาศอสังหาริมทรัพย์ให้เช่า',
    'pricing.trigger.booking': 'สมัครสมาชิกเพื่อจองอสังหาริมทรัพย์นี้',
    'pricing.trigger.community': 'สมัครสมาชิกเพื่อสร้างและจัดการชุมชน',
    'pricing.trigger.default': 'อัพเกรดบัญชีของคุณ',
    'landing.pricing.perYear': 'ปี',
    'landing.pricing.billingMonthly': 'รายเดือน',
    'landing.pricing.billingAnnual': 'รายปี',
    
    // Pricing Page (Thai)
    'pricingPage.backToHome': 'กลับหน้าหลัก',
    'pricingPage.title': 'ราคาที่ชัดเจนและโปร่งใส',
    'pricingPage.subtitle': 'เลือกแผนที่เหมาะกับความต้องการของคุณ ไม่มีค่าใช้จ่ายแอบแฝง',
    'pricingPage.saveUpTo': 'ประหยัด 17%',
    'pricingPage.compareFeatures': 'เปรียบเทียบคุณสมบัติ',
    'pricingPage.feature': 'คุณสมบัติ',
    'pricingPage.feature.bookRentals': 'จองที่พักหรู',
    'pricingPage.feature.aiConcierge': 'ผู้ช่วย AI Concierge',
    'pricingPage.feature.secureMessaging': 'การส่งข้อความที่ปลอดภัย',
    'pricingPage.feature.listProperty': 'ลงประกาศอสังหาริมทรัพย์ให้เช่า',
    'pricingPage.feature.guestManagement': 'แดชบอร์ดจัดการผู้เข้าพัก',
    'pricingPage.feature.revenueAnalytics': 'วิเคราะห์รายได้',
    'pricingPage.feature.createCommunities': 'สร้างชุมชน',
    'pricingPage.feature.inviteHomeowners': 'เชิญและจัดการเจ้าของบ้าน',
    'pricingPage.feature.hoaCollection': 'เก็บค่า HOA',
    'pricingPage.feature.vendorManagement': 'ตลาดผู้ให้บริการ',
    'pricingPage.feature.customBranding': 'แบรนด์ที่กำหนดเอง',
    'pricingPage.contactSalesTitle': 'ติดต่อฝ่ายขาย',
    'pricingPage.contactSalesSubtitle': 'สนใจแผน Sovereign หรือ Architect หรือไม่? ติดต่อทีมงานของเรา',
    'pricingPage.yourName': 'ชื่อของคุณ',
    'pricingPage.namePlaceholder': 'สมชาย ใจดี',
    'pricingPage.yourEmail': 'อีเมลของคุณ',
    'pricingPage.emailPlaceholder': 'somchai@example.com',
    'pricingPage.companyOptional': 'บริษัท (ไม่บังคับ)',
    'pricingPage.companyPlaceholder': 'ชื่อบริษัทของคุณ',
    'pricingPage.planInterest': 'แผนที่สนใจ',
    'pricingPage.homeownerPlan': 'Sovereign (เจ้าของบ้าน)',
    'pricingPage.developerPlan': 'Architect (ผู้พัฒนา)',
    'pricingPage.tellUsMore': 'บอกเราเพิ่มเติม',
    'pricingPage.messagePlaceholder': 'บอกเราเกี่ยวกับความต้องการ พอร์ตโฟลิโออสังหาริมทรัพย์ หรือคำถามของคุณ...',
    'pricingPage.sending': 'กำลังส่ง...',
    'pricingPage.sendInquiry': 'ส่งคำถาม',
    'pricingPage.inquirySent': 'คำถามของคุณถูกส่งแล้ว! ทีมขายของเราจะติดต่อกลับเร็วๆ นี้',
    'pricingPage.inquiryFailed': 'ส่งคำถามไม่สำเร็จ กรุณาลองอีกครั้ง',
    'pricing.inviteRequired': 'ต้องได้รับคำเชิญจากผู้ดูแลชุมชน',
    'pricing.enterpriseOnboarding': 'ต้องมีการ Onboarding สำหรับองค์กร',
    
    // Support Page - Sales Inquiry (Thai)
    'support.salesInquiryTitle': 'ลงทะเบียนเจ้าของบ้านและผู้พัฒนา',
    'support.salesInquirySubtitle': 'สนใจลงประกาศอสังหาริมทรัพย์หรือจัดการชุมชน? ติดต่อทีมขายของเรา',
    'support.homeownerPlan': 'เจ้าของบ้าน (Sovereign)',
    'support.homeownerPlanDesc': 'ลงประกาศอสังหาริมทรัพย์ให้เช่าระยะสั้นหรือระยะยาว',
    'support.developerPlan': 'ผู้พัฒนา (Architect)',
    'support.developerPlanDesc': 'สร้างและจัดการชุมชนที่มีอสังหาริมทรัพย์หลายแห่ง',
    'support.yourName': 'ชื่อของคุณ',
    'support.yourEmail': 'อีเมลของคุณ',
    'support.companyOptional': 'บริษัท (ไม่บังคับ)',
    'support.companyPlaceholder': 'ชื่อบริษัทของคุณ',
    'support.tellUsMore': 'บอกเราเพิ่มเติม',
    'support.salesMessagePlaceholder': 'บอกเราเกี่ยวกับอสังหาริมทรัพย์ ขนาดชุมชน หรือคำถามของคุณ...',
    'support.submitSalesInquiry': 'ส่งคำถามการขาย',
    'support.salesInquirySent': 'ขอบคุณ! ทีมขายของเราจะติดต่อคุณภายใน 24 ชั่วโมง',
    'support.salesInquiryFailed': 'ส่งคำถามไม่สำเร็จ กรุณาลองอีกครั้ง',
    'support.otherWaysToReach': 'วิธีอื่นในการติดต่อเรา',
    
    // SmartHome Guide Page (Thai)
    'smartHome.guide.lighting': 'ไฟ',
    'smartHome.guide.lightingDesc': 'ควบคุมไฟอัจฉริยะและฉาก',
    'smartHome.guide.smartLocks': 'ล็อคอัจฉริยะ',
    'smartHome.guide.smartLocksDesc': 'ล็อค/ปลดล็อคประตู (ถ้าเปิดใช้งาน)',
    'smartHome.guide.doorbell': 'กริ่งประตู',
    'smartHome.guide.doorbellDesc': 'ดูว่าใครอยู่ที่ประตู',
    'smartHome.guide.voiceCommands': 'คำสั่งเสียง',
    'smartHome.guide.voiceCommandsNote': 'ถ้าอสังหาริมทรัพย์มี Google Nest speakers ลองคำสั่งเหล่านี้:',
    'smartHome.guide.appleHomeKit': 'ทางเลือก Apple HomeKit',
    'smartHome.guide.appleHomeKitDesc': 'สำหรับอสังหาริมทรัพย์ที่ใช้ระบบนิเวศ Apple เท่านั้น',
    'smartHome.guide.howToShareHomeKit': 'วิธีแชร์การเข้าถึง HomeKit กับแขก',
    'smartHome.guide.helpfulResources': 'แหล่งข้อมูลที่เป็นประโยชน์',
    'smartHome.guide.homeKitNote': 'HomeKit ต้องการให้แขกมี Apple ID และอุปกรณ์ iOS/macOS Google Home รองรับอุปกรณ์หลายประเภทและทำงานข้ามแพลตฟอร์ม',
    
    // AI Page (Thai)
    'aiPage.title': 'Aether AI',
    'aiPage.subtitle': 'ผู้ช่วยส่วนตัวของคุณ',
    'aiPage.welcome': 'สวัสดี! ฉันเป็นคอนเซียร์จ AI ของคุณ ฉันสามารถช่วยคุณได้เรื่อง:',
    'aiPage.help.booking': 'จองบริการและนัดหมาย',
    'aiPage.help.guestAccess': 'จัดการการเข้าถึงของแขก',
    'aiPage.help.maintenance': 'คำขอซ่อมบำรุงอสังหาริมทรัพย์',
    'aiPage.help.lifestyle': 'คำแนะนำไลฟ์สไตล์',
    'aiPage.trySaying': 'ลองพูดว่า',
    'aiPage.placeholder': 'ถามฉันอะไรก็ได้...',
    'aiPage.suggestion1': 'จองเชฟส่วนตัวสำหรับวันเสาร์',
    'aiPage.suggestion2': 'นัดหมายบำรุงรักษาสระว่ายน้ำ',
    'aiPage.suggestion3': 'อนุญาตให้แขกของฉันเข้าพรุ่งนี้',
    'aiPage.suggestion4': 'มีบริการอะไรบ้าง?',
    
    // Public Sales Page (Thai)
    'sales.priceRange': 'ช่วงราคา',
    'sales.minBedrooms': 'ห้องนอนขั้นต่ำ',
    'sales.any': 'ใดๆ',
    'sales.features': 'คุณสมบัติ',
    'sales.clearFilters': 'ล้างตัวกรองทั้งหมด',
    'sales.exclusiveProperties': 'อสังหาริมทรัพย์พิเศษ',
    'sales.propertiesForSale': 'อสังหาริมทรัพย์ขาย',
    'sales.discoverProperties': 'ค้นพบอสังหาริมทรัพย์พิเศษในชุมชนหรูหราของเรา',
    'sales.forSale': 'ขาย',
    'sales.sqft': 'ตร.ฟุต',
    'sales.keyFeatures': 'คุณสมบัติหลัก',
    'sales.propertyDirections': 'เส้นทางไปอสังหาริมทรัพย์',
    'sales.searchPlaceholder': 'ค้นหาตามที่ตั้งหรือชุมชน...',
    'sales.filters': 'ตัวกรอง',
    'sales.propertiesFound': 'พบ {count} อสังหาริมทรัพย์',
    'sales.propertyFound': 'พบ 1 อสังหาริมทรัพย์',
    'sales.viewRentals': 'ดูการเช่า',
    'sales.noProperties': 'ไม่มีอสังหาริมทรัพย์ตรงกับตัวกรองของคุณ',
    'sales.tryAdjusting': 'ลองปรับเกณฑ์การค้นหา',
    'sales.luxuryProperty': 'อสังหาริมทรัพย์หรูหรา',
    'sales.exclusiveCommunity': 'ชุมชนพิเศษ',
    'sales.bed': 'ห้องนอน',
    'sales.bath': 'ห้องน้ำ',
    'sales.parking': 'ที่จอดรถ',
    'sales.built': 'สร้างเมื่อ',
    'sales.moreFeatures': '+{count} เพิ่มเติม',
    'sales.contactForDetails': 'ติดต่อเพื่อรายละเอียด',
    
    // GodMode Dashboard (Thai)
    'godMode.tenantAnalytics': 'การวิเคราะห์ผู้เช่า',
    'godMode.tenantAnalyticsDesc': 'ดูเมตริกการใช้งานและการแบ่งรายได้',
    'godMode.communityAdmins': 'ผู้ดูแลชุมชน',
    'godMode.revenue': 'รายได้',
    'godMode.configure': 'กำหนดค่า',
    'godMode.admins': 'ผู้ดูแล',
    
    // SaleDetailPage (Thai)
    'saleDetail.back': 'กลับ',
    'saleDetail.forSale': 'ขาย',
    'saleDetail.bedrooms': 'ห้องนอน',
    'saleDetail.bathrooms': 'ห้องน้ำ',
    'saleDetail.parking': 'ที่จอดรถ',
    'saleDetail.builtIn': 'สร้างเมื่อ',
    'saleDetail.floor': 'ชั้น',
    'saleDetail.sqft': 'ตร.ฟุต',
    'saleDetail.aboutProperty': 'เกี่ยวกับอสังหาริมทรัพย์นี้',
    'saleDetail.keyFeatures': 'คุณสมบัติหลัก',
    'saleDetail.amenities': 'สิ่งอำนวยความสะดวก',
    'saleDetail.requestInfo': 'ขอข้อมูล',
    'saleDetail.name': 'ชื่อ',
    'saleDetail.email': 'อีเมล',
    'saleDetail.phone': 'โทรศัพท์',
    'saleDetail.message': 'ข้อความ',
    'saleDetail.interestedMessage': 'ฉันสนใจอสังหาริมทรัพย์นี้...',
    'saleDetail.sendInquiry': 'ส่งคำขอ',
    'saleDetail.sending': 'กำลังส่ง...',
    'saleDetail.listingNotFound': 'ไม่พบรายการ',
    'saleDetail.propertyUnavailable': 'อสังหาริมทรัพย์นี้อาจไม่มีอยู่แล้ว',
    'saleDetail.browseProperties': 'ดูอสังหาริมทรัพย์',
    'saleDetail.exclusiveLocation': 'ทำเลพิเศษ',
    'saleDetail.fillNameEmail': 'กรุณากรอกชื่อและอีเมลของคุณ',
    'saleDetail.inquirySubmitted': 'ส่งคำขอของคุณแล้ว! ตัวแทนจะติดต่อคุณเร็วๆ นี้',
    'saleDetail.inquiryFailed': 'ส่งคำขอไม่สำเร็จ กรุณาลองอีกครั้ง',
    'saleDetail.yourFullName': 'ชื่อเต็มของคุณ',
    'saleDetail.yourEmail': 'your@email.com',
    'saleDetail.contactWithin24h': 'ตัวแทนฝ่ายขายจะติดต่อคุณภายใน 24 ชั่วโมง',
    
    // PropertyDirectionsPage (Thai)
    'directions.propertyDirections': 'เส้นทางไปอสังหาริมทรัพย์',
    'directions.booking': 'การจอง',
    'directions.confirmed': 'ยืนยันแล้ว',
    'directions.fullPropertyAddress': 'ที่อยู่อสังหาริมทรัพย์เต็ม',
    'directions.copyAddress': 'คัดลอกที่อยู่',
    'directions.openInMaps': 'เปิดในแผนที่',
    'directions.getDirections': 'รับเส้นทาง',
    'directions.stayDetails': 'รายละเอียดการพัก',
    'directions.checkIn': 'เช็คอิน',
    'directions.checkOut': 'เช็คเอาท์',
    'directions.after3pm': 'หลัง 15:00 น.',
    'directions.before11am': 'ก่อน 11:00 น.',
    'directions.guests': 'แขก',
    'directions.guest': 'แขก',
    'directions.houseRules': 'กฎบ้าน',
    'directions.smartHomeAccess': 'การเข้าถึง Smart Home',
    'directions.confidentialInfo': 'ข้อมูลนี้เป็นความลับและไม่ควรแบ่งปันกับผู้อื่น',
    'directions.needHelp': 'ต้องการความช่วยเหลือ?',
    'directions.messageHost': 'ส่งข้อความถึงเจ้าของ',
    'directions.contactSupport': 'ติดต่อฝ่ายสนับสนุน',
    'directions.bookingNotFound': 'ไม่พบการจอง',
    'directions.noAccess': 'การจองนี้ไม่มีอยู่หรือคุณไม่มีสิทธิ์เข้าถึง',
    'directions.goToBookings': 'ไปที่การจอง',
    'directions.addressCopied': 'คัดลอกที่อยู่ไปยังคลิปบอร์ดแล้ว',
    'directions.addressOnlyConfirmed': 'ที่อยู่อสังหาริมทรัพย์มีเฉพาะสำหรับการจองที่ยืนยันแล้วเท่านั้น',
    'directions.failedToLoad': 'โหลดรายละเอียดอสังหาริมทรัพย์ไม่สำเร็จ',

    
    // Privacy Page (Thai)
    'privacy.backToHome': 'กลับหน้าแรก',
    'privacy.title': 'นโยบายความเป็นส่วนตัว',
    'privacy.lastUpdated': 'อัปเดตล่าสุด: ธันวาคม 2025',
    'privacy.footer': '© 2025 Aether. การจัดการอสังหาริมทรัพย์ ง่ายขึ้น',
    'privacy.section1.title': '1. ข้อมูลที่เราเก็บรวบรวม',
    'privacy.section1.intro': 'ที่ Aether เราเก็บรวบรวมข้อมูลที่คุณให้โดยตรง เช่น เมื่อคุณสร้างบัญชี ชำระเงิน หรือติดต่อขอความช่วยเหลือ ซึ่งอาจรวมถึง:',
    'privacy.section1.item1': 'ชื่อและข้อมูลติดต่อ (อีเมล หมายเลขโทรศัพท์)',
    'privacy.section1.item2': 'รายละเอียดและที่อยู่อสังหาริมทรัพย์',
    'privacy.section1.item3': 'ข้อมูลการชำระเงิน (ดำเนินการอย่างปลอดภัยผ่าน Stripe)',
    'privacy.section1.item4': 'การตั้งค่าการสื่อสาร',
    'privacy.section1.item5': 'ข้อมูลการใช้งานและการวิเคราะห์',
    'privacy.section2.title': '2. วิธีที่เราใช้ข้อมูลของคุณ',
    'privacy.section2.intro': 'เราใช้ข้อมูลที่เก็บรวบรวมเพื่อ:',
    'privacy.section2.item1': 'ให้บริการ บำรุงรักษา และปรับปรุงบริการของเรา',
    'privacy.section2.item2': 'ดำเนินการธุรกรรมและส่งการแจ้งเตือนที่เกี่ยวข้อง',
    'privacy.section2.item3': 'ตอบความคิดเห็น คำถาม และคำขอการสนับสนุนของคุณ',
    'privacy.section2.item4': 'ส่งประกาศทางเทคนิค อัปเดต และการแจ้งเตือนความปลอดภัย',
    'privacy.section2.item5': 'สื่อสารเกี่ยวกับผลิตภัณฑ์ บริการ และกิจกรรม',
    'privacy.section3.title': '3. ระดับการสมัครสมาชิกและการจัดการข้อมูล',
    'privacy.section3.intro': 'เราจัดการข้อมูลตามระดับการสมัครสมาชิกของคุณ:',
    'privacy.section3.explorer': 'Explorer (ผู้เข้าพัก)',
    'privacy.section3.explorerDesc': 'ประวัติการจอง การตั้งค่า บันทึกการชำระเงินสำหรับทดลองใช้ 15 วันและหลังจากนั้น',
    'privacy.section3.sovereign': 'Sovereign (เจ้าของบ้าน)',
    'privacy.section3.sovereignDesc': 'ข้อมูลอสังหาริมทรัพย์ รายการเช่า การจัดการผู้เข้าพัก การวิเคราะห์รายได้สำหรับทดลองใช้ 30 วันและหลังจากนั้น',
    'privacy.section3.architect': 'Architect (ผู้พัฒนา)',
    'privacy.section3.architectDesc': 'ข้อมูลชุมชน การจัดการเจ้าของบ้าน การชำระ HOA ความสัมพันธ์กับผู้ให้บริการ',
    'privacy.section4.title': '4. การเข้าถึงโดยการเชิญเท่านั้น',
    'privacy.section4.intro': 'สำหรับระดับการสมัครบางระดับ การเข้าถึงเป็นแบบเชิญเท่านั้น:',
    'privacy.section4.item1': 'เจ้าของบ้าน (Sovereign) ได้รับคำเชิญจากผู้ดูแลชุมชน',
    'privacy.section4.item2': 'ผู้พัฒนาอสังหาริมทรัพย์ (Architect) ได้รับคำเชิญจากพนักงาน Aether หลังการปรึกษาการขาย',
    'privacy.section5.title': '5. ความปลอดภัยของข้อมูล',
    'privacy.section5.content': 'เราใช้การเข้ารหัส SSL 256 บิตระดับธนาคารสำหรับการส่งข้อมูลทั้งหมด ข้อมูลของคุณถูกเก็บในศูนย์ข้อมูลที่ได้รับการรับรอง SOC 2 Type II ที่มี uptime 99.99% เราใช้มาตรการทางเทคนิคและองค์กรที่เหมาะสมเพื่อปกป้องข้อมูลส่วนบุคคลของคุณ',
    'privacy.section6.title': '6. บริการของบุคคลที่สาม',
    'privacy.section6.intro': 'เราใช้บริการบุคคลที่สามที่เชื่อถือได้เพื่อให้บริการแพลตฟอร์มของเรา:',
    'privacy.section6.stripe': 'การประมวลผลการชำระเงินที่ปลอดภัยสำหรับการสมัครสมาชิก ค่า HOA และการชำระค่าเช่า',
    'privacy.section6.mapbox': 'แผนที่แบบโต้ตอบสำหรับตำแหน่งอสังหาริมทรัพย์และสิ่งอำนวยความสะดวกของชุมชน',
    'privacy.section6.openai': 'บริการ Concierge ที่ขับเคลื่อนด้วย AI สำหรับการจองบริการและคำแนะนำ',
    'privacy.section7.title': '7. การเก็บรักษาข้อมูล',
    'privacy.section7.content': 'เราเก็บรักษาข้อมูลของคุณตราบเท่าที่บัญชีของคุณยังใช้งานอยู่หรือจำเป็นต้องให้บริการ ข้อมูลการสมัครสมาชิกและธุรกรรมจะถูกเก็บ 7 ปีเพื่อปฏิบัติตามภาษีและกฎหมาย คุณสามารถขอลบบัญชีและข้อมูลส่วนบุคคลได้ตลอดเวลา',
    'privacy.section8.title': '8. สิทธิ์ของคุณ',
    'privacy.section8.intro': 'คุณมีสิทธิ์ที่จะ:',
    'privacy.section8.item1': 'เข้าถึงและรับสำเนาข้อมูลส่วนบุคคลของคุณ',
    'privacy.section8.item2': 'แก้ไขข้อมูลส่วนบุคคลที่ไม่ถูกต้อง',
    'privacy.section8.item3': 'ขอให้ลบข้อมูลส่วนบุคคลของคุณ',
    'privacy.section8.item4': 'คัดค้านการประมวลผลข้อมูลส่วนบุคคลของคุณ',
    'privacy.section8.item5': 'ขอจำกัดการประมวลผลข้อมูลส่วนบุคคลของคุณ',
    'privacy.section8.item6': 'การโอนย้ายข้อมูล',
    'privacy.section9.title': '9. ติดต่อเรา',
    'privacy.section9.content': 'หากคุณมีคำถามเกี่ยวกับนโยบายความเป็นส่วนตัวนี้ โปรดติดต่อเราที่',
    
    // Terms Page (Thai)
    'terms.backToHome': 'กลับหน้าแรก',
    'terms.title': 'ข้อกำหนดการให้บริการ',
    'terms.lastUpdated': 'อัปเดตล่าสุด: ธันวาคม 2025',
    'terms.footer': '© 2025 Aether. การจัดการอสังหาริมทรัพย์ ง่ายขึ้น',
    'terms.section1.title': '1. การยอมรับข้อกำหนด',
    'terms.section1.content': 'การเข้าถึงหรือใช้ Aether ("บริการ") หมายความว่าคุณยอมรับข้อกำหนดการให้บริการเหล่านี้ หากคุณไม่เห็นด้วย โปรดอย่าใช้บริการของเรา เราขอสงวนสิทธิ์ในการอัปเดตข้อกำหนดเหล่านี้ได้ตลอดเวลา',
    'terms.section2.title': '2. คำอธิบายบริการ',
    'terms.section2.content': 'Aether เป็นแพลตฟอร์มการจัดการอสังหาริมทรัพย์ที่ให้เครื่องมือสำหรับผู้จัดการชุมชน เจ้าของอสังหาริมทรัพย์ และผู้เข้าพัก บริการของเรารวมถึง: การประมวลผลการชำระเงิน การจัดการบัตรผ่านผู้เข้าพัก การจองบริการ ประกาศชุมชน รายการเช่า และบริการ Concierge ที่ขับเคลื่อนด้วย AI',
    'terms.section3.title': '3. แผนการสมัครสมาชิก',
    'terms.section3.intro': 'Aether เสนอระดับการสมัครสมาชิกสามระดับ:',
    'terms.section3.explorer': 'Explorer ($9.99/เดือน)',
    'terms.section3.explorerDesc': 'สำหรับนักเดินทางที่ต้องการที่พักหรู จองเช่าระยะสั้น เช่าระยะยาว และเข้าถึงบริการ AI Concierge',
    'terms.section3.sovereign': 'Sovereign ($19.99/เดือน)',
    'terms.section3.sovereignDesc': 'สำหรับเจ้าของบ้าน ลงประกาศอสังหาริมทรัพย์ จัดการผู้เข้าพัก เข้าถึงการวิเคราะห์ และสร้างรายได้จากการเช่า',
    'terms.section3.architect': 'Architect (ราคาที่กำหนดเอง)',
    'terms.section3.architectDesc': 'สำหรับผู้พัฒนาอสังหาริมทรัพย์ สร้างชุมชน จัดการเจ้าของบ้าน เก็บค่า HOA และเข้าถึงฟีเจอร์องค์กร',
    'terms.section4.title': '4. ระยะเวลาทดลองใช้',
    'terms.section4.intro': 'การทดลองใช้ฟรีมีให้สำหรับแผนที่เลือก:',
    'terms.section4.explorer': 'Explorer',
    'terms.section4.explorerTrial': 'ทดลองใช้ฟรี 15 วัน ไม่ต้องใช้บัตรเครดิตในการเริ่มต้น',
    'terms.section4.sovereign': 'Sovereign',
    'terms.section4.sovereignTrial': 'ทดลองใช้ฟรี 30 วันหลังได้รับคำเชิญชุมชนจากผู้ดูแล',
    'terms.section4.architect': 'Architect',
    'terms.section4.architectTrial': 'ไม่มีการทดลองใช้ ติดต่อฝ่ายขายเพื่อขอสาธิตและราคาที่กำหนดเอง',
    'terms.section5.title': '5. การเข้าถึงโดยการเชิญเท่านั้น',
    'terms.section5.intro': 'แผนบางแผนต้องการคำเชิญ:',
    'terms.section5.item1': 'เจ้าของบ้าน (แผน Sovereign) ต้องได้รับเชิญจากผู้ดูแลชุมชน',
    'terms.section5.item2': 'ผู้พัฒนาอสังหาริมทรัพย์ (แผน Architect) ต้องได้รับเชิญจากพนักงาน Aether หลังการปรึกษาการขาย',
    'terms.section5.item3': 'คำเชิญไม่สามารถโอนได้และมีไว้สำหรับผู้รับที่ระบุเท่านั้น',
    'terms.section6.title': '6. บัญชีผู้ใช้',
    'terms.section6.intro': 'เพื่อเข้าถึงฟีเจอร์บางอย่างของบริการ คุณต้องสร้างบัญชี คุณตกลงที่จะ:',
    'terms.section6.item1': 'ให้ข้อมูลที่ถูกต้อง เป็นปัจจุบัน และสมบูรณ์',
    'terms.section6.item2': 'รักษาและอัปเดตข้อมูลบัญชีของคุณทันที',
    'terms.section6.item3': 'รักษาความปลอดภัยของรหัสผ่านและบัญชีของคุณ',
    'terms.section6.item4': 'รับผิดชอบต่อกิจกรรมทั้งหมดภายใต้บัญชีของคุณ',
    'terms.section6.item5': 'แจ้งให้เราทราบทันทีหากมีการใช้งานโดยไม่ได้รับอนุญาต',
    'terms.section7.title': '7. การควบคุมการเข้าถึงตามบทบาท',
    'terms.section7.intro': 'Aether ใช้การเข้าถึงตามบทบาทเพื่อจัดการสิทธิ์:',
    'terms.section7.admin': 'Admin (ผู้จัดการชุมชน)',
    'terms.section7.adminDesc': 'เข้าถึงเต็มที่ในการจัดการอสังหาริมทรัพย์ เจ้าของบ้าน ผู้ให้บริการ และการตั้งค่าชุมชน',
    'terms.section7.sovereign': 'Sovereign (เจ้าของอสังหาริมทรัพย์)',
    'terms.section7.sovereignDesc': 'เข้าถึงอสังหาริมทรัพย์ที่เป็นเจ้าของ การชำระ HOA การจัดการการเช่า และบริการ Concierge',
    'terms.section7.demiUser': 'Demi-User (ผู้เข้าพัก)',
    'terms.section7.demiUserDesc': 'เข้าถึงจำกัดเวลาสำหรับแผนที่ บริการพื้นฐาน และการสื่อสารกับเจ้าของ',
    'terms.section8.title': '8. การใช้งานที่ยอมรับได้',
    'terms.section8.intro': 'คุณตกลงที่จะไม่:',
    'terms.section8.item1': 'ใช้บริการเพื่อวัตถุประสงค์ที่ผิดกฎหมาย',
    'terms.section8.item2': 'พยายามเข้าถึงส่วนใดของบริการโดยไม่ได้รับอนุญาต',
    'terms.section8.item3': 'แทรกแซงหรือรบกวนบริการหรือเซิร์ฟเวอร์',
    'terms.section8.item4': 'อัปโหลดโค้ดหรือเนื้อหาที่เป็นอันตราย',
    'terms.section8.item5': 'ปลอมตัวเป็นบุคคลหรือหน่วยงานใด',
    'terms.section8.item6': 'ละเมิดกฎหมายหรือข้อบังคับที่บังคับใช้',
    'terms.section9.title': '9. การเรียกเก็บเงินและการยกเลิก',
    'terms.section9.content': 'ค่าสมาชิกเรียกเก็บล่วงหน้าเป็นรายเดือนหรือรายปี ค่าธรรมเนียมทั้งหมดไม่สามารถขอคืนได้ยกเว้นตามที่กฎหมายกำหนด คุณสามารถยกเลิกการสมัครสมาชิกได้ตลอดเวลาผ่าน Stripe Customer Portal ในการตั้งค่า การเข้าถึงของคุณจะยังคงอยู่จนถึงสิ้นรอบการเรียกเก็บเงิน',
    'terms.section10.title': '10. การจำกัดความรับผิด',
    'terms.section10.content': 'ในขอบเขตสูงสุดที่กฎหมายอนุญาต Aether จะไม่รับผิดชอบต่อความเสียหายทางอ้อม โดยบังเอิญ พิเศษ ที่เป็นผลสืบเนื่อง หรือเชิงลงโทษใดๆ ที่เกิดจากการใช้หรือไม่สามารถใช้บริการของคุณ',
    'terms.section11.title': '11. ติดต่อ',
    'terms.section11.content': 'สำหรับคำถามเกี่ยวกับข้อกำหนดเหล่านี้ โปรดติดต่อเราที่',
    
    // Support FAQ (Thai - Updated)
    'support.faq1Question': 'ฉันจะสมัคร Aether ได้อย่างไร?',
    'support.faq1Answer': 'ผู้เข้าพักสามารถสมัครโดยตรงบนเว็บไซต์ของเราและทดลองใช้ฟรี 15 วัน เจ้าของบ้านต้องได้รับคำเชิญจากผู้ดูแลชุมชน (ทดลองใช้ 30 วัน) ผู้พัฒนาอสังหาริมทรัพย์ควรติดต่อทีมขายของเรา',
    'support.faq2Question': 'แต่ละแผนการสมัครสมาชิกรวมอะไรบ้าง?',
    'support.faq2Answer': 'Explorer ($9.99/เดือน) สำหรับนักเดินทาง - จองเช่า ใช้ AI Concierge Sovereign ($19.99/เดือน) สำหรับเจ้าของบ้าน - ลงประกาศอสังหาริมทรัพย์ จัดการผู้เข้าพัก Architect (ราคาที่กำหนดเอง) สำหรับผู้พัฒนาที่จัดการชุมชน',
    'support.faq3Question': 'การทดลองใช้ฟรีทำงานอย่างไร?',
    'support.faq3Answer': 'แผน Explorer: ทดลองใช้ฟรี 15 วัน ไม่ต้องใช้บัตรเครดิต แผน Sovereign: ทดลองใช้ฟรี 30 วันหลังได้รับคำเชิญชุมชน แผน Architect: ไม่มีการทดลองใช้ - ติดต่อฝ่ายขายเพื่อขอสาธิตและราคาที่กำหนดเอง',
    'support.faq4Question': 'ฉันจะได้รับเชิญเป็นเจ้าของบ้านได้อย่างไร?',
    'support.faq4Answer': 'ติดต่อผู้ดูแลชุมชนหรือผู้พัฒนาอสังหาริมทรัพย์ของคุณและขอให้พวกเขาส่งคำเชิญผ่าน Aether คุณจะได้รับอีเมลพร้อมคำแนะนำในการตั้งค่าบัญชี',
    'support.faq5Question': 'ฉันจะเป็นผู้พัฒนาอสังหาริมทรัพย์บน Aether ได้อย่างไร?',
    'support.faq5Answer': 'ติดต่อทีมขายของเราผ่านหน้าการสนับสนุนหรืออีเมล sales@aether.app เราเสนอ onboarding สำหรับองค์กรพร้อมราคาที่กำหนดเองตามขนาดพอร์ตโฟลิโอของคุณ',
    'support.faq6Question': 'ฉันจะยกเลิกหรือเปลี่ยนการสมัครสมาชิกได้อย่างไร?',
    'support.faq6Answer': 'ไปที่ การตั้งค่า > การสมัครสมาชิก และคลิก "จัดการการสมัครสมาชิก" เพื่อเข้าถึง Stripe Customer Portal คุณสามารถยกเลิกได้ตลอดเวลา - การเข้าถึงของคุณจะยังคงอยู่จนถึงสิ้นรอบการเรียกเก็บเงิน',
  },
  ja: {
    // Common
    'common.signIn': 'ログイン',
    'common.signUp': '新規登録',
    'common.signOut': 'ログアウト',
    'common.loading': '読み込み中...',
    'common.save': '保存',
    'common.cancel': 'キャンセル',
    'common.delete': '削除',
    'common.edit': '編集',
    'common.search': '検索',
    'common.filter': 'フィルター',
    'common.clear': 'クリア',
    'common.submit': '送信',
    'common.back': '戻る',
    'common.next': '次へ',
    'common.viewAll': 'すべて表示',
    'common.learnMore': '詳細を見る',
    
    // Role Simulator
    'roleSimulator.title': 'ロールシミュレーター',
    'roleSimulator.description': '異なるユーザーの視点からアプリをテスト',
    'roleSimulator.godMode': 'God Mode',
    'roleSimulator.admin': 'コミュニティ管理者',
    'roleSimulator.homeowner': 'オーナー',
    'roleSimulator.guest': 'ゲスト',
    'roleSimulator.godModeDesc': 'フルシステムアクセス',
    'roleSimulator.adminDesc': 'コミュニティを管理',
    'roleSimulator.homeownerDesc': 'プロパティを管理',
    'roleSimulator.guestDesc': '限定アクセス',
    'roleSimulator.active': 'アクティブ',
    'roleSimulator.exitSimulation': 'シミュレーションを終了',
    
    // Simulation Banner
    'simulation.simulating': 'シミュレーション中',
    'simulation.exit': 'シミュレーションを終了',
    
    // Cookie Consent
    'cookies.message': '体験を向上させるためにCookieを使用しています。',
    'cookies.learnMore': 'プライバシーポリシーを読む',
    'cookies.accept': '承認',
    'cookies.dismiss': '閉じる',
    'common.close': '閉じる',
    
    // Notification Center
    'notificationCenter.new': '件の新着',
    'notificationCenter.noNotifications': '通知はまだありません',
    'notificationCenter.noNotificationsDesc': '支払いアラートと更新情報がここに表示されます',
    
    // Toast Messages
    'toast.loadFailed': 'データの読み込みに失敗しました',
    'toast.emailRequired': 'メールアドレスを入力してください',
    'toast.inviteSent': '招待を送信しました',
    'toast.inviteFailed': '招待の送信に失敗しました',
    'toast.addressRequired': '住所を入力してください',
    'toast.propertyAdded': '物件を追加しました',
    'toast.propertyAddFailed': '物件の追加に失敗しました',
    'toast.rentalModeEnabled': '賃貸モードを有効にしました',
    'toast.rentalModeDisabled': '賃貸モードを無効にしました',
    'toast.rentalModeUpdateFailed': '賃貸モードの更新に失敗しました',
    'toast.rentalListingSaved': '賃貸リストを保存しました',
    'toast.rentalListingSaveFailed': '賃貸リストの保存に失敗しました',
    'toast.communityNameRequired': 'コミュニティ名を入力してください',
    'toast.communityCreated': 'コミュニティを作成しました',
    'toast.communityCreateFailed': 'コミュニティの作成に失敗しました',
    'toast.adminInviteSent': '管理者招待を送信しました',
    'toast.inviteDeleted': '招待を削除しました',
    'toast.deleteFailed': '削除に失敗しました',
    'toast.listingSaved': 'リスティングを保存しました',
    'toast.listingSaveFailed': 'リスティングの保存に失敗しました',
    'toast.calendarUpdated': 'カレンダーを更新しました',
    'toast.calendarUpdateFailed': 'カレンダーの更新に失敗しました',
    'toast.ownerInviteSent': 'オーナーへの招待が送信されました',
    'toast.updateFailed': '更新に失敗しました',
    'common.error': 'エラー',
    'common.unknown': '不明',
    
    // Smart Home & Rental & Homeowner (Phase 5 - Japanese)
    'smartHome.title': 'スマートホームアクセス',
    'smartHome.shareTitle': 'ゲストとスマートホームを共有',
    'smartHome.shareDesc': 'Google Homeを使用してゲストにスマートデバイスへのアクセスを提供',
    'smartHome.quickSteps': 'クイックステップ',
    'smartHome.step1': 'Google Homeアプリを開く',
    'smartHome.step2': '設定 → 家族に移動',
    'smartHome.step3': 'メールでゲストを招待',
    'smartHome.remember': '忘れずに',
    'smartHome.reminderText': 'チェックイン前にゲストアクセスを追加し、チェックアウト後に削除',
    'smartHome.viewGuide': '完全ガイドを見る',
    'smartHome.worksWith': '対応:',
    'smartHome.devicesList': 'Nest、Ring、Yale、August、Philips Hueなど50,000以上のGoogle Home対応デバイス',
    'rental.status': 'レンタル状況',
    'rental.bookings': '予約',
    'rental.pending': '保留中',
    'rental.revenue': '収益',
    'rental.rating': '評価',
    'rental.view': '表示',
    'rental.edit': '編集',
    'rental.calendar': 'カレンダー',
    'rental.completeListing': 'リスティングを完成させて収益開始',
    'rental.enableMode': 'レンタルモードを有効にして物件を掲載',
    'rental.enableButton': 'レンタルモードを有効化',
    'rental.modeEnabled': 'レンタルモードを有効化しました',
    'rental.modeDisabled': 'レンタルモードを無効化しました',
    'rental.updateFailed': 'レンタルモードの更新に失敗しました',
    'homeowner.upcomingHOA': '今後のHOA会費',
    'homeowner.payNow': '今すぐ支払う',
    'homeowner.noHOADues': 'HOA会費は設定されていません',
    'homeowner.dueToday': '今日が期日',
    'homeowner.daysLeft': '{days}日',
    'homeowner.communityUpdates': 'コミュニティ更新',
    'homeowner.new': '新着',
    'homeowner.noAnnouncements': '最近のお知らせはありません',
    
    // Navigation
    'nav.home': 'ダッシュボード',
    'nav.dashboard': 'ダッシュボード',
    'nav.services': 'サービス',
    'nav.rentals': '賃貸',
    'nav.map': '地図',
    'nav.wallet': 'お支払い',
    'nav.settings': '設定',
    'nav.messages': 'メッセージ',
    'nav.notifications': '通知',
    'nav.concierge': 'コンシェルジュ',
    
    // Auth
    'auth.welcomeBack': 'おかえりなさい',
    'auth.createAccount': 'アカウント作成',
    'auth.resetPassword': 'パスワードリセット',
    'auth.setPassword': 'パスワードを設定',
    'auth.email': 'メールアドレス',
    'auth.password': 'パスワード',
    'auth.confirmPassword': 'パスワード確認',
    'auth.forgotPassword': 'パスワードを忘れましたか?',
    'auth.noAccount': 'アカウントをお持ちでないですか?',
    'auth.hasAccount': 'すでにアカウントをお持ちですか?',
    'auth.backToHome': 'ホームに戻る',
    
    // Dashboard
    'dashboard.welcome': 'おかえりなさい',
    'dashboard.properties': '物件',
    'dashboard.bookings': '予約',
    'dashboard.payments': '支払い',
    'dashboard.guests': 'ゲスト',
    
    // Rentals
    'rentals.title': '特別な物件を発見',
    'rentals.subtitle': '世界クラスのアメニティとパーソナライズされたコンシェルジュサービスを体験',
    'rentals.searchPlaceholder': '場所で検索...',
    'rentals.pricePerNight': '/泊',
    'rentals.bedrooms': '寝室',
    'rentals.bathrooms': 'バスルーム',
    'rentals.bookNow': '今すぐ予約',
    'rentals.checkIn': 'チェックイン',
    'rentals.checkOut': 'チェックアウト',
    'rentals.guests': 'ゲスト',
    'rentals.noResults': '条件に一致する物件がありません',
    'rentals.askAI': 'AIに賃貸を探してもらう',
    
    // AI Assistant
    'ai.greeting': 'こんにちは！Aetherコンシェルジュです。何かお手伝いできますか?',
    'ai.placeholder': '何でも聞いてください...',
    'ai.listening': '聞いています...',
    'ai.findRentals': '空き物件を探す',
    
    // Settings
    'settings.title': '設定',
    'settings.language': '言語',
    'settings.currency': '通貨',
    'settings.timezone': 'タイムゾーン',
    'settings.notifications': '通知',
    'settings.appearance': '外観',
    'settings.darkMode': 'ダークモード',
    'settings.profile': 'プロフィール情報',
    'settings.profileDesc': '名前、電話、アバター',
    'settings.notificationsDesc': 'メールとプッシュ通知の設定',
    'settings.payment': '支払い方法',
    'settings.paymentDesc': 'カードと請求の管理',
    'settings.subscriptions': 'サブスクリプション',
    'settings.subscriptionsDesc': 'SaaSとHOAプラン',
    'settings.guestPasses': 'ゲストパス',
    'settings.guestPassesDesc': '訪問者アクセスの管理',
    'settings.languageCurrency': '言語と通貨',
    'settings.languageCurrencyDesc': '地域の設定',
    'settings.smartHome': 'スマートホーム共有',
    'settings.smartHomeDesc': 'ゲストとGoogle Homeアクセスを共有',
    'settings.viewAs': 'ロールシミュレーター',
    'settings.viewAsDesc': '異なるユーザー体験をテスト',
    'settings.signOut': 'ログアウト',
    
    // Messages
    'messages.title': 'メッセージ',
    'messages.subtitle': '会話、AI、通知',
    'messages.chats': 'チャット',
    'messages.aether': 'Aether',
    'messages.alerts': 'アラート',
    'messages.noConversations': '会話はまだありません',
    'messages.noConversationsDesc': 'ゲストや物件オーナーとのメッセージがここに表示されます',
    'messages.noNotifications': '通知はありません',
    'messages.noNotificationsDesc': 'コミュニティのお知らせがここに表示されます',
    'messages.markAllRead': 'すべて既読にする',
    'messages.startConversation': '会話を始める',
    'messages.aetherTitle': 'Aether AIコンシェルジュ',
    'messages.aetherDesc': 'サービスの予約、おすすめの取得、物件ニーズの管理のためのパーソナルAIアシスタント。',
    
    // Browse
    'nav.browse': '探索',
    
    // Dashboard specific
    'dashboard.guestAccess': 'ゲストアクセス',
    'dashboard.accessExpires': 'アクセス期限',
    'dashboard.daysRemaining': '日間のゲストアクセスが残っています',
    'dashboard.browseRentals': '物件を探す',
    'dashboard.services': 'サービス',
    'dashboard.personalizeSearch': '検索をカスタマイズ',
    'dashboard.updatePreferences': '賃貸の好みを更新',
    'dashboard.availableServices': '利用可能なサービス',
    'dashboard.communityMap': 'コミュニティマップ',
    'dashboard.exploreAmenities': '設備を探索',
    'dashboard.dining': 'ダイニング',
    'dashboard.requestMeals': '食事をリクエスト',
    'dashboard.transport': '交通',
    'dashboard.requestValet': 'バレーをリクエスト',
    'dashboard.housekeeping': 'ハウスキーピング',
    'dashboard.ownerOnly': 'オーナー専用',
    'dashboard.guestGuidelines': 'ゲストガイドライン',
    'dashboard.propertyManager': 'プロパティマネージャー',
    'dashboard.guestAtProperty': 'この物件のゲスト',
    'dashboard.guidelineQuietHours': '静粛時間は22:00から7:00まで',
    'dashboard.guidelinePoolGym': 'プールとジムは6:00-22:00利用可能',
    'dashboard.guidelineConcierge': 'ご不明な点はコンシェルジュへ',
    'dashboard.guidelineRespect': 'コミュニティの設備を大切にしてください',
    'dashboard.greetingMorning': 'おはようございます',
    'dashboard.greetingAfternoon': 'こんにちは',
    'dashboard.greetingEvening': 'こんばんは',
    'dashboard.loading': 'ダッシュボードを読み込み中...',
    'dashboard.welcomeAether': 'Aetherへようこそ',
    'dashboard.yourSmartHome': 'あなたのスマートホーム',
    'dashboard.allSystemsOperational': '全システム正常稼働中',
    'dashboard.quickActions': 'クイックアクション',
    'dashboard.propertyStatus': '物件ステータス',
    'dashboard.communityUpdates': 'コミュニティ更新',
    'dashboard.upcoming': '予定',
    'dashboard.confirmed': '確認済み',
    'dashboard.rentalMode': '賃貸モード',
    'dashboard.rentalModeDesc': '短期賃貸用に掲載',
    'dashboard.rentalModeEnabled': '賃貸モードが有効になりました',
    'dashboard.rentalModeDisabled': '賃貸モードが無効になりました',
    'dashboard.createGuestPass': 'ゲストパスを作成',
    'dashboard.guestEmail': 'ゲストのメールアドレス',
    'dashboard.accessDuration': 'アクセス期間（日）',
    'dashboard.sendGuestPass': 'ゲストパスを送信',
    'dashboard.needProperty': 'ゲストパスを作成するには物件を所有する必要があります',
    'dashboard.privateDinner': 'プライベートディナー',
    
    // AI Page
    'ai.title': 'Aether AI',
    'ai.subtitle': 'あなたのパーソナルアシスタント',
    'ai.welcomeMessage': 'こんにちは！AIコンシェルジュです。以下のお手伝いができます：',
    'ai.helpBooking': 'サービスと予約の手配',
    'ai.helpGuest': 'ゲストアクセスの管理',
    'ai.helpMaintenance': '物件メンテナンスのリクエスト',
    'ai.helpLifestyle': 'ライフスタイルのおすすめ',
    'ai.trySaying': '話しかけてみてください',
    'ai.suggestion1': '土曜日にプライベートシェフを予約して',
    'ai.suggestion2': 'プールのメンテナンスを予約して',
    'ai.suggestion3': '明日のゲストを承認して',
    'ai.suggestion4': '利用できるサービスは?',
    
    // Transactions
    'transaction.pending': '保留中',
    'transaction.processing': '処理中',
    'transaction.completed': '完了',
    'transaction.failed': '失敗',
    'transaction.refunded': '返金済み',
    'transaction.type.hoa_dues': 'HOA料金',
    'transaction.type.service_payment': 'サービス支払い',
    'transaction.type.refund': '返金',
    'transaction.type.deposit': 'デポジット',
    
    // HOA Payments
    'hoa.monthlyDues': '月額HOA料金',
    'hoa.dueOnDay': '毎月{day}日が期日',
    'hoa.perMonth': '/月',
    'hoa.nextPayment': '次回の支払い',
    'hoa.payNow': '今すぐ支払う',
    'hoa.allCaughtUp': '全額支払い済み!',
    'hoa.notConfigured': 'この物件ではHOA料金が設定されていません',
    'hoa.paymentHistory': '支払い履歴',
    'hoa.noProperties': '物件が見つかりません',
    'hoa.noPropertiesDesc': 'HOA料金を支払うには物件を所有する必要があります',
    'hoa.choosePaymentMethod': '支払い方法を選択',
    'hoa.paid': '支払い済み',
    'hoa.verifying': '確認中',
    
    // Concierge Page
    'concierge.title': 'コンシェルジュ',
    'concierge.subtitle': 'お好みのサービスをご用命ください',
    'concierge.askAether': 'Aetherに聞く',
    'concierge.services': 'サービス',
    'concierge.myBookings': '予約一覧',
    'concierge.serviceCategories': 'サービスカテゴリ',
    'concierge.yourBookings': 'あなたの予約',
    'concierge.viewAllBookings': 'すべての予約を見る',
    'concierge.manageBookings': '今後のサービス予約を表示・管理',
    'concierge.notAvailable': 'コンシェルジュは利用できません',
    'concierge.notAvailableDesc': 'コンシェルジュサービスはコミュニティで有効になっていません。詳しくは管理者にお問い合わせください。',
    'concierge.noCommunity': 'コミュニティアクセスなし',
    'concierge.noCommunityDesc': 'コミュニティに参加するとコンシェルジュサービスが利用可能になります。',
    'concierge.createCommunity': '最初のコミュニティを作成',
    'concierge.noServices': 'サービス近日公開',
    'concierge.noServicesDesc': 'コミュニティ管理者がコンシェルジュサービスを設定中です。後ほどご確認ください!',
    
    // Service Categories
    'service.housekeeping': 'ハウスキーピング',
    'service.housekeepingDesc': 'プレミアムクリーニング＆ランドリー',
    'service.wellness': 'ウェルネス',
    'service.wellnessDesc': 'スパ、フィットネス＆リラクゼーション',
    'service.maintenance': 'メンテナンス',
    'service.maintenanceDesc': '物件ケア＆修理',
    'service.dining': 'プライベートダイニング',
    'service.diningDesc': '厳選された料理体験',
    'service.transport': '交通',
    'service.transportDesc': '高級車サービス',
    
    // Service Marketplace
    'marketplace.title': 'サービス',
    'marketplace.subtitle': 'プレミアムサービスを予約',
    'marketplace.all': 'すべて',
    'marketplace.noServices': 'サービスがありません',
    'marketplace.checkBack': '後でもう一度確認してください',
    'marketplace.book': '予約',
    'marketplace.estimatedPrice': '見積価格',
    'marketplace.priceVaries': '*最終価格は物件サイズにより異なる場合があります',
    'marketplace.scheduleDateTime': '日時を選択',
    'marketplace.specialRequests': '特別リクエスト（任意）',
    'marketplace.specialRequestsPlaceholder': '特別な指示...',
    'marketplace.payWithCard': 'カードで支払う (USD)',
    
    // Sovereign Dashboard
    'sovereign.propertyManager': 'プロパティマネージャー',
    'sovereign.properties': '物件',
    'sovereign.selectProperty': '物件を選択',
    'sovereign.noPropertyTitle': '物件が見つかりません',
    'sovereign.noPropertyDesc': 'アカウントに物件が割り当てられていません。',
    'sovereign.goToDashboard': 'ダッシュボードへ',
    'sovereign.standaloneProperty': '独立物件',
    'sovereign.community': 'コミュニティ',
    'sovereign.rentalMode': '賃貸モード',
    'sovereign.rentalModeDesc': '短期賃貸用に物件を掲載',
    'sovereign.totalBookings': '総予約数',
    'sovereign.pending': '保留中',
    'sovereign.revenue': '収益',
    'sovereign.rating': '評価',
    'sovereign.bookings': '予約',
    'sovereign.calendar': 'カレンダー',
    'sovereign.listing': 'リスティング',
    'sovereign.reviews': 'レビュー',
    'sovereign.smartHome': 'スマートホーム',
    'sovereign.guestReviews': 'ゲストレビュー',
    'sovereign.enableRentalMode': '賃貸モードを有効にする',
    'sovereign.enableRentalModeDesc': '短期賃貸用にマーケットプレイスに物件を掲載。独自の料金を設定し、空き状況を管理し、収入を得ましょう。',
    'sovereign.setRates': '宿泊料金とクリーニング料金を設定',
    'sovereign.controlCalendar': '空き状況カレンダーを管理',
    'sovereign.manageGuests': 'ゲストを承認し予約を管理',
    
    // Admin Dashboard
    'admin.title': 'ダッシュボード',
    'admin.role': '管理者',
    'admin.godMode': 'ゴッドモード',
    'admin.properties': '物件',
    'admin.owners': 'オーナー',
    'admin.collected': '徴収済み',
    'admin.overview': '概要',
    'admin.forSale': '販売中',
    'admin.announcements': 'お知らせ',
    'admin.bookings': '予約',
    'admin.services': 'サービス',
    'admin.vendors': 'ベンダー',
    'admin.earnings': '収益',
    'admin.reviews': 'レビュー',
    'admin.more': 'その他',
    'admin.hoaStatus': 'HOA徴収状況',
    'admin.paid': '支払済み',
    'admin.pending': '保留中',
    'admin.overdue': '期限超過',
    'admin.invite': '招待',
    'admin.inviteHomeowner': 'オーナーを招待',
    'admin.addProperty': '物件を追加',
    'admin.sendInvitation': '招待を送信',
    'admin.noProperties': 'まだ物件がありません',
    'admin.addFirstProperty': '最初の物件を追加して始めましょう',
    'admin.moreProperties': 'さらに物件',
    'admin.unassigned': '未割当',
    'admin.rental': '賃貸',
    'admin.rentalMode': '賃貸モード',
    'admin.manageListing': '管理',
    'admin.rentalListingTitle': '賃貸リストを管理',
    'admin.streetAddress': '住所',
    'admin.unitNumber': '部屋番号（任意）',
    'admin.bedrooms': '寝室',
    'admin.bathrooms': 'バスルーム',
    'admin.manageAnnouncements': 'お知らせを管理',
    'admin.dragToReschedule': 'ドラッグして再スケジュール',
    'admin.allReservations': 'すべての予約',
    'admin.createCommunity': '新しいコミュニティを作成',
    'admin.createCommunityDesc': '物件とオーナーを管理するための新しいコミュニティを設定',
    'admin.communityMap': 'コミュニティマップ',
    'admin.welcome': 'ようこそ',
    'admin.createFirstCommunity': '最初のコミュニティを作成',
    'admin.createFirstCommunityDesc': 'コミュニティを作成して、物件とオーナーの管理を始めましょう。',
    
    // Browse Rentals
    'browse.title': '賃貸物件を探す',
    'browse.luxuryStays': 'ラグジュアリーステイ',
    'browse.heroTitle': '特別な物件を発見',
    'browse.heroSubtitle': '専属コミュニティで世界クラスのアメニティとパーソナライズされたコンシェルジュサービスを体験',
    'browse.searchPlaceholder': '場所またはコミュニティで検索...',
    'browse.filters': 'フィルター',
    'browse.priceRange': '価格帯（1泊あたり）',
    'browse.minBedrooms': '最小寝室数',
    'browse.amenities': 'アメニティ',
    'browse.any': '指定なし',
    'browse.clearFilters': 'すべてのフィルターをクリア',
    'browse.propertiesFound': '{count}件の物件が見つかりました',
    'browse.propertyFound': '1件の物件が見つかりました',
    'browse.noReviews': 'まだレビューがありません',
    'browse.luxuryProperty': 'ラグジュアリー物件',
    'browse.privateCommunity': 'プライベートコミュニティ',
    'browse.beds': 'ベッド',
    'browse.baths': 'バス',
    'browse.moreAmenities': '+{count}件',
    'browse.additionalAmenities': '追加アメニティ:',
    'browse.viewDetails': '詳細を見る',
    'browse.bookNow': '今すぐ予約',
    'browse.bookYourStay': '宿泊を予約',
    'browse.booking': '予約',
    'browse.reviews': 'レビュー',
    'browse.addressRevealed': '予約確定後に完全な住所が表示されます',
    'browse.checkIn': 'チェックイン',
    'browse.checkOut': 'チェックアウト',
    'browse.selectDate': '日付を選択',
    'browse.guests': 'ゲスト',
    'browse.nights': '泊',
    'browse.cleaningFee': '清掃料金',
    'browse.total': '合計',
    'browse.confirmPay': '確認して支払う',
    'browse.signInToBook': 'ログインして予約',
    'browse.processing': '処理中...',
    'browse.minNights': '最小{min}泊',
    'browse.maxNights': '最大{max}泊',
    'browse.noPropertiesMatch': '条件に一致する物件がありません',
    'browse.tryAdjusting': '検索条件を調整してみてください',
    
    // Listing, Confirmation, Wallet, GuestPass, Reviews, Chat, Auth, Booking/Payment History, Onboarding, Support, Notifications (new)
    'listing.notFound': 'リスティングが見つかりません',
    'listing.notFoundDesc': 'この物件は利用できなくなった可能性があります',
    'listing.browseRentals': '物件を探す',
    'listing.viewAllPhotos': '全{count}枚の写真を見る',
    'listing.allPhotos': 'すべての写真',
    'listing.about': 'この物件について',
    'listing.aboutProperty': 'この物件について',
    'listing.amenities': 'アメニティ',
    'listing.houseRules': 'ハウスルール',
    'listing.sqft': '平方フィート',
    'listing.night': '泊',
    'listing.image': '写真',
    'listing.payWithCard': 'カードで支払う',
    'listing.payWithKBZ': 'KBZPayで支払う',
    'listing.payWithWave': 'Wave Moneyで支払う',
    'listing.signInRequired': '予約するにはログインしてください',
    'listing.pleaseSignIn': '予約するにはログインしてください',
    'listing.selectDates': 'チェックインとチェックアウトの日付を選択してください',
    'listing.minStay': '最小{min}泊の滞在が必要です',
    'listing.maxStay': '最大{max}泊まで滞在可能です',
    'confirmation.invalid': '無効な予約',
    'confirmation.invalidDesc': '予約IDが提供されていません',
    'confirmation.browseProperties': '物件を探す',
    'confirmation.notFound': '予約が見つかりません',
    'confirmation.notFoundDesc': 'この予約が見つかりませんでした',
    'confirmation.confirmed': '予約が確定しました！',
    'confirmation.pending': '予約保留中',
    'confirmation.accessPass': '物件アクセスパス',
    'confirmation.propertyDetails': '物件詳細',
    'confirmation.stayDetails': '滞在詳細',
    'confirmation.paymentSummary': '支払い概要',
    'confirmation.leaveReview': 'レビューを書く',
    'confirmation.viewDirections': '物件への道順と地図を見る',
    'confirmation.messageOwner': 'オーナーにメッセージ',
    'confirmation.viewAllBookings': 'すべての予約を見る',
    'confirmation.goToDashboard': 'ダッシュボードへ',
    'confirmation.checkIn': 'チェックイン',
    'confirmation.checkOut': 'チェックアウト',
    'confirmation.guests': 'ゲスト',
    'confirmation.nights': '泊',
    'confirmation.cleaningFee': '清掃料金',
    'confirmation.total': '合計',
    'confirmation.paymentMethod': '支払い方法',
    'confirmation.verificationPending': '支払い確認中',
    'wallet.title': 'ウォレット',
    'wallet.subtitle': '支払いと取引',
    'wallet.paymentMethods': '支払い方法',
    'wallet.hoaDues': 'HOA料金',
    'wallet.serviceHistory': 'サービス履歴',
    'wallet.viewAll': 'すべて見る',
    'wallet.noTransactions': '取引はまだありません',
    'wallet.noTransactionsDesc': 'サービスを予約するとここに表示されます',
    'wallet.paymentSuccess': '支払い成功',
    'wallet.paymentSuccessDesc': '支払いが処理されました。',
    'wallet.paymentCanceled': '支払いキャンセル',
    'wallet.paymentCanceledDesc': '支払いがキャンセルされました。',
    'wallet.cardAdded': 'カード追加',
    'wallet.cardAddedDesc': '支払い方法が保存されました。',
    'wallet.noPaymentMethods': '保存された支払い方法はありません',
    'wallet.addCardDesc': 'サービスと料金を支払うためにカードを追加してください',
    'wallet.addCard': 'カードを追加',
    'wallet.continueAddCard': 'カード追加を続行',
    'wallet.addPaymentMethod': '支払い方法を追加',
    'wallet.default': 'デフォルト',
    'wallet.manage': '管理',
    'wallet.securePayment': '安全な支払い',
    'wallet.cardSecure': 'カード情報は暗号化され安全です',
    'wallet.stripeRedirect': 'カードを追加するためにStripeの安全な支払いページにリダイレクトされます。',
    'wallet.openingStripe': 'Stripeを開いてカードを追加中...',
    'wallet.failedAddMethod': '支払い方法の追加に失敗しました',
    'wallet.failedOpenPortal': '請求ポータルを開けませんでした',
    'wallet.expires': '有効期限',
    
    // Payments Page (Japanese)
    'payments.title': '支払い',
    'payments.subtitle': '支払いと取引を管理',
    'payments.noCommunity': 'コミュニティアクセスなし',
    'payments.noCommunityDesc': 'コミュニティに参加すると支払い機能が利用可能になります。',
    'payments.createCommunity': '最初のコミュニティを作成',
    
    // Amenity translations (Japanese)
    'amenity.privatePool': 'プライベートプール',
    'amenity.pool': 'プール',
    'amenity.hotTub': 'ホットタブ',
    'amenity.oceanView': 'オーシャンビュー',
    'amenity.sunsetViews': 'サンセットビュー',
    'amenity.beachAccess': 'ビーチアクセス',
    'amenity.smartHome': 'スマートホーム',
    'amenity.wineCellar': 'ワインセラー',
    'amenity.homeTheater': 'ホームシアター',
    'amenity.gym': 'ジム',
    'amenity.spa': 'スパ',
    'amenity.rooftopTerrace': 'ルーフトップテラス',
    'amenity.concierge': 'コンシェルジュ',
    'amenity.wifi': 'WiFi',
    'amenity.parking': '駐車場',
    'amenity.airConditioning': 'エアコン',
    'amenity.heating': '暖房',
    'amenity.kitchen': 'キッチン',
    'amenity.washer': '洗濯機',
    'amenity.dryer': '乾燥機',
    'amenity.washerDryer': '洗濯乾燥機',
    'amenity.petFriendly': 'ペット可',
    'amenity.garden': '庭',
    'amenity.fireplace': '暖炉',
    'amenity.balcony': 'バルコニー',
    'guestPass.title': 'ゲストパス',
    'guestPass.subtitle': '一時的な訪問者アクセスを管理',
    'guestPass.header': 'ゲストパス',
    'guestPass.newPass': '新規パス',
    'guestPass.createPass': 'ゲストパスを作成',
    'guestPass.property': '物件',
    'guestPass.selectProperty': '物件を選択',
    'guestPass.guestEmail': 'ゲストメール',
    'guestPass.guestName': 'ゲスト名（任意）',
    'guestPass.accessStart': 'アクセス開始',
    'guestPass.accessEnd': 'アクセス終了',
    'guestPass.selectStartDate': '開始日を選択',
    'guestPass.selectEndDate': '終了日を選択',
    'guestPass.duration': '期間: {days}日',
    'guestPass.sendPass': 'ゲストパスを送信',
    'guestPass.sending': '送信中...',
    'guestPass.noProperties': 'パスを作成できません',
    'guestPass.noPropertiesDesc': 'ゲストパスを作成するには物件を所有する必要があります。',
    'guestPass.activePasses': '有効なパス',
    'guestPass.expiredPasses': '期限切れのパス',
    'guestPass.noActivePasses': '有効なゲストパスはありません',
    'guestPass.noExpiredPasses': '期限切れのゲストパスはありません',
    'guestPass.guest': 'ゲスト',
    'guestPass.expires': '有効期限',
    'guestPass.expired': '期限切れ',
    'guestPass.showQR': 'QRを表示',
    'guestPass.revoke': '取り消す',
    'guestPass.revokeConfirm': 'このゲストパスを取り消してもよろしいですか？この操作は元に戻せません。',
    'reviews.title': 'レビュー',
    'reviews.noReviews': 'レビューはまだありません',
    'reviews.noReviewsDesc': 'この物件にはまだレビューがありません',
    'reviews.overallRating': '総合評価',
    'reviews.cleanliness': '清潔さ',
    'reviews.accuracy': '正確さ',
    'reviews.communication': 'コミュニケーション',
    'reviews.location': 'ロケーション',
    'reviews.value': 'コスパ',
    'reviews.ownerResponse': 'オーナーの返信',
    'reviews.basedOn': '{count}件のレビューに基づく',
    'chat.title': 'Aether AIアシスタント',
    'chat.placeholder': 'メッセージを入力または「こんにちは」と言ってください...',
    'chat.send': '送信',
    'chat.voiceInput': '音声入力',
    'chat.listening': '聞いています...',
    'chat.clearHistory': '履歴をクリア',
    'chat.suggestedPrompts': 'おすすめのプロンプト',
    'chat.findRental': '自分に合った賃貸を探して',
    'chat.bookService': 'サービスを予約したい',
    'chat.checkPayment': '支払い状況を確認',
    'chat.getDirections': '物件への道順を取得',
    'auth.fullName': 'フルネーム',
    'auth.phone': '電話番号',
    'auth.signingIn': 'ログイン中...',
    'auth.signingUp': '登録中...',
    'auth.resetting': 'リセット中...',
    'auth.checkEmail': 'リセットリンクのメールを確認してください',
    'auth.emailRequired': 'メールアドレスを入力してください',
    'auth.passwordRequired': 'パスワードを入力してください',
    'auth.passwordMismatch': 'パスワードが一致しません',
    'auth.termsAgree': '利用規約とプライバシーポリシーに同意します',
    'bookingHistory.title': '予約履歴',
    'bookingHistory.totalBookings': '全{count}件の予約',
    'bookingHistory.upcoming': '今後',
    'bookingHistory.past': '過去',
    'bookingHistory.noUpcoming': '今後の予約はありません',
    'bookingHistory.noPast': '過去の予約はありません',
    'bookingHistory.bookService': 'サービスを予約するとここに表示されます',
    'bookingHistory.willAppear': '完了した予約がここに表示されます',
    'bookingHistory.browseServices': 'サービスを探す',
    'bookingHistory.reschedule': '予約変更',
    'bookingHistory.cancel': 'キャンセル',
    'bookingHistory.viewReceipt': '領収書を見る',
    'bookingHistory.rescheduleBooking': '予約を変更',
    'bookingHistory.service': 'サービス',
    'bookingHistory.newDateTime': '新しい日時',
    'bookingHistory.confirmReschedule': '変更を確認',
    'bookingHistory.filterStatus': 'ステータスでフィルター',
    'bookingHistory.allStatuses': 'すべてのステータス',
    'bookingHistory.inProgress': '進行中',
    'paymentHistory.title': '支払い履歴',
    'paymentHistory.subtitle': 'すべての支払いと領収書を表示',
    'paymentHistory.all': 'すべて',
    'paymentHistory.hoa': 'HOA',
    'paymentHistory.rentals': '賃貸',
    'paymentHistory.services': 'サービス',
    'paymentHistory.noPayments': '支払いが見つかりません',
    'paymentHistory.noPaymentsSubtitle': '支払い履歴がここに表示されます',
    'paymentHistory.hoaDues': 'HOA会費',
    'paymentHistory.rentalBooking': '宿泊予約',
    'paymentHistory.servicePayment': 'サービス料金',
    'paymentHistory.paid': '支払済',
    'paymentHistory.pending': '保留中',
    'paymentHistory.rejected': '拒否',
    'paymentHistory.period': '期間',
    'paymentHistory.paymentDetails': '支払い詳細',
    'paymentHistory.amount': '金額',
    'paymentHistory.type': '種類',
    'paymentHistory.status': 'ステータス',
    'paymentHistory.date': '日付',
    'paymentHistory.paymentMethod': '支払い方法',
    'paymentHistory.property': '物件',
    'paymentHistory.verified': '確認済み',
    'paymentHistory.paymentProof': '支払い証明',
    'paymentHistory.downloadReceipt': '領収書をダウンロード',
    'paymentHistory.bankTransfer': '銀行振込',
    'paymentHistory.card': 'カード',
    'common.notAvailable': 'なし',
    
    // Onboarding (Japanese)
    'onboarding.homeowner': '物件オーナー',
    'onboarding.homeownerDesc': '物件を所有しており、Aetherサービスを利用したい',
    'onboarding.guest': 'ゲスト',
    'onboarding.guestDesc': '物件を借りるか宿泊したい',
    'onboarding.profile': 'プロフィール',
    'onboarding.profileDesc': 'あなたについて教えてください',
    'onboarding.properties': '物件',
    'onboarding.propertiesDesc': '物件を追加',
    'onboarding.preferences': '設定',
    'onboarding.preferencesDesc': '体験をカスタマイズ',
    'onboarding.fullName': '氏名',
    'onboarding.fullNamePlaceholder': '氏名を入力',
    'onboarding.phoneNumber': '電話番号',
    'onboarding.phonePlaceholder': '電話番号を入力',
    'onboarding.iAmA': '私は...',
    'onboarding.selectRole': '役割を選択',
    'onboarding.community': 'コミュニティ',
    'onboarding.loadingCommunities': 'コミュニティを読み込み中...',
    'onboarding.noCommunities': '利用可能なコミュニティがありません',
    'onboarding.noCommunitiesDesc': '不動産開発会社にお問い合わせいただくか、後でもう一度ご確認ください。',
    'onboarding.selectCommunity': 'コミュニティを選択',
    'onboarding.propertyAddress': '物件住所',
    'onboarding.addressPlaceholder': '住所を入力してください...',
    'onboarding.unitNumber': '部屋番号',
    'onboarding.propertyType': '物件タイプ',
    'onboarding.singleFamilyHome': '一戸建て',
    'onboarding.condominium': 'マンション',
    'onboarding.townhouse': 'タウンハウス',
    'onboarding.villa': 'ヴィラ',
    'onboarding.penthouse': 'ペントハウス',
    'onboarding.gatedCommunity': 'ゲーテッドコミュニティ',
    'onboarding.bedrooms': '寝室',
    'onboarding.bathrooms': 'バスルーム',
    'onboarding.sqft': '平方フィート',
    'onboarding.enableRentalMode': '賃貸モードを有効化',
    'onboarding.rentalModeDesc': 'この物件を短期賃貸としてリスト',
    'onboarding.rentalSettings': '賃貸設定',
    'onboarding.nightlyRate': '1泊料金 ($)',
    'onboarding.cleaningFee': '清掃料金 ($)',
    'onboarding.amenities': 'アメニティ',
    'onboarding.wifi': 'WiFi',
    'onboarding.parking': '駐車場',
    'onboarding.pool': 'プール',
    'onboarding.gym': 'ジム',
    'onboarding.kitchen': 'キッチン',
    'onboarding.ac': 'エアコン',
    'onboarding.smartTv': 'スマートTV',
    'onboarding.hotTub': 'ホットタブ',
    'onboarding.garden': '庭',
    'onboarding.security': '24時間セキュリティ',
    'onboarding.property': '物件',
    'onboarding.add': '追加',
    'onboarding.language': '言語',
    'onboarding.currency': '通貨',
    'onboarding.timezone': 'タイムゾーン',
    'onboarding.notifications': '通知',
    'onboarding.bookingUpdates': '予約の更新',
    'onboarding.messageAlerts': 'メッセージ通知',
    'onboarding.skipForNow': 'スキップ',
    'onboarding.welcomeToAether': 'Aetherへようこそ',
    'onboarding.letsSetup': 'アカウントを設定しましょう',
    'onboarding.back': '戻る',
    'onboarding.continue': '続ける',
    'onboarding.getStarted': '始める',
    'onboarding.skipped': 'オンボーディングをスキップしました',
    'onboarding.skippedDesc': '設定でいつでもプロフィールを完成できます。',
    'onboarding.propertyAdded': '物件が追加されました！',
    'onboarding.propertiesAdded': '物件が追加されました！',
    'onboarding.setupComplete': 'アカウントが正常に設定されました。',
    'onboarding.setupFailed': '設定に失敗しました。もう一度お試しください。',
    
    // Support (Japanese)
    'support.liveChat': 'ライブチャット',
    'support.liveChatDesc': 'サポートチームとリアルタイムでチャット',
    'support.liveChatAvailability': '営業時間: 9:00 - 18:00',
    'support.startChat': 'チャットを開始',
    'support.emailSupport': 'メールサポート',
    'support.emailSupportDesc': '24時間以内に返信',
    'support.sendEmail': 'メールを送信',
    'support.phoneSupport': '電話サポート',
    'support.phoneSupportDesc': 'エンタープライズ顧客のみ',
    'support.callNow': '今すぐ電話',
    'support.backToHome': 'ホームに戻る',
    'support.supportCenter': 'サポートセンター',
    'support.howCanWeHelp': 'どのようなお手伝いができますか？',
    'support.heroSubtitle': '私たちのチームはあなたの質問や問題をサポートします。',
    'support.quickAnswers': 'よくある質問',
    'support.commonQuestions': 'ユーザーからのよくある質問',
    'support.viewAllFAQs': 'すべてのFAQを見る',
    'support.sendUsMessage': 'メッセージを送信',
    'support.name': '名前',
    'support.namePlaceholder': 'お名前',
    'support.email': 'メール',
    'support.emailPlaceholder': 'your@email.com',
    'support.subject': '件名',
    'support.subjectPlaceholder': 'どのようなお手伝いができますか？',
    'support.message': 'メッセージ',
    'support.messagePlaceholder': '問題や質問を説明してください...',
    'support.sendMessage': 'メッセージを送信',
    'support.sending': '送信中...',
    'support.messageSent': 'メッセージが送信されました！折り返しご連絡いたします。',
    'support.messageFailed': 'メッセージの送信に失敗しました。もう一度お試しください。',
    'support.footer': '© 2025 Aether. 不動産管理をシンプルに。',
    
    // Notifications (Japanese)
    'notifications.title': '通知',
    'notifications.markAllRead': 'すべて既読にする',
    'notifications.all': 'すべて',
    'notifications.urgent': '緊急',
    'notifications.unread': '未読',
    'notifications.info': '情報',
    'notifications.noNotifications': '通知はありません',
    'notifications.allCaughtUp': 'すべて確認済み！',
    'notifications.checkBackLater': '後でもう一度確認してください',
    'notifications.today': '今日',
    'notifications.yesterday': '昨日',
    'notifications.thisWeek': '今週',
    'notifications.earlier': 'それ以前',
    
    // Rental Editor (Japanese)
    'rentalEditor.pricing': '料金設定',
    'rentalEditor.nightlyRate': '1泊料金',
    'rentalEditor.cleaningFee': '清掃料金',
    'rentalEditor.minNights': '最低宿泊数',
    'rentalEditor.maxNights': '最大宿泊数',
    'rentalEditor.description': '説明',
    'rentalEditor.descriptionPlaceholder': '物件を説明してください...',
    'rentalEditor.amenities': 'アメニティ',
    'rentalEditor.houseRules': 'ハウスルール',
    'rentalEditor.houseRulesPlaceholder': 'ハウスルールを入力（例：禁煙、パーティー禁止...）',
    'rentalEditor.smartHomeInstructions': 'スマートホームの説明',
    'rentalEditor.smartHomeDesc': '予約確認後にゲストに表示されます',
    'rentalEditor.tip': 'ヒント',
    'rentalEditor.smartHomeTip': 'WiFiパスワード、ドアコード、音声コマンド、デバイスの場所をここに含めてください。',
    'rentalEditor.publishListing': 'リスティングを公開',
    'rentalEditor.publishDesc': 'ゲストにリスティングを表示',
    'rentalEditor.saveListing': 'リスティングを保存',
    'rentalEditor.rentalType': '賃貸タイプ',
    'rentalEditor.shortTerm': '短期賃貸',
    'rentalEditor.shortTermDesc': 'バケーションや短期滞在のための1泊単位の予約（1〜30泊）',
    'rentalEditor.longTerm': '長期賃貸',
    'rentalEditor.longTermDesc': '長期滞在のための月単位のリース（6ヶ月以上）',
    'rentalEditor.bothTypes': '短期・長期両方',
    'rentalEditor.bothTypesDesc': '1泊単位と月単位の両方の予約を受け付け',
    'rentalEditor.shortTermPricing': '短期料金',
    'rentalEditor.longTermPricing': '長期料金',
    'rentalEditor.monthlyRate': '月額料金',
    'rentalEditor.monthlyRateHint': '長期入居者の月額料金',
    'rentalEditor.minLeaseDuration': '最短リース期間（月）',
    
    // Admin Dashboard (Japanese)
    'admin.rentals': '賃貸',
    'admin.news': 'ニュース',
    'admin.inviteOwner': 'オーナーを招待',
    'admin.inviteOwnerTitle': '物件オーナーを招待',
    'admin.inviteOwnerDesc': 'オーナーにはアカウント設定と物件管理のためのメール招待状が送信されます。',
    
    // Community Switcher (Japanese)
    'community.selectCommunity': 'コミュニティを選択',
    'community.createNew': '新しいコミュニティを作成',
    
    // Map Page (Japanese)
    'map.title': 'コミュニティマップ',
    'map.amenities': 'アメニティ',
    'map.properties': '物件',
    
    // Service Marketplace additional (Japanese)
    'service.services': 'サービス',
    'service.bookPremium': 'プレミアムサービスを予約',
    'service.all': 'すべて',
    'service.noServices': '利用可能なサービスはありません',
    'service.checkBackLater': '後でもう一度確認してください',
    'service.by': '提供：',
    'service.book': '予約',
    'service.service': 'サービス',
    'service.estimatedPrice': '見積もり価格',
    'service.priceVaryNote': '最終価格は物件のサイズによって異なる場合があります',
    'service.scheduleDateTime': '日時を選択',
    'service.specialRequests': '特別なリクエスト（任意）',
    'service.specialInstructions': '特別な指示があれば...',
    'service.payWithCard': 'カードで支払う（USD）',
    'service.category.housekeeping': 'ハウスキーピング',
    'service.category.wellness': 'ウェルネス',
    'service.category.maintenance': 'メンテナンス',
    'service.category.dining': 'ダイニング',
    'service.category.transport': '交通',
    
    // Chat (additional - Japanese)
    'chat.fallbackError': '申し訳ありませんが、リクエストを処理できませんでした。もう一度お試しください。',
    'chat.bookingSuccess': '予約が確認されました。',
    'chat.bookingIssue': '予約の作成中に問題が発生しました。もう一度お試しください。',
    'chat.temporaryError': '申し訳ありませんが、一時的な接続の問題が発生しています。しばらくしてからもう一度お試しください。',
    
    // Map (additional - Japanese)
    'map.loading': '地図を読み込み中...',
    
    // God Mode Dashboard (Japanese)
    'godMode.title': 'ゴッドモード',
    'godMode.commandCenter': '管理パネル',
    'godMode.adminPanel': '管理パネル',
    'godMode.communities': 'コミュニティ',
    'godMode.homes': '住宅',
    'godMode.mrr': '月間収益',
    'godMode.propertyDevelopers': '不動産開発者',
    'godMode.developerInviteDesc': 'プラットフォーム上でコミュニティを作成・管理する不動産開発者を招待します。',
    'godMode.add': '追加',
    'godMode.createNewCommunity': '新しいコミュニティを作成',
    'godMode.communityName': 'コミュニティ名',
    'godMode.city': '市区町村',
    'godMode.state': '都道府県',
    'godMode.baseFee': '基本料金/月',
    'godMode.perHomeFee': '住宅当たり料金',
    'godMode.createCommunity': 'コミュニティを作成',
    'godMode.noCommunities': 'まだコミュニティがありません',
    'godMode.createFirstCommunity': '最初のコミュニティを作成して始めましょう',
    'godMode.active': 'アクティブ',
    'godMode.inactive': '非アクティブ',
    'godMode.noLocation': '場所なし',
    
    // Admin additional
    'admin.owner': 'オーナー',
    'admin.homeownerEmail': '住宅所有者のメール',
    'admin.property': '物件',
    'admin.totalProperties': '物件総数',
    'admin.rentalModeOn': '賃貸モードオン',
    'admin.activeListings': 'アクティブな掲載',
    'admin.upcomingArrivals': '到着予定',
    'admin.rentalProperties': '賃貸物件',
    'admin.rentalOn': '賃貸オン',
    'admin.rentalOff': '賃貸オフ',
    'admin.upcomingBookings': '今後の予約',
    'admin.listingInactive': '掲載非アクティブ',
    
    // Rental types
    'rental.shortTerm': '短期',
    'rental.longTerm': '長期',
    'rental.bothTypes': '両方',
    
    // Review Form
    'reviewForm.howWasStay': 'ご滞在はいかがでしたか?',
    'reviewForm.overallExperience': '総合評価',
    'reviewForm.ratingRequired': '総合評価をお願いします',
    'reviewForm.submitSuccess': 'レビューを送信しました!',
    'reviewForm.submitFailed': 'レビューの送信に失敗しました',
    'reviewForm.titleLabel': 'レビュータイトル（任意）',
    'reviewForm.titlePlaceholder': '体験を要約してください',
    'reviewForm.contentLabel': 'レビュー内容（任意）',
    'reviewForm.contentPlaceholder': '滞在の詳細を共有してください...',
    'reviewForm.submit': 'レビューを送信',
    
    // Review Moderation
    'moderation.title': 'レビュー管理',
    'moderation.total': '合計',
    'moderation.pending': '保留中',
    'moderation.flagged': 'フラグ付き',
    'moderation.all': 'すべて',
    'moderation.published': '公開済み',
    'moderation.hidden': '非表示',
    'moderation.lowRating': '低評価',
    'moderation.unknownProperty': '不明な物件',
    'moderation.anonymous': '匿名',
    'moderation.commShort': 'コミュニケーション',
    'moderation.hide': '非表示',
    'moderation.publish': '公開',
    'moderation.edit': '編集',
    'moderation.respond': '返信',
    'moderation.statusUpdated': 'レビューステータスを更新しました',
    'moderation.updateFailed': 'レビューの更新に失敗しました',
    'moderation.responseAdded': '返信を追加しました',
    'moderation.responseFailed': '返信の追加に失敗しました',
    'moderation.noReviews': 'レビューはまだありません',
    'moderation.noPending': '保留中のレビューはありません',
    'moderation.noPublished': '公開済みのレビューはありません',
    'moderation.noFlagged': 'フラグ付きのレビューはありません',
    'moderation.editResponse': '返信を編集',
    'moderation.addResponse': '返信を追加',
    'moderation.by': 'by',
    'moderation.responsePlaceholder': 'このレビューへの返信を書いてください...',
    'moderation.saveResponse': '返信を保存',
    
    // Audit Log (Japanese)
    'audit.title': '監査ログ',
    'audit.subtitle': 'システム活動記録',
    'audit.noLogs': '監査ログが見つかりません',
    'audit.noLogsDesc': 'システム活動がここに表示されます',
    'audit.filter': 'アクションでフィルター',
    'audit.allActions': 'すべてのアクション',
    'audit.allResources': 'すべてのリソース',
    'audit.searchPlaceholder': 'アクション、リソースを検索...',
    'audit.loadMore': 'もっと読み込む',
    'common.refresh': '更新',
    
    // Services Manager (Japanese)
    'services.title': 'サービス管理',
    'services.addService': 'サービスを追加',
    'services.editService': 'サービスを編集',
    'services.serviceName': 'サービス名',
    'services.serviceNamePlaceholder': '例：ディープクリーニング',
    'services.description': '説明',
    'services.descriptionPlaceholder': 'サービスを説明...',
    'services.category': 'カテゴリー',
    'services.selectCategory': 'カテゴリーを選択',
    'services.vendor': 'ベンダー（任意）',
    'services.selectVendor': 'ベンダーを選択',
    'services.noVendor': 'ベンダーなし',
    'services.basePrice': '基本価格',
    'services.pricePerBedroom': '寝室あたりの価格',
    'services.pricePerSqft': '平方フィートあたりの価格',
    'services.duration': '所要時間（分）',
    'services.isGlobal': 'すべてのコミュニティで利用可能',
    'services.isActive': '有効',
    'services.saveService': 'サービスを保存',
    'services.deleteConfirm': 'このサービスを削除しますか？',
    'services.deleteSuccess': 'サービスを削除しました',
    'services.deleteFailed': 'サービスの削除に失敗しました',
    'services.saveSuccess': 'サービスを保存しました',
    'services.saveFailed': 'サービスの保存に失敗しました',
    'services.noServices': 'サービスがまだありません',
    'services.addFirst': '最初のサービスを追加して始めましょう',
    'services.global': 'グローバル',
    'services.housekeeping': 'ハウスキーピング',
    'services.wellness': 'ウェルネス',
    'services.maintenance': 'メンテナンス',
    'services.dining': 'ダイニング',
    'services.transport': '交通',
    
    // Vendor Manager (Japanese)
    'vendor.title': 'ベンダー管理',
    'vendor.addVendor': 'ベンダーを追加',
    'vendor.editVendor': 'ベンダーを編集',
    'vendor.vendorName': 'ベンダー名',
    'vendor.vendorNamePlaceholder': '例：プレミアムクリーニング社',
    'vendor.email': 'メール',
    'vendor.emailPlaceholder': 'vendor@example.com',
    'vendor.description': '説明',
    'vendor.descriptionPlaceholder': 'ベンダーを説明...',
    'vendor.category': 'カテゴリー',
    'vendor.selectCategory': 'カテゴリーを選択',
    'vendor.commissionRate': 'コミッション率（%）',
    'vendor.isGlobal': 'すべてのコミュニティで利用可能',
    'vendor.isActive': '有効',
    'vendor.saveVendor': 'ベンダーを保存',
    'vendor.deleteConfirm': 'このベンダーを削除しますか？',
    'vendor.deleteSuccess': 'ベンダーを削除しました',
    'vendor.deleteFailed': 'ベンダーの削除に失敗しました',
    'vendor.saveSuccess': 'ベンダーを保存しました',
    'vendor.saveFailed': 'ベンダーの保存に失敗しました',
    'vendor.noVendors': 'ベンダーがまだありません',
    'vendor.addFirst': '最初のベンダーを追加して始めましょう',
    'vendor.global': 'グローバル',
    'vendor.commission': 'コミッション',
    'vendor.paymentSettings': '支払い設定',
    'vendor.stripeConnected': 'Stripe接続済み',
    'vendor.stripeNotConnected': 'Stripe未接続',
    'vendor.mobilePayment': 'モバイル決済',
    'vendor.accountName': 'アカウント名',
    'vendor.phoneNumber': '電話番号',
    
    // Payment Verification (Japanese)
    'paymentVerification.title': '確認待ち',
    'paymentVerification.noPayments': '確認待ちの支払いはありません',
    'paymentVerification.allProcessed': 'すべての支払いが処理されました',
    'paymentVerification.verifyPayment': '支払いを確認',
    'paymentVerification.amount': '金額',
    'paymentVerification.paymentMethod': '支払い方法',
    'paymentVerification.customer': '顧客',
    'paymentVerification.date': '日付',
    'paymentVerification.property': '物件',
    'paymentVerification.paymentProof': '支払い証明',
    'paymentVerification.fullSize': 'フルサイズ',
    'paymentVerification.noProof': '支払い証明がアップロードされていません',
    'paymentVerification.verifyManually': '銀行アプリでこの支払いを手動で確認してください',
    'paymentVerification.rejectionReason': '拒否理由（拒否する場合）',
    'paymentVerification.rejectionPlaceholder': '例：金額が一致しない、スクリーンショットが無効...',
    'paymentVerification.approve': '承認',
    'paymentVerification.reject': '拒否',
    'paymentVerification.approved': '支払いが承認されました',
    'paymentVerification.rejected': '支払いが拒否されました',
    'paymentVerification.approveFailed': '支払いの承認に失敗しました',
    'paymentVerification.rejectFailed': '支払いの拒否に失敗しました',
    'paymentVerification.provideReason': '拒否理由を入力してください',
    'paymentVerification.hasProof': '証明あり',
    'paymentVerification.hoa': 'HOA',
    'paymentVerification.rental': '賃貸',
    'paymentVerification.loadFailed': '保留中の支払いの読み込みに失敗しました',
    
    // Vendor Payouts (Japanese)
    'vendorPayouts.totalRevenue': '総収益',
    'vendorPayouts.platformFees': 'プラットフォーム手数料',
    'vendorPayouts.vendorPayouts': 'ベンダー支払い',
    'vendorPayouts.pendingPayouts': '保留中の支払い',
    'vendorPayouts.completedPayouts': '完了した支払い',
    'vendorPayouts.noVendors': 'ベンダーがまだありません',
    'vendorPayouts.addVendorsFirst': '収益を表示するにはベンダーを追加してください',
    'vendorPayouts.vendor': 'ベンダー',
    'vendorPayouts.category': 'カテゴリー',
    'vendorPayouts.earnings': '収益',
    'vendorPayouts.commission': 'コミッション',
    'vendorPayouts.payout': '支払い',
    'vendorPayouts.status': 'ステータス',
    'vendorPayouts.actions': 'アクション',
    'vendorPayouts.markPaid': '支払い済みにする',
    'vendorPayouts.viewHistory': '履歴を表示',
    'vendorPayouts.paid': '支払い済み',
    'vendorPayouts.pending': '保留中',
    
    // Calendar (Japanese)
    'calendar.title': '予約カレンダー',
    'calendar.today': '今日',
    'calendar.week': '週',
    'calendar.month': '月',
    'calendar.noBookings': '予約がまだありません',
    'calendar.dragToReschedule': 'ドラッグして日程変更',
    'calendar.reschedule': '日程変更',
    'calendar.newDate': '新しい日付',
    'calendar.newTime': '新しい時間',
    'calendar.from': 'から:',
    'calendar.to': 'まで:',
    'calendar.at': '時刻',
    'calendar.confirm': '確認',
    'calendar.rescheduled': '予約の日程を変更しました',
    'calendar.rescheduleFailed': '予約の日程変更に失敗しました',
    'calendar.availabilityCalendar': '空き状況カレンダー',
    
    // Relative Dates (Japanese)
    'dates.today': '今日',
    'dates.yesterday': '昨日',
    'dates.tomorrow': '明日',
    'dates.daysAgo': '{count}日前',
    'dates.inDays': '{count}日後',
    'dates.justNow': 'たった今',
    'dates.dueToday': '本日期限',
    
    // Settings Page (Phase 4 additions - Japanese)
    'settings.manageAccount': 'アカウントを管理',
    'settings.developerIntegrations': '開発者と統合',
    'settings.yourProperties': 'あなたの物件',
    'settings.setupProfile': 'プロフィールを設定',
    'settings.profileUpdated': 'プロフィールが更新されました',
    'settings.profileUpdateFailed': 'プロフィールの更新に失敗しました',
    'settings.pushNotSupported': 'このブラウザではプッシュ通知がサポートされていません',
    'settings.pushEnabled': 'プッシュ通知が有効になりました',
    'settings.pushDenied': 'プッシュ通知の権限が拒否されました',
    'settings.pushEnableFailed': 'プッシュ通知の有効化に失敗しました',
    'settings.openingPortal': '決済ポータルを開いています...',
    'settings.portalFailed': '決済ポータルを開けませんでした',
    'settings.editProfile': 'プロフィールを編集',
    'settings.fullName': '氏名',
    'settings.enterName': '名前を入力',
    'settings.email': 'メール',
    'settings.phoneNumber': '電話番号',
    'settings.enterPhone': '電話番号を入力',
    'settings.saveChanges': '変更を保存',
    'settings.emailNotifications': 'メール通知',
    'settings.bookingUpdates': '予約の更新',
    'settings.bookingUpdatesDesc': '予約の更新をメールで受け取る',
    'settings.paymentAlerts': '支払いアラート',
    'settings.paymentAlertsDesc': '支払いアクティビティのメールアラートを受け取る',
    'settings.guestPassActivity': 'ゲストパスアクティビティ',
    'settings.guestPassActivityDesc': 'ゲストがパスを使用したときに通知を受け取る',
    'settings.pushNotifications': 'プッシュ通知',
    'settings.enablePush': 'プッシュ通知を有効にする',
    'settings.enablePushDesc': 'デバイスでリアルタイムアラートを受け取る',
    'settings.bookingNotifications': '予約通知',
    'settings.bookingNotificationsDesc': '新しい予約と変更',
    'settings.messageNotifications': 'メッセージ通知',
    'settings.messageNotificationsDesc': 'ゲストからの新しいメッセージ',
    'settings.announcementNotifications': 'お知らせ通知',
    'settings.announcementNotificationsDesc': 'コミュニティのお知らせ',
    'settings.managePaymentMethods': '支払い方法を管理',
    'settings.managePaymentMethodsDesc': '安全なポータルで支払い方法を更新',
    'settings.openPaymentPortal': '決済ポータルを開く',
    'settings.paymentSecurityNote': '決済情報はStripeによって安全に処理されます',
    'settings.preferences': '設定',
    'settings.selectTimezone': 'タイムゾーンを選択',
    'settings.timezoneSet': 'タイムゾーンを設定：',
    'settings.selectLanguage': '言語を選択',
    'settings.languageChanged': '言語を変更：',
    'settings.selectCurrency': '通貨を選択',
    'settings.currencyChanged': '通貨を変更：',
    'settings.preferencesSaved': '設定は自動的に保存されます',
    
    // Service Marketplace (Phase 4 - Japanese)
    'service.selectDateTime': '日時を選択してください',
    'service.bookingCreated': '予約が作成されました！確認のために支払いを完了してください。',
    'service.bookingFailed': 'サービスの予約に失敗しました',
    'service.paymentSubmitted': '支払いが送信されました！確認待ち。',
    
    // Browse Rentals (Phase 4 - Japanese)
    'browse.pleaseSignIn': 'レンタルを予約するにはログインしてください',
    'browse.selectDates': 'チェックインとチェックアウトの日付を選択してください',
    'browse.minStayError': '最低{nights}泊必要です',
    'browse.maxStayError': '最大{nights}泊です',
    'browse.redirectingPayment': '支払いにリダイレクト中...',
    'browse.bookingFailed': '予約の作成に失敗しました',
    
    // Booking History (Phase 4 - Japanese additions)
    'bookingHistory.cancelSuccess': '予約をキャンセルしました',
    'bookingHistory.cancelFailed': '予約のキャンセルに失敗しました',
    'bookingHistory.rescheduleSuccess': '予約の日程を変更しました',
    'bookingHistory.rescheduleFailed': '予約の日程変更に失敗しました',
    'bookingHistory.paymentPaid': '支払い済み',
    'bookingHistory.paymentProcessing': '処理中',
    'bookingHistory.paymentFailed': '失敗',
    'bookingHistory.paymentPending': '支払い待ち',
    
    // Rental Management (Japanese)
    'rentalManagement.title': 'レンタル管理',
    'rentalManagement.notEnabled': 'レンタルモードが有効になっていません',
    'rentalManagement.enableDesc': 'ダッシュボードから物件のレンタルモードを有効にして掲載を開始してください',
    'rentalManagement.live': '公開中',
    'rentalManagement.draft': '下書き',
    'rentalManagement.perNight': '1泊',
    'rentalManagement.nights': '泊',
    'rentalManagement.bedrooms': '寝室',
    'rentalManagement.listing': 'リスティング',
    'rentalManagement.calendar': 'カレンダー',
    'rentalManagement.analytics': '分析',
    'rentalManagement.bookings': '予約',
    'rentalManagement.createFirst': '空き状況を管理するにはまずリスティングを作成してください',
    'rentalManagement.saveSuccess': 'リスティングを保存しました',
    'rentalManagement.saveFailed': 'リスティングの保存に失敗しました',
    'rentalManagement.availabilityUpdated': '空き状況を更新しました',
    'rentalManagement.availabilityFailed': '空き状況の更新に失敗しました',
    
    // Messaging (Japanese)
    'messaging.noMessages': 'メッセージがありません',
    'messaging.startConversation': '会話を始めましょう！',
    'messaging.typeMessage': 'メッセージを入力...',
    'messaging.loadFailed': 'メッセージの読み込みに失敗しました',
    'messaging.sendFailed': 'メッセージの送信に失敗しました',
    
    // Form Validation (Japanese)
    'validation.nameRequired': '名前は必須です',
    'validation.nameMaxLength': '名前は100文字以内にしてください',
    'validation.emailInvalid': '無効なメールアドレスです',
    'validation.emailMaxLength': 'メールアドレスは255文字以内にしてください',
    'validation.subjectRequired': '件名は必須です',
    'validation.subjectMaxLength': '件名は200文字以内にしてください',
    'validation.messageRequired': 'メッセージは必須です',
    'validation.messageMaxLength': 'メッセージは2000文字以内にしてください',
    
    // QR Payment Dialog
    'qrPayment.notAvailable': 'お支払いは利用できません',
    'qrPayment.notSetUp': 'この物件では{method}支払いが設定されていません。',
    'qrPayment.contactManager': '物件管理者に連絡するか、別の支払い方法をご利用ください。',
    'qrPayment.payWith': '{method}で支払う',
    'qrPayment.amountToPay': 'お支払い金額',
    'qrPayment.copyAmount': '金額をコピー',
    'qrPayment.howToPay': '支払い方法:',
    'qrPayment.step1': '{method}アプリを開く',
    'qrPayment.step2': '「QRスキャン」をタップして上記のコードをスキャン',
    'qrPayment.step3': '正確な金額を入力: {amount}',
    'qrPayment.step4': '支払いを完了する',
    'qrPayment.step5': '確認のスクリーンショットを撮る',
    'qrPayment.madePayment': '支払いを完了しました',
    'qrPayment.confirmPayment': 'お支払いを確認してください',
    'qrPayment.uploadScreenshot': '迅速な確認のため、支払い確認のスクリーンショットをアップロードしてください。',
    'qrPayment.paymentScreenshot': '支払いスクリーンショット（推奨）',
    'qrPayment.tapToUpload': 'タップしてスクリーンショットをアップロード',
    'qrPayment.remove': '削除',
    'qrPayment.submitting': '送信中...',
    'qrPayment.confirm': '支払いを確認',
    'qrPayment.submitted': '支払いが送信されました！',
    'qrPayment.beingVerified': 'お支払いを確認中です。確認が完了したら通知されます。',
    'qrPayment.verificationTime': '通常、営業日で1〜24時間かかります。',
    'qrPayment.done': '完了',
    'qrPayment.phoneCopied': '電話番号をコピーしました',
    'qrPayment.amountCopied': '金額をコピーしました',
    'qrPayment.uploadImage': '画像ファイルをアップロードしてください',
    'qrPayment.screenshotUploaded': '支払いスクリーンショットがアップロードされました',
    'qrPayment.uploadFailed': 'スクリーンショットのアップロードに失敗しました',
    'qrPayment.submitFailed': '支払いの送信に失敗しました',
    
    // Landing Page (Japanese)
    'landing.hero.badge': '不動産開発者・HOA向け',
    'landing.hero.title': '物件を管理する',
    'landing.hero.titleHighlight': '混乱なく',
    'landing.hero.subtitle': '支払い、ゲストアクセス、サービス予約、住民コミュニケーションを1つのプラットフォームで。管理業務を減らし、住民の満足度を高める。',
    'landing.hero.getStarted': '始める',
    'landing.hero.watchDemo': 'デモを見る',
    'landing.hero.trustSecurity': '銀行レベルのセキュリティ',
    'landing.hero.trustProperties': '500+物件',
    'landing.hero.trustResidents': '10,000+住民',
    'landing.howItWorks.title': '3つの簡単なステップで開始',
    'landing.howItWorks.subtitle': 'サインアップから本稼働まで10分以内。',
    'landing.howItWorks.step1.title': 'アカウントを作成',
    'landing.howItWorks.step1.desc': 'メールで登録。コミュニティの詳細を追加し、チームを招待。',
    'landing.howItWorks.step2.title': '物件と住民を追加',
    'landing.howItWorks.step2.desc': '物件リストをインポートまたは手動で追加。住民に招待を送信。',
    'landing.howItWorks.step3.title': '運用開始',
    'landing.howItWorks.step3.desc': '支払いの受付、ゲストパスの管理、サービス予約をすぐに開始。',
    'landing.howItWorks.cta': '無料トライアルを開始',
    'landing.personas.title': 'コミュニティの全員のために構築',
    'landing.personas.subtitle': '管理、所有、訪問—Aetherはあなたのために働きます。',
    'landing.personas.manager': 'コミュニティマネージャー',
    'landing.personas.managerTagline': '運営を管理し、雑用をしない',
    'landing.personas.managerBenefit1': 'すべての物件を一覧できるフルダッシュボード',
    'landing.personas.managerBenefit2': '自動支払い回収とリマインダー',
    'landing.personas.managerBenefit3': 'ベンダーとサービスプロバイダーの管理',
    'landing.personas.managerBenefit4': '全住民に即座にお知らせを送信',
    'landing.personas.managerQuote': '"以前は1日3時間を管理業務に費やしていました。今は30分です。"',
    'landing.personas.owner': '物件オーナー',
    'landing.personas.ownerTagline': 'あなたの物件、あなたのやり方',
    'landing.personas.ownerBenefit1': 'ワンタップでHOA会費を支払い',
    'landing.personas.ownerBenefit2': '自宅のコンシェルジュサービスを予約',
    'landing.personas.ownerBenefit3': '短期賃貸として物件を掲載',
    'landing.personas.ownerBenefit4': '訪問者にゲストパスを安全に発行',
    'landing.personas.ownerQuote': '"ついに、私の物件を資産として扱うアプリができました。"',
    'landing.personas.guest': 'ゲスト',
    'landing.personas.guestTagline': '住人のように到着',
    'landing.personas.guestBenefit1': 'QRコードで入場—ゲートで待たない',
    'landing.personas.guestBenefit2': 'コミュニティマップとアメニティを探索',
    'landing.personas.guestBenefit3': 'アプリ内でホストに直接メッセージ',
    'landing.personas.guestBenefit4': '滞在中のみアクセス有効',
    'landing.personas.guestQuote': '"これまでで最もスムーズなチェックインでした。"',
    'landing.communities.title': 'プレミアムコミュニティのために構築',
    'landing.communities.subtitle': 'コンド、ゲートコミュニティ、高級賃貸の管理—Aetherはポートフォリオに合わせて拡張。',
    'landing.communities.penthouses': 'アーバンペントハウス',
    'landing.communities.penthousesDesc': 'スカイラインビュー、ルーフトップテラス',
    'landing.communities.estates': 'モダンエステート',
    'landing.communities.estatesDesc': '建築の傑作',
    'landing.communities.beachfront': 'ビーチフロントリトリート',
    'landing.communities.beachfrontDesc': 'プライベートビーチ、果てしない水平線',
    'landing.features.title': '必要なものすべて、不要なものなし',
    'landing.features.subtitle': '5つ以上のツールを1つのプラットフォームに置き換え。初日から時間とコストを節約。',
    'landing.features.portfolio': '集中ポートフォリオ管理',
    'landing.features.portfolioDesc': '1つのダッシュボードからすべての物件を管理。スプレッドシート、メール、アプリの使い分け不要。',
    'landing.features.bookings': 'サービス予約の自動化',
    'landing.features.bookingsDesc': '住民が自分でクリーニング、メンテナンス、コンシェルジュサービスを予約—管理業務を80%削減。',
    'landing.features.response': 'より迅速な対応',
    'landing.features.responseDesc': '組み込みのメッセージングでオーナー、ゲスト、ベンダーを接続—電話の逃しやメールの紛失なし。',
    'landing.features.payments': '期限通りの支払い回収',
    'landing.features.paymentsDesc': 'HOA会費とサービス支払いの自動回収。遅延支払いを60%削減。',
    'landing.features.maps': 'コミュニティをアピール',
    'landing.features.mapsDesc': 'インタラクティブマップで住民とゲストがアメニティを発見、エンゲージメントと満足度を向上。',
    'landing.features.access': '安全なゲストアクセス',
    'landing.features.accessDesc': '時間制限付きQRコードパス。誰がいつ物件に入ったかを把握—セキュリティギャップを解消。',
    'landing.pricing.title': 'シンプルで予測可能な料金',
    'landing.pricing.subtitle': '隠れた料金なし。取引ごとの手数料なし。定額料金のみ。',
    'landing.pricing.sovereign': 'Sovereign',
    'landing.pricing.sovereignDesc': '卓越性を求める物件オーナー向け',
    'landing.pricing.elite': 'Elite',
    'landing.pricing.eliteDesc': '大規模ポートフォリオをお持ちの方向け',
    'landing.pricing.enterprise': 'Enterprise',
    'landing.pricing.enterpriseDesc': 'コミュニティとHOA向け',
    'landing.pricing.month': '月',
    'landing.pricing.custom': 'カスタム',
    'landing.pricing.mostPopular': '最も人気',
    'landing.pricing.getStarted': '始める',
    'landing.pricing.contactSales': '営業に連絡',
    'landing.pricing.upTo3': '最大3物件',
    'landing.pricing.aiConcierge': 'AIコンシェルジュアクセス',
    'landing.pricing.guestPass': 'ゲストパス管理',
    'landing.pricing.hoaPortal': 'HOA支払いポータル',
    'landing.pricing.notifications': 'リアルタイム通知',
    'landing.pricing.basicAnalytics': '基本分析',
    'landing.pricing.unlimited': '無制限の物件',
    'landing.pricing.priorityAi': '優先AIコンシェルジュ',
    'landing.pricing.advancedGuest': '高度なゲスト管理',
    'landing.pricing.rentalMarketplace': 'レンタルマーケットプレイスアクセス',
    'landing.pricing.revenueAnalytics': '収益分析ダッシュボード',
    'landing.pricing.whiteGlove': 'ホワイトグローブサポート',
    'landing.pricing.customIntegrations': 'カスタム統合',
    'landing.pricing.communityWide': 'コミュニティ全体への導入',
    'landing.pricing.dedicatedManager': '専任アカウントマネージャー',
    'landing.pricing.customBranding': 'カスタムブランディング',
    'landing.pricing.apiAccess': 'APIアクセス',
    'landing.pricing.sla': 'SLA保証',
    'landing.pricing.onSiteTraining': 'オンサイトトレーニング',
    'landing.pricing.multiCommunity': 'マルチコミュニティサポート',
    'landing.testimonials.title': '物件マネージャーはAetherが大好き',
    'landing.testimonials.subtitle': 'チームがどのように時間を節約し、住民を幸せにしているかをご覧ください。',
    'landing.testimonials.quote1': '"管理時間を半分に削減。住民が自分でサービスを予約し、支払いが自動で入金されます。"',
    'landing.testimonials.author1': 'アレクサンダー・チェン',
    'landing.testimonials.role1': '不動産開発者、ビバリーヒルズ',
    'landing.testimonials.quote2': '"ゲストアクセスは悪夢でした。今は訪問者がQRコードを受け取り、完全な監査証跡があります。"',
    'landing.testimonials.author2': 'ビクトリア・スターリング',
    'landing.testimonials.role2': 'HOA理事会長、マイアミ',
    'landing.testimonials.quote3': '"住民はこのアプリが大好きです。サービスリクエストが40%減少—自分で答えを見つけられるからです。"',
    'landing.testimonials.author3': 'マーカス・ウィリアムズ',
    'landing.testimonials.role3': 'コミュニティマネージャー、200+ユニット',
    'landing.cta.title': '運営を簡素化する準備はできましたか?',
    'landing.cta.subtitle': '数分で設定。数日で成果。契約なし、いつでもキャンセル可能。',
    'landing.faq.title': 'よくある質問',
    'landing.faq.subtitle': 'Aetherの開始について知っておくべきことすべて。',
    'landing.faq.q1': 'コミュニティへのAether設定にはどのくらい時間がかかりますか?',
    'landing.faq.a1': 'ほとんどのコミュニティは10-15分で完全に運用開始できます。アカウントを作成し、物件と住民を追加すれば準備完了。オンボーディングウィザードがすべてのステップをガイドし、24時間年中無休でサポートチームが対応しています。',
    'landing.faq.q2': 'データはAetherで安全ですか?',
    'landing.faq.a2': 'もちろんです。すべてのデータ送信に銀行レベルの256ビットSSL暗号化を使用しています。データはSOC 2 Type II認定データセンターに99.99%の稼働率で保存されます。二要素認証とロールベースのアクセス制御も提供しています。',
    'landing.faq.q3': '有料プランにコミットする前にAetherを試すことはできますか?',
    'landing.faq.a3': 'はい！すべての機能に完全アクセスできる14日間の無料トライアルを提供しています。開始にクレジットカードは不要です。プラットフォームを探索し、テスト物件を追加し、Aetherがどのように運営を合理化するかを確認できます。',
    'landing.faq.q4': '支払い処理はどのように機能しますか?',
    'landing.faq.a4': 'Aetherは安全な支払い処理のためにStripeと統合しています。住民はアプリから直接HOA会費、サービス料、賃料を支払えます。資金は2-3営業日以内にあなたのアカウントに入金されます。定額のプラットフォーム料金を請求—隠れた取引ごとの手数料はありません。',
    'landing.faq.q5': 'Aetherを既存のツールと統合できますか?',
    'landing.faq.a5': 'EliteとEnterpriseプランにはAPIアクセスとカスタム統合が含まれています。人気の会計ソフト、物件管理システム、コミュニケーションツールと統合しています。チームがあなたと協力して、Aetherを既存の技術スタックに接続します。',
    'landing.faq.q6': '問題がある場合、どのようなサポートがありますか?',
    'landing.faq.a6': 'すべてのプランに24時間以内の応答時間でメールサポートが含まれています。Proプランは4時間の応答時間で優先サポートを受けられます。Enterpriseのお客様は専任のアカウントマネージャーと24時間年中無休の電話サポートを受けられます。包括的なドキュメントとビデオチュートリアルも提供しています。',
    'landing.faq.stillQuestions': 'まだ質問がありますか?',
    'landing.faq.contactSupport': 'サポートに連絡',
    'landing.footer.tagline': 'Sovereign Living',
    'landing.footer.copyright': '© {{year}} Aether. 物件管理をシンプルに。',
    'landing.footer.privacy': 'プライバシー',
    'landing.footer.terms': '利用規約',
    'landing.footer.support': 'サポート',
    
    // Google Auth
    'auth.orContinueWith': 'または以下で続ける',
    'auth.signInWithGoogle': 'Googleでログイン',
    'auth.googleSignInError': 'Googleでのログインに失敗しました',
    'landing.nav.howItWorks': '使い方',
    'landing.nav.features': '機能',
    'landing.nav.pricing': '料金',
    'landing.nav.signIn': 'サインイン',
    
    // Community Creator (Japanese)
    'communityCreator.title': '新しいコミュニティを作成',
    'communityCreator.completePaymentSetup': '支払い設定を完了',
    'communityCreator.uploadQrCodes': '支払いQRコードをアップロード',
    'communityCreator.createButton': 'コミュニティを作成',
    'communityCreator.communityName': 'コミュニティ名',
    'communityCreator.communityNamePlaceholder': '例: オーシャンビュー・エステート',
    'communityCreator.streetAddress': '住所',
    'communityCreator.streetAddressPlaceholder': '住所を入力...',
    'communityCreator.city': '市区町村',
    'communityCreator.cityPlaceholder': '市区町村',
    'communityCreator.state': '都道府県',
    'communityCreator.statePlaceholder': '都道府県',
    'communityCreator.zipCode': '郵便番号',
    'communityCreator.zipCodePlaceholder': '郵便番号',
    'communityCreator.description': '説明',
    'communityCreator.descriptionPlaceholder': 'コミュニティの簡単な説明...',
    'communityCreator.detailsTab': '詳細',
    'communityCreator.paymentsTab': '支払い',
    'communityCreator.nextPaymentSettings': '次へ: 支払い設定',
    'communityCreator.defaultCurrency': 'デフォルト通貨',
    'communityCreator.currencyHintMMK': '国内支払い用ミャンマーチャット',
    'communityCreator.currencyHintUSD': '国際支払い用米ドル',
    'communityCreator.acceptedPaymentMethods': '利用可能な支払い方法',
    'communityCreator.selectPaymentMethod': '少なくとも1つの支払い方法を選択してください',
    'communityCreator.qrCodePrompt': 'コミュニティ作成後、モバイルウォレット支払い用のQRコードをアップロードするよう求められます。',
    'communityCreator.creating': '作成中...',
    'communityCreator.completeStripeSetup': 'Stripe設定を完了',
    'communityCreator.stripeOnboardingDescription': 'コミュニティが作成されました！HOA支払いを受け取るには、Stripeの登録を完了してください。',
    'communityCreator.doLater': '後で行う',
    'communityCreator.qrUploadDescription': 'KBZPayとWave MoneyのQRコードをアップロードして、住民が料金を支払えるようにしてください。',
    'communityCreator.skipForNow': '今はスキップ',
    'communityCreator.success': 'コミュニティが正常に作成されました！',
    'communityCreator.error': 'コミュニティの作成に失敗しました',
    
    // Property Delete Dialog
    'propertyDelete.title': '物件を削除',
    'propertyDelete.confirm': '削除してもよろしいですか',
    'propertyDelete.willRemove': 'この操作は以下を行います:',
    'propertyDelete.removeProperty': 'アカウントから物件を削除',
    'propertyDelete.deactivateListings': 'アクティブな賃貸リストを無効化',
    'propertyDelete.cancelBookings': '保留中の予約をキャンセル',
    'propertyDelete.cannotUndo': 'この操作は取り消せません。',
    'propertyDelete.deleting': '削除中...',
    'propertyDelete.success': '物件が正常に削除されました',
    'propertyDelete.failed': '物件の削除に失敗しました',
    
    // Sale Listing Manager
    'saleListing.title': '販売リスト',
    'saleListing.edit': '販売リストを編集',
    'saleListing.create': '販売リストを作成',
    'saleListing.noProperties': '物件がありません',
    'saleListing.addPropertiesFirst': 'まずこのコミュニティに物件を追加してから、販売リストに登録できます。',
    'saleListing.listForSale': '販売登録',
    'saleListing.published': '公開済み',
    'saleListing.available': '販売可能',
    'saleListing.pending': '保留中',
    'saleListing.sold': '売却済み',
    'saleListing.priceRange': '価格帯',
    'saleListing.description': '見積価格帯で販売物件をリストします。実際の販売はオフラインで処理されます。',
    'saleListing.updated': '販売リストが更新されました',
    'saleListing.created': '販売リストが作成されました',
    'saleListing.saveFailed': 'リストの保存に失敗しました',
    
    // Amenity Booking Dialog
    'amenityBooking.book': '予約',
    'amenityBooking.reserveSpace': '最大{capacity}名様までこのスペースを予約',
    'amenityBooking.selectDate': '日付を選択',
    'amenityBooking.startTime': '開始時間',
    'amenityBooking.duration': '時間',
    'amenityBooking.minutes': '{minutes}分（{endTime}まで）',
    'amenityBooking.guestCount': 'ゲスト数（最大{max}名）',
    'amenityBooking.specialRequests': '特別リクエスト（任意）',
    'amenityBooking.specialPlaceholder': '特別な要件やリクエスト...',
    'amenityBooking.confirmBooking': '予約を確定',
    'amenityBooking.booking': '予約中...',
    'amenityBooking.confirmed': '予約が確定しました！',
    'amenityBooking.on': 'に',
    'amenityBooking.success': '{name}の予約が完了しました！',
    'amenityBooking.selectDateError': '日付を選択してください',
    'amenityBooking.fullyBooked': 'この時間枠は満席です。最大収容人数: {capacity}',
    'amenityBooking.spotsAvailable': 'この時間は{spots}名のみ空きがあります。ゲスト数を減らしてください。',
    'amenityBooking.failed': 'アメニティの予約に失敗しました。もう一度お試しください。',
    
    // Common additions
    'common.notAuthenticated': '認証されていません',
    'common.note': '注意',
    
    // Admin - Developer Invite
    'admin.developer.invite': '開発者を招待',
    'admin.developer.inviteTitle': '物件開発者を招待',
    'admin.developer.description': '物件開発者はコミュニティの作成・管理、物件・サービス・ベンダーの追加ができます。',
    'admin.developer.emailLabel': 'メールアドレス',
    'admin.developer.emailPlaceholder': 'developer@company.com',
    'admin.developer.nameLabel': 'フルネーム（任意）',
    'admin.developer.namePlaceholder': '山田 開発者',
    'admin.developer.note': '開発者にはアカウント設定の手順が記載されたメールが届きます。コミュニティを作成するための管理者レベルのアクセス権が付与されます。',
    'admin.developer.sendInvite': '招待を送信',
    'admin.developer.emailRequired': 'メールアドレスは必須です',
    'admin.developer.inviteSent': '開発者の招待が正常に送信されました',
    'admin.developer.inviteFailed': '招待の送信に失敗しました',
    
    // Admin - Amenity Manager
    'admin.amenity.title': 'コミュニティアメニティ',
    'admin.amenity.add': 'アメニティを追加',
    'admin.amenity.addTitle': 'アメニティを追加',
    'admin.amenity.namePlaceholder': 'アメニティ名',
    'admin.amenity.selectCategory': 'カテゴリを選択',
    'admin.amenity.descriptionPlaceholder': '説明（任意）',
    'admin.amenity.latitudePlaceholder': '緯度（例：25.7617）',
    'admin.amenity.longitudePlaceholder': '経度（例：-80.1918）',
    'admin.amenity.coordsTip': 'ヒント：Googleマップで場所を右クリックして座標を取得できます',
    'admin.amenity.addButton': 'アメニティを追加',
    'admin.amenity.noAmenities': 'アメニティがありません',
    'admin.amenity.addFirst': '最初のコミュニティアメニティを追加しましょう',
    'admin.amenity.loadFailed': 'アメニティの読み込みに失敗しました',
    'admin.amenity.nameAndCoordsRequired': '名前と座標は必須です',
    'admin.amenity.addSuccess': 'アメニティが正常に追加されました',
    'admin.amenity.addFailed': 'アメニティの追加に失敗しました',
    'admin.amenity.confirmDelete': 'このアメニティを削除してもよろしいですか？',
    'admin.amenity.deleteSuccess': 'アメニティが削除されました',
    'admin.amenity.deleteFailed': 'アメニティの削除に失敗しました',
    'admin.amenity.category.pool': 'プール',
    'admin.amenity.category.gym': 'フィットネスセンター',
    'admin.amenity.category.restaurant': 'レストラン',
    'admin.amenity.category.valet': 'バレーパーキング',
    'admin.amenity.category.spa': 'スパ＆ウェルネス',
    'admin.amenity.category.park': '公園 / ガーデン',
    'admin.amenity.category.clubhouse': 'クラブハウス',
    'admin.amenity.category.other': 'その他',
    
    // Admin - Announcement Manager
    'admin.announcement.title': 'お知らせ',
    'admin.announcement.new': '新規',
    'admin.announcement.newTitle': '新しいお知らせ',
    'admin.announcement.editTitle': 'お知らせを編集',
    'admin.announcement.titleLabel': 'タイトル',
    'admin.announcement.titlePlaceholder': 'お知らせのタイトル',
    'admin.announcement.contentLabel': '内容',
    'admin.announcement.contentPlaceholder': 'お知らせを入力してください...',
    'admin.announcement.priorityLabel': '優先度',
    'admin.announcement.priority.normal': '通常',
    'admin.announcement.priority.important': '重要',
    'admin.announcement.priority.urgent': '緊急',
    'admin.announcement.publishImmediately': 'すぐに公開',
    'admin.announcement.visibleToResidents': 'すべての住民に表示',
    'admin.announcement.create': 'お知らせを作成',
    'admin.announcement.update': 'お知らせを更新',
    'admin.announcement.publish': '公開',
    'admin.announcement.unpublish': '非公開',
    'admin.announcement.status.published': '公開済み',
    'admin.announcement.status.draft': '下書き',
    'admin.announcement.noAnnouncements': 'お知らせがありません',
    'admin.announcement.createToInform': '住民に情報を伝えるお知らせを作成しましょう',
    'admin.announcement.titleContentRequired': 'タイトルと内容は必須です',
    'admin.announcement.createSuccess': 'お知らせが作成されました',
    'admin.announcement.updateSuccess': 'お知らせが更新されました',
    'admin.announcement.saveFailed': 'お知らせの保存に失敗しました',
    'admin.announcement.deleteSuccess': 'お知らせが削除されました',
    'admin.announcement.deleteFailed': 'お知らせの削除に失敗しました',
    'admin.announcement.published': 'お知らせが公開されました',
    'admin.announcement.unpublished': 'お知らせが非公開になりました',
    'admin.announcement.updateFailed': 'お知らせの更新に失敗しました',
    
    // Admin - Guest Pass Manager
    'admin.guestPass.title': 'ゲストパス管理',
    'admin.guestPass.subtitle': 'すべての訪問者のアクセスを管理',
    'admin.guestPass.status.active': '有効',
    'admin.guestPass.status.expired': '期限切れ',
    'admin.guestPass.status.revoked': '取り消し済み',
    'admin.guestPass.noGuestPasses': 'ゲストパスが見つかりません',
    'admin.guestPass.unknownGuest': '不明なゲスト',
    'admin.guestPass.expires': '期限',
    'admin.guestPass.extend': '延長',
    'admin.guestPass.reactivate': '再有効化（7日間）',
    'admin.guestPass.extendTitle': 'ゲストアクセスを延長',
    'admin.guestPass.guestLabel': 'ゲスト',
    'admin.guestPass.newEndDate': '新しい終了日',
    'admin.guestPass.selectEndDate': '新しい終了日を選択',
    'admin.guestPass.extendAccess': 'アクセスを延長',
    'admin.guestPass.loadFailed': 'ゲストパスの読み込みに失敗しました',
    'admin.guestPass.revoked': 'ゲストパスが取り消されました',
    'admin.guestPass.revokeFailed': 'パスの取り消しに失敗しました',
    'admin.guestPass.extendedTo': 'アクセスが延長されました',
    'admin.guestPass.extendFailed': 'アクセスの延長に失敗しました',
    'admin.guestPass.reactivated': 'ゲストパスが7日間再有効化されました',
    'admin.guestPass.reactivateFailed': 'パスの再有効化に失敗しました',
    
    // Date/Time Picker
    'datePicker.pickDate': '日付を選択',
    'datePicker.selectDate': '日時を選択',
    'datePicker.time': '時間',
    
    // Services Manager Additional
    'services.addNewService': '新しいサービスを追加',
    'services.serviceUpdated': 'サービスが更新されました',
    'services.hasBeenUpdated': '更新されました',
    'services.serviceCreated': 'サービスが作成されました',
    'services.hasBeenAdded': '追加されました',
    'services.failedToSave': 'サービスの保存に失敗しました',
    'services.noVendorAssigned': 'ベンダー未割り当て',
    'services.unknownVendor': '不明なベンダー',
    'services.loadingServices': 'サービスを読み込み中...',
    'services.perBedroom': '寝室ごと',
    'services.perSqft': '平方フィートごと',
    'services.pricing': '料金',
    'services.min': '分',
    'services.active': 'アクティブ',
    'services.inactive': '非アクティブ',
    'services.createService': 'サービスを作成',
    'services.updateService': 'サービスを更新',
    
    // Service Names (from database) - Japanese
    'serviceName.poolService': 'プールサービス',
    'serviceName.poolMaintenance': 'プールメンテナンス',
    'serviceName.poolSpaMaintenance': 'プール＆スパメンテナンス',
    'serviceName.deepCleaning': '大掃除',
    'serviceName.deepClean': '大掃除',
    'serviceName.regularCleaning': '通常清掃',
    'serviceName.housekeeping': 'ハウスキーピング',
    'serviceName.expressHousekeeping': 'エクスプレスハウスキーピング',
    'serviceName.signatureHousekeeping': 'シグネチャーハウスキーピング',
    'serviceName.fullEstateCleaning': '邸宅全体クリーニング',
    'serviceName.weeklyMaintenanceClean': '週次メンテナンス清掃',
    'serviceName.turndownService': 'ターンダウンサービス',
    'serviceName.laundryService': 'ランドリーサービス',
    'serviceName.laundry': 'ランドリー',
    'serviceName.laundryPressing': 'ランドリー＆プレス',
    'serviceName.carpetCleaning': 'カーペットクリーニング',
    'serviceName.windowCleaning': '窓清掃',
    'serviceName.inHomeSpa': '出張スパ＆ウェルネス',
    'serviceName.spaWellness': 'スパ＆ウェルネス',
    'serviceName.couplesSpaDay': 'カップルスパデー',
    'serviceName.couplesSpaPackage': 'カップルスパパッケージ',
    'serviceName.inVillaMassage': 'ヴィラ内マッサージ',
    'serviceName.swedishMassage': 'スウェーデン式マッサージ',
    'serviceName.hotStoneTherapy': 'ホットストーンセラピー',
    'serviceName.personalTraining': 'パーソナルトレーニング',
    'serviceName.personalTrainingSession': 'パーソナルトレーニングセッション',
    'serviceName.privateYogaClass': 'プライベートヨガクラス',
    'serviceName.privateYogaSession': 'プライベートヨガセッション',
    'serviceName.massageTherapy': 'マッサージセラピー',
    'serviceName.massage': 'マッサージ',
    'serviceName.yogaSession': 'ヨガセッション',
    'serviceName.yoga': 'ヨガ',
    'serviceName.meditation': '瞑想',
    'serviceName.beautyServices': 'ビューティーサービス',
    'serviceName.salonServices': 'サロンサービス',
    'serviceName.generalMaintenance': '一般メンテナンス',
    'serviceName.plumbing': '配管工事',
    'serviceName.emergencyPlumbing': '緊急配管工事',
    'serviceName.electrical': '電気工事',
    'serviceName.hvacService': '空調サービス',
    'serviceName.hvac': '空調',
    'serviceName.hvacMaintenance': '空調メンテナンス',
    'serviceName.handyman': 'ハンディマン',
    'serviceName.handymanServices': 'ハンディマンサービス',
    'serviceName.landscaping': '造園',
    'serviceName.landscapingService': '造園サービス',
    'serviceName.gardenLandscaping': 'ガーデン造園',
    'serviceName.gardenMaintenance': '庭園管理',
    'serviceName.pestControl': '害虫駆除',
    'serviceName.privateChef': 'プライベートシェフ',
    'serviceName.privateChefDinner': 'プライベートシェフディナー',
    'serviceName.privateChefExperience': 'プライベートシェフ体験',
    'serviceName.italianDinnerExperience': 'イタリアンディナー体験',
    'serviceName.sushiOmakase': '寿司おまかせ',
    'serviceName.winePairingDinner': 'ワインペアリングディナー',
    'serviceName.wineSommelierService': 'ソムリエサービス',
    'serviceName.breakfastInBed': 'ベッドで朝食',
    'serviceName.cocktailPartyCatering': 'カクテルパーティーケータリング',
    'serviceName.catering': 'ケータリング',
    'serviceName.mealDelivery': '食事配達',
    'serviceName.groceryShopping': '食料品買い物',
    'serviceName.bartenderService': 'バーテンダーサービス',
    'serviceName.bartender': 'バーテンダー',
    'serviceName.airportTransfer': '空港送迎',
    'serviceName.airportLimousine': '空港リムジン',
    'serviceName.carRental': 'レンタカー',
    'serviceName.golfCartRental': 'ゴルフカートレンタル',
    'serviceName.chauffeurService': '運転手サービス',
    'serviceName.chauffeur': '運転手',
    'serviceName.valetParking': 'バレーパーキング',
    'serviceName.valetParkingService': 'バレーパーキングサービス',
    'serviceName.valet': 'バレー',
    'serviceName.boatCharter': 'ボートチャーター',
    'serviceName.yachtCharter': 'ヨットチャーター',
    'serviceName.yachtCharterHalfDay': 'ヨットチャーター - 半日',
    'serviceName.yachtDetailing': 'ヨットディテーリング',
    
    // Vendor Manager Additional
    'vendor.failedToLoad': 'ベンダーの読み込みに失敗しました',
    'vendor.nameRequired': 'ベンダー名は必須です',
    'vendor.addedSuccess': 'ベンダーが追加されました',
    'vendor.failedToAdd': 'ベンダーの追加に失敗しました',
    'vendor.updatedSuccess': 'ベンダーが更新されました',
    'vendor.failedToUpdate': 'ベンダーの更新に失敗しました',
    'vendor.confirmDelete': 'このベンダーを削除してもよろしいですか?',
    'vendor.deleted': 'ベンダーが無効化されました',
    'vendor.failedToDelete': 'ベンダーの削除に失敗しました',
    'vendor.stripeOnboardingOpened': 'Stripeオンボーディングが新しいタブで開きました',
    'vendor.failedToStartOnboarding': 'Stripeオンボーディングの開始に失敗しました',
    'vendor.vendorsAndProviders': 'ベンダーとサービスプロバイダー',
    'vendor.addNewVendor': '新しいベンダーを追加',
    'vendor.enterVendorName': 'ベンダー名を入力',
    'vendor.contactEmail': '連絡先メール',
    'vendor.emailNotification': '支払いと予約通知用',
    'vendor.platformKeeps': 'プラットフォームが保持',
    'vendor.notConnected': '未接続',
    'vendor.fullSetup': 'フルセットアップ',
    'vendor.stripeOnly': 'Stripeのみ',
    'vendor.qrOnly': 'QRのみ',
    'vendor.noVendorsDesc': 'サービスを提供するためにベンダーを追加',
    'vendor.updateStripeAccount': 'Stripeアカウントを更新',
    'vendor.connectStripeAccount': 'Stripeアカウントを接続',
    'vendor.editDetails': 'ベンダーを編集',
    'vendor.qrCodes': 'QRコード',
    
    // Guest Preferences Quiz (Japanese)
    'quiz.saved': '設定を保存しました！',
    'quiz.savedDesc': 'お好みに合わせて賃貸検索をカスタマイズします。',
    'quiz.saveFailed': '設定の保存に失敗しました',
    'quiz.howTravel': 'どのような旅行スタイルが好きですか？',
    'quiz.selectAll': '該当するものをすべて選択',
    'quiz.whatBrings': 'ここに来た目的は？',
    'quiz.whoTraveling': '誰と一緒に旅行しますか？',
    'quiz.selectGroupSize': '一般的なグループサイズを選択',
    'quiz.stayLength': '一般的な滞在期間',
    'quiz.nights': '泊',
    'quiz.whatBudget': '予算はいくらですか？',
    'quiz.perNightPref': '1泊あたりの価格帯',
    'quiz.mustHaveAmenities': '必須アメニティ',
    'quiz.selectEssentials': '必需品を選択',
    'quiz.petFriendly': 'ペット可',
    'quiz.petFriendlyDesc': 'ペットと一緒に旅行',
    'quiz.accessibility': 'アクセシビリティ',
    'quiz.accessibilityDesc': 'バリアフリー機能が必要',
    'quiz.personalizeExperience': '体験をパーソナライズ',
    'quiz.updatePreferences': '設定を更新',
    'quiz.helpFind': 'ぴったりの物件を見つけるお手伝いをします',
    'quiz.skipForNow': '今はスキップ',
    'quiz.savePreferences': '設定を保存',
    'quiz.style.short_trip': '短期旅行',
    'quiz.style.short_tripDesc': '1〜3泊',
    'quiz.style.long_term': '長期滞在',
    'quiz.style.long_termDesc': '数週間または数ヶ月',
    'quiz.style.luxury': 'ラグジュアリー',
    'quiz.style.luxuryDesc': 'プレミアム体験',
    'quiz.style.budget': 'お手頃',
    'quiz.style.budgetDesc': 'コスパ重視',
    'quiz.purpose.vacation': '休暇',
    'quiz.purpose.business': '出張',
    'quiz.purpose.relocation': '引っ越し',
    'quiz.purpose.remote_work': 'リモートワーク',
    'quiz.group.solo': '一人',
    'quiz.group.couple': 'カップル',
    'quiz.group.family': '家族',
    'quiz.group.group': 'グループ',
    'quiz.budget.budget': 'エコノミー',
    'quiz.budget.moderate': 'スタンダード',
    'quiz.budget.luxury': 'ラグジュアリー',
    'quiz.budget.ultra_luxury': 'ウルトララグジュアリー',
    'quiz.amenity.wifi': 'WiFi',
    'quiz.amenity.parking': '駐車場',
    'quiz.amenity.pool': 'プール',
    'quiz.amenity.gym': 'ジム',
    'quiz.amenity.kitchen': 'キッチン',
    'quiz.amenity.air_conditioning': 'エアコン',
    'quiz.amenity.tv': 'スマートTV',
    'quiz.amenity.hot_tub': 'ホットタブ',
    'quiz.amenity.garden': '庭',
    'quiz.amenity.security': 'セキュリティ',
    
    // Payment Method Selector (Japanese)
    'payment.totalAmount': '合計金額',
    'payment.myanmarOptions': 'ミャンマー決済オプション',
    'payment.localPayment': '現地決済',
    'payment.internationalCards': '国際カード',
    'payment.processing': '処理中...',
    'payment.payWith': '{method}で支払う',
    'payment.selectMethod': '支払い方法を選択',
    'payment.securityNote': 'お支払いはエンドツーエンド暗号化で保護されています',
    
    // Amenity Booking Manager - Admin
    'amenityBooking.reservations': '施設予約',
    'amenityBooking.allStatus': 'すべてのステータス',
    'amenityBooking.statusConfirmed': '確認済み',
    'amenityBooking.statusCancelled': 'キャンセル済み',
    'amenityBooking.statusCompleted': '完了',
    'amenityBooking.statusPending': '保留中',
    'amenityBooking.noBookings': '予約が見つかりません',
    'amenityBooking.noBookingsDesc': '住民が施設を予約すると、ここに表示されます',
    'amenityBooking.failedToLoad': '予約の読み込みに失敗しました',
    'amenityBooking.failedToUpdate': '予約の更新に失敗しました',
    'amenityBooking.bookingUpdated': '予約が更新されました',
    'amenityBooking.amenity': '施設',
    'amenityBooking.dateTime': '日時',
    'amenityBooking.guest': 'ゲスト',
    'amenityBooking.status': 'ステータス',
    'amenityBooking.actions': 'アクション',
    'amenityBooking.filterStatus': 'ステータスでフィルター',
    
    // Common additional
    'common.guest': '名',
    'common.guests': '名',
    
    // New Pricing Plans - Explorer (Japanese)
    'pricing.explorer.name': 'Explorer',
    'pricing.explorer.desc': '特別な滞在を求める旅行者向け',
    'pricing.explorer.feature1': '高級短期賃貸を予約',
    'pricing.explorer.feature2': '長期賃貸を予約',
    'pricing.explorer.feature3': 'AIコンシェルジュサービス',
    'pricing.explorer.feature4': 'ホストとの安全なメッセージ',
    'pricing.explorer.feature5': '優先予約サポート',
    
    // New Pricing Plans - Sovereign (Japanese)
    'pricing.sovereign.name': 'Sovereign',
    'pricing.sovereign.desc': '物件を貸し出す準備ができたオーナー向け',
    'pricing.sovereign.feature1': '短期賃貸用に物件を掲載',
    'pricing.sovereign.feature2': '長期賃貸用に物件を掲載',
    'pricing.sovereign.feature3': 'ダイナミックプライシングツール',
    'pricing.sovereign.feature4': 'ゲスト管理ダッシュボード',
    'pricing.sovereign.feature5': '収益分析',
    'pricing.sovereign.feature6': '優先ホストサポート',
    
    // New Pricing Plans - Architect (Japanese)
    'pricing.architect.name': 'Architect',
    'pricing.architect.desc': 'コミュニティを管理する開発者向け',
    'pricing.architect.feature1': 'コミュニティの作成と管理',
    'pricing.architect.feature2': 'オーナーの招待と管理',
    'pricing.architect.feature3': 'HOA料金の徴収',
    'pricing.architect.feature4': 'ベンダーマーケットプレイス管理',
    'pricing.architect.feature5': 'コミュニティお知らせ',
    'pricing.architect.feature6': '高度な分析ダッシュボード',
    'pricing.architect.feature7': 'カスタムブランディング',
    
    // Pricing Modal & Common (Japanese)
    'pricing.monthly': '月額',
    'pricing.annual': '年額',
    'pricing.save': '{percent}%お得',
    'pricing.trialDays': '{days}日間無料トライアル',
    'pricing.startTrial': '無料トライアルを開始',
    'pricing.subscribe': '今すぐ登録',
    'pricing.securePayment': 'Stripeによる安全な決済',
    'pricing.redirecting': '決済ページへ移動中...',
    'pricing.error': 'サブスクリプションの作成に失敗しました。もう一度お試しください。',
    
    // Pricing Triggers (Japanese)
    'pricing.trigger.rentalMode': '物件を掲載するには登録してください',
    'pricing.trigger.booking': 'この物件を予約するには登録してください',
    'pricing.trigger.community': 'コミュニティを作成・管理するには登録してください',
    'pricing.trigger.default': 'アカウントをアップグレード',
    'landing.pricing.perYear': '年',
    'landing.pricing.billingMonthly': '月額',
    'landing.pricing.billingAnnual': '年額',
    
    // Pricing Page (Japanese)
    'pricingPage.backToHome': 'ホームに戻る',
    'pricingPage.title': 'シンプルで透明な料金体系',
    'pricingPage.subtitle': 'ニーズに合ったプランをお選びください。隠れた費用はありません。',
    'pricingPage.saveUpTo': '17%お得',
    'pricingPage.compareFeatures': '機能を比較',
    'pricingPage.feature': '機能',
    'pricingPage.feature.bookRentals': '高級物件の予約',
    'pricingPage.feature.aiConcierge': 'AIコンシェルジュアシスタント',
    'pricingPage.feature.secureMessaging': 'セキュアなメッセージング',
    'pricingPage.feature.listProperty': '物件を掲載',
    'pricingPage.feature.guestManagement': 'ゲスト管理ダッシュボード',
    'pricingPage.feature.revenueAnalytics': '収益分析',
    'pricingPage.feature.createCommunities': 'コミュニティを作成',
    'pricingPage.feature.inviteHomeowners': 'オーナーを招待・管理',
    'pricingPage.feature.hoaCollection': 'HOA料金の回収',
    'pricingPage.feature.vendorManagement': 'ベンダーマーケットプレイス',
    'pricingPage.feature.customBranding': 'カスタムブランディング',
    'pricingPage.contactSalesTitle': '営業にお問い合わせ',
    'pricingPage.contactSalesSubtitle': 'SovereignまたはArchitectプランにご興味がありますか？チームにお問い合わせください。',
    'pricingPage.yourName': 'お名前',
    'pricingPage.namePlaceholder': '山田 太郎',
    'pricingPage.yourEmail': 'メールアドレス',
    'pricingPage.emailPlaceholder': 'yamada@example.com',
    'pricingPage.companyOptional': '会社名（任意）',
    'pricingPage.companyPlaceholder': '会社名',
    'pricingPage.planInterest': 'ご興味のあるプラン',
    'pricingPage.homeownerPlan': 'Sovereign（オーナー）',
    'pricingPage.developerPlan': 'Architect（開発者）',
    'pricingPage.tellUsMore': '詳細を教えてください',
    'pricingPage.messagePlaceholder': 'ニーズ、物件ポートフォリオ、またはご質問について教えてください...',
    'pricingPage.sending': '送信中...',
    'pricingPage.sendInquiry': 'お問い合わせを送信',
    'pricingPage.inquirySent': 'お問い合わせを送信しました！営業チームから近日中にご連絡いたします。',
    'pricingPage.inquiryFailed': 'お問い合わせの送信に失敗しました。もう一度お試しください。',
    'pricing.inviteRequired': 'コミュニティ管理者からの招待が必要です',
    'pricing.enterpriseOnboarding': 'エンタープライズオンボーディングが必要です',
    
    // Support Page - Sales Inquiry (Japanese)
    'support.salesInquiryTitle': 'オーナー・開発者登録',
    'support.salesInquirySubtitle': '物件を掲載したり、コミュニティを管理したいですか？営業チームにお問い合わせください。',
    'support.homeownerPlan': 'オーナー（Sovereign）',
    'support.homeownerPlanDesc': '短期または長期の賃貸物件を掲載',
    'support.developerPlan': '開発者（Architect）',
    'support.developerPlanDesc': '複数の物件を持つコミュニティを作成・管理',
    'support.companyOptional': '会社名（任意）',
    'support.companyPlaceholder': '会社名',
    'support.tellUsMore': '詳細を教えてください',
    'support.salesMessagePlaceholder': '物件、コミュニティの規模、またはご質問について教えてください...',
    'support.submitSalesInquiry': '営業お問い合わせを送信',
    'support.salesInquirySent': 'ありがとうございます！営業チームから24時間以内にご連絡いたします。',
    'support.salesInquiryFailed': 'お問い合わせの送信に失敗しました。もう一度お試しください。',
    'support.otherWaysToReach': 'その他のお問い合わせ方法',
    
    // SmartHome Guide Page (Japanese)
    'smartHome.guide.lighting': '照明',
    'smartHome.guide.lightingDesc': 'スマート照明とシーンを制御',
    'smartHome.guide.smartLocks': 'スマートロック',
    'smartHome.guide.smartLocksDesc': 'ドアのロック/解除（有効な場合）',
    'smartHome.guide.doorbell': 'ドアベル',
    'smartHome.guide.doorbellDesc': '誰がドアにいるか確認',
    'smartHome.guide.voiceCommands': '音声コマンド',
    'smartHome.guide.voiceCommandsNote': '物件にGoogle Nestスピーカーがある場合、これらのコマンドを試してください：',
    'smartHome.guide.appleHomeKit': 'Apple HomeKit代替',
    'smartHome.guide.appleHomeKitDesc': 'Appleエコシステムのみを使用する物件向け',
    'smartHome.guide.howToShareHomeKit': 'ゲストとHomeKitアクセスを共有する方法',
    'smartHome.guide.helpfulResources': '役立つリソース',
    'smartHome.guide.homeKitNote': 'HomeKitにはゲストがApple IDとiOS/macOSデバイスを持っている必要があります。Google Homeはより多くのデバイスタイプをサポートし、プラットフォーム間で動作します。',
    
    // AI Page (Japanese)
    'aiPage.title': 'Aether AI',
    'aiPage.subtitle': 'あなた専用のアシスタント',
    'aiPage.welcome': 'こんにちは！AIコンシェルジュです。以下のことでお手伝いできます：',
    'aiPage.help.booking': 'サービスと予約の手配',
    'aiPage.help.guestAccess': 'ゲストアクセスの管理',
    'aiPage.help.maintenance': '物件メンテナンスのリクエスト',
    'aiPage.help.lifestyle': 'ライフスタイルのおすすめ',
    'aiPage.trySaying': 'こう言ってみてください',
    'aiPage.placeholder': '何でも聞いてください...',
    'aiPage.suggestion1': '土曜日にプライベートシェフを予約',
    'aiPage.suggestion2': 'プールのメンテナンスをスケジュール',
    'aiPage.suggestion3': '明日のゲストを承認',
    'aiPage.suggestion4': 'どんなサービスがありますか？',
    
    // Public Sales Page (Japanese)
    'sales.priceRange': '価格帯',
    'sales.minBedrooms': '最低寝室数',
    'sales.any': 'すべて',
    'sales.features': '特徴',
    'sales.clearFilters': 'すべてのフィルターをクリア',
    'sales.exclusiveProperties': '限定物件',
    'sales.propertiesForSale': '販売中の物件',
    'sales.discoverProperties': '当社の高級コミュニティで特別な物件を発見',
    'sales.forSale': '販売中',
    'sales.sqft': '平方フィート',
    'sales.keyFeatures': '主な特徴',
    'sales.propertyDirections': '物件への道順',
    'sales.searchPlaceholder': '場所またはコミュニティで検索...',
    'sales.filters': 'フィルター',
    'sales.propertiesFound': '{count}件の物件が見つかりました',
    'sales.propertyFound': '1件の物件が見つかりました',
    'sales.viewRentals': '賃貸を見る',
    'sales.noProperties': 'フィルターに一致する物件がありません',
    'sales.tryAdjusting': '検索条件を調整してみてください',
    'sales.luxuryProperty': '高級物件',
    'sales.exclusiveCommunity': '限定コミュニティ',
    'sales.bed': '寝室',
    'sales.bath': 'バスルーム',
    'sales.parking': '駐車場',
    'sales.built': '築年',
    'sales.moreFeatures': '+{count}件',
    'sales.contactForDetails': '詳細はお問い合わせください',
    
    // GodMode Dashboard (Japanese)
    'godMode.tenantAnalytics': 'テナント分析',
    'godMode.tenantAnalyticsDesc': '使用状況メトリクスと収益内訳を表示',
    'godMode.communityAdmins': 'コミュニティ管理者',
    'godMode.revenue': '収益',
    'godMode.configure': '設定',
    'godMode.admins': '管理者',
    
    // SaleDetailPage (Japanese)
    'saleDetail.back': '戻る',
    'saleDetail.forSale': '販売中',
    'saleDetail.bedrooms': '寝室',
    'saleDetail.bathrooms': 'バスルーム',
    'saleDetail.parking': '駐車場',
    'saleDetail.builtIn': '築年',
    'saleDetail.floor': '階',
    'saleDetail.sqft': '平方フィート',
    'saleDetail.aboutProperty': 'この物件について',
    'saleDetail.keyFeatures': '主な特徴',
    'saleDetail.amenities': '設備・アメニティ',
    'saleDetail.requestInfo': '資料請求',
    'saleDetail.name': '氏名',
    'saleDetail.email': 'メールアドレス',
    'saleDetail.phone': '電話番号',
    'saleDetail.message': 'メッセージ',
    'saleDetail.interestedMessage': 'この物件に興味があります...',
    'saleDetail.sendInquiry': 'お問い合わせを送信',
    'saleDetail.sending': '送信中...',
    'saleDetail.listingNotFound': 'リストが見つかりません',
    'saleDetail.propertyUnavailable': 'この物件はご利用いただけなくなった可能性があります。',
    'saleDetail.browseProperties': '物件を閲覧',
    'saleDetail.exclusiveLocation': '限定ロケーション',
    'saleDetail.fillNameEmail': '氏名とメールアドレスをご入力ください',
    'saleDetail.inquirySubmitted': 'お問い合わせを送信しました！担当者から間もなくご連絡いたします。',
    'saleDetail.inquiryFailed': 'お問い合わせの送信に失敗しました。もう一度お試しください。',
    'saleDetail.yourFullName': 'お名前（フルネーム）',
    'saleDetail.yourEmail': 'your@email.com',
    'saleDetail.contactWithin24h': '営業担当者が24時間以内にご連絡いたします。',
    
    // PropertyDirectionsPage (Japanese)
    'directions.propertyDirections': '物件への道順',
    'directions.booking': '予約',
    'directions.confirmed': '確認済み',
    'directions.fullPropertyAddress': '物件の完全な住所',
    'directions.copyAddress': '住所をコピー',
    'directions.openInMaps': 'マップで開く',
    'directions.getDirections': '道順を取得',
    'directions.stayDetails': '滞在詳細',
    'directions.checkIn': 'チェックイン',
    'directions.checkOut': 'チェックアウト',
    'directions.after3pm': '午後3時以降',
    'directions.before11am': '午前11時まで',
    'directions.guests': 'ゲスト',
    'directions.guest': 'ゲスト',
    'directions.houseRules': 'ハウスルール',
    'directions.smartHomeAccess': 'スマートホームアクセス',
    'directions.confidentialInfo': 'この情報は機密であり、他の人と共有しないでください。',
    'directions.needHelp': 'お困りですか？',
    'directions.messageHost': 'ホストにメッセージ',
    'directions.contactSupport': 'サポートに連絡',
    'directions.bookingNotFound': '予約が見つかりません',
    'directions.noAccess': 'この予約は存在しないか、アクセス権がありません。',
    'directions.goToBookings': '予約一覧へ',
    'directions.addressCopied': '住所をクリップボードにコピーしました',
    'directions.addressOnlyConfirmed': '物件の住所は確認済みの予約のみ利用可能です',
    'directions.failedToLoad': '物件詳細の読み込みに失敗しました',
    
    // Privacy Page (Japanese)
    'privacy.backToHome': 'ホームに戻る',
    'privacy.title': 'プライバシーポリシー',
    'privacy.lastUpdated': '最終更新：2025年12月',
    'privacy.footer': '© 2025 Aether. 不動産管理をシンプルに。',
    'privacy.section1.title': '1. 収集する情報',
    'privacy.section1.intro': 'Aetherでは、アカウント作成、支払い、サポートへのお問い合わせなど、お客様が直接提供する情報を収集します。これには以下が含まれる場合があります：',
    'privacy.section1.item1': '氏名と連絡先情報（メール、電話番号）',
    'privacy.section1.item2': '物件の詳細と住所',
    'privacy.section1.item3': '支払い情報（Stripeを通じて安全に処理）',
    'privacy.section1.item4': '通信設定',
    'privacy.section1.item5': '使用データと分析',
    'privacy.section2.title': '2. 情報の使用方法',
    'privacy.section2.intro': '収集した情報は以下の目的で使用します：',
    'privacy.section2.item1': 'サービスの提供、維持、改善',
    'privacy.section2.item2': '取引の処理と関連通知の送信',
    'privacy.section2.item3': 'コメント、質問、サポートリクエストへの対応',
    'privacy.section2.item4': '技術的な通知、更新、セキュリティアラートの送信',
    'privacy.section2.item5': '製品、サービス、イベントに関するコミュニケーション',
    'privacy.section3.title': '3. サブスクリプション層とデータ処理',
    'privacy.section3.intro': 'サブスクリプション層に基づいてデータを処理します：',
    'privacy.section3.explorer': 'Explorer（ゲスト）',
    'privacy.section3.explorerDesc': '15日間トライアル以降の予約履歴、設定、支払い記録',
    'privacy.section3.sovereign': 'Sovereign（オーナー）',
    'privacy.section3.sovereignDesc': '30日間トライアル以降の物件データ、賃貸リスト、ゲスト管理、収益分析',
    'privacy.section3.architect': 'Architect（開発者）',
    'privacy.section3.architectDesc': 'コミュニティデータ、オーナー管理、HOA支払い、ベンダー関係',
    'privacy.section4.title': '4. 招待制アクセス',
    'privacy.section4.intro': '特定のサブスクリプション層では、アクセスは招待制です：',
    'privacy.section4.item1': 'オーナー（Sovereign）はコミュニティ管理者から招待を受けます',
    'privacy.section4.item2': '不動産開発者（Architect）は営業相談後にAetherスタッフから招待を受けます',
    'privacy.section5.title': '5. データセキュリティ',
    'privacy.section5.content': 'すべてのデータ送信には銀行レベルの256ビットSSL暗号化を使用しています。データはSOC 2 Type II認証のデータセンターに99.99%のアップタイムで保管されています。不正アクセス、改ざん、開示、破壊から個人情報を保護するために適切な技術的・組織的措置を実施しています。',
    'privacy.section6.title': '6. サードパーティサービス',
    'privacy.section6.intro': 'プラットフォームを提供するために信頼できるサードパーティサービスを使用しています：',
    'privacy.section6.stripe': 'サブスクリプション、HOA料金、賃貸支払いの安全な決済処理',
    'privacy.section6.mapbox': '物件の場所とコミュニティ施設のインタラクティブマップ',
    'privacy.section6.openai': 'サービス予約と推奨のためのAIコンシェルジュ',
    'privacy.section7.title': '7. データ保持',
    'privacy.section7.content': 'アカウントがアクティブである限り、またはサービス提供に必要な限りデータを保持します。サブスクリプションと取引データは税務・法的コンプライアンスのため7年間保持されます。アカウントと個人データの削除はいつでもリクエストできます。',
    'privacy.section8.title': '8. お客様の権利',
    'privacy.section8.intro': 'お客様には以下の権利があります：',
    'privacy.section8.item1': '個人データへのアクセスとコピーの受領',
    'privacy.section8.item2': '不正確な個人データの訂正',
    'privacy.section8.item3': '個人データの削除リクエスト',
    'privacy.section8.item4': '個人データの処理への異議',
    'privacy.section8.item5': '個人データの処理制限のリクエスト',
    'privacy.section8.item6': 'データポータビリティ',
    'privacy.section9.title': '9. お問い合わせ',
    'privacy.section9.content': 'このプライバシーポリシーについてご質問がある場合は、以下までお問い合わせください：',
    
    // Terms Page (Japanese)
    'terms.backToHome': 'ホームに戻る',
    'terms.title': '利用規約',
    'terms.lastUpdated': '最終更新：2025年12月',
    'terms.footer': '© 2025 Aether. 不動産管理をシンプルに。',
    'terms.section1.title': '1. 規約の承諾',
    'terms.section1.content': 'Aether（「サービス」）にアクセスまたは使用することにより、これらの利用規約に拘束されることに同意するものとします。これらの規約に同意しない場合は、サービスを使用しないでください。当社はいつでもこれらの規約を更新する権利を留保し、サービスの継続使用は変更の承諾を構成します。',
    'terms.section2.title': '2. サービスの説明',
    'terms.section2.content': 'Aetherは、コミュニティマネージャー、不動産オーナー、ゲスト向けのツールを提供する不動産管理プラットフォームです。サービスには、決済処理、ゲストパス管理、サービス予約、コミュニティ告知、賃貸リスト、AIコンシェルジュサービスが含まれますが、これらに限定されません。',
    'terms.section3.title': '3. サブスクリプションプラン',
    'terms.section3.intro': 'Aetherは3つのサブスクリプション層を提供しています：',
    'terms.section3.explorer': 'Explorer（$9.99/月）',
    'terms.section3.explorerDesc': '高級滞在を求める旅行者向け。短期賃貸、長期リース、AIコンシェルジュサービスにアクセス。',
    'terms.section3.sovereign': 'Sovereign（$19.99/月）',
    'terms.section3.sovereignDesc': 'オーナー向け。物件の掲載、ゲスト管理、分析へのアクセス、賃貸からの収益獲得。',
    'terms.section3.architect': 'Architect（カスタム価格）',
    'terms.section3.architectDesc': '不動産開発者向け。コミュニティの作成、オーナー管理、HOA料金徴収、エンタープライズ機能へのアクセス。',
    'terms.section4.title': '4. トライアル期間',
    'terms.section4.intro': '一部のプランでは無料トライアルが利用可能です：',
    'terms.section4.explorer': 'Explorer',
    'terms.section4.explorerTrial': '15日間無料トライアル。開始にクレジットカード不要。',
    'terms.section4.sovereign': 'Sovereign',
    'terms.section4.sovereignTrial': '管理者からコミュニティ招待を受けた後、30日間無料トライアル。',
    'terms.section4.architect': 'Architect',
    'terms.section4.architectTrial': 'トライアルなし。デモとカスタム価格については営業にお問い合わせください。',
    'terms.section5.title': '5. 招待制アクセス',
    'terms.section5.intro': '一部のプランには招待が必要です：',
    'terms.section5.item1': 'オーナー（Sovereignプラン）はコミュニティ管理者から招待される必要があります',
    'terms.section5.item2': '不動産開発者（Architectプラン）は営業相談後にAetherスタッフから招待される必要があります',
    'terms.section5.item3': '招待は譲渡不可で、特定の受信者のみを対象としています',
    'terms.section6.title': '6. ユーザーアカウント',
    'terms.section6.intro': 'サービスの特定の機能にアクセスするには、アカウントを作成する必要があります。以下に同意するものとします：',
    'terms.section6.item1': '正確で最新の完全な情報を提供する',
    'terms.section6.item2': 'アカウント情報を維持し、速やかに更新する',
    'terms.section6.item3': 'パスワードとアカウントのセキュリティを維持する',
    'terms.section6.item4': 'アカウント下のすべての活動に責任を負う',
    'terms.section6.item5': '不正使用があった場合は直ちに通知する',
    'terms.section7.title': '7. ロールベースのアクセス制御',
    'terms.section7.intro': 'Aetherは権限を管理するためにロールベースのアクセスを使用しています：',
    'terms.section7.admin': 'Admin（コミュニティマネージャー）',
    'terms.section7.adminDesc': '物件、オーナー、ベンダー、コミュニティ設定を管理するフルアクセス',
    'terms.section7.sovereign': 'Sovereign（物件オーナー）',
    'terms.section7.sovereignDesc': '所有物件、HOA支払い、賃貸管理、コンシェルジュサービスへのアクセス',
    'terms.section7.demiUser': 'Demi-User（ゲスト）',
    'terms.section7.demiUserDesc': 'マップ、基本サービス、ホストとのコミュニケーションへの時間制限付きアクセス',
    'terms.section8.title': '8. 許容される使用',
    'terms.section8.intro': '以下を行わないことに同意するものとします：',
    'terms.section8.item1': '違法な目的でサービスを使用する',
    'terms.section8.item2': 'サービスの任意の部分への不正アクセスを試みる',
    'terms.section8.item3': 'サービスまたはサーバーに干渉または妨害する',
    'terms.section8.item4': '悪意のあるコードまたはコンテンツをアップロードする',
    'terms.section8.item5': '人物またはエンティティになりすます',
    'terms.section8.item6': '適用される法律または規制に違反する',
    'terms.section9.title': '9. 請求とキャンセル',
    'terms.section9.content': 'サブスクリプション料金は月次または年次で前払いで請求されます。法律で義務付けられている場合を除き、すべての料金は返金不可です。設定のStripe Customer Portalからいつでもサブスクリプションをキャンセルできます。アクセスは請求期間の終了まで継続します。',
    'terms.section10.title': '10. 責任の制限',
    'terms.section10.content': '法律で許可される最大限の範囲において、Aetherは、サービスの使用または使用不能から生じる間接的、付随的、特別、結果的、または懲罰的損害について責任を負いません。',
    'terms.section11.title': '11. お問い合わせ',
    'terms.section11.content': 'これらの規約についてご質問がある場合は、以下までお問い合わせください：',
    
    // Support FAQ (Japanese - Updated)
    'support.faq1Question': 'Aetherにはどのように登録しますか？',
    'support.faq1Answer': 'ゲストは当社ウェブサイトで直接登録し、15日間の無料トライアルを利用できます。オーナーは参加するためにコミュニティ管理者からの招待が必要です（30日間トライアル）。不動産開発者は当社の営業チームにお問い合わせください。',
    'support.faq2Question': '各サブスクリプションプランには何が含まれていますか？',
    'support.faq2Answer': 'Explorer（$9.99/月）は旅行者向け - 賃貸を予約、AIコンシェルジュを使用。Sovereign（$19.99/月）はオーナー向け - 物件を掲載、ゲストを管理。Architect（カスタム価格）はコミュニティを管理する開発者向け。',
    'support.faq3Question': '無料トライアルはどのように機能しますか？',
    'support.faq3Answer': 'Explorerプラン：15日間無料トライアル、クレジットカード不要。Sovereignプラン：コミュニティ招待を受けた後30日間無料トライアル。Architectプラン：トライアルなし - デモとカスタム価格については営業にお問い合わせください。',
    'support.faq4Question': 'オーナーとして招待を受けるにはどうすればいいですか？',
    'support.faq4Answer': 'コミュニティ管理者または不動産開発者に連絡し、Aetherを通じて招待を送ってもらうよう依頼してください。アカウント設定の手順が記載されたメールが届きます。',
    'support.faq5Question': 'Aetherで不動産開発者になるにはどうすればいいですか？',
    'support.faq5Answer': 'サポートページまたはsales@aether.appへのメールで当社の営業チームにお問い合わせください。ポートフォリオのサイズに基づいたカスタム価格でエンタープライズオンボーディングを提供しています。',
    'support.faq6Question': 'サブスクリプションをキャンセルまたは変更するにはどうすればいいですか？',
    'support.faq6Answer': '設定 > サブスクリプションに移動し、「サブスクリプション管理」をクリックしてStripe Customer Portalにアクセスします。いつでもキャンセルできます - アクセスは請求期間の終了まで継続します。',
  },
};

interface I18nContextValue {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  languages: LanguageInfo[];
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

// Helper function to detect browser language
function detectBrowserLanguage(): SupportedLanguage {
  const browserLang = navigator.language || (navigator as any).userLanguage || 'en';
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  // Map browser language codes to our supported languages
  const langMap: Record<string, SupportedLanguage> = {
    'en': 'en',
    'zh': 'zh',
    'my': 'my',
    'th': 'th',
    'ja': 'ja',
  };
  
  return langMap[langCode] || 'en';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    const saved = localStorage.getItem('aether-language');
    if (saved && ['en', 'zh', 'my', 'th', 'ja'].includes(saved)) {
      return saved as SupportedLanguage;
    }
    // Check if this is first visit (no language preference saved)
    const hasVisited = localStorage.getItem('aether-has-visited');
    if (!hasVisited) {
      // First visit - detect browser language
      const detectedLang = detectBrowserLanguage();
      localStorage.setItem('aether-has-visited', 'true');
      return detectedLang;
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('aether-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    setLanguageState(lang);
  }, []);

  const t = useCallback((key: string, vars?: Record<string, string | number>): string => {
    let translation = translations[language][key] || translations.en[key] || key;
    
    if (vars) {
      Object.entries(vars).forEach(([varKey, value]) => {
        translation = translation.replace(new RegExp(`{{${varKey}}}`, 'g'), String(value));
      });
    }
    
    return translation;
  }, [language]);

  return (
    <I18nContext.Provider value={{ language, setLanguage, t, languages: SUPPORTED_LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
}
